import { sql, relations } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, integer, bigint, boolean, decimal, serial, real, jsonb, uniqueIndex, index, primaryKey, customType, date, smallint, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Re-exports hoisted to the top alongside imports. Keeping them adjacent to
// the const declarations below would mix module-init (import-like) statements
// with regular code, which Rollup can reorder into a production TDZ crash.
// See script/check-imports-first.ts.
export * from "./models/auth";
// Task #705 — Ads Manager Phase 1 schema (in its own file to keep this one tractable).
export * from "./ads-schema";

// Postgres tsvector type — used for full-text search on knowledge_articles.
// The actual column is created/maintained by ensureKnowledgeSearchIndex()
// at runtime; we only declare it here so Drizzle won't try to drop it on push.
const tsvector = customType<{ data: string }>({
  dataType() {
    return "tsvector";
  },
});


// Workspace tier types
export type WorkspaceTier = "lite" | "pro" | "business" | "enterprise" | "enterprise_plus";

// Workspace type discriminator — Task #102.
// 'tenant' = a normal Clarik customer workspace (entertainment booking
//   modules: leads/clients/shows/schedule/talent/etc).
// 'saas'   = the Clarik-the-company workspace itself, used to run our own
//   sales/billing funnel. Entertainment-only modules are hidden in the UI
//   and skipped by per-workspace schedulers; new modules
//   (/customers, /subscription-invoices) appear instead.
export type WorkspaceType = "tenant" | "saas";

// Workspaces (tenants) - currently only one exists, but schema supports multi-tenant
export const workspaces = pgTable("workspaces", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: varchar("slug", { length: 100 }).notNull().unique(), // URL-safe identifier
  name: varchar("name", { length: 255 }).notNull(),
  tier: varchar("tier", { length: 20 }).notNull().default("lite").$type<WorkspaceTier>(), // Platform tier
  isActive: boolean("is_active").notNull().default(true), // Platform can deactivate
  isLocked: boolean("is_locked").notNull().default(false), // Instant lock by platform
  stripeCustomerId: varchar("stripe_customer_id", { length: 255 }),
  stripeSubscriptionId: varchar("stripe_subscription_id", { length: 255 }),
  billingCycle: varchar("billing_cycle", { length: 20 }).default("monthly"), // 'monthly' | 'annual'
  // Billing enforcement (platform-charged plan fee). Source of truth for whether
  // a workspace is paid up. 'active' = paid or no charge due; 'past_due' = a
  // plan charge is owed and the grace clock is running (see billingDueSince);
  // 'suspended' = grace lapsed (>14d unpaid) and member logins are blocked until
  // payment processes (workspace DATA stays intact); 'comped' = a genuine $0
  // price override means this workspace is intentionally free and never charged.
  billingStatus: varchar("billing_status", { length: 20 }).notNull().default("active"),
  // When the current unpaid plan charge first became due. The 14-day grace
  // window is measured from here. NULL when the workspace is paid / comped.
  billingDueSince: timestamp("billing_due_since"),
  // Super Admin manual price overrides (highest priority). NULL = no override.
  priceOverrideMonthly: decimal("price_override_monthly", { precision: 10, scale: 2 }),
  priceOverrideYearly: decimal("price_override_yearly", { precision: 10, scale: 2 }),
  // Locked-in price at time of subscription. Protects existing customers from tier price changes.
  subscribedPriceMonthly: decimal("subscribed_price_monthly", { precision: 10, scale: 2 }),
  subscribedPriceYearly: decimal("subscribed_price_yearly", { precision: 10, scale: 2 }),
  emailFallbackEnabled: boolean("email_fallback_enabled").notNull().default(false), // Allow workspace connector fallback when user Gmail fails
  // Task #699 — When emailFallbackEnabled is true, the fallback retry is sent
  // through THIS workspace member's connected Gmail (must have OAuth tokens).
  // NULL means "no fallback sender configured" — treated identically to
  // emailFallbackEnabled=false (returns the existing "needs reconnect" error).
  // We deliberately do NOT add a FK to users.id with cascade because users
  // can move between workspaces; the lookup at send-time validates membership
  // + token presence, and a missing user just degrades to needs-reconnect.
  fallbackEmailUserId: varchar("fallback_email_user_id", { length: 255 }),
  // Lite tier: card-on-file Stripe payment method ID (capture via SetupIntent).
  // Presence indicates the workspace has a card on file for monthly revenue-share billing.
  litePaymentMethodId: varchar("lite_payment_method_id", { length: 255 }),
  // Lite tier: when the user dismissed the upsell banner. Used for 7-day suppression.
  liteUpsellDismissedAt: timestamp("lite_upsell_dismissed_at"),
  // Lead Gen Credits — split into two pools so monthly grant is "use it or lose it"
  // while purchased credits roll over indefinitely. Total available = grant + purchased.
  // Spend draws from grant first, then purchased.
  leadCreditGrantBalance: integer("lead_credit_grant_balance").notNull().default(0),
  leadCreditPurchasedBalance: integer("lead_credit_purchased_balance").notNull().default(0),
  // Lead Gen — tag results with compass quadrant (NE/NW/SE/SW) relative to the search center.
  // When enabled, the tag is computed on every search result and persisted onto the imported lead.
  leadGenQuadrantTagsEnabled: boolean("lead_gen_quadrant_tags_enabled").notNull().default(true),
  // Workspace-default hidden columns in the Leads spreadsheet. Applied to every
  // member who hasn't customized their own (userProfiles.leadColumnsCustomized=false).
  // Empty = show all columns.
  defaultHiddenLeadColumns: text("default_hidden_lead_columns").array().notNull().default(sql`'{}'::text[]`),
  // Lead Gen AI search (Claude-backed advanced lead discovery + enrichment + Apollo).
  // Toggles below let the admin scope which AI features the workspace can use.
  leadGenAiEnabled: boolean("lead_gen_ai_enabled").notNull().default(true),
  leadGenAllowUnverified: boolean("lead_gen_allow_unverified").notNull().default(true),
  leadGenEmailFinderEnabled: boolean("lead_gen_email_finder_enabled").notNull().default(true),
  leadGenApolloEnabled: boolean("lead_gen_apollo_enabled").notNull().default(false),
  leadGenRecurringEnabled: boolean("lead_gen_recurring_enabled").notNull().default(true),
  leadGenAiResultCap: integer("lead_gen_ai_result_cap"),
  leadGenAiDefaultModel: varchar("lead_gen_ai_default_model", { length: 64 }).notNull().default("claude-haiku-4-5"),
  // Apollo.io API key for LinkedIn-style person/contact enrichment. Stored at the
  // workspace level (admin-set in lead gen settings). Apollo bills the workspace
  // owner directly; we charge a small wrapper credit per lookup as a throttling gate.
  apolloApiKey: text("apollo_api_key"),
  // Social Media module — workspace-level configuration.
  // storageMode: 'drive' = collaborator's Google Drive (free), 'hosted' = our object storage (counts toward quota).
  socialMediaStorageMode: varchar("social_media_storage_mode", { length: 20 }).notNull().default("drive"),
  // Optional override of the plan-tier default storage limit (GB). NULL = use plan default from plan_tier_features.
  socialMediaStorageGbLimit: integer("social_media_storage_gb_limit"),
  // Stripe-purchased extra storage (GB), additive to plan grant. Mirrors the lead-credit two-bucket model.
  socialMediaPurchasedStorageGb: integer("social_media_purchased_storage_gb").notNull().default(0),
  // When true, posts drafted/scheduled by external collaborators (Editor or Manager level) require an internal
  // user's approval before they go live. Always required for Viewer/Commenter/Contributor levels regardless of this flag.
  socialMediaRequireApprovalForExternalPublish: boolean("social_media_require_approval_for_external_publish").notNull().default(true),
  // White-label the external collaborator portal with this workspace's branding. Visibility/availability is gated
  // by the plan tier's `socialAllowsCollaboratorWhitelabel` value in plan_tier_features.
  socialMediaCollaboratorBrandingEnabled: boolean("social_media_collaborator_branding_enabled").notNull().default(false),
  // Security Training Enforcement — when enabled, any internal user (not external collaborators) who has not
  // acknowledged every slug in securityTrainingRequiredSlugs is blocked from the rest of the app by a full-screen
  // gate until they read and acknowledge each required article. Defaults to OFF — most workspaces will not need
  // this layer, but admins of regulated/enterprise tenants can flip it on selectively.
  securityTrainingEnforced: boolean("security_training_enforced").notNull().default(false),
  // The specific Knowledge Center article slugs (in section='security-training') that must be acknowledged when
  // securityTrainingEnforced=true. Empty array = enforcement is on but nothing is required (no-op gate).
  securityTrainingRequiredSlugs: text("security_training_required_slugs").array().notNull().default(sql`'{}'::text[]`),
  // Solo-team mode: when true AND ≤3 active users, hide region routing, sales-rep
  // assignment, and other multi-rep org features in the UI. Vocabulary is unchanged.
  // Toggled by workspace admins; super-admin can override. Wave 1 ships the flag +
  // schema; the UI gates land alongside the role-permissions sweep.
  isSoloMode: boolean("is_solo_mode").notNull().default(false),
  // When true, the artist/talent voice role cannot hear the client-facing
  // event rate (agencies that don't want talent knowing client pay).
  // Default false = talent CAN see the rate.
  hideRateFromArtist: boolean("hide_rate_from_artist").notNull().default(false),
  // ── Ads Manager Phase 3 (Task #707) ─────────────────────────────
  // Workspace-level default attribution model for the /marketing/ads
  // Performance dashboard. Per-request `?model=` overrides this, but
  // when omitted the dashboard + CSV export use this value. Values:
  // 'first' | 'last' | 'linear'. Default 'last' matches industry norms.
  adsAttributionDefault: varchar("ads_attribution_default", { length: 16 }).notNull().default("last"),
  // ── White-label (Wave 2) ────────────────────────────────────────────
  // Full hostname like "bookings.acme.com" — agency points a CNAME at our
  // platform CNAME target and we resolve the workspace from this column.
  // Lower-cased on write so lookups can be exact-match.
  customDomain: varchar("custom_domain", { length: 255 }).unique(),
  // Set when the CNAME has been verified out-of-band; until set, the agency
  // can save the value but public links won't switch over.
  customDomainVerifiedAt: timestamp("custom_domain_verified_at"),
  // Wave 3 — automated DNS verification challenge token. Workspaces can prove
  // ownership by either pointing the domain's CNAME to our public CNAME target
  // OR placing this token at `_entertaincrm.<domain>` as a TXT record.
  customDomainChallengeToken: varchar("custom_domain_challenge_token", { length: 64 }),
  // Overrides the workspace name in outbound `From: "Name <addr>"` headers
  // and the public portal/sign page header.
  customEmailFromName: varchar("custom_email_from_name", { length: 255 }),
  // Reply-To address used on outbound emails. Useful when Gmail isn't
  // connected and the platform sends from a generic address.
  customEmailReplyTo: varchar("custom_email_reply_to", { length: 255 }),
  // Free-form key/value overrides for white-label strings ("Sign documents",
  // "Pay invoice", agency tagline). Schema is open: { [key: string]: string }.
  brandingStrings: jsonb("branding_strings"),
  // Support contact rendered on the locked / inactive workspace error
  // pages (Task #138). White-label workspaces on their own custom
  // domain / brand override this so a Reseller's user sees the right
  // brand to email instead of being told to contact Clarik
  // directly. NULL = fall back to the platform default.
  supportEmail: varchar("support_email", { length: 255 }),
  // The kind of entertainer this workspace IS (Speaker, Comedian, DJ,
  // Musician, etc.) — mirrors platform_business_types.name. Captured during
  // onboarding (skipped when already known) and editable afterward only by a
  // super admin from the customer record. The canonical source the AI agents
  // read so they know what business they're operating inside. NULL = not set.
  businessType: varchar("business_type", { length: 100 }),
  // Task #560 — "Powered by Clarik" attribution toggle.
  // Lite tier: forced TRUE (server enforces; PATCH from a Lite admin
  // returns 403). Pro / Business / Enterprise: defaults FALSE the first
  // time the workspace transitions into a paid tier (so paying customers
  // start cleanly white-labelled), then admin-editable. Surfaces that
  // honour this flag: public booking page, public client portal, public
  // invoice viewer, public knowledge mirror, invoice PDF footer, and the
  // auto-appended footer on outbound client-facing emails.
  showPoweredByAttribution: boolean("show_powered_by_attribution").notNull().default(true),
  // Companion flag for the "first transition into paid tier" rule above.
  // Set to TRUE the first time an admin saves the toggle on a paid tier
  // (or the first time the platform records a non-Lite default). Once
  // true, future Lite ↔ paid transitions never re-default the field
  // back to false — only the explicit Lite-tier "force on" rule fires.
  attributionPreferenceSet: boolean("attribution_preference_set").notNull().default(false),
  // ── Free + 3% billing engine (Wave 2) ───────────────────────────────
  // Per-workspace override of the platform-wide `lite_revenue_share_percent`.
  // NULL = use platform default. Stored as decimal so super-admin can grant
  // discounts (e.g. 2.5%) per partner.
  litePlatformFeePercent: decimal("lite_platform_fee_percent", { precision: 5, scale: 2 }),
  // ── Wave 8 — AI assist (per-workspace controls) ────────────────────
  // Master toggle. When false the AI gateway returns 403 and the "Draft with AI"
  // affordances hide across the app. Defaults true so workspaces opt-in by tier.
  aiEnabled: boolean("ai_enabled").notNull().default(true),
  // Hard cap (in cents) on overage spend per calendar month above the gifted
  // monthly allowance. NULL = unlimited (admin opted-in to no cap). 0 = no
  // overage allowed. Defaults to 0 so a workspace cannot bill itself by
  // accident.
  aiOverageCapCents: integer("ai_overage_cap_cents").default(0),
  // Super-admin override of the gifted monthly credit allowance for this
  // workspace. NULL = use the per-tier default in plan_tier_features.extraConfig.aiMonthlyGiftedCents.
  aiMonthlyGiftedOverrideCents: integer("ai_monthly_gifted_override_cents"),
  // Per-feature AI toggle: "Catch me up" 3-bullet activity-history summary
  // surfaced on lead/client activity timelines. Defaults true so workspaces
  // with `aiEnabled` get the feature out of the box; admins can switch this
  // off independently of the master AI toggle.
  aiCatchMeUpEnabled: boolean("ai_catch_me_up_enabled").notNull().default(true),
  // ── Client portal master switch ─────────────────────────────────────
  // When false the entire token-gated /portal/:token surface is disabled
  // for this workspace. The "Send portal link" affordance hides on the
  // client record, the public bootstrap returns 410, and inbound portal
  // messages are rejected. Existing portal session rows are preserved
  // (not destroyed) so flipping the switch back on restores access
  // without re-issuing links. To explicitly destroy all outstanding
  // tokens, admins use the "Revoke all past portal links" action in
  // Admin → Client Portal. Defaults true so workspaces opt-in by tier.
  clientPortalEnabled: boolean("client_portal_enabled").notNull().default(true),
  // ── Task #601 — AI Reply Management (per-workspace controls) ───────
  // Master switch for the AI Reply Management feature: inbound triage
  // (auto-categorization) + AI-drafted replies in the inbox. When false
  // the categorize/draft pipelines never run and the inbox UI hides the
  // category chips, draft cards, and Priority Queue. Existing reply
  // workflow (rep types reply, hits Send) is unaffected.
  aiReplyManagementEnabled: boolean("ai_reply_management_enabled").notNull().default(true),
  // Sub-toggle: AI auto-categorization of inbound messages into one of
  // the seven labels (logistics_question, negotiation, objection,
  // scheduling, confirmation, spam, other). Defaults true so admins can
  // see the categorize labels even if they keep AI drafts off.
  aiReplyCategorizationEnabled: boolean("ai_reply_categorization_enabled").notNull().default(true),
  // Sub-toggle: AI draft-reply generation. When off, drafts are never
  // created automatically (rep can still hand-type their reply). The
  // master `aiReplyManagementEnabled` switch overrides this — both must
  // be true for drafts to render.
  aiReplyDraftEnabled: boolean("ai_reply_draft_enabled").notNull().default(true),
  // ── Task #244 — Gmail restricted-scope (gmail.readonly) gating ───────
  // Google's restricted `gmail.readonly` scope requires an expensive annual
  // CASA security assessment and counts against a 100-user LIFETIME cap.
  // We default this OFF for EVERY workspace so no user can reach the read
  // OAuth flow without an explicit super-admin approval. When false: the
  // inbound poller skips this workspace's users, the Unified Inbox shows a
  // coming-soon page, drip pause-on-reply is disabled, and mailbox backfill
  // is greyed out. Send-only features (outbound + NeverBounce + opt-out) are
  // unaffected. Flipped on (per workspace) once a super-admin approves a
  // read seat for at least one of its users.
  gmailReadEnabled: boolean("gmail_read_enabled").notNull().default(false),
  // Per-workspace allowance of restricted read SEATS. Seats are counted per
  // connecting USER (each user who completes the read OAuth flow consumes one
  // permanent Google lifetime slot). A workspace can hold N approved seats;
  // this is the soft cap the approval gate checks before allocating another
  // grant. Default 0 = no seats until a super-admin grants them.
  gmailReadSeatAllowance: integer("gmail_read_seat_allowance").notNull().default(0),
  // Tier perk (Task #244 step 7): manual single-address NeverBounce check in
  // the compose / lead UI. Super-admin toggles this on for paid tiers
  // ($129 tier + top agency tier). Background NeverBounce on mass/drip is
  // ALWAYS on regardless of this flag; this only governs the manual button.
  neverbounceManualEnabled: boolean("neverbounce_manual_enabled").notNull().default(false),
  // ── Wave 9 — AI image generation (per-workspace controls) ──────────
  // Master switch for AI image generation (Nano Banana / Runway). Independent
  // of `aiEnabled` so an admin can keep text drafting on while turning images
  // off. Defaults true so the feature is opt-out per workspace.
  aiImageEnabled: boolean("ai_image_enabled").notNull().default(true),
  // Per-provider toggles. Defaults true; admin can flip either off so the
  // generator dialog only offers the cheaper / sanctioned provider.
  aiImageNanoBananaEnabled: boolean("ai_image_nano_banana_enabled").notNull().default(true),
  aiImageRunwayEnabled: boolean("ai_image_runway_enabled").notNull().default(true),
  // Per-user per-day cap on image generations (counted at gateway time, before
  // the provider call). NULL = unlimited. 0 = effectively disabled (every call
  // rejected). Workspace overage cap and credit ledger still apply on top.
  aiImageMaxPerUserPerDay: integer("ai_image_max_per_user_per_day"),
  // ── Task #25 — AI Life Coach workspace master toggle ────────────────
  // When false, coachEnabled is forced off for all users regardless of
  // their per-user coach_user_settings.enabled value. Default true so
  // existing workspaces keep the feature available for users who opted in.
  aiCoachEnabled: boolean("ai_coach_enabled").notNull().default(true),
  // Task #274 — workspace-level floor for coachSchedulingMode. See COACH_PERSONAL_BLOCK_POLICIES.
  // The stricter of this and the individual user's setting applies.
  coachPersonalBlockPolicy: varchar("coach_personal_block_policy", { length: 16 }).notNull().default("avoid"),
  // ── Browser dialer (Wave 2 — narrow scope foundations) ──────────────
  // Master switch. When false, no dialer routes serve and the click-to-dial
  // affordance hides. The "calls" table is still queryable for historical
  // logs even when the dialer is later disabled.
  dialerEnabled: boolean("dialer_enabled").notNull().default(false),
  // Task #205 — Sponsor Hub (Marketing section). Off by default; toggled by platform admins.
  sponsorHubEnabled: boolean("sponsor_hub_enabled").notNull().default(false),
  // Recording mode controls whether outbound calls are recorded:
  //  off       — never record
  //  all       — record every call (single-party-consent states only — admin opts in knowing the legal trade-off)
  //  smart     — record by default, skip calls into 11 two-party-consent states (CA, CT, FL, IL, MD, MA, MT, NV, NH, PA, WA) detected by area code
  //  opt_in    — only record when the user clicks "record" mid-call
  //  opt_out   — record by default, user can stop mid-call
  dialerRecordingMode: varchar("dialer_recording_mode", { length: 16 }).notNull().default("off"),
  // When true, reps cannot override the workspace's dialerRecordingMode in their personal settings.
  dialerRecordingModeLocked: boolean("dialer_recording_mode_locked").notNull().default(false),
  // E.164 number purchased on Twilio for outbound caller-ID. NULL until set.
  dialerCallerIdNumber: varchar("dialer_caller_id_number", { length: 32 }),
  // ── Security suite (Phase 1) ────────────────────────────────────────
  // MFA enforcement scope. Both default false so existing workspaces are
  // not surprised. When `mfaRequiredForAll` is true, every internal user
  // must enroll within `mfaGracePeriodDays`; admins always counted.
  mfaRequiredForAdmins: boolean("mfa_required_for_admins").notNull().default(false),
  mfaRequiredForAll: boolean("mfa_required_for_all").notNull().default(false),
  // Allow-list of factor codes the workspace permits: any subset of
  // ['totp','email','sms','passkey']. Empty/NULL = all four allowed.
  mfaAllowedMethodsJson: jsonb("mfa_allowed_methods_json"),
  // Minimum strength bucket the admin requires:
  //   'any'              — any enrolled factor (default)
  //   'no_sms'           — block SMS-only enrolment (SMS may be a backup
  //                        but at least one stronger factor required)
  //   'passkey_or_totp'  — only passkey or TOTP counts as primary factor
  mfaMinimumStrength: varchar("mfa_minimum_strength", { length: 24 }).notNull().default("any"),
  // Days a user has after policy turns on (or after they're invited) to
  // finish MFA enrolment before they're blocked from the app.
  mfaGracePeriodDays: integer("mfa_grace_period_days").notNull().default(14),
  // Session timeouts. 480 min = 8 h idle for regular users, 30 min for
  // admins; absolute cap is 7 days regardless. All overridable here.
  sessionIdleTimeoutMinutes: integer("session_idle_timeout_minutes").notNull().default(480),
  sessionAdminIdleTimeoutMinutes: integer("session_admin_idle_timeout_minutes").notNull().default(30),
  sessionAbsoluteMaxHours: integer("session_absolute_max_hours").notNull().default(168),
  // IP allow-list. Master switch + list of CIDRs (IPv4 or IPv6).
  // Loopback / RFC1918 are still allowed during local dev to avoid lockout.
  ipAllowlistEnabled: boolean("ip_allowlist_enabled").notNull().default(false),
  ipAllowlistCidrs: text("ip_allowlist_cidrs").array().default(sql`ARRAY[]::text[]`),
  // SSO — Phase 2 will wire SAML/OIDC; Phase 1 ships the toggle so the
  // admin UI can render and the storage round-trip is in place.
  ssoEnabled: boolean("sso_enabled").notNull().default(false),
  ssoProvider: varchar("sso_provider", { length: 32 }), // 'saml' | 'oidc' | 'google-workspace' | 'okta' | 'azure-ad'
  ssoMetadataXml: text("sso_metadata_xml"),
  ssoEntityId: varchar("sso_entity_id", { length: 255 }),
  ssoSsoUrl: varchar("sso_sso_url", { length: 512 }),
  ssoCertFingerprint: varchar("sso_cert_fingerprint", { length: 128 }),
  // ── Lead Qualification Engine (Wave 9) ────────────────────────────
  // Master toggle for the AI lead qualification system. When false, the live,
  // batch, and nightly qualification modes are all skipped. Existing scores
  // remain frozen.
  qualificationEnabled: boolean("qualification_enabled").notNull().default(false),
  // Cost in lead credits per qualification run. Defaults to 2 — same as a
  // verified AI discovery row — so admins can reason about price uniformly.
  qualificationCreditsPerRun: integer("qualification_credits_per_run").notNull().default(2),
  // Per-mode opt-in controls. Live runs qualification when a lead is saved
  // from the AI lead-gen sandbox; nightly is the catch-up cron.
  qualificationLiveEnabled: boolean("qualification_live_enabled").notNull().default(true),
  qualificationNightlyEnabled: boolean("qualification_nightly_enabled").notNull().default(true),
  // Bookmark for the nightly cron — only leads created/touched after this
  // timestamp (and still without a score) are picked up.
  qualificationLastNightlyAt: timestamp("qualification_last_nightly_at"),
  // ── AI Lead Catcher (Task #599) ────────────────────────────────
  // Master kill switch for the inbound AI first-responder. When false, no
  // channel adapter dispatches to the engine and the public web widget
  // bootstrap returns 410. Per-channel toggles below let admins ship the
  // feature on, say, the website only.
  aiLeadCatcherEnabled: boolean("ai_lead_catcher_enabled").notNull().default(false),
  aiLeadCatcherWebEnabled: boolean("ai_lead_catcher_web_enabled").notNull().default(true),
  aiLeadCatcherSmsEnabled: boolean("ai_lead_catcher_sms_enabled").notNull().default(false),
  aiLeadCatcherMissedCallEnabled: boolean("ai_lead_catcher_missed_call_enabled").notNull().default(false),
  aiLeadCatcherInstagramEnabled: boolean("ai_lead_catcher_instagram_enabled").notNull().default(false),
  aiLeadCatcherMessengerEnabled: boolean("ai_lead_catcher_messenger_enabled").notNull().default(false),
  aiLeadCatcherFormEnabled: boolean("ai_lead_catcher_form_enabled").notNull().default(false),
  // Per-day cap on full conversations (start → handoff or close) the engine
  // will accept across all channels for this workspace. Defaults 500; 0
  // disables the engine for the rest of the day.
  aiLeadCatcherMaxConversationsPerDay: integer("ai_lead_catcher_max_conversations_per_day").notNull().default(500),
  // Per-conversation token budget. Hard ceiling so a single chatty
  // prospect can't burn unlimited credits. Default 4000 input+output.
  aiLeadCatcherMaxTokensPerConversation: integer("ai_lead_catcher_max_tokens_per_conversation").notNull().default(4000),
  // Public widget key embedded in the JS snippet. Issued once on enable;
  // workspace admins can rotate it from Admin → AI Lead Catcher.
  aiLeadCatcherWebWidgetKey: varchar("ai_lead_catcher_web_widget_key", { length: 64 }),
  // Free-form business / persona / off-limits intro the engine prepends to
  // every system prompt (e.g. "You're answering for Warehouse Productions,
  // an entertainment booking agency. Don't quote prices.")
  aiLeadCatcherPersona: text("ai_lead_catcher_persona"),
  // Ordered list of qualifying questions the engine should try to extract.
  // Stored as JSON: [{ key: 'eventType', label: 'What kind of event?',
  // required: true }, ...]. Email + date are always implicitly required.
  aiLeadCatcherQuestionsJson: jsonb("ai_lead_catcher_questions_json"),
  // Display name used in confirmation templates (rep emails to prospects,
  // SMS confirms, etc). Falls back to `workspaces.name` when null.
  aiLeadCatcherAgencyName: varchar("ai_lead_catcher_agency_name", { length: 120 }),
  // Configurable wording for the "we'll follow up …" promise in every
  // confirmation template. Default "within the hour" matches the spec
  // but workspaces with different SLAs can change it (e.g. "within one
  // business day").
  aiLeadCatcherFollowUpWording: varchar("ai_lead_catcher_follow_up_wording", { length: 120 }),
  // ── Multi-show Runs + Multi-Talent (Wave 10) ────────────────────
  // When true, the booking dialog exposes the "Run" sub-section so a
  // booking can belong to a parent multi-show run with its own contract,
  // cancellation policy, and optional run-level deposits. When false the
  // run entities still exist in the schema but are hidden from the UI.
  // Defaults false for new workspaces; backfill flips existing workspaces
  // to true so they don't lose features they were already using.
  runsEnabled: boolean("runs_enabled").notNull().default(false),
  // When true, a single booking can have multiple talent attached via the
  // bookingTalent junction with per-talent role + payout. When false the
  // booking is single-talent (legacy behavior).
  multiTalentEnabled: boolean("multi_talent_enabled").notNull().default(false),
  // Task #586 — Hidden Agency Dashboard with per-talent Roster Health Score.
  // Defaults false on every workspace; flipping it on requires a multi-talent
  // workspace and is only honored for users with the `admin` role. The route
  // returns 404 (not 403) when the gate fails so the feature stays invisible.
  agencyDashboardEnabled: boolean("agency_dashboard_enabled").notNull().default(false),
  // Per-workspace weights for the four roster-health sub-scores. Stored as a
  // jsonb object so future scores can be added without another migration.
  // The runtime normalizes to sum-100 before applying, so admins can edit
  // individual sliders without doing the math.
  rosterHealthWeights: jsonb("roster_health_weights").notNull().default(sql`'{"velocity":25,"churn":25,"payment":25,"social":25}'::jsonb`),
  // Who pays for AI assistant credits:
  //   workspace — workspace pays everything (default, current behavior)
  //   talent    — each talent funds their own credits via Talent Billing
  //   hybrid    — workspace covers a per-talent monthly subsidy, overage to talent
  aiCostsPaidBy: varchar("ai_costs_paid_by", { length: 16 }).notNull().default("workspace"),
  // Hybrid mode: monthly per-talent subsidy in cents. Talent pays overage.
  aiHybridSubsidyCentsPerTalent: integer("ai_hybrid_subsidy_cents_per_talent"),
  // Platform-gifted AI credit balance in cents. Super-admin can top up from
  // the platform workspaces panel. Workspace AI features may draw from this
  // pool when aiCostsPaidBy is 'workspace'.
  aiCreditsBalanceCents: integer("ai_credits_balance_cents").notNull().default(0),
  // ── AI Credit Wallet V2 (two-bucket, fixed-point microcredits) ───────────
  // 1 credit = $0.10 (CREDIT_UNIT_USD). Stored as microcredits (1 credit =
  // 1_000_000 µcr) so token-level sub-cent costs never need rounding. Two
  // buckets mirror lead credits: GRANT (monthly, use-it-or-lose-it) and
  // PURCHASED (paid/rollover). Source of truth going forward; the legacy
  // aiCreditsBalanceCents column above is dual-written during migration and
  // retired after cutover. See server/lib/ai-credit-wallet.ts.
  aiCreditGrantMicrocredits: bigint("ai_credit_grant_microcredits", { mode: "number" }).notNull().default(0),
  aiCreditPurchasedMicrocredits: bigint("ai_credit_purchased_microcredits", { mode: "number" }).notNull().default(0),
  // ── AI Credit Distribution ──────────────────────────────────────────────
  // How credits are allocated across members:
  //   shared            — all users draw from workspace pool (default)
  //   per_user_monthly  — each user gets a monthly allotment
  //   per_user_allocated — admin assigns a fixed total per user
  aiCreditMode: varchar("ai_credit_mode", { length: 32 }).notNull().default("shared"),
  // Who purchases AI credits: 'workspace' (admin buys) or 'user' (each user buys own)
  aiCreditPayer: varchar("ai_credit_payer", { length: 16 }).notNull().default("workspace"),
  // Unused monthly allotment rolls over (capped at allotment × capMultiplier)
  aiCreditRolloverEnabled: boolean("ai_credit_rollover_enabled").notNull().default(true),
  // Max rollover as a multiple of monthly allotment (e.g. 3.0 = max 3 months stored)
  aiCreditRolloverCapMultiplier: real("ai_credit_rollover_cap_multiplier").notNull().default(3.0),
  // What to do when per-user allotment runs out:
  //   hard_stop — block the action
  //   fallback_shared — draw from workspace pool
  //   allow_negative — allow up to aiCreditNegativeLimitCents below zero
  aiCreditOveragePolicy: varchar("ai_credit_overage_policy", { length: 20 }).notNull().default("hard_stop"),
  // Max negative balance (cents) when overagePolicy='allow_negative'. Default $500.
  aiCreditNegativeLimitCents: integer("ai_credit_negative_limit_cents").notNull().default(50000),
  // Monthly allotment per role in cents. {} = use workspace pool for that role.
  // e.g. {"admin":0,"sales_rep":2000,"artist":500,"billing":500,"marketing":1000}
  // 0 = unlimited. Absent key = inherit workspace default (falls back to shared pool).
  aiCreditDefaultByRole: jsonb("ai_credit_default_by_role").notNull().default(sql`'{}'::jsonb`),
  // ── Lead Credit Distribution ─────────────────────────────────────────────
  leadCreditMode: varchar("lead_credit_mode", { length: 32 }).notNull().default("shared"),
  leadCreditPayer: varchar("lead_credit_payer", { length: 16 }).notNull().default("workspace"),
  leadCreditRolloverEnabled: boolean("lead_credit_rollover_enabled").notNull().default(true),
  leadCreditRolloverCapMultiplier: real("lead_credit_rollover_cap_multiplier").notNull().default(3.0),
  leadCreditOveragePolicy: varchar("lead_credit_overage_policy", { length: 20 }).notNull().default("hard_stop"),
  // Max negative balance (count) when overagePolicy='allow_negative'. Default 500 credits.
  leadCreditNegativeLimitCount: integer("lead_credit_negative_limit_count").notNull().default(500),
  leadCreditDefaultByRole: jsonb("lead_credit_default_by_role").notNull().default(sql`'{}'::jsonb`),
  // Rep purchasing controls — admins can disallow non-admin members from
  // buying lead credits on their own, and optionally cap how many credits
  // they can purchase per calendar month. Cap = null means unlimited.
  repCanBuyLeadCredits: boolean("rep_can_buy_lead_credits").notNull().default(true),
  repMonthlyLeadCreditPurchaseCap: integer("rep_monthly_lead_credit_purchase_cap"),
  // Router mode for the AI voice assistant. 'sonnet_all' = always use Claude Sonnet;
  // 'mini_escalate' = use mini by default, escalate to Sonnet on complexity/frustration.
  aiRouterMode: varchar("ai_router_mode", { length: 20 }).notNull().default("mini_escalate"),
  // Default agency dues (talent rows can override per-talent if NULL).
  duesDefaultMonthlyCents: integer("dues_default_monthly_cents"),
  duesDefaultPercentOfBooking: decimal("dues_default_percent_of_booking", { precision: 5, scale: 2 }),
  duesDefaultOnboardingFeeCents: integer("dues_default_onboarding_fee_cents"),
  // When true, sales reps (not just artists) can author/edit call sheets.
  // Defaults false — call sheets are an artist-portal artifact by design.
  salesRepCanEditCallSheets: boolean("sales_rep_can_edit_call_sheets").notNull().default(false),
  // ── M1 (Drip Foundation) — round-robin pointer ─────────────────────
  // Stores the userId of the last sales rep picked by `pickNextSalesRep`
  // so the next call advances to the rep right after them. Only read by
  // the round-robin helper when a drip campaign has `sendAsRoundRobin`
  // and the contact has no assigned rep. Null = pointer hasn't started
  // yet (next pick falls to the first rep alphabetically).
  dripRoundRobinPointerUserId: varchar("drip_round_robin_pointer_user_id"),
  // Commission trigger mode — controls when sales-rep commission accrues.
  // 'on_balance' (default, legacy behavior): commission counts only when the
  //   invoice is fully paid (status='paid' / isPaid=true).
  // 'on_deposit': commission counts as soon as the deposit is received
  //   (status='deposit_paid' or 'paid'). Useful for owners who want to pay
  //   reps as soon as cash hits the door.
  // 'split_50_50': deposit_paid invoices contribute half their event rate
  //   toward the commission basis; fully-paid invoices contribute the full
  //   rate. The remaining 50% accrues when the balance lands.
  commissionTriggerMode: varchar("commission_trigger_mode", { length: 20 }).notNull().default("on_balance"),
  // Task #611 — when true, sales reps in this workspace can see and act
  // on every other rep's leads/clients/invoices (the workspace becomes a
  // shared pipeline). Default false preserves rep-isolation.
  shareLeadsAcrossReps: boolean("share_leads_across_reps").notNull().default(false),
  // ── Press-kit approval workflow (task #628) ──────────────────────
  // Governs talent + rep edits to the public artist press-kit:
  //  off            — anyone with edit permission saves directly
  //  talent_to_admin — talent edits queue for admin approval; admins save direct
  //  rep_to_talent  — rep edits queue for talent approval; talent saves direct
  pressKitApprovalMode: varchar("press_kit_approval_mode", { length: 32 }).notNull().default("off"),
  // ── Anomaly detection v2 (task #593) ────────────────────────────
  // Maximum implied travel speed (km/h) between two consecutive
  // successful logins for the same user before the impossible-travel
  // alerter pages admins. 900 km/h ≈ a commercial jet at cruise; the
  // expectation is "anything faster than a plane is not a real human
  // boarding two devices in different cities."
  impossibleTravelKmh: integer("impossible_travel_kmh").notNull().default(900),
  // Multiplier for volumetric-export detection: a single bulk export's
  // row_count must exceed `multiplier × trailing-30d median` (per
  // user, per export kind) for the alert to fire. Defaults to 10×.
  volumetricExportMultiplier: integer("volumetric_export_multiplier").notNull().default(10),
  // Absolute floor on row_count below which no volumetric alert fires
  // even if the multiplier is exceeded. Stops a brand-new user's
  // first export (median = 0, multiplier = ∞) from tripping the
  // alarm. Defaults to 500 rows.
  volumetricExportFloor: integer("volumetric_export_floor").notNull().default(500),
  // Task #644 — retention window (days) for raw login + export event
  // rows. Floor is 365d (matches the audit-log retention floor enforced
  // by migration 0022 triggers and `pruneOldRecapEmailLogs`); admins
  // may extend it but never lower it. The cleanup sweeper applies
  // GREATEST(setting, 365) per-workspace.
  securityEventRetentionDays: integer("security_event_retention_days").notNull().default(365),
  // ── Conflict Radar (task #584) ─────────────────────────────────────
  // Threshold (open follow-ups due in a single day, including overdue)
  // above which a sales rep is flagged as "overcommitted". Defaults to
  // 8. Setting it to 0 disables the rep-overload detector entirely
  // (the talent double-booking and venue/tour detectors are not
  // affected). Capped at 50 by the API to keep the value sane.
  conflictRadarOverloadThreshold: integer("conflict_radar_overload_threshold").notNull().default(8),
  // ── Super Admin Onboarding Import (Phase 1) ────────────────────────
  // When `true`, this workspace was created by the platform-admin
  // onboarding import tool and is in the staging stage of a two-stage
  // migration. ALL side-effects must be suppressed: no outbound email/
  // SMS, no Stripe charges, no automation trigger fires, no scheduler
  // enqueue, no public links honored. The platform's atomic commit step
  // flips this to false in a single transaction with `importCommittedAt`
  // set to now(); after that the workspace is indistinguishable from any
  // other live workspace.
  isStagingImport: boolean("is_staging_import").notNull().default(false),
  importCommittedAt: timestamp("import_committed_at"),
  importedFromJobId: varchar("imported_from_job_id", { length: 255 }),
  // ── Coach v3.1 — multi-tenant routing ──────────────────────────────
  // Designated AR contact for ar-overdue coach pings. NULL falls back
  // to first billing/admin member at delivery time. Single-target so
  // a 50-admin enterprise doesn't fan out a ping to all 50 admins.
  billingOwnerUserId: varchar("billing_owner_user_id", { length: 255 }),
  // Designated manager for coach performance escalations. NULL falls
  // back to first non-subject admin at escalation time. Set per
  // workspace so a 200-person agency routes through the right HRBP /
  // sales lead instead of paging every admin.
  coachManagerUserId: varchar("coach_manager_user_id", { length: 255 }),
  // ── Task #708 — Calendar push field controls (workspace defaults) ──
  // Per-field toggles for what gets written into Google Calendar events
  // when bookings are pushed to artist/workspace calendars. Defaults
  // mirror current behaviour (everything on) so flipping the migration
  // doesn't change what existing workspaces see. Per-talent overrides
  // live on the `talent` table as nullable booleans (null = inherit).
  calPushTitleAccountName: boolean("cal_push_title_account_name").notNull().default(true),
  calPushTitleAddress: boolean("cal_push_title_address").notNull().default(true),
  calPushTitleRebookPrefix: boolean("cal_push_title_rebook_prefix").notNull().default(true),
  calPushAddressInBody: boolean("cal_push_address_in_body").notNull().default(true),
  calPushContactName: boolean("cal_push_contact_name").notNull().default(true),
  calPushContactPhone: boolean("cal_push_contact_phone").notNull().default(true),
  calPushRate: boolean("cal_push_rate").notNull().default(true),
  calPushReviewLink: boolean("cal_push_review_link").notNull().default(true),
  calPushHoldStatus: boolean("cal_push_hold_status").notNull().default(true),
  // Allow the post-event review page to email the client. When false,
  // the public-review page hides the "Email Event Contact" card and the
  // /send-followup endpoint returns 403. Per-talent override on `talent`.
  allowPostReviewClientEmail: boolean("allow_post_review_client_email").notNull().default(true),
  // Task #410 — when on (default), the workspace-level (shared/fallback)
  // Google Calendar shows up as a selectable, view-only source in the
  // Calendar Hub's calendar-source dropdown (alongside "My Calendar" and
  // shared teammate calendars). Admin-controlled per workspace; the entry
  // only appears when this is true AND a workspace-level calendar is
  // actually connected.
  showWorkspaceCalendarInHub: boolean("show_workspace_calendar_in_hub").notNull().default(true),
  // Task #102 — Workspace type discriminator. 'tenant' is the default
  // entertainment-booking workspace (every existing row). 'saas' marks
  // the Clarik-the-company workspace itself, which uses a different
  // module set (customers + subscription invoices instead of leads/
  // clients/shows). The column has a server-side default so existing
  // rows backfill safely without code changes.
  workspaceType: varchar("workspace_type", { length: 20 }).notNull().default("tenant").$type<WorkspaceType>(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"), // Soft delete; hard delete removes the row entirely
});

// Lite Tier Billing Runs — one row per workspace per billed period.
// Records the result of the monthly revenue-share charge cycle.
export const liteBillingRuns = pgTable("lite_billing_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  trackedRevenueCents: integer("tracked_revenue_cents").notNull().default(0),
  sharePercent: decimal("share_percent", { precision: 5, scale: 2 }).notNull(),
  amountChargedCents: integer("amount_charged_cents").notNull().default(0),
  stripeInvoiceId: varchar("stripe_invoice_id", { length: 255 }),
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
  // 'pending' | 'charged' | 'skipped_zero' | 'skipped_no_card' | 'failed'
  status: varchar("status", { length: 30 }).notNull().default("pending"),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertLiteBillingRunSchema = createInsertSchema(liteBillingRuns).omit({ id: true, createdAt: true });
export type InsertLiteBillingRun = z.infer<typeof insertLiteBillingRunSchema>;
export type LiteBillingRun = typeof liteBillingRuns.$inferSelect;

// Pending Signups - temporary storage for signup data during Stripe checkout
export const pendingSignups = pgTable("pending_signups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  stripeSessionId: varchar("stripe_session_id", { length: 255 }).notNull().unique(),
  workspaceName: varchar("workspace_name", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 100 }).notNull(),
  tier: varchar("tier", { length: 20 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  passwordHash: text("password_hash").notNull(),
  billingCycle: varchar("billing_cycle", { length: 20 }).notNull(),
  // Coupon code that was applied at signup (uppercased), if any. Used after payment to award bonus credits.
  couponCode: varchar("coupon_code", { length: 50 }),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

// SEC-015 (Task #668) — bound user-controllable text fields that get
// rendered into PDFs and HTML pages. The downstream renderers always
// `escapeHtml`, so this isn't an XSS gate; it's a sanity cap so a 50 KB
// workspace name can't blow up a generated PDF / email subject / log
// line. The DB column is varchar(255) but the create-Insert schema
// previously had no client-side bound, allowing the request body to be
// arbitrarily large until pg rejected it.
export const insertWorkspaceSchema = createInsertSchema(workspaces, {
  name: z.string().min(1).max(200),
  slug: z.string().min(1).max(100),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertWorkspace = z.infer<typeof insertWorkspaceSchema>;
export type Workspace = typeof workspaces.$inferSelect;

// User roles enum
export type UserRole = "sales_rep" | "artist" | "billing" | "admin" | "marketing";

// ── Manager Role Foundation (Task #702) ──────────────────────────────
// Fixed list of departments every workspace user belongs to. New users
// (and the back-fill on existing users) default to "ops". Workspace-
// configurable departments are explicitly out of scope for v1 — we want
// the list small enough that the dashboard tile and approvals routing
// downstream tasks can branch on it without a per-workspace lookup.
export const DEPARTMENT_IDS = ["sales", "marketing", "talent", "billing", "ops"] as const;
export type DepartmentId = typeof DEPARTMENT_IDS[number];
export const DEPARTMENT_LABELS: Record<DepartmentId, string> = {
  sales: "Sales",
  marketing: "Marketing",
  talent: "Talent",
  billing: "Billing",
  ops: "Ops",
};

// Extended user profile with role and Gmail OAuth tokens
export const userProfiles = pgTable("user_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().unique(),
  role: varchar("role", { length: 50 }).notNull().default("sales_rep"),
  // DEPRECATED legacy columns — declared here only so Drizzle does not try to drop them
  // during deploys. NEVER read or write these. All OAuth tokens live in user_gmail_tokens.
  // See replit.md "Gmail Architecture" for details.
  gmailAccessToken: text("gmail_access_token"),
  gmailRefreshToken: text("gmail_refresh_token"),
  gmailTokenExpiry: timestamp("gmail_token_expiry"),
  // gmailConnectedEmail is still used as a reference email address in a few places.
  gmailConnectedEmail: varchar("gmail_connected_email", { length: 255 }),
  emailSignature: text("email_signature"),
  useLogoInSignature: boolean("use_logo_in_signature").notNull().default(true),
  hiddenFromActivity: boolean("hidden_from_activity").notNull().default(false),
  voiceToTextEnabled: boolean("voice_to_text_enabled").notNull().default(false),
  // Per-user lead list preferences
  hideBookedLeads: boolean("hide_booked_leads").notNull().default(false),
  // Per-user hidden columns in the Leads spreadsheet. Empty = show all columns (default).
  hiddenLeadColumns: text("hidden_lead_columns").array().notNull().default(sql`'{}'::text[]`),
  // Once a user toggles their column visibility, this flips to true and their
  // `hiddenLeadColumns` becomes the source of truth (admin changes to workspace
  // defaults no longer apply to this user). Reset button clears it back to false.
  leadColumnsCustomized: boolean("lead_columns_customized").notNull().default(false),
  // Per-user client type preferences
  // null = falls back to workspace default client type
  defaultClientTypeId: varchar("default_client_type_id"),
  // Empty array = unrestricted (sees all client types). Populated = restricted to those types.
  // Admin and billing roles bypass this restriction (they always see everything).
  allowedClientTypeIds: text("allowed_client_type_ids").array().notNull().default(sql`'{}'::text[]`),
  // Per-user region restriction. Empty array = unrestricted (sees all regions).
  // Admin and billing roles bypass this restriction.
  allowedRegionIds: text("allowed_region_ids").array().notNull().default(sql`'{}'::text[]`),
  // Onboarding tutorial wizard state (Task #5)
  // null = onboarding not yet completed; set to a timestamp once required steps are done.
  onboardingCompletedAt: timestamp("onboarding_completed_at"),
  // 0 = not started; tracks the index of the next step to resume on.
  onboardingStep: integer("onboarding_step").notNull().default(0),
  // Per-user toggle for in-app tutorial hints (the `?` popover icons). Defaults ON for new users.
  tutorialModeEnabled: boolean("tutorial_mode_enabled").notNull().default(true),
  // ToS acceptance timestamp (Task #522). Null = not yet accepted.
  tosAcceptedAt: timestamp("tos_accepted_at"),
  // Per-user "Smart search" preference (Task #120 — natural-language filter parsing).
  // Nullable so we can apply a role-based default when reading: `null` →
  // admins get true, sales reps get false. Once the user explicitly toggles
  // the setting we persist a concrete boolean which then beats the default.
  smartSearchEnabled: boolean("smart_search_enabled"),
  // Per-user opt-out for the auto drive-time block (Calendar Hub Slice 2.5).
  // Defaults TRUE. Users who fly to events instead of driving can flip
  // this off so no auto drive blocks ever appear on their calendar.
  // Workspace-tier permission `calendar.driveCascade.enabled` gates the
  // feature globally for a tier; this user toggle is only consulted when
  // the tier already allows it.
  driveCascadeEnabled: boolean("drive_cascade_enabled").notNull().default(true),
  // Solo-mode "wears multiple hats". Optional set drawn from
  // ["booking_rep", "manager", "artist", "admin"]. Used by solo workspaces
  // (≤3 active users) where one person performs several jobs. Empty array
  // means "use the legacy `role` column only" — backwards compatible.
  wearsHats: text("wears_hats").array().notNull().default(sql`'{}'::text[]`),
  // Per-user UI lens. "admin" (default) shows the standard Admin/Sales-rep view.
  // "artist" shows the artist dashboard layout. Toggle in the user menu.
  uiLens: varchar("ui_lens", { length: 16 }).notNull().default("admin"),
  // Getting Started side-drawer state. When the user clicks "Done with setup"
  // the drawer hides permanently and any sample-seed rows for the workspace
  // are deleted. Re-show by visiting the Knowledge Walkthroughs section.
  gettingStartedDismissedAt: timestamp("getting_started_dismissed_at"),
  // Artist Portal one-time educational dialogs (members vs. collaborators).
  // Each is set to a timestamp the first time the portal manager acknowledges
  // the corresponding explainer; null = not yet seen. The two are independent:
  // acknowledging one never suppresses the other.
  portalMembersExplainerSeenAt: timestamp("portal_members_explainer_seen_at"),
  portalCollaboratorsExplainerSeenAt: timestamp("portal_collaborators_explainer_seen_at"),
  // Calendar Hub slice 1 — per-user defaults for the three-layer model.
  // defaultBlockVisibility governs how Time Blocks the user creates show
  // up on shared calendars unless the user overrides per-block. 'private'
  // means only the owner sees the block content (others see "Busy");
  // 'role' shares with same-role peers; 'org' shares with everyone.
  defaultBlockVisibility: varchar("default_block_visibility", { length: 16 }).notNull().default("private"),
  // Default address the future cascade engine treats as "home" when a
  // tour itinerary doesn't override it for a city. Free-text so it can
  // be a street address, an Airbnb name, or "TBD".
  homeBaseAddress: text("home_base_address"),
  // The Calendar Hub's conflict-tolerance learning: how much overlap
  // (in minutes) the user has previously accepted between back-to-back
  // shows / blocks before we stop flagging the next one as a conflict.
  // Starts at 0 (strict); the bot bumps this up as it observes the user
  // intentionally double-booking buffer time.
  bufferToleranceMinutes: integer("buffer_tolerance_minutes").notNull().default(0),
  // ── Manager Role Foundation (Task #702) ──────────────────────────
  // Department the user belongs to inside this workspace. Defaults to
  // "ops" so legacy users back-fill into a neutral bucket — admins can
  // re-assign via the Roles tab in Users & Permissions.
  departmentId: varchar("department_id", { length: 20 }).notNull().default("ops"),
  // Direct manager (the user this person reports to). NULL = top of
  // chart / no manager. Cycle prevention is enforced at the API layer
  // in `server/lib/manager-visibility.ts` because Postgres can't
  // express "no cycles in this self-referential edge" as a constraint.
  reportsToUserId: varchar("reports_to_user_id"),
  // When true, this user inherits visibility/action rights over every
  // record owned by their direct + indirect reports (leads, clients,
  // invoices, schedule). Off by default — being a department head is
  // a separate signal from people simply reporting to you.
  isManager: boolean("is_manager").notNull().default(false),
  // ── Calendar Hub Slice 4 — sharing & overlay preferences ─────────────
  // calendarSharingMode: how this user's calendar appears to others when
  // they add this user to the overlay.
  //   hidden           → not visible even if added to an overlay
  //   availability_only → others see "Busy" blocks, no titles/details
  //   shared           → full block details visible per block visibility rules
  calendarSharingMode: varchar("calendar_sharing_mode", { length: 24 }).notNull().default("shared").$type<CalendarSharingMode>(),
  // Ordered list of userIds to show in the Calendar Hub overlay by default.
  defaultCalendarViewUserIds: text("default_calendar_view_user_ids").array().notNull().default(sql`'{}'::text[]`),
  // Random token used to generate a personal .ics feed URL.
  // Generated on first request via PATCH /api/user/calendar-sharing.
  icsToken: uuid("ics_token"),
  // ── Task #411 — dedicated "Clarik Coach" Google Calendar id ──────────
  // The id of the secondary calendar Clarik creates once (via
  // calendars.insert) under this user's connected Google account to
  // mirror coach-created time blocks into. Stored per (user, workspace)
  // because the calendar is bound to the workspace-scoped user calendar
  // OAuth tokens. null until the first coach block is mirrored.
  coachCalendarId: varchar("coach_calendar_id"),
  // Per-user override for the voice assistant provider.
  // null = use workspace default; 'realtime' | 'whisper' to pin a specific engine.
  voiceProviderPreference: varchar("voice_provider_preference", { length: 16 }),
  // Tour planning preferences — used by the voice assistant tour intelligence tools.
  // tourMaxDriveHours: personal max leg drive time before the assistant warns / refuses to suggest that leg.
  // tourDaysOffPerWeek: how many days off per week the assistant should include when sketching itineraries.
  tourMaxDriveHours: integer("tour_max_drive_hours").notNull().default(6),
  tourDaysOffPerWeek: integer("tour_days_off_per_week").notNull().default(1),
  // tourCreditConfirmThreshold: AI skips the credit-cost confirmation prompt for tour planning
  // operations costing fewer credits than this value. Saved when the user dismisses a warning.
  tourCreditConfirmThreshold: integer("tour_credit_confirm_threshold").notNull().default(5),
  // ── Per-user browser dialer overrides (Task #262) ─────────────────
  // dialerRecordingModeOverride: NULL = follow workspace default.
  // Set to any valid mode string when the rep has their own preference
  // and the workspace has NOT force-locked it.
  dialerRecordingModeOverride: varchar("dialer_recording_mode_override", { length: 16 }),
  // dialerCallerIdOverride: NULL = use workspace-level dialerCallerIdNumber.
  // When an admin assigns a dedicated number to a rep this is populated.
  dialerCallerIdOverride: varchar("dialer_caller_id_override", { length: 32 }),
  // callerIdLocked: when true the admin has pinned this rep's caller-ID
  // and the rep cannot change it via their personal settings.
  callerIdLocked: boolean("caller_id_locked").notNull().default(false),
  // dialerAutoFocusLog: when true, the notes textarea auto-focuses when a
  // browser Twilio call ends so the rep can immediately start typing.
  dialerAutoFocusLog: boolean("dialer_auto_focus_log").notNull().default(true),
  // bridgePhone: the rep's personal/mobile number used for IVR bridge calls.
  // When set, Twilio calls this number as part of round-robin / auto-attendant
  // routing instead of (or in addition to) ringing the browser client.
  // NULL = browser-only (current default behaviour).
  bridgePhone: varchar("bridge_phone", { length: 32 }),
  // dialerRoutingPreference: per-user choice of where dialer calls ring.
  //   'browser' (default) = WebRTC browser softphone only (e.g. VAs).
  //   'phone'             = outbound "Call now" rings this rep's bridge
  //                         phone first then bridges to the lead, and
  //                         inbound rings their cell alongside the browser.
  // Requires bridgePhone to be set before 'phone' can be selected.
  dialerRoutingPreference: varchar("dialer_routing_preference", { length: 16 }).notNull().default("browser"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  reportsToIdx: index("user_profiles_reports_to_idx").on(t.reportsToUserId),
}));

// Onboarding wizard step constants — single source of truth shared between
// the React wizard UI and the server-side onboardingStep validator. Bump
// these together if the wizard ever grows additional steps.
// Required steps: talent, eventOffering, clientType, region, brand
// (the customer-facing brand name that signs every outbound email/invoice;
// see Task #559 — without this the universal fallback signs as
// "Clarik" which is a poor customer experience).
export const ONBOARDING_REQUIRED_STEP_COUNT = 5;
export const ONBOARDING_MAX_STEP = 9; // 9 steps (0-8) in the onboarding wizard

export const userProfilesRelations = relations(userProfiles, ({ one }) => ({}));

// Regions for organizing leads
export const regions = pgTable("regions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  // Empty array = unrestricted (available for all client types). Populated = scoped.
  clientTypeIds: text("client_type_ids").array().notNull().default(sql`'{}'::text[]`),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const regionsRelations = relations(regions, ({ many }) => ({
  leads: many(leads),
}));

// Client Types - configurable client categories
export const clientTypes = pgTable("client_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  isActive: boolean("is_active").notNull().default(true),
  isDefault: boolean("is_default").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  // Per-type opt-out for the client portal. When false, the "Send portal
  // link" affordance hides for clients of this type and the send endpoint
  // refuses with 403. Existing portal sessions for these clients keep
  // working — the workspace-level master switch is the only soft-revoke
  // path; explicit destruction is the "Revoke all past portal links"
  // admin action. Defaults true so existing types are unaffected.
  clientPortalEnabled: boolean("client_portal_enabled").notNull().default(true),
  // Task #687 — marks types that represent a venue/theater-style booking
  // (Theater, Performing Arts Center, Venue, Casino, etc.) so the Client
  // Record dialog can auto-expand the Venue Details section when the
  // selected type is one of these. Pure UX hint; no behavioural impact
  // on save/storage of venue fields. Defaults false so existing custom
  // types stay collapsed-by-default.
  isVenueStyle: boolean("is_venue_style").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceNameUq: uniqueIndex("client_types_workspace_name_uq").on(t.workspaceId, t.name),
}));

export const insertClientTypeSchema = createInsertSchema(clientTypes).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertClientTypeRecord = z.infer<typeof insertClientTypeSchema>;
export type ClientTypeRecord = typeof clientTypes.$inferSelect;

// Task #687 — per-(workspace, user) rolling counter for "did this rep
// touch any venue field on the last N client saves?". Used by the
// Client Record dialog to decide whether to start the Venue Details
// section expanded for reps who routinely use those fields. The array
// is FIFO-trimmed to VENUE_USAGE_WINDOW newest entries (1 = touched a
// venue field on that save, 0 = did not). Threshold for "frequent
// venue user" is VENUE_USAGE_THRESHOLD touches inside that window.
export const VENUE_USAGE_WINDOW = 10;
export const VENUE_USAGE_THRESHOLD = 3;
export const userVenueFieldUsage = pgTable("user_venue_field_usage", {
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  recentSaves: integer("recent_saves").array().notNull().default(sql`'{}'::int[]`),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  pk: uniqueIndex("user_venue_field_usage_pk").on(t.workspaceId, t.userId),
}));
export type UserVenueFieldUsage = typeof userVenueFieldUsage.$inferSelect;

// ── AI Lead Qualification — per-client-type profiles ────────────────
// One row per client type that wants AI-driven scoring. Values in the
// `config` JSON drive the prompt the engine sends to Claude per lead.
//
// Shape (TypeScript-side `QualificationProfileConfig`):
//   {
//     positiveSignals:  Array<{ id, label, weight, hint? }>,
//     negativeSignals:  Array<{ id, label, weight, hint? }>,
//     disqualifiers:    Array<{ id, label, hint? }>,
//     thresholds:       { unqualified, low, fair, good, strong },  // numeric cut-offs (1–5)
//     prioritizedSources: string[],                                // freeform URLs/keywords
//     customInstructions: string,
//   }
export const clientTypeQualificationProfiles = pgTable("client_type_qualification_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  clientTypeId: varchar("client_type_id").notNull().references(() => clientTypes.id, { onDelete: "cascade" }).unique(),
  config: jsonb("config").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceIdx: index("ct_qual_profiles_ws_idx").on(t.workspaceId),
}));

export const insertClientTypeQualificationProfileSchema = createInsertSchema(clientTypeQualificationProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertClientTypeQualificationProfile = z.infer<typeof insertClientTypeQualificationProfileSchema>;
export type ClientTypeQualificationProfile = typeof clientTypeQualificationProfiles.$inferSelect;

// JSON-side config shape — referenced by the storage + UI layers.
export type QualificationSignal = {
  id: string;
  label: string;
  weight: number;       // positive integer; engine treats sign by category
  hint?: string;        // optional research hint for the AI prompt
};
export type QualificationDisqualifier = {
  id: string;
  label: string;
  hint?: string;
};
export type QualificationProfileConfig = {
  positiveSignals: QualificationSignal[];
  negativeSignals: QualificationSignal[];
  disqualifiers: QualificationDisqualifier[];
  thresholds: { unqualified: number; low: number; fair: number; good: number; strong: number };
  prioritizedSources: string[];
  customInstructions: string;
};

// Per-lead audit trail for every qualification attempt (live, batch, or nightly).
// One row per attempt — the most recent row drives the lead's `rating*` columns.
// Append-only audit table; see `docs/security/audit-log-policy.md`.
export const qualificationRuns = pgTable("qualification_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  leadId: varchar("lead_id").notNull(),
  // 'live' | 'batch' | 'nightly' | 'manual_rerun'
  mode: varchar("mode", { length: 20 }).notNull().default("live"),
  // Final integer score the engine recommended (1–5). Null on failure.
  recommendedScore: integer("recommended_score"),
  recommendedLabel: varchar("recommended_label", { length: 20 }),
  justification: text("justification"),
  // Array of `{ label, weight, found, source, sign: 'positive'|'negative' }`.
  signalsJson: jsonb("signals_json"),
  // Array of `{ url, title, snippet }`.
  sourcesJson: jsonb("sources_json"),
  // Whether a hard disqualifier matched. When true, the score is forced to 1.
  disqualifierTriggered: boolean("disqualifier_triggered").notNull().default(false),
  errorMessage: text("error_message"),
  creditsCharged: integer("credits_charged").notNull().default(0),
  msTaken: integer("ms_taken").notNull().default(0),
  modelKey: varchar("model_key", { length: 64 }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  leadIdx: index("qual_runs_lead_idx").on(t.leadId, t.createdAt),
  workspaceIdx: index("qual_runs_ws_idx").on(t.workspaceId, t.createdAt),
}));

export const insertQualificationRunSchema = createInsertSchema(qualificationRuns).omit({ id: true, createdAt: true });
export type InsertQualificationRun = z.infer<typeof insertQualificationRunSchema>;
export type QualificationRun = typeof qualificationRuns.$inferSelect;

// Talent - performers/artists that can be booked
export const talent = pgTable("talent", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }), // Contact email for the talent
  bookingSlug: varchar("booking_slug", { length: 100 }).unique(), // Unique URL slug for booking page
  photoUrl: text("photo_url"),
  bio: text("bio"),
  eventDescription: text("event_description"),
  websiteUrl: text("website_url"),
  socialMediaUrls: text("social_media_urls").array(),
  // DEPRECATED legacy column — replaced by socialMediaUrls (plural array). Declared
  // here only so Drizzle does not drop it on deploy. Do not read or write.
  socialMediaUrl: text("social_media_url"),
  googleDriveUrl: text("google_drive_url"), // EPK, Rider, etc.
  googleReviewUrl: text("google_review_url"), // Google Maps/Business review page URL
  calendarIds: text("calendar_ids").array(), // Google Calendar IDs to check for availability
  userId: varchar("user_id"), // Optional link to user account (artist role)
  // Booking form customization
  bookingFormTitle: text("booking_form_title"), // e.g., "Book Josh Farley!"
  bookingFormSubtitle: text("booking_form_subtitle"), // Show details text
  bookingFormDisclaimer: text("booking_form_disclaimer"), // Single line disclaimer text
  isActive: boolean("is_active").notNull().default(true),
  isDefault: boolean("is_default").notNull().default(false), // First/default talent
  // ── Talent Billing (per-talent payment identity, Wave 10) ────────
  // Stripe customer ID for billing this talent (separate from the
  // workspace's Stripe customer used for platform subscription).
  // Lazy-created on first billing transaction. NULL = not yet enrolled.
  stripeCustomerId: varchar("stripe_customer_id", { length: 255 }),
  // AI credit balance in cents. When workspaces.aiCostsPaidBy is 'talent'
  // (or 'hybrid' overage), AI feature usage draws from this pool.
  aiCreditsBalanceCents: integer("ai_credits_balance_cents").notNull().default(0),
  // Monthly auto-charge configuration for AI top-ups. NULL = manual top-up only.
  aiAutoChargeMonthlyCents: integer("ai_auto_charge_monthly_cents"),
  // Per-talent dues overrides — workspace-level defaults apply when NULL.
  duesMonthlyCents: integer("dues_monthly_cents"),
  duesPercentOfBooking: decimal("dues_percent_of_booking", { precision: 5, scale: 2 }),
  duesOnboardingFeeCents: integer("dues_onboarding_fee_cents"),
  duesOnboardingFeePaidAt: timestamp("dues_onboarding_fee_paid_at"),
  // FINANCIAL VISIBILITY: when false (default) the artist linked to this talent
  // never sees pay/booking dollar amounts. Server strips totals from API
  // responses; client omits the DOM nodes entirely. Flip to true to opt-in.
  financialsVisibleToTalent: boolean("financials_visible_to_talent").notNull().default(false),
  // ── Task #708 — Per-talent calendar push field overrides ──────────
  // Three-state booleans: NULL = inherit workspace default, true = on,
  // false = off. Used by `resolveCalendarPushFlags` in
  // server/lib/calendar/calendar-push-flags.ts to merge with workspace
  // defaults when building Google Calendar events.
  calPushTitleAccountName: boolean("cal_push_title_account_name"),
  calPushTitleAddress: boolean("cal_push_title_address"),
  calPushTitleRebookPrefix: boolean("cal_push_title_rebook_prefix"),
  calPushAddressInBody: boolean("cal_push_address_in_body"),
  calPushContactName: boolean("cal_push_contact_name"),
  calPushContactPhone: boolean("cal_push_contact_phone"),
  calPushRate: boolean("cal_push_rate"),
  calPushReviewLink: boolean("cal_push_review_link"),
  calPushHoldStatus: boolean("cal_push_hold_status"),
  allowPostReviewClientEmail: boolean("allow_post_review_client_email"),
  // ── Press-kit (task #628) ────────────────────────────────────────
  // Public-facing artist press-kit page at /artist/:slug. Distinct from
  // the booking page so the page can be richly merchandised (videos,
  // gallery, credits, FAQ) without dragging the booking form along.
  pressKitPublished: boolean("press_kit_published").notNull().default(false),
  heroPhotoUrl: text("hero_photo_url"),
  longBio: text("long_bio"),
  videoLinks: text("video_links").array(),
  audioLinks: text("audio_links").array(),
  galleryImageUrls: text("gallery_image_urls").array(),
  // jsonb arrays of small structured objects — kept as jsonb instead of
  // separate child tables because they're owner-edited together and we
  // never query inside them from the server.
  // credits: [{ title, year?, role?, description? }]
  credits: jsonb("credits"),
  // pressQuotes: [{ quote, source?, sourceUrl? }]
  pressQuotes: jsonb("press_quotes"),
  // logoUrls: brand/festival/venue logo URLs
  logoUrls: text("logo_urls").array(),
  // Document URLs — EPK / Tech Rider / Hospitality / Stage Plot. Optional;
  // links open in a new tab and are tracked separately from page views.
  epkUrl: text("epk_url"),
  techRiderUrl: text("tech_rider_url"),
  hospitalityRiderUrl: text("hospitality_rider_url"),
  stagePlotUrl: text("stage_plot_url"),
  // faqItems: [{ question, answer }]
  faqItems: jsonb("faq_items"),
  // Contact card shown on press-kit page (separate from `email` which is
  // the talent's internal contact). Often the agency/manager.
  contactName: varchar("contact_name", { length: 255 }),
  contactEmail: varchar("contact_email", { length: 255 }),
  contactPhone: varchar("contact_phone", { length: 32 }),
  contactCompany: varchar("contact_company", { length: 255 }),
  // Admin-selected booking form the press-kit CTA deep-links into.
  // NULL → use talent's default booking form (or /book/:slug if none).
  pressKitDefaultBookingFormId: varchar("press_kit_default_booking_form_id"),
  // ── Talent Type (two-tier catalog) ────────────────────────────────────────
  // Per-talent act type (Speaker, DJ, Comedian, etc.) chosen from a two-tier
  // catalog: platform defaults (curated by super admin) plus workspace-custom
  // entries. Stored as a stable ID — never as a label string — so renames
  // and catalog hide toggles don't strand existing records.
  //   talentTypeSource: 'platform' → look up in platform_talent_types
  //                     'workspace' → look up in workspace_talent_types
  //   Both NULL for untyped talent (existing records unaffected).
  talentTypeId: varchar("talent_type_id"),
  talentTypeSource: varchar("talent_type_source", { length: 20 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ── Review link tokens ─────────────────────────────────────────────────────
// Unique per-recipient tokens embedded in "leave us a review" emails.
// When the client clicks the link, the server records the click and redirects
// to the talent's googleReviewUrl. Used to match Google reviews back to a
// specific client without ambiguity.
export const reviewLinkTokens = pgTable("review_link_tokens", {
  id: uuid("id").primaryKey().defaultRandom(),
  workspaceId: uuid("workspace_id").notNull(),
  talentId: uuid("talent_id").notNull(),
  clientId: uuid("client_id"),
  recipientEmail: text("recipient_email").notNull(),
  automationId: uuid("automation_id"),
  clickedAt: timestamp("clicked_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export type ReviewLinkToken = typeof reviewLinkTokens.$inferSelect;
export type InsertReviewLinkToken = typeof reviewLinkTokens.$inferInsert;

export const talentRelations = relations(talent, ({ many }) => ({
  eventOfferings: many(eventOfferings),
  clients: many(clients),
  bookingForms: many(bookingForms),
}));

export const insertTalentSchema = createInsertSchema(talent).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTalent = z.infer<typeof insertTalentSchema>;
export type Talent = typeof talent.$inferSelect;

// ─── Talent Type Catalog ──────────────────────────────────────────────────────
// Two-tier catalog: platform defaults (super admin) + per-workspace custom.
// talent.talentTypeId stores a stable ID from one of these two tables.
// talent.talentTypeSource = 'platform' | 'workspace' discriminates which table.
//
// A workspace can hide any platform default; hiding only affects future
// selection — talent already tagged with a hidden type keeps its type.

// Platform-managed defaults (e.g. Speaker, DJ, Comedian). Managed by super
// admin in Operations → Talent Types. Visible to all workspaces unless hidden.
export const platformTalentTypes = pgTable("platform_talent_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 100 }).notNull().unique(),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertPlatformTalentTypeSchema = createInsertSchema(platformTalentTypes).omit({
  id: true, createdAt: true,
});
export type InsertPlatformTalentType = z.infer<typeof insertPlatformTalentTypeSchema>;
export type PlatformTalentType = typeof platformTalentTypes.$inferSelect;

// Workspace-custom talent types. Visible ONLY inside the workspace that created
// them — never surfaced in platform defaults or other workspaces.
export const workspaceTalentTypes = pgTable("workspace_talent_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  wsNameUq: uniqueIndex("workspace_talent_types_ws_name_uq").on(t.workspaceId, t.name),
}));
export const insertWorkspaceTalentTypeSchema = createInsertSchema(workspaceTalentTypes).omit({
  id: true, createdAt: true,
});
export type InsertWorkspaceTalentType = z.infer<typeof insertWorkspaceTalentTypeSchema>;
export type WorkspaceTalentType = typeof workspaceTalentTypes.$inferSelect;

// Records which platform-default types a workspace has hidden from its dropdown.
// Hiding is soft — talent already typed with that ID keeps the value.
export const workspaceTalentTypeHides = pgTable("workspace_talent_type_hides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  platformTalentTypeId: varchar("platform_talent_type_id").notNull().references(() => platformTalentTypes.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  wsTypeUq: uniqueIndex("workspace_talent_type_hides_ws_type_uq").on(t.workspaceId, t.platformTalentTypeId),
}));
export type WorkspaceTalentTypeHide = typeof workspaceTalentTypeHides.$inferSelect;

// Booking forms (multiple per talent, each targeting specific client types)
export const bookingForms = pgTable("booking_forms", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(), // e.g., "Senior Living Booking Form"
  slug: varchar("slug", { length: 100 }).notNull(), // e.g., "senior-living"
  clientTypes: text("client_types").array(), // Array of client type names this form applies to
  regionIds: text("region_ids").array(), // Optional: limit to specific regions
  calendarIds: text("calendar_ids").array(), // Override talent's default calendars
  openAvailability: boolean("open_availability").notNull().default(false), // Skip calendar check, show "dates confirmed upon booking"
  // Availability configuration
  availabilityMode: varchar("availability_mode", { length: 20 }).notNull().default("calendar"), // "calendar" or "custom"
  customTimeslotsJson: text("custom_timeslots_json"), // JSON: [{day: "monday", startTime: "09:00", endTime: "17:00", label?: "Morning"}]
  // Form customization (inherits from talent if null)
  formTitle: text("form_title"),
  formSubtitle: text("form_subtitle"),
  formDisclaimer: text("form_disclaimer"),
  isActive: boolean("is_active").notNull().default(true),
  isDefault: boolean("is_default").notNull().default(false), // Default form for this talent
  // Form Builder (Task #96) — portable JSON schema; null = legacy hardcoded two-step flow
  formSchemaJson: text("form_schema_json"),
  analyticsEnabled: boolean("analytics_enabled").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const bookingFormsRelations = relations(bookingForms, ({ one }) => ({
  talent: one(talent, {
    fields: [bookingForms.talentId],
    references: [talent.id],
  }),
}));

export const insertBookingFormSchema = createInsertSchema(bookingForms).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertBookingForm = z.infer<typeof insertBookingFormSchema>;
export type BookingForm = typeof bookingForms.$inferSelect;

// Form Analytics Events (Task #96 — funnel tracking for schema-driven forms)
export const formAnalyticsEvents = pgTable("form_analytics_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  formId: varchar("form_id").notNull().references(() => bookingForms.id, { onDelete: "cascade" }),
  sessionId: varchar("session_id", { length: 128 }).notNull(),
  eventType: varchar("event_type", { length: 32 }).notNull(), // 'view' | 'page_advance' | 'field_focus' | 'submit' | 'abandon'
  pageIndex: integer("page_index"),
  fieldId: varchar("field_id", { length: 64 }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertFormAnalyticsEventSchema = createInsertSchema(formAnalyticsEvents).omit({ id: true, createdAt: true });
export type InsertFormAnalyticsEvent = z.infer<typeof insertFormAnalyticsEventSchema>;
export type FormAnalyticsEvent = typeof formAnalyticsEvents.$inferSelect;

// Show offerings (admin-configurable event types with calendar durations)
export const eventOfferings = pgTable("event_offerings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id"), // Links to talent (null = available to all)
  service: varchar("service", { length: 255 }).notNull().default("Entertainment Performance"),
  name: varchar("name", { length: 255 }).notNull(),
  rate: decimal("rate", { precision: 10, scale: 2 }).default("0"),
  minutesBefore: integer("minutes_before").notNull().default(30),
  minutesAfter: integer("minutes_after").notNull().default(60),
  isDefault: boolean("is_default").notNull().default(false),
  hasDynamicPricing: boolean("has_dynamic_pricing").notNull().default(false),
  noSetRate: boolean("no_set_rate").notNull().default(false), // "Custom Show" — no auto-fill, user types rate per booking
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  // Task #111 — soft delete so accidentally deleted offerings (and their
  // dynamic pricing) can be restored within a 90-day window.
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by"),
}, (table) => ({
  // Hot path filters out soft-deleted rows; partial index keeps it fast.
  workspaceActiveIdx: index("event_offerings_ws_active_idx")
    .on(table.workspaceId)
    .where(sql`${table.deletedAt} IS NULL`),
}));

export const eventOfferingsRelations = relations(eventOfferings, ({ one, many }) => ({
  talent: one(talent, {
    fields: [eventOfferings.talentId],
    references: [talent.id],
  }),
  marketPricing: many(eventOfferingMarketPricing),
}));

export const insertEventOfferingSchema = createInsertSchema(eventOfferings).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true, deletedBy: true });
export type InsertEventOffering = z.infer<typeof insertEventOfferingSchema>;
export type EventOffering = typeof eventOfferings.$inferSelect;

// Market-specific pricing for show offerings (dynamic pricing per region and client type)
export const eventOfferingMarketPricing = pgTable("event_offering_market_pricing", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  eventOfferingId: varchar("event_offering_id").notNull().references(() => eventOfferings.id, { onDelete: "cascade" }),
  regionId: varchar("region_id").references(() => regions.id, { onDelete: "cascade" }), // null when appliesToAllRegions=true
  appliesToAllRegions: boolean("applies_to_all_regions").notNull().default(false), // sentinel: applies to every region, including future-added ones
  clientType: varchar("client_type", { length: 100 }), // null means applies to all client types, otherwise specific client type name
  rate: decimal("rate", { precision: 10, scale: 2 }), // null means use base rate
  isAvailable: boolean("is_available").notNull().default(true), // false = not available in this market
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  // Task #111 — cascades from the parent event offering's soft delete so a
  // restore brings the dynamic pricing rules back together with the offering.
  deletedAt: timestamp("deleted_at"),
  deletedBy: varchar("deleted_by"),
}, (table) => ({
  activeByOfferingIdx: index("event_offering_market_pricing_active_idx")
    .on(table.eventOfferingId)
    .where(sql`${table.deletedAt} IS NULL`),
}));

export const eventOfferingMarketPricingRelations = relations(eventOfferingMarketPricing, ({ one }) => ({
  eventOffering: one(eventOfferings, {
    fields: [eventOfferingMarketPricing.eventOfferingId],
    references: [eventOfferings.id],
  }),
  region: one(regions, {
    fields: [eventOfferingMarketPricing.regionId],
    references: [regions.id],
  }),
}));

export const insertEventOfferingMarketPricingSchema = createInsertSchema(eventOfferingMarketPricing).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true, deletedBy: true });
export type InsertEventOfferingMarketPricing = z.infer<typeof insertEventOfferingMarketPricingSchema>;
export type EventOfferingMarketPricing = typeof eventOfferingMarketPricing.$inferSelect;

// Client Types for late fee schedules
export type ClientType = "schools" | "corporate" | "private" | "non_profit" | "government";

// Late Fee Tiers - configurable late fee schedule per client type
export const lateFeeTiers = pgTable("late_fee_tiers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  clientType: varchar("client_type", { length: 50 }).notNull().default("schools"),
  daysAfterEvent: integer("days_after_event").notNull(),
  feeType: varchar("fee_type", { length: 20 }).notNull().default("flat"), // "flat" or "percent"
  feeAmount: decimal("fee_amount", { precision: 10, scale: 2 }).notNull(),
  isRecurring: boolean("is_recurring").notNull().default(false),
  recurringIntervalDays: integer("recurring_interval_days").default(30),
  gracePeriodDays: integer("grace_period_days").notNull().default(0),
  isCumulative: boolean("is_cumulative").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertLateFeeTierSchema = createInsertSchema(lateFeeTiers).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertLateFeeTier = z.infer<typeof insertLateFeeTierSchema>;
export type LateFeeTier = typeof lateFeeTiers.$inferSelect;

// Late Fee State Exceptions - override master schedule for specific states
export const lateFeeStateExceptions = pgTable("late_fee_state_exceptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  state: varchar("state", { length: 2 }).notNull(), // State abbreviation (TX, CA, DC, etc.)
  clientType: varchar("client_type", { length: 50 }).notNull().default("schools"),
  maxFeeAmount: decimal("max_fee_amount", { precision: 10, scale: 2 }), // Max fee cap
  maxFeePercent: decimal("max_fee_percent", { precision: 5, scale: 2 }), // Max percentage cap
  isRecurring: boolean("is_recurring").notNull().default(false), // Whether the capped fee applies recurrently
  notes: text("notes"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertLateFeeStateExceptionSchema = createInsertSchema(lateFeeStateExceptions).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertLateFeeStateException = z.infer<typeof insertLateFeeStateExceptionSchema>;
export type LateFeeStateException = typeof lateFeeStateExceptions.$inferSelect;

// Event Request Types and Status
export type EventRequestType = "event_request" | "call_request" | "waitlist";
export type EventRequestStatus = "pending" | "approved" | "rejected" | "converted";

// Public Event Requests - intake from public scheduler
export const eventRequests = pgTable("event_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  requestType: varchar("request_type", { length: 50 }).notNull().default("event_request"),
  status: varchar("status", { length: 50 }).notNull().default("pending"),
  // Contact Info
  contactName: varchar("contact_name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  phone: varchar("phone", { length: 50 }),
  companyName: varchar("company_name", { length: 255 }),
  cityState: varchar("city_state", { length: 255 }),
  // Request Details
  eventOfferingId: varchar("event_offering_id"),
  preferredDate: timestamp("preferred_date"),
  alternateDate: timestamp("alternate_date"),
  eventDetails: text("event_details"),
  estimatedAttendees: integer("estimated_attendees"),
  venueAddress: text("venue_address"),
  venueCity: varchar("venue_city", { length: 100 }),
  venueState: varchar("venue_state", { length: 50 }),
  venueZip: varchar("venue_zip", { length: 20 }),
  // For waitlist
  preferredTimeframe: varchar("preferred_timeframe", { length: 100 }),
  // For call requests
  preferredCallTime: varchar("preferred_call_time", { length: 100 }),
  // Internal fields
  notes: text("notes"),
  reviewedBy: varchar("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
  viewedAt: timestamp("viewed_at"),
  convertedToLeadId: varchar("converted_to_lead_id"),
  convertedToClientId: varchar("converted_to_client_id"),
  // Notification tracking (Tier-A #5: "never lose a submission"). The
  // public POST always persists the submission first, then attempts the
  // admin-notification email; if the email fails we still keep the row
  // and surface it in the admin recovery view so it can be re-sent.
  // notificationStatus: 'pending' (just created) | 'sent' | 'failed'.
  notificationStatus: varchar("notification_status", { length: 20 }).notNull().default("pending"),
  notificationSentAt: timestamp("notification_sent_at"),
  notificationError: text("notification_error"),
  notificationAttemptCount: integer("notification_attempt_count").notNull().default(0),
  // Task #705 — Ads Manager Phase 1 attribution capture from the public form.
  attributionVisitorId: varchar("attribution_visitor_id", { length: 64 }),
  attributionGclid: varchar("attribution_gclid", { length: 255 }),
  attributionFbclid: varchar("attribution_fbclid", { length: 255 }),
  // Task #706 — Phase 2 click ids (TikTok / LinkedIn).
  attributionTtclid: varchar("attribution_ttclid", { length: 255 }),
  attributionLiFatId: varchar("attribution_li_fat_id", { length: 255 }),
  attributionUtmSource: varchar("attribution_utm_source", { length: 128 }),
  attributionUtmMedium: varchar("attribution_utm_medium", { length: 128 }),
  attributionUtmCampaign: varchar("attribution_utm_campaign", { length: 255 }),
  attributionUtmTerm: varchar("attribution_utm_term", { length: 255 }),
  attributionUtmContent: varchar("attribution_utm_content", { length: 255 }),
  attributionLandingUrl: varchar("attribution_landing_url", { length: 2048 }),
  attributionCampaignId: varchar("attribution_campaign_id"),
  attributionCapturedAt: timestamp("attribution_captured_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceStatusCreatedIdx: index("event_requests_ws_status_created_idx").on(t.workspaceId, t.status, t.createdAt),
  notificationStatusIdx: index("event_requests_notification_status_idx").on(t.workspaceId, t.notificationStatus, t.createdAt),
}));

export const eventRequestsRelations = relations(eventRequests, ({ one }) => ({
  eventOffering: one(eventOfferings, {
    fields: [eventRequests.eventOfferingId],
    references: [eventOfferings.id],
  }),
}));

export const insertEventRequestSchema = createInsertSchema(eventRequests).omit({ id: true, createdAt: true, updatedAt: true, reviewedBy: true, reviewedAt: true, viewedAt: true, convertedToLeadId: true, convertedToClientId: true, notificationStatus: true, notificationSentAt: true, notificationError: true, notificationAttemptCount: true });
export type InsertEventRequest = z.infer<typeof insertEventRequestSchema>;
export type EventRequest = typeof eventRequests.$inferSelect;

// Funnel Submissions - tracks partial completions in the booking funnel
export const funnelSubmissions = pgTable("funnel_submissions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  // Contact Info from Step 1
  contactName: varchar("contact_name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  phone: varchar("phone", { length: 50 }),
  companyName: varchar("company_name", { length: 255 }),
  cityState: varchar("city_state", { length: 255 }),
  // Track if completed full funnel
  completed: boolean("completed").notNull().default(false),
  viewedAt: timestamp("viewed_at"),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceCreatedIdx: index("funnel_submissions_ws_created_idx").on(t.workspaceId, t.createdAt),
  workspaceCompletedIdx: index("funnel_submissions_ws_completed_idx").on(t.workspaceId, t.completed),
}));

export const insertFunnelSubmissionSchema = createInsertSchema(funnelSubmissions).omit({ id: true, createdAt: true, updatedAt: true, viewedAt: true });
export type InsertFunnelSubmission = z.infer<typeof insertFunnelSubmissionSchema>;
export type FunnelSubmission = typeof funnelSubmissions.$inferSelect;

// Lead status
export type LeadStatus = "new" | "interested" | "disqualified" | "booked";

// Leads spreadsheet
// Suppression reason enum for leads
export type SuppressionReason = "STOP_REPLY" | "MANUAL" | "BOUNCE" | "COMPLAINT";

export const leads = pgTable("leads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  recordId: varchar("record_id", { length: 20 }).unique(),
  regionId: varchar("region_id").notNull(),
  talentId: varchar("talent_id"), // Links to talent for this lead
  leadName: varchar("lead_name", { length: 255 }),
  phoneNumber: text("phone_number"),
  mobilePhone: text("mobile_phone"),
  emailAddress: varchar("email_address", { length: 255 }),
  contactName: varchar("contact_name", { length: 255 }),
  clientType: varchar("client_type", { length: 50 }).default("Senior Living"),
  // Venue Details (carry forward on conversion to client; financial fields live on client only)
  venueType: varchar("venue_type", { length: 100 }),
  venueCapacity: varchar("venue_capacity", { length: 50 }),
  lastContacted: timestamp("last_contacted"),
  notes: text("notes"),
  rating: integer("rating"), // 1-5 star rating, null = unrated. Lead-only (does not propagate to clients).
  status: varchar("status", { length: 50 }).notNull().default("new"),
  sortOrder: integer("sort_order").notNull().default(0),
  rep: varchar("rep", { length: 100 }),
  artist: varchar("artist", { length: 100 }).notNull().default(""),
  emailOptedOut: boolean("email_opted_out").notNull().default(false), // Set via unsubscribe link or manually
  // Suppression fields (admin-only view)
  isSuppressed: boolean("is_suppressed").notNull().default(false),
  suppressedAt: timestamp("suppressed_at"),
  suppressedReason: varchar("suppressed_reason", { length: 50 }), // STOP_REPLY, MANUAL, BOUNCE, COMPLAINT
  suppressionSourceMessageId: varchar("suppression_source_message_id", { length: 255 }),
  // Address fields — populated from Lead Gen import, carry forward to client on conversion
  address: text("address"),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  // Hidden Google Places import fields
  website: varchar("website", { length: 500 }), // Hidden until surfaced by admin
  googlePlaceId: varchar("google_place_id", { length: 255 }), // Deduplication key — hidden
  // Lead Gen origin tags — populated when imported from Lead Gen with quadrant tagging enabled
  quadrant: varchar("quadrant", { length: 2 }), // NE | NW | SE | SW relative to the search center
  distanceMiles: real("distance_miles"), // Straight-line distance from the search center at import time
  // Lead-source attribution. Free-text but agencies typically use a small
  // vocabulary like "lead-gen", "import", "manual", "website", "referral",
  // "facebook", "google-ads". Used by the reports lead-source attribution
  // section. Carries forward to clients/invoices via the originating lead row.
  source: varchar("source", { length: 50 }),
  createdBy: varchar("created_by").notNull(),
  // Per-lead visibility allowlist. When non-empty, only these user IDs may see
  // this lead — on EVERY read surface (lead lists, detail, dropdowns, reports)
  // AND inbound reply threads matched to it. NULL/empty = unrestricted.
  // Task #510: AI-agent imports stamp this with the workspace's ADMIN user set
  // at import time, making agent-discovered leads admin-only. Strict semantics:
  // no role auto-bypass — membership is purely the array.
  visibleUserIds: text("visible_user_ids").array(),
  // AI-agent ownership/linkage (Task #510). Set when this lead was imported by
  // an AI Agent run, so admins get a private agent-scoped view filterable by
  // agent or run. Plain varchar (no FK) to avoid constraint locks on this
  // high-write table; logically references ai_agents.id / ai_agent_runs.id.
  aiAgentId: varchar("ai_agent_id"),
  sourceAgentRunId: varchar("source_agent_run_id"),
  // User-toggled favourite/star flag. Lets reps mark high-priority leads for
  // quick filtering. Per-lead, not per-user — stored on the row so it survives
  // login across devices and shows up in exports.
  starred: boolean("starred").notNull().default(false),
  // Sample-seed marker. When true, the row was inserted by the onboarding
  // sample-data seeder. Cleared in bulk when the workspace clicks "Done with
  // setup" or via the super-admin "wipe demo data" tool.
  isDemoSeed: boolean("is_demo_seed").notNull().default(false),
  // Honeytoken marker (task #594). Planted decoy rows used as a
  // tripwire — workspace members never see them because every
  // list/search/export query in storage filters `is_honeytoken = false`
  // by default. Indexed so the filter is a cheap conditional index
  // probe rather than a sequential scan.
  isHoneytoken: boolean("is_honeytoken").notNull().default(false),
  // ── AI Lead Qualification (Wave 9) ────────────────────────────────
  // Plain-English summary of why the lead scored what it did. Shown in the
  // `?` tooltip + breakdown popup on the leads spreadsheet.
  ratingSummary: text("rating_summary"),
  // Coarse-grained label for at-a-glance review. Drives the colored badge
  // on the spreadsheet row. Values: 'Unqualified' | 'Low' | 'Fair' | 'Good' | 'Strong'.
  ratingLabel: varchar("rating_label", { length: 20 }),
  // JSON list of `{ url, title, snippet }` references the engine cited.
  ratingSource: jsonb("rating_source"),
  // When the most recent qualification run completed.
  qualifiedAt: timestamp("qualified_at"),
  // Lifecycle state for the engine. 'Pending' means the lead is queued or
  // never been touched; 'Processing' is in-flight (watchdog resets after 10m);
  // 'Complete' has a finished score; 'Failed' will be re-queued by nightly.
  qualificationStatus: varchar("qualification_status", { length: 20 }),
  // Manual override fields — set by admins (or reps with the override
  // permission) from the breakdown popup.
  qualificationOverrideScore: integer("qualification_override_score"),
  qualificationOverrideNote: text("qualification_override_note"),
  qualificationOverrideBy: varchar("qualification_override_by"),
  qualificationOverrideAt: timestamp("qualification_override_at"),
  // ── M1 (Drip Foundation) — denormalized "last touched" timestamp ──
  // Bumped whenever a `lead_activities` row is inserted or any inbound/
  // outbound email/SMS is logged against the lead. Drives the
  // "lead.inactive" drip trigger so the polled scheduler can do a
  // single indexed scan instead of joining lead_activities for every
  // workspace. Backfilled once on first boot from MAX(activity.createdAt).
  lastActivityAt: timestamp("last_activity_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
  // Task #705 — Ads Manager Phase 1. Attribution captured at lead-creation
  // time and forwarded onto the resulting client + invoice. See
  // server/lib/ads/attribution.ts for the canonical capture helper.
  attributionVisitorId: varchar("attribution_visitor_id", { length: 64 }),
  attributionGclid: varchar("attribution_gclid", { length: 255 }),
  attributionFbclid: varchar("attribution_fbclid", { length: 255 }),
  // Task #706 — Phase 2 click ids (TikTok / LinkedIn).
  attributionTtclid: varchar("attribution_ttclid", { length: 255 }),
  attributionLiFatId: varchar("attribution_li_fat_id", { length: 255 }),
  attributionUtmSource: varchar("attribution_utm_source", { length: 128 }),
  attributionUtmMedium: varchar("attribution_utm_medium", { length: 128 }),
  attributionUtmCampaign: varchar("attribution_utm_campaign", { length: 255 }),
  attributionUtmTerm: varchar("attribution_utm_term", { length: 255 }),
  attributionUtmContent: varchar("attribution_utm_content", { length: 255 }),
  attributionLandingUrl: varchar("attribution_landing_url", { length: 2048 }),
  attributionCampaignId: varchar("attribution_campaign_id"),
  attributionCapturedAt: timestamp("attribution_captured_at"),
}, (t) => ({
  workspaceDeletedIdx: index("leads_ws_deleted_idx").on(t.workspaceId, t.deletedAt),
  lastActivityIdx: index("leads_ws_last_activity_idx").on(t.workspaceId, t.lastActivityAt),
  workspaceStatusIdx: index("leads_ws_status_idx").on(t.workspaceId, t.status),
  regionIdx: index("leads_region_idx").on(t.regionId),
  talentIdx: index("leads_talent_idx").on(t.talentId),
  workspaceCreatedIdx: index("leads_ws_created_idx").on(t.workspaceId, t.createdAt),
  qualificationStatusIdx: index("leads_qualification_status_idx").on(t.workspaceId, t.qualificationStatus),
  // Honeytoken filter index (task #594). Most reads exclude honeytokens
  // so an index on (workspaceId, isHoneytoken) keeps the default WHERE
  // clause cheap, and the diagnostic `WHERE is_honeytoken=true` query
  // can use the same composite index for a single-row seek.
  workspaceHoneytokenIdx: index("leads_ws_honeytoken_idx").on(t.workspaceId, t.isHoneytoken),
}));

export const leadsRelations = relations(leads, ({ one, many }) => ({
  region: one(regions, {
    fields: [leads.regionId],
    references: [regions.id],
  }),
  talent: one(talent, {
    fields: [leads.talentId],
    references: [talent.id],
  }),
  activities: many(leadActivities),
}));

// Lead activities (calls, emails, notes).
export type LeadActivityType = "call" | "email" | "note";

export const leadActivities = pgTable("lead_activities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  leadId: varchar("lead_id").notNull(),
  type: varchar("type", { length: 50 }).notNull(), // call, email, note
  notes: text("notes"),
  createdBy: varchar("created_by").notNull(),
  createdByName: varchar("created_by_name", { length: 255 }),
  trackingId: varchar("tracking_id"), // email tracking pixel ID for open detection
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  leadCreatedIdx: index("lead_activities_lead_created_idx").on(t.leadId, t.createdAt),
  workspaceCreatedIdx: index("lead_activities_ws_created_idx").on(t.workspaceId, t.createdAt),
}));

export const leadActivitiesRelations = relations(leadActivities, ({ one }) => ({
  lead: one(leads, {
    fields: [leadActivities.leadId],
    references: [leads.id],
  }),
}));

export const insertLeadActivitySchema = createInsertSchema(leadActivities).omit({ id: true, createdAt: true });
export type InsertLeadActivity = z.infer<typeof insertLeadActivitySchema>;
export type LeadActivity = typeof leadActivities.$inferSelect;

// Clients (booked shows)
export const clients = pgTable("clients", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  recordId: varchar("record_id", { length: 20 }).unique(),
  leadId: varchar("lead_id"),
  regionId: varchar("region_id"),
  talentId: varchar("talent_id"), // Primary talent for this booking (additional talent in bookingTalent)
  runId: varchar("run_id"), // When set, this booking belongs to a parent multi-show run
  accountName: varchar("account_name", { length: 255 }),
  // PII: AES-256-GCM envelope (`enc:v1:...`); stored as text because the
  // envelope string is much longer than a raw phone number.
  phoneNumber: text("phone_number"),
  // PII: AES-256-GCM envelope (`enc:v1:...`); stored as text — see above.
  mobilePhone: text("mobile_phone"),
  emailAddress: varchar("email_address", { length: 255 }),
  contactName: varchar("contact_name", { length: 255 }),
  clientType: varchar("client_type", { length: 50 }).default("Senior Living"),
  // PII: AES-256-GCM envelope, master key in env.
  address: text("address"),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  timezone: varchar("timezone", { length: 100 }).default("America/Chicago"),
  rate: decimal("rate", { precision: 10, scale: 2 }),
  discount: decimal("discount", { precision: 10, scale: 2 }).default("0"),
  // Task #112 — canonical calendar-day storage. See shared/event-date.ts.
  dateOfEvent: date("date_of_event", { mode: "string" }),
  dateBooked: timestamp("date_booked").defaultNow(),
  invoiceNumber: varchar("invoice_number", { length: 50 }),
  eventTime: varchar("event_time", { length: 50 }),
  customEndTime: varchar("custom_end_time", { length: 50 }),
  bookingDetails: text("booking_details"),
  eventDetails: varchar("event_details", { length: 255 }),
  eventOfferingId: varchar("event_offering_id"),
  // PII / sensitive free-text: AES-256-GCM envelope, master key in env.
  notes: text("notes"),
  // PII / sensitive free-text: AES-256-GCM envelope, master key in env.
  calendarNotes: text("calendar_notes"),
  calendarEventId: varchar("calendar_event_id", { length: 255 }),
  rep: varchar("rep", { length: 100 }),
  artist: varchar("artist", { length: 100 }).notNull().default(""),
  requiresDocuments: boolean("requires_documents").notNull().default(false),
  emailOptedOut: boolean("email_opted_out").notNull().default(false),
  emailOptedOutAt: timestamp("email_opted_out_at"),
  // Booking flags — control rate calculation behavior
  isTicketedEvent: boolean("is_ticketed_event").notNull().default(false),
  isNoFeeShow: boolean("is_no_fee_show").notNull().default(false),
  // Venue Details
  venueType: varchar("venue_type", { length: 100 }),
  venueCapacity: varchar("venue_capacity", { length: 50 }),
  venueRentalCost: varchar("venue_rental_cost", { length: 50 }),
  venueTicketPricing: varchar("venue_ticket_pricing", { length: 100 }),
  venueTicketSplit: boolean("venue_ticket_split").notNull().default(false),
  venueGuarantee: boolean("venue_guarantee").notNull().default(false),
  venueBuyout: boolean("venue_buyout").notNull().default(false),
  venueRental: boolean("venue_rental").notNull().default(false),
  // Hidden Google Places import fields
  website: varchar("website", { length: 500 }), // Hidden until surfaced by admin
  googlePlaceId: varchar("google_place_id", { length: 255 }), // Deduplication key — hidden
  createdBy: varchar("created_by").notNull(),
  // Sample-seed marker — see leads.isDemoSeed.
  isDemoSeed: boolean("is_demo_seed").notNull().default(false),
  // Honeytoken marker (task #594). See leads.isHoneytoken — same
  // tripwire mechanism applied to client rows.
  isHoneytoken: boolean("is_honeytoken").notNull().default(false),
  // Wave 7 — mobile run sheet check-in / completion timestamps.
  arrivedAt: timestamp("arrived_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
  // Task #705 — Ads Manager Phase 1 attribution. Carried over from the
  // originating lead at booking time so client-level reports retain
  // campaign provenance.
  attributionVisitorId: varchar("attribution_visitor_id", { length: 64 }),
  attributionGclid: varchar("attribution_gclid", { length: 255 }),
  attributionFbclid: varchar("attribution_fbclid", { length: 255 }),
  // Task #706 — Phase 2 click ids (TikTok / LinkedIn).
  attributionTtclid: varchar("attribution_ttclid", { length: 255 }),
  attributionLiFatId: varchar("attribution_li_fat_id", { length: 255 }),
  attributionUtmSource: varchar("attribution_utm_source", { length: 128 }),
  attributionUtmMedium: varchar("attribution_utm_medium", { length: 128 }),
  attributionUtmCampaign: varchar("attribution_utm_campaign", { length: 255 }),
  attributionUtmTerm: varchar("attribution_utm_term", { length: 255 }),
  attributionUtmContent: varchar("attribution_utm_content", { length: 255 }),
  attributionLandingUrl: varchar("attribution_landing_url", { length: 2048 }),
  attributionCampaignId: varchar("attribution_campaign_id"),
  attributionCapturedAt: timestamp("attribution_captured_at"),
}, (t) => ({
  workspaceDeletedIdx: index("clients_ws_deleted_idx").on(t.workspaceId, t.deletedAt),
  regionIdx: index("clients_region_idx").on(t.regionId),
  talentIdx: index("clients_talent_idx").on(t.talentId),
  leadIdx: index("clients_lead_idx").on(t.leadId),
  workspaceEventDateIdx: index("clients_ws_event_date_idx").on(t.workspaceId, t.dateOfEvent),
  // Honeytoken filter index (task #594) — see leads_ws_honeytoken_idx.
  workspaceHoneytokenIdx: index("clients_ws_honeytoken_idx").on(t.workspaceId, t.isHoneytoken),
  workspaceCityStateIdx: index("clients_ws_city_state_idx").on(t.workspaceId, t.city, t.state),
}));

// Honeytoken touch events (task #594). Audit log of every read /
// update / delete / export that hit a row flagged `is_honeytoken =
// true`. Powers the super-admin diagnostic page that surfaces
// suspicious access patterns and keeps the long-tail history that the
// in-app inbox truncates after 90 days. Note this table is not
// FK'd to `workspaces` because we want events to outlive a deleted
// workspace (forensics).
export const honeytokenTouchEvents = pgTable("honeytoken_touch_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  recordType: varchar("record_type", { length: 16 }).notNull(),
  recordId: varchar("record_id").notNull(),
  recordName: varchar("record_name", { length: 255 }),
  action: varchar("action", { length: 32 }).notNull(),
  actorUserId: varchar("actor_user_id"),
  actorEmail: varchar("actor_email", { length: 255 }),
  metadataJson: text("metadata_json"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  wsCreatedIdx: index("honeytoken_touch_events_ws_created_idx").on(t.workspaceId, t.createdAt),
  recordIdx: index("honeytoken_touch_events_record_idx").on(t.recordType, t.recordId),
  createdIdx: index("honeytoken_touch_events_created_idx").on(t.createdAt),
}));

export type HoneytokenTouchEvent = typeof honeytokenTouchEvents.$inferSelect;
export type InsertHoneytokenTouchEvent = typeof honeytokenTouchEvents.$inferInsert;

export const clientsRelations = relations(clients, ({ one }) => ({
  region: one(regions, {
    fields: [clients.regionId],
    references: [regions.id],
  }),
  eventOffering: one(eventOfferings, {
    fields: [clients.eventOfferingId],
    references: [eventOfferings.id],
  }),
  talent: one(talent, {
    fields: [clients.talentId],
    references: [talent.id],
  }),
}));

// Invoices
export const invoices = pgTable("invoices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  recordId: varchar("record_id", { length: 20 }).unique(),
  clientId: varchar("client_id"), // nullable: sponsor invoices have no client record
  talentId: varchar("talent_id"), // Primary talent for this invoice
  regionId: varchar("region_id"), // Links to region for geographic metrics
  runId: varchar("run_id"), // When set, this invoice covers one or more bookings inside a run
  seriesId: varchar("series_id"), // When set, this invoice was generated by an event_series cadence (Recurring Event / Multi-date)
  invoiceNumber: varchar("invoice_number", { length: 50 }).notNull(),
  publicToken: varchar("public_token", { length: 64 }).unique(),
  // Task #590 — public-token hardening. `publicTokenIssuedAt` is set on
  // invoice create + every rotate; `publicTokenExpiresAt` is the hard
  // cutoff after which the public viewer returns 410 Gone. Legacy rows
  // migrate with both NULL = "no expiry" so links already shared with
  // clients keep working until an admin explicitly rotates them.
  // `publicTokenViews` is incremented on every public read; once it
  // crosses PUBLIC_TOKEN_MAX_VIEWS_BEFORE_SOFT_EXPIRE the row is
  // soft-expired (`publicTokenSoftExpiredAt = now()`) so further reads
  // 410 — admin rotates to issue a fresh link.
  publicTokenIssuedAt: timestamp("public_token_issued_at"),
  publicTokenExpiresAt: timestamp("public_token_expires_at"),
  publicTokenViews: integer("public_token_views").notNull().default(0),
  publicTokenLastViewedAt: timestamp("public_token_last_viewed_at"),
  publicTokenSoftExpiredAt: timestamp("public_token_soft_expired_at"),
  publicTokenRotatedAt: timestamp("public_token_rotated_at"),
  publicTokenRotatedBy: varchar("public_token_rotated_by"),
  accountName: varchar("account_name", { length: 255 }),
  accountAddress: text("account_address"),
  serviceName: varchar("service_name", { length: 255 }).default("Entertainment Performance"),
  eventRate: decimal("event_rate", { precision: 10, scale: 2 }),
  discount: decimal("discount", { precision: 10, scale: 2 }).default("0"),
  // Task #112 — canonical calendar-day storage. See shared/event-date.ts.
  dateOfEvent: date("date_of_event", { mode: "string" }),
  invoiceDate: timestamp("invoice_date").defaultNow(),
  paymentTerms: varchar("payment_terms", { length: 50 }).default("due_on_completion"), // due_on_completion, net_15, net_30, net_60
  bookingDetails: text("booking_details"),
  billingNotes: text("billing_notes"),
  billingContactName: varchar("billing_contact_name", { length: 255 }),
  billingPhoneNumber: varchar("billing_phone_number", { length: 50 }),
  billingEmail: varchar("billing_email", { length: 255 }),
  billingLastContact: timestamp("billing_last_contact"),
  status: varchar("status", { length: 50 }).notNull().default("pending"),
  confirmationSentAt: timestamp("confirmation_sent_at"),
  skipAutomatedEmails: boolean("skip_automated_emails").default(false),
  cancellationReason: text("cancellation_reason"),
  cancelledAt: timestamp("cancelled_at"),
  // Balance side of payment (the existing "paid" columns now mean
  // "balance side" of a deposit-required invoice; for non-deposit
  // invoices they continue to mean "the invoice was paid in full").
  datePaid: timestamp("date_paid"),
  paidByUserId: varchar("paid_by_user_id", { length: 255 }),
  paymentType: varchar("payment_type", { length: 50 }),
  paymentNotes: text("payment_notes"),
  // ── Deposit / date-hold workflow ───────────────────────────────────
  // When true, the invoice is split into deposit + balance halves. The
  // deposit must be collected before the date is "held" — until then
  // the calendar event carries a "HOLD: " prefix and the client/invoice
  // row shows a yellow status dot.
  depositRequired: boolean("deposit_required").notNull().default(false),
  depositAmountCents: integer("deposit_amount_cents"),
  // 'cash' | 'stripe' | 'paypal' | 'check' | 'other'
  depositMethod: varchar("deposit_method", { length: 50 }),
  depositPaidAt: timestamp("deposit_paid_at"),
  depositNotes: text("deposit_notes"),
  // Sales rep / admin user who collected the deposit (or "Customer" if
  // self-served via the public payment portal).
  depositCollectedBy: varchar("deposit_collected_by", { length: 255 }),
  // Optional explicit due dates surfaced in confirmation emails as
  // {{deposit_due_date}} / {{balance_due_date}} merge tokens.
  depositDueDate: timestamp("deposit_due_date"),
  balanceDueDate: timestamp("balance_due_date"),
  lateFee: decimal("late_fee", { precision: 10, scale: 2 }),
  lateFeeManualOverride: boolean("late_fee_manual_override").default(false),
  isPaid: boolean("is_paid").notNull().default(false),
  // Explicit no-show flag, used by the reports no-show-rate section.
  // Mutually independent from `cancelledAt` — an invoice can be marked as a
  // no-show without being a hard cancellation, e.g. when the event was a paid
  // no-show under contract.
  noShow: boolean("no_show").notNull().default(false),
  rep: varchar("rep", { length: 100 }),
  artist: varchar("artist", { length: 100 }).notNull().default(""),
  createdBy: varchar("created_by").notNull(),
  // Task #241: timestamp + recipient of the most recent successful "email
  // me a copy" / "Email PDF" send. Surfaced inline on the public invoice
  // page (and the internal preview header) so clients see persistent
  // confirmation that their copy actually arrived, not just a transient
  // toast that disappears on refresh.
  lastEmailedAt: timestamp("last_emailed_at"),
  lastEmailedTo: varchar("last_emailed_to", { length: 255 }),
  // Phase 2.5 — auto-send retry bookkeeping for recurring-series invoices.
  // The sweep retries up to 3 times across sweeps, then notifies the rep.
  autoSendAttempts: integer("auto_send_attempts").notNull().default(0),
  autoSendLastAttemptAt: timestamp("auto_send_last_attempt_at"),
  autoSendLastError: text("auto_send_last_error"),
  // Sample-seed marker — see leads.isDemoSeed.
  isDemoSeed: boolean("is_demo_seed").notNull().default(false),
  // ── Eventbrite (Task #694) ─────────────────────────────────────────
  // True iff the user flagged this booking as a ticketed/settlement-
  // later show. The invoice starts at $0/TBD; once the EB event ends
  // and a payout is reported, the settlement worker rewrites
  // eventRate to the NET payout (gross − fees − refunds) and, when
  // the payout is "paid_out", flips the invoice paid via the existing
  // markInvoicePaidIfUnpaid path. Pull-only — we never push events
  // back to Eventbrite from here.
  eventbriteEnabled: boolean("eventbrite_enabled").notNull().default(false),
  eventbriteEventId: varchar("eventbrite_event_id", { length: 64 }),
  // Snapshot of the most recent EB settlement so retries are idempotent.
  eventbritePayoutId: varchar("eventbrite_payout_id", { length: 64 }),
  // Task #694 — bigint cents per platform money convention. EB
  // gross/fees/refunds/net values for high-volume promoters (e.g.
  // multi-night festivals) routinely exceed 32-bit integer range
  // when expressed in cents, so all four are bigint columns. We
  // use Drizzle's `mode: "number"` because EB amounts in cents
  // sit comfortably within Number.MAX_SAFE_INTEGER (~$90T).
  eventbriteGrossCents: bigint("eventbrite_gross_cents", { mode: "number" }),
  eventbriteFeesCents: bigint("eventbrite_fees_cents", { mode: "number" }),
  eventbriteRefundsCents: bigint("eventbrite_refunds_cents", { mode: "number" }),
  eventbriteNetCents: bigint("eventbrite_net_cents", { mode: "number" }),
  eventbritePayoutPaidAt: timestamp("eventbrite_payout_paid_at"),
  eventbriteSettledAt: timestamp("eventbrite_settled_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
  // Task #705 — Ads Manager Phase 1 attribution. Forwarded from the
  // originating lead/client when the invoice is created so revenue can
  // be credited back to a campaign by the Conversions-API ping job.
  attributionVisitorId: varchar("attribution_visitor_id", { length: 64 }),
  attributionGclid: varchar("attribution_gclid", { length: 255 }),
  attributionFbclid: varchar("attribution_fbclid", { length: 255 }),
  // Task #706 — Phase 2 click ids (TikTok / LinkedIn).
  attributionTtclid: varchar("attribution_ttclid", { length: 255 }),
  attributionLiFatId: varchar("attribution_li_fat_id", { length: 255 }),
  attributionUtmSource: varchar("attribution_utm_source", { length: 128 }),
  attributionUtmMedium: varchar("attribution_utm_medium", { length: 128 }),
  attributionUtmCampaign: varchar("attribution_utm_campaign", { length: 255 }),
  attributionUtmTerm: varchar("attribution_utm_term", { length: 255 }),
  attributionUtmContent: varchar("attribution_utm_content", { length: 255 }),
  attributionLandingUrl: varchar("attribution_landing_url", { length: 2048 }),
  attributionCampaignId: varchar("attribution_campaign_id"),
  attributionCapturedAt: timestamp("attribution_captured_at"),
  // Task #205 — Sponsor Hub. 'standard' (default) vs 'sponsor'. Sponsor invoices
  // are excluded from the main invoice list and shown only in Sponsor Hub + Billing.
  invoiceType: varchar("invoice_type", { length: 50 }).notNull().default("standard"),
}, (t) => ({
  workspaceDeletedIdx: index("invoices_ws_deleted_idx").on(t.workspaceId, t.deletedAt),
  workspaceStatusIdx: index("invoices_ws_status_idx").on(t.workspaceId, t.status),
  clientIdx: index("invoices_client_idx").on(t.clientId),
  talentIdx: index("invoices_talent_idx").on(t.talentId),
  eventbriteEventIdx: index("invoices_eventbrite_event_idx").on(t.workspaceId, t.eventbriteEventId),
  regionIdx: index("invoices_region_idx").on(t.regionId),
  workspaceEventDateIdx: index("invoices_ws_event_date_idx").on(t.workspaceId, t.dateOfEvent),
  workspacePaidIdx: index("invoices_ws_paid_idx").on(t.workspaceId, t.isPaid),
  // Backs `getInvoicesPage` cursor traversal — keyset seeks on
  // `(invoiceDate DESC, id DESC)` so Postgres can satisfy each page in
  // O(limit) with a single index seek instead of a full sort.
  workspaceInvoiceDateIdx: index("invoices_ws_invoice_date_idx").on(t.workspaceId, t.invoiceDate, t.id),
}));

export const invoicesRelations = relations(invoices, ({ one }) => ({
  client: one(clients, {
    fields: [invoices.clientId],
    references: [clients.id],
  }),
  talent: one(talent, {
    fields: [invoices.talentId],
    references: [talent.id],
  }),
}));

// ── Task #205 — Sponsor Hub ───────────────────────────────────────────────────
// Fully isolated domain: sponsor_types / sponsor_leads / sponsor_clients /
// sponsor_lead_activities. No FK dependency on client_types or business_type.
// Gated by workspaces.sponsor_hub_enabled (off by default).

export const sponsorTypes = pgTable("sponsor_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  isDefault: boolean("is_default").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceNameUq: uniqueIndex("sponsor_types_workspace_name_uq").on(t.workspaceId, t.name),
}));

export const insertSponsorTypeSchema = createInsertSchema(sponsorTypes).omit({ id: true, createdAt: true });
export type InsertSponsorType = z.infer<typeof insertSponsorTypeSchema>;
export type SponsorType = typeof sponsorTypes.$inferSelect;

export const sponsorLeads = pgTable("sponsor_leads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  sponsorTypeId: varchar("sponsor_type_id").references(() => sponsorTypes.id, { onDelete: "set null" }),
  regionId: varchar("region_id"),
  talentId: varchar("talent_id"),
  leadName: varchar("lead_name", { length: 255 }),
  phoneNumber: text("phone_number"),
  emailAddress: varchar("email_address", { length: 255 }),
  contactName: varchar("contact_name", { length: 255 }),
  lastContacted: timestamp("last_contacted"),
  notes: text("notes"),
  rating: integer("rating"),
  address: text("address"),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  rep: varchar("rep", { length: 100 }),
  emailOptedOut: boolean("email_opted_out").notNull().default(false),
  associatedEventId: varchar("associated_event_id").references(() => invoices.id, { onDelete: "set null" }),
  convertedAt: timestamp("converted_at"),
  convertedToClientId: varchar("converted_to_client_id"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (t) => ({
  workspaceDeletedIdx: index("sponsor_leads_ws_deleted_idx").on(t.workspaceId, t.deletedAt),
  workspaceCreatedIdx: index("sponsor_leads_ws_created_idx").on(t.workspaceId, t.createdAt),
}));

export const sponsorLeadsRelations = relations(sponsorLeads, ({ one, many }) => ({
  sponsorType: one(sponsorTypes, { fields: [sponsorLeads.sponsorTypeId], references: [sponsorTypes.id] }),
  activities: many(sponsorLeadActivities),
}));

export const insertSponsorLeadSchema = createInsertSchema(sponsorLeads).omit({ id: true, createdAt: true, updatedAt: true, convertedAt: true, convertedToClientId: true });
export type InsertSponsorLead = z.infer<typeof insertSponsorLeadSchema>;
export type SponsorLead = typeof sponsorLeads.$inferSelect;

export const sponsorLeadActivities = pgTable("sponsor_lead_activities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  sponsorLeadId: varchar("sponsor_lead_id").notNull(),
  type: varchar("type", { length: 50 }).notNull(),
  notes: text("notes"),
  createdBy: varchar("created_by").notNull(),
  createdByName: varchar("created_by_name", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  leadCreatedIdx: index("sponsor_lead_activities_lead_created_idx").on(t.sponsorLeadId, t.createdAt),
}));

export const sponsorLeadActivitiesRelations = relations(sponsorLeadActivities, ({ one }) => ({
  sponsorLead: one(sponsorLeads, { fields: [sponsorLeadActivities.sponsorLeadId], references: [sponsorLeads.id] }),
}));

export const insertSponsorLeadActivitySchema = createInsertSchema(sponsorLeadActivities).omit({ id: true, createdAt: true });
export type InsertSponsorLeadActivity = z.infer<typeof insertSponsorLeadActivitySchema>;
export type SponsorLeadActivity = typeof sponsorLeadActivities.$inferSelect;

export const sponsorClients = pgTable("sponsor_clients", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  sponsorLeadId: varchar("sponsor_lead_id"),
  sponsorTypeId: varchar("sponsor_type_id").references(() => sponsorTypes.id, { onDelete: "set null" }),
  regionId: varchar("region_id"),
  talentId: varchar("talent_id"),
  associatedEventId: varchar("associated_event_id").references(() => invoices.id, { onDelete: "set null" }),
  accountName: varchar("account_name", { length: 255 }),
  phoneNumber: text("phone_number"),
  emailAddress: varchar("email_address", { length: 255 }),
  contactName: varchar("contact_name", { length: 255 }),
  address: text("address"),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  notes: text("notes"),
  rep: varchar("rep", { length: 100 }),
  emailOptedOut: boolean("email_opted_out").notNull().default(false),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (t) => ({
  workspaceDeletedIdx: index("sponsor_clients_ws_deleted_idx").on(t.workspaceId, t.deletedAt),
  workspaceCreatedIdx: index("sponsor_clients_ws_created_idx").on(t.workspaceId, t.createdAt),
}));

export const sponsorClientsRelations = relations(sponsorClients, ({ one }) => ({
  sponsorType: one(sponsorTypes, { fields: [sponsorClients.sponsorTypeId], references: [sponsorTypes.id] }),
}));

export const insertSponsorClientSchema = createInsertSchema(sponsorClients).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertSponsorClient = z.infer<typeof insertSponsorClientSchema>;
export type SponsorClient = typeof sponsorClients.$inferSelect;


// ── Email Template Scopes
export type TemplateScope = "STANDARD" | "USER" | "ADMIN_PRIVATE";

// Confirmation Email Templates - per client type, talent, and show offering
export const confirmationEmailTemplates = pgTable("confirmation_email_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  clientType: varchar("client_type", { length: 50 }).notNull(), // schools, corporate, private, non_profit, government
  talentId: varchar("talent_id").references(() => talent.id, { onDelete: "set null" }),
  eventOfferingId: varchar("event_offering_id").references(() => eventOfferings.id, { onDelete: "set null" }),
  subject: varchar("subject", { length: 255 }).notNull(),
  body: text("body").notNull(),
  // 'standard' | 'multi_show' | 'multi_talent' | 'multi_combined'
  // Determines which variant of the template engine is used. The
  // confirmation-draft endpoint chooses the variant based on whether
  // the invoice spans multiple bookings (run) and/or multiple talent.
  variantType: varchar("variant_type", { length: 24 }).notNull().default("standard"),
  isActive: boolean("is_active").notNull().default(true),
  // Workspace-wide fallback template used when no client-type-specific template matches.
  isWorkspaceFallback: boolean("is_workspace_fallback").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Talent-facing invoice rows include an `eventTime` field that the
// `getInvoicesByTalent` storage method derives by falling back to the
// linked client's event time when the invoice itself doesn't store one.
// (The invoices table itself does not carry an event_time column —
// only clients/leads do.)
//
// `venueCity` / `venueState` are similarly backfilled from the linked
// client. Invoices only persist a single concatenated `accountAddress`
// string, so without this join the talent dashboard can't render a
// clean "City, ST" line for upcoming shows (Task #208).
export type TalentInvoice = Invoice & {
  eventTime: string | null;
  venueCity: string | null;
  venueState: string | null;
};

// Talent-facing runsheet row. `getRunsheetForTalent` walks the talent's
// invoices for a given day and decorates each row with the linked
// client's address/contact/event-time so the mobile run sheet doesn't
// have to do an N+1 fetch. Same fallback rationale as `TalentInvoice`:
// the invoices table doesn't store event_time, so we read it off the
// joined client row.
export type TalentRunsheetShow = {
  id: string;
  showDate: Date | null;
  eventTime: string | null;
  endTime: string | null;
  venueName: string | null;
  venueAddress: string | null;
  venueCity: string | null;
  venueState: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  notes: string | null;
  status: string | null;
  talentName: string | null;
  showType: string | null;
  packagePrice: string | null;
  arrivedAt: Date | null;
  completedAt: Date | null;
  clientId: string;
};

export const insertConfirmationEmailTemplateSchema = createInsertSchema(confirmationEmailTemplates).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertConfirmationEmailTemplate = z.infer<typeof insertConfirmationEmailTemplateSchema>;
export type ConfirmationEmailTemplate = typeof confirmationEmailTemplates.$inferSelect;

// "Booked Without Calendar" template — sent to a talent who's been booked but
// hasn't connected their Google Calendar. This is a tenant-owned (workspace-scoped)
// system template, stored in `confirmation_email_templates` under this reserved
// clientType. Each workspace edits its own copy; the default below is the fallback
// when a workspace hasn't customized it. Supported variables: {{talent_name}},
// {{account_name}}, {{event_date}}, {{event_time}}, {{event_details}},
// {{venue_address}}, {{connect_calendar_url}}.
export const NO_CALENDAR_TEMPLATE_CLIENT_TYPE = "talent_booking_no_calendar" as const;

export const DEFAULT_NO_CALENDAR_TEMPLATE = {
  subject: "You've Been Booked! — {{account_name}}",
  body: `<p>Hi {{talent_name}},</p>

<p>Great news — you've been booked for an event and we wanted to make sure you have all the details:</p>

<ul>
  <li><strong>Client / Venue:</strong> {{account_name}}</li>
  <li><strong>Date:</strong> {{event_date}}</li>
  <li><strong>Time:</strong> {{event_time}}</li>
  <li><strong>Event Type:</strong> {{event_details}}</li>
  <li><strong>Location:</strong> {{venue_address}}</li>
</ul>

<p>To make sure this event appears on your Google Calendar automatically for future bookings, please take a moment to connect your calendar:</p>

<p style="margin: 24px 0;">
  <a href="{{connect_calendar_url}}" style="background-color:#2563eb;color:#fff;padding:12px 24px;border-radius:6px;text-decoration:none;display:inline-block;">
    Connect Your Calendar
  </a>
</p>

<p style="color:#666;font-size:14px;">This link expires in 7 days. If you have questions, just reply to this email.</p>`,
} as const;

// Email Templates
export const emailTemplates = pgTable("email_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  subject: varchar("subject", { length: 255 }).notNull(),
  body: text("body").notNull(),
  bodyRich: text("body_rich"), // Sanitized HTML for rich text
  scope: varchar("scope", { length: 20 }).notNull().default("USER"), // STANDARD, USER, ADMIN_PRIVATE
  ownerUserId: varchar("owner_user_id"), // null for STANDARD, user id for USER/ADMIN_PRIVATE
  tags: text("tags"), // comma-separated tags
  // Stable identifier for rows inserted by `server/seed-automations.ts`.
  // Null for everything else (USER/ADMIN_PRIVATE, admin-created STANDARD
  // templates). Used as the canonical idempotency key so that renaming a
  // seeded preset in the UI no longer causes the next boot to insert a
  // fresh duplicate. Unique per workspace via `email_templates_ws_seed_key_uniq`.
  seedKey: varchar("seed_key", { length: 100 }),
  // Role-based visibility for STANDARD (master) templates only.
  // Empty array = visible to everyone (default, preserves legacy behavior).
  // Populated = restricted to listed roles. Admins always bypass this filter.
  // Has no effect for USER or ADMIN_PRIVATE scopes.
  visibleToRoles: text("visible_to_roles").array().notNull().default(sql`'{}'::text[]`),
  // Admin-only flag (task #696). When true, the template is filtered out of
  // every interactive human-facing template picker (composer, inbox replies,
  // invoices, payment email, mass email, leads/clients spreadsheet bulk
  // actions, public review follow-up, etc.). Automations / drip campaigns /
  // reminder + campaign schedulers — anything resolving by id or seedKey —
  // continue to use the template regardless of this flag, so admins can hide
  // automation-only seeds without breaking the system fires that depend on
  // them. Surfaced + togglable only by admins on the Templates Manager.
  hiddenFromPickers: boolean("hidden_from_pickers").notNull().default(false),
  isActive: boolean("is_active").default(true),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  // Unique-per-workspace so seed inserts can't race-create duplicates.
  // Postgres treats NULLs as distinct in unique indexes, so non-seeded
  // rows (which leave seed_key null) are unaffected.
  workspaceSeedKeyUniq: uniqueIndex("email_templates_ws_seed_key_uniq").on(t.workspaceId, t.seedKey),
}));

// Persistent record of preset-template seed failures from
// `server/seed-automations.ts` (task #127). Replaces the in-process
// `failureCache` introduced by task #118 so admins, alerting, and other
// services see the same view across restarts and across processes in
// multi-instance deployments. Rows are upserted on a failed seed attempt
// and deleted as soon as the matching seed succeeds for the workspace,
// keyed per (workspace, seedKey) so a single workspace can surface
// multiple failed seeds independently.
export const seedAutomationFailures = pgTable("seed_automation_failures", {
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  seedKey: varchar("seed_key", { length: 100 }).notNull(),
  // Stringified error message captured from the failing insert/backfill
  // call. Mirrors the `err` field on the in-process WorkspaceSeedFailure
  // shape so the status endpoint formatting is unchanged.
  error: text("error").notNull(),
  // First time this (workspace, seedKey) failure was observed since the
  // last successful run. Useful for alerting on long-standing failures.
  firstSeenAt: timestamp("first_seen_at").defaultNow().notNull(),
  // Most recent attempt timestamp, refreshed on every retry so the
  // Email Hub banner shows when ops last saw the problem.
  lastAttemptAt: timestamp("last_attempt_at").defaultNow().notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.workspaceId, table.seedKey] }),
}));
export type SeedAutomationFailure = typeof seedAutomationFailures.$inferSelect;

// Append-only history of every seed-failure event (task #210).
//
// The live `seed_automation_failures` table only carries rows for seeds
// that are *currently* broken — a successful retry deletes the row, so
// any "new failures in the last N days" stat derived from it can only
// see currently-broken seeds and silently undercounts churn (a seed
// that broke and was fixed inside the window vanishes from the trend).
//
// This table records one row per failed seed attempt, regardless of
// whether the seed eventually recovers, so the platform "Seed Failures"
// page can show real regression patterns over time. Rows are pruned by
// `pruneSeedFailureHistory` after a 365-day retention window so the
// table doesn't grow forever — long enough to surface yearly trends,
// short enough to stay cheap to scan.
//
// `observedAt` is indexed for the prune sweep and the windowed
// aggregation queries; `(seed_key, observed_at)` is indexed for the
// "top failing seed keys in the last 30 days" group-by.
//
// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `observed_at`. See `docs/security/audit-log-policy.md`.
export const seedAutomationFailureHistory = pgTable("seed_automation_failure_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  seedKey: varchar("seed_key", { length: 100 }).notNull(),
  // Stringified error message captured at the moment the seed attempt
  // failed. Mirrors `seed_automation_failures.error` so historical rows
  // remain readable even after the live row has been cleared.
  error: text("error").notNull(),
  observedAt: timestamp("observed_at").defaultNow().notNull(),
}, (table) => ({
  observedAtIdx: index("seed_automation_failure_history_observed_at_idx").on(table.observedAt),
  seedKeyObservedAtIdx: index("seed_automation_failure_history_seed_key_observed_at_idx").on(table.seedKey, table.observedAt),
}));
export type SeedAutomationFailureHistory = typeof seedAutomationFailureHistory.$inferSelect;

// Persistent throttle backing for the virus-scanner failure alerter
// (task #257). The alerter previously kept its per-scanner "last alerted
// at" map in process memory only, so a deploy or crash reset the
// throttle and the next tick re-paged on-call for an outage ops was
// already mid-incident on. Scanner outages (clamd OOMs, VirusTotal rate
// limits) happen often enough that the duplicate-page risk hurts more
// than the seed-automation equivalent.
//
// One row per scanner name (`clamd`, `virustotal`, etc). The alerter
// reads this lazily for any candidate that breaches its threshold and
// upserts on every fresh alert. A subsequent process — same instance
// after restart, or a peer instance in a multi-instance deploy — sees
// the timestamp and respects the throttle window (default 30 min).
export const scannerAlertThrottle = pgTable("scanner_alert_throttle", {
  scanner: varchar("scanner", { length: 64 }).primaryKey(),
  lastAlertedAt: timestamp("last_alerted_at").notNull(),
});
export type ScannerAlertThrottle = typeof scannerAlertThrottle.$inferSelect;

// Per-scanner severity transition state for the admin notification
// dispatch in the virus-scanner failure alerter (task #462).
//
// The on-call pager throttle in `scanner_alert_throttle` (task #257)
// answers "have we already paged on this scanner recently?". This
// table answers a different, orthogonal question: "what was the last
// per-scanner severity (ok/warn/crit) we observed and notified
// admins about?". The alerter detects transitions into `crit`
// (threshold breached) and back to `ok` (recovery) and fans out an
// in-app + system-email notification to every workspace's admins so
// they don't have to keep the Scanner Health page open.
//
// Persisting the state lets transitions survive process restarts —
// a deploy mid-incident must not re-notify on a still-broken
// scanner, and a scanner that recovered while the process was down
// must not re-notify on the next boot. The `last_notified_at`
// column drives the per-scanner notification throttle that prevents
// a flapping scanner from spamming admins.
export const scannerHealthState = pgTable("scanner_health_state", {
  scanner: varchar("scanner", { length: 64 }).primaryKey(),
  // One of "ok" | "warn" | "crit". Mirrors the admin panel's
  // `severityFor()` calculation so the dot the admin sees matches
  // what would notify.
  lastSeverity: varchar("last_severity", { length: 8 }).notNull(),
  // When `lastSeverity` was first observed — i.e. the timestamp of
  // the most recent transition. Useful for diagnostics and as a
  // building block for future "scanner has been red for 2h" alerts.
  lastTransitionAt: timestamp("last_transition_at").notNull(),
  // The severity we last notified admins about, NULL if we've
  // never notified for this scanner yet. Stops us re-notifying on
  // the same side of the watershed twice (crit → still-crit).
  lastNotifiedSeverity: varchar("last_notified_severity", { length: 8 }),
  // When we last sent an admin notification for this scanner. The
  // alerter uses this together with the configured notify-throttle
  // window to suppress repeats while a flapping scanner thrashes.
  lastNotifiedAt: timestamp("last_notified_at"),
});
export type ScannerHealthState = typeof scannerHealthState.$inferSelect;

// Per-workspace Stripe Connect webhook health state (task #669).
//
// The Stripe webhook handler in `server/webhookHandlers.ts` is the
// happy path for marking invoices paid the instant the customer
// completes Stripe Checkout. When the webhook misses (account event
// dropped, our endpoint 5xx'd, or the platform's webhook subscription
// is misconfigured), the public success-redirect catches the most
// common case but a customer who closes the tab before redirect leaves
// us with a paid Stripe session and an unpaid invoice in our DB.
//
// The reconciler scheduler (server/stripe-checkout-reconciler-scheduler.ts)
// closes that gap by polling Stripe for completed Checkout Sessions per
// connected account and flipping any invoices it finds unpaid via the
// shared `markStripeInvoicePaid` helper. The webhook health probe
// (server/lib/stripe-webhook-health.ts) classifies each workspace's
// Stripe Connect setup as healthy / missing_events / failing_deliveries
// / not_configured so admins can see the wiring status without paging
// support.
//
// This table is the persistent state for the probe + alert gate. One
// row per workspace; the alerter notifies admins on healthy → unhealthy
// transitions and stays silent while the status is unchanged so a
// long-running misconfiguration can't spam the inbox.
export const stripeWebhookHealthState = pgTable("stripe_webhook_health_state", {
  workspaceId: varchar("workspace_id").primaryKey().references(() => workspaces.id, { onDelete: "cascade" }),
  // One of "healthy" | "missing_events" | "failing_deliveries" |
  // "not_configured". Mirrors the badge the admin sees in the
  // Payment Portal dialog so the inbox row matches the live UI.
  status: varchar("status", { length: 32 }).notNull(),
  // Free-form diagnostic detail from the most recent probe run
  // (e.g. "missing event: checkout.session.completed", or the count
  // of unprocessed sessions the reconciler caught). Surfaced in the
  // admin UI tooltip; truncated to keep the row bounded.
  detail: text("detail"),
  // When the most recent probe ran. Drives the "last checked X minutes
  // ago" hint in the admin UI.
  lastCheckedAt: timestamp("last_checked_at").notNull(),
  // The status we last notified admins about, NULL if we've never
  // notified. Stops a still-unhealthy workspace from re-paging on
  // every tick.
  lastNotifiedStatus: varchar("last_notified_status", { length: 32 }),
  // When we last sent an admin notification for this workspace.
  // Used together with the configured throttle window to suppress
  // re-pages while a flapping account thrashes.
  lastNotifiedAt: timestamp("last_notified_at"),
});
export type StripeWebhookHealthState = typeof stripeWebhookHealthState.$inferSelect;
export type StripeWebhookHealthStatus = "healthy" | "missing_events" | "failing_deliveries" | "not_configured";

// One row per Stripe Checkout Session the backstop reconciler caught
// (i.e. flipped paid because the webhook missed it). Drives the TRUE
// rolling-24h `failing_deliveries` classification: the probe queries
// `COUNT(*) WHERE caughtAt >= now() - 24h` instead of leaning on a
// counter that has to be reset on a fixed schedule. Rows older than
// the rolling window are pruned by the probe scheduler so this table
// stays small (one row per genuine webhook miss).
export const stripeReconcilerCatches = pgTable("stripe_reconciler_catches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // The invoice the reconciler flipped paid. References invoices(id)
  // intentionally without a FK so deleting an invoice doesn't blow
  // away the audit trail of the webhook miss.
  invoiceId: varchar("invoice_id").notNull(),
  // Stripe Checkout Session id the reconciler matched. Surfaced in
  // the per-session warning log so on-call can pivot from the log to
  // the Stripe Dashboard.
  sessionId: varchar("session_id").notNull(),
  // When the reconciler made the catch (server time). Used by the
  // probe to compute the rolling 24h window and by the admin UI to
  // show "X minutes ago".
  caughtAt: timestamp("caught_at").notNull().defaultNow(),
  // Lateness window in minutes: how far behind the customer's actual
  // payment we caught the miss. Logged so on-call can spot patterns
  // (e.g. "every miss is ~6 minutes late ⇒ webhook handler is
  // crashing on retry but eventually our 5-min poll picks it up").
  caughtLateByMinutes: integer("caught_late_by_minutes").notNull(),
}, (table) => [
  // Composite index supports the probe's "any catches in the last
  // 24h for this workspace?" query without scanning the whole table.
  index("idx_stripe_reconciler_catches_ws_caught").on(table.workspaceId, table.caughtAt),
]);
export type StripeReconcilerCatch = typeof stripeReconcilerCatches.$inferSelect;

// Persistent backing for the virus-scanner rolling metric window
// (task #453). Task #257 made the per-scanner alert throttle survive
// process restarts; the companion data the alerter actually inspects
// — recent scan events — still lived in process memory only. So a
// deploy / crash mid-incident reset the count of "errors in the last
// N minutes" and the very next post-restart tick could under-count
// errors and silently miss what would have been the next page.
// Multi-instance deploys also evaluated independent windows because
// each instance only saw its own slice of traffic.
//
// One row per recorded scan event. The metrics module writes here on
// every `recordScan()` and reads here on every `getErrorRateSnapshot()`,
// so any process — same instance after restart, or a peer instance in
// a multi-instance deploy — sees the same window. Old events outside
// the rolling horizon are pruned on a throttled schedule from the
// write path so the table stays bounded.
export const scannerMetricEvents = pgTable(
  "scanner_metric_events",
  {
    id: serial("id").primaryKey(),
    scanner: varchar("scanner", { length: 64 }).notNull(),
    verdict: varchar("verdict", { length: 16 }).notNull(),
    threat: text("threat"),
    occurredAt: timestamp("occurred_at").notNull().defaultNow(),
  },
  (t) => ({
    occurredAtIdx: index("scanner_metric_events_occurred_at_idx").on(
      t.occurredAt,
    ),
  }),
);
export type ScannerMetricEventRow = typeof scannerMetricEvents.$inferSelect;

// Per-workspace dedup state for the boot-time seed-failure admin emails
// (task #157). When the boot seeder finishes with a non-empty
// `failedSeedKeys` for a workspace we email its admins so they don't
// have to discover the in-app banner. We only want to send one email
// per (workspace, set-of-failed-keys), so we record the sorted, joined
// seed-key signature here. The next boot only re-emails when the
// signature changes (i.e. a different set of seeds has regressed).
//
// The retry endpoint deletes the row when the workspace's seeds all
// succeed, so a future regression reliably re-pages admins instead of
// being silently suppressed by a stale dedup record.
export const seedAutomationFailureNotifications = pgTable("seed_automation_failure_notifications", {
  workspaceId: varchar("workspace_id").primaryKey().references(() => workspaces.id, { onDelete: "cascade" }),
  // Sorted, comma-joined list of failed seedKeys at the moment of
  // sending. We compare incoming failure sets against this signature
  // to dedup; any difference triggers a fresh email.
  notifiedSeedKeys: text("notified_seed_keys").notNull(),
  lastNotifiedAt: timestamp("last_notified_at").defaultNow().notNull(),
  // Sender attribution (task #192). The notice fans out via the
  // workspace Gmail connector for `system.*` categories, but neither
  // the email body nor the admin diagnostics card previously revealed
  // *which* Gmail account it was sent from. We capture both:
  //   - `senderUserId`: the first admin we'd have sent-as if the
  //     legacy per-admin OAuth path were still in use. Useful for
  //     traceability when an admin asks "who's the From: header tied
  //     to" and not just the email itself.
  //   - `senderEmail`: the actual address the connector ended up
  //     sending from (i.e. `getGmailConnectorEmail()`), so admins can
  //     check the right inbox when a test notice doesn't show up.
  // Both nullable so legacy rows from before this column existed
  // still load cleanly.
  senderUserId: varchar("sender_user_id"),
  senderEmail: text("sender_email"),
});
export type SeedAutomationFailureNotification = typeof seedAutomationFailureNotifications.$inferSelect;

// Append-only fan-out log for the seed-failure admin notifier (task #200).
//
// The dedup row in `seed_automation_failure_notifications` only stores the
// *most recent* sender for a workspace, so when the Gmail connector
// account changes between sends the audit trail of "which inbox sent the
// April 12 notice" is lost on the very next send. This table captures one
// row per actual fan-out so admins can answer that question from the
// diagnostics card without grepping server logs.
//
// One row per successful notifier delivery: written immediately after the
// notify() helper reports a non-zero `inAppCreated + emailsSent`. The
// table is append-only — the dedup row continues to gate whether a fresh
// send happens; this log just records each one that does.
//
// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `sent_at`. See `docs/security/audit-log-policy.md`.
export const seedAutomationFailureNotificationLog = pgTable("seed_automation_failure_notification_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // Same canonical sorted+joined seedKey set the dedup row stores. Kept
  // here so the history view can render the failure set per-row without
  // re-joining against the dedup table (which only has the *latest* set).
  signature: text("signature").notNull(),
  // Sender attribution captured at delivery time. Both nullable for the
  // same reasons as the dedup row's columns: legacy rows pre-date the
  // attribution (n/a here since the log is new in #200, but kept
  // nullable so a connector lookup that returns null doesn't crash the
  // write), and the connector lookup itself is best-effort.
  senderUserId: varchar("sender_user_id"),
  senderEmail: text("sender_email"),
  // How many recipients the notify() fan-out reported. Useful in the
  // history UI so admins can spot a "we got paged but only 0 admins
  // received it" anomaly per row.
  recipientCount: integer("recipient_count").notNull().default(0),
  sentAt: timestamp("sent_at").defaultNow().notNull(),
}, (t) => ({
  // Indexed so the per-workspace "last N sends" query stays cheap as
  // the log grows. Composite (workspace_id, sent_at DESC) is the exact
  // shape `getSeedFailureNotificationHistory` selects on.
  workspaceSentAtIdx: index("seed_failure_notif_log_ws_sent_idx").on(t.workspaceId, t.sentAt),
}));
export type SeedAutomationFailureNotificationLogRow = typeof seedAutomationFailureNotificationLog.$inferSelect;

// Automation Trigger Types
export type AutomationTriggerType = "TOUR_BEFORE_REGION" | "POST_PERFORMANCE" | "INVOICE_DELINQUENT";
export type AutomationAudienceType = "REGION" | "CONTACTS" | "CLIENT";
export type AutomationOffsetUnit = "DAYS" | "WEEKS" | "MONTHS";

// Email Automations
export const emailAutomations = pgTable("email_automations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  scope: varchar("scope", { length: 20 }).notNull().default("ADMIN_PRIVATE"), // ADMIN_PRIVATE for admin-only automations
  ownerUserId: varchar("owner_user_id"), // User who owns this automation
  assignedToUserId: varchar("assigned_to_user_id"), // User this automation is assigned to, or "CLIENT_REP" for client's assigned rep
  triggerType: varchar("trigger_type", { length: 50 }).notNull(), // BEFORE_EVENT_DATE, AFTER_EVENT_DATE (0 = day of event)
  triggerOffsetValue: integer("trigger_offset_value").notNull().default(0), // Number of days before/after show
  triggerOffsetUnit: varchar("trigger_offset_unit", { length: 20 }).notNull().default("DAYS"),
  audienceType: varchar("audience_type", { length: 20 }).notNull().default("CLIENT"), // CLIENT only now
  audienceValue: text("audience_value"), // Not used for CLIENT audience type
  // Empty array = applies to all client types. Populated = restricted to those client types.
  clientTypeIds: text("client_type_ids").array().notNull().default(sql`'{}'::text[]`),
  templateId: varchar("template_id").notNull(),
  sendTime: varchar("send_time", { length: 10 }).default("09:00"), // Time to send in HH:mm format
  sendTimezone: varchar("send_timezone", { length: 50 }).default("CLIENT"), // Timezone or "CLIENT" for client's address timezone
  requireUnpaidInvoice: boolean("require_unpaid_invoice").default(false), // Only send if invoice is not marked paid
  // Cooldown window (days) used by the reminder scheduler to suppress repeat sends
  // to the same recipient. Read in server/reminder-scheduler.ts.
  cooldownDays: integer("cooldown_days").default(90),
  isActive: boolean("is_active").default(true),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const emailAutomationsRelations = relations(emailAutomations, ({ one }) => ({
  template: one(emailTemplates, {
    fields: [emailAutomations.templateId],
    references: [emailTemplates.id],
  }),
}));

// Automation Execution Logs
export const automationLogs = pgTable("automation_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  automationId: varchar("automation_id").notNull(),
  runAt: timestamp("run_at").defaultNow(),
  status: varchar("status", { length: 50 }).notNull(), // success, partial, failed
  sentCount: integer("sent_count").default(0),
  failedCount: integer("failed_count").default(0),
  results: text("results"), // JSON with details
  createdAt: timestamp("created_at").defaultNow(),
});

export const automationLogsRelations = relations(automationLogs, ({ one }) => ({
  automation: one(emailAutomations, {
    fields: [automationLogs.automationId],
    references: [emailAutomations.id],
  }),
}));

// Email Send Logs - tracks individual emails sent to recipients for cooldown/deduplication
export const emailSendLogs = pgTable("email_send_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  automationId: varchar("automation_id"), // null for manual sends
  templateId: varchar("template_id"),
  recipientEmail: varchar("recipient_email", { length: 255 }).notNull(),
  recipientType: varchar("recipient_type", { length: 20 }).notNull(), // lead, client, contact
  recipientId: varchar("recipient_id"), // lead_id, client_id, etc.
  subject: varchar("subject", { length: 500 }),
  // Threading metadata captured for in-app inbox + reply chaining
  messageIdHeader: varchar("message_id_header", { length: 998 }), // RFC Message-Id of the outbound email
  threadId: varchar("thread_id", { length: 255 }), // Gmail thread id
  inReplyTo: varchar("in_reply_to", { length: 998 }), // RFC In-Reply-To header (Message-Id of the message we replied to)
  toEmails: text("to_emails").array(), // Full To: list (RFC addresses)
  ccEmails: text("cc_emails").array(), // Full Cc: list
  bodyHtml: text("body_html"), // Rendered HTML body — used to display in inbox thread view
  sentAt: timestamp("sent_at").defaultNow(),
  sentBy: varchar("sent_by"), // user who triggered it (null for automated)
  status: varchar("status", { length: 20 }).notNull().default("sent"), // sent, failed, skipped
  trackingId: varchar("tracking_id").unique(), // UUID used for open/click pixel tracking
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceSentIdx: index("email_send_logs_ws_sent_idx").on(t.workspaceId, t.sentAt),
  recipientIdx: index("email_send_logs_recipient_idx").on(t.recipientType, t.recipientId),
  automationIdx: index("email_send_logs_automation_idx").on(t.automationId),
  threadIdx: index("email_send_logs_thread_idx").on(t.workspaceId, t.threadId),
}));

// Scheduled emails — backs the "Schedule Send" affordance on every direct
// email composer in the app (mass-email composer, invoice send-email,
// invoice send-to-user, payment-email modal, booking-confirmation flow,
// etc.). Rows are inserted at schedule time with the FULLY-RENDERED
// payload (subject + htmlBody after merge-var substitution + cc list +
// attachment metadata) so the dispatcher can call sendEmailAsUser
// directly without re-running any per-route validation. The sole
// exception is sourceType='mass_email' whose payload stores the original
// bulk-send body and is re-expanded via the bulk-send helper at dispatch
// time (since the recipient list can change between schedule + send).
//
// status lifecycle: pending → sending → sent | failed | cancelled.
// claimDueScheduledEmails atomically flips pending→sending where
// scheduledFor <= NOW() RETURNING the rows, so a multi-instance deploy
// can't double-fire the same schedule. Failed rows can be retried up to
// attemptCount=3 before terminal 'failed'.
export const scheduledEmails = pgTable("scheduled_emails", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // The user who scheduled it — also the gmail mailbox we send through.
  userId: varchar("user_id").notNull(),
  scheduledFor: timestamp("scheduled_for").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  // Determines which dispatcher branch to take in the scheduler.
  // 'one_off' covers anything dispatched as a fully-rendered email
  // (invoice_confirmation, invoice_send_to_user, payment_email,
  // booking_confirmation, document_email all reduce to one_off).
  // 'mass_email' triggers the bulk-send re-expansion path.
  sourceType: varchar("source_type", { length: 40 }).notNull(),
  // Fully-rendered email payload. For one_off:
  //   { to, cc?, subject, htmlBody, attachments?, leadId?, clientId?,
  //     invoiceId?, activityType? }.
  // For mass_email: the raw POST /api/email/bulk-send body.
  sourcePayload: jsonb("source_payload").notNull(),
  // Display columns so the Scheduled tab can render without parsing
  // the JSON payload on every row.
  recipientEmail: varchar("recipient_email", { length: 255 }),
  recipientName: varchar("recipient_name", { length: 255 }),
  subject: varchar("subject", { length: 500 }),
  attemptCount: integer("attempt_count").notNull().default(0),
  lastError: text("last_error"),
  sentAt: timestamp("sent_at"),
  cancelledAt: timestamp("cancelled_at"),
  cancelledByUserId: varchar("cancelled_by_user_id"),
  // Stamped by claimDueScheduledEmails when a row flips pending -> sending.
  // Used by the watchdog (requeueStaleClaimedScheduledEmails) to detect
  // rows where a worker crashed mid-dispatch so they can be returned to
  // pending instead of being silently lost.
  claimedAt: timestamp("claimed_at"),
  // Set immediately BEFORE the provider (Gmail) call inside the
  // dispatcher. The watchdog uses this to distinguish "worker died
  // before contacting Gmail" (safe to requeue) from "worker died
  // during/after Gmail call" (delivery status unknown — must NOT
  // requeue, gets parked in 'failed' with a needs-review marker
  // instead). See scheduled-email-scheduler.ts.
  lastSendAttemptedAt: timestamp("last_send_attempted_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  workspaceStatusForIdx: index("scheduled_emails_ws_status_for_idx").on(t.workspaceId, t.status, t.scheduledFor),
  // Cross-workspace claim index used by the scheduler's hot path.
  statusForIdx: index("scheduled_emails_status_for_idx").on(t.status, t.scheduledFor),
  userIdx: index("scheduled_emails_user_idx").on(t.userId),
}));

export const insertScheduledEmailSchema = createInsertSchema(scheduledEmails).omit({
  id: true,
  createdAt: true,
  sentAt: true,
  cancelledAt: true,
  cancelledByUserId: true,
  claimedAt: true,
  attemptCount: true,
  lastError: true,
  status: true,
});
export type InsertScheduledEmail = z.infer<typeof insertScheduledEmailSchema>;
export type ScheduledEmail = typeof scheduledEmails.$inferSelect;

export const emailSendLogsRelations = relations(emailSendLogs, ({ one }) => ({
  automation: one(emailAutomations, {
    fields: [emailSendLogs.automationId],
    references: [emailAutomations.id],
  }),
  template: one(emailTemplates, {
    fields: [emailSendLogs.templateId],
    references: [emailTemplates.id],
  }),
}));

export const insertEmailSendLogSchema = createInsertSchema(emailSendLogs).omit({ id: true, createdAt: true });
export type InsertEmailSendLog = z.infer<typeof insertEmailSendLogSchema>;
export type EmailSendLog = typeof emailSendLogs.$inferSelect;

// Email Events - open/click tracking events for all outbound emails
export const emailEvents = pgTable("email_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  trackingId: varchar("tracking_id").notNull(), // matches email_send_logs.tracking_id / drip_campaign_messages.tracking_id / campaign_recipients.tracking_id
  eventType: varchar("event_type", { length: 20 }).notNull(), // "open" | "click"
  url: text("url"), // for click events: the destination URL that was clicked
  userAgent: text("user_agent"),
  ipAddress: varchar("ip_address", { length: 50 }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  trackingTypeIdx: index("email_events_tracking_type_idx").on(t.trackingId, t.eventType),
  trackingCreatedIdx: index("email_events_tracking_created_idx").on(t.trackingId, t.createdAt),
}));

export const insertEmailEventSchema = createInsertSchema(emailEvents).omit({ id: true, createdAt: true });
export type InsertEmailEvent = z.infer<typeof insertEmailEventSchema>;
export type EmailEvent = typeof emailEvents.$inferSelect;

// Invoice Reminders - tracks sent reminder emails to prevent duplicates
export type ReminderType = "day_of_event" | "30_day" | "57_day" | "59_day";

export const invoiceReminders = pgTable("invoice_reminders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  invoiceId: varchar("invoice_id").notNull(),
  reminderType: varchar("reminder_type", { length: 50 }).notNull(),
  sentAt: timestamp("sent_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  invoiceTypeIdx: index("invoice_reminders_invoice_type_idx").on(t.invoiceId, t.reminderType),
}));

export const invoiceRemindersRelations = relations(invoiceReminders, ({ one }) => ({
  invoice: one(invoices, {
    fields: [invoiceReminders.invoiceId],
    references: [invoices.id],
  }),
}));

// Invoice Activities (for logging calls, emails, notes on invoices)
export type InvoiceActivityType = "call" | "email" | "note";

export const invoiceActivities = pgTable("invoice_activities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  invoiceId: varchar("invoice_id").notNull(),
  type: varchar("type", { length: 50 }).notNull(), // call, email, note
  notes: text("notes"),
  createdBy: varchar("created_by").notNull(),
  createdByName: varchar("created_by_name", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow(),
  trackingId: varchar("tracking_id"), // email tracking pixel ID for open detection
}, (t) => ({
  invoiceCreatedIdx: index("invoice_activities_invoice_created_idx").on(t.invoiceId, t.createdAt),
}));

export const invoiceActivitiesRelations = relations(invoiceActivities, ({ one }) => ({
  invoice: one(invoices, {
    fields: [invoiceActivities.invoiceId],
    references: [invoices.id],
  }),
}));

export const insertInvoiceActivitySchema = createInsertSchema(invoiceActivities).omit({ id: true, createdAt: true });
export type InsertInvoiceActivity = z.infer<typeof insertInvoiceActivitySchema>;
export type InvoiceActivity = typeof invoiceActivities.$inferSelect;

// Task #242 — Invoice Change Log (field-level audit trail). A dedicated
// table (rather than reusing invoice_activities, which is a notes-style
// timeline) because audit entries are a field-diff shape: one row per
// changed field capturing fieldName + old/new values + actor + timestamp.
// Workspace-scoped; populated automatically when tracked invoice fields
// change on the edit paths. No PDFs/HTML snapshots — text/data only.
export const invoiceChangeLogs = pgTable("invoice_change_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  invoiceId: varchar("invoice_id").notNull(),
  // Snapshot of the invoice number at change time so the log stays
  // searchable/displayable even if the invoice is later renumbered or
  // soft-deleted.
  invoiceNumber: varchar("invoice_number", { length: 50 }),
  fieldName: varchar("field_name", { length: 100 }).notNull(),
  // Human-readable label snapshot so the UI is decoupled from the
  // server-side tracked-field map.
  fieldLabel: varchar("field_label", { length: 150 }),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedBy: varchar("changed_by").notNull(),
  changedByName: varchar("changed_by_name", { length: 255 }),
  changedAt: timestamp("changed_at").notNull().defaultNow(),
}, (t) => ({
  workspaceChangedIdx: index("invoice_change_logs_workspace_changed_idx").on(t.workspaceId, t.changedAt),
  workspaceInvoiceChangedIdx: index("invoice_change_logs_workspace_invoice_changed_idx").on(t.workspaceId, t.invoiceId, t.changedAt),
}));

export const insertInvoiceChangeLogSchema = createInsertSchema(invoiceChangeLogs).omit({ id: true, changedAt: true });
export type InsertInvoiceChangeLog = z.infer<typeof insertInvoiceChangeLogSchema>;
export type InvoiceChangeLog = typeof invoiceChangeLogs.$inferSelect;

// Event Change Log — field-level audit trail for key event fields on client records.
// Append-only (mirrors invoice_change_logs pattern). Source discriminator:
//   client_patch       — direct edit via PATCH /api/clients/:id
//   invoice_patch      — indirect edit via PATCH /api/invoices/:id
//   booking_orchestrate — initial booking creation (fieldName = 'booking_created')
//   voice              — voice assistant write tool
//   import             — bulk import
export const eventChangeLogs = pgTable("event_change_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  clientId: varchar("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  invoiceId: varchar("invoice_id"),
  fieldName: varchar("field_name", { length: 100 }).notNull(),
  fieldLabel: varchar("field_label", { length: 150 }),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedByUserId: varchar("changed_by_user_id").notNull(),
  changedByName: varchar("changed_by_name", { length: 255 }),
  changedAt: timestamp("changed_at").notNull().defaultNow(),
  source: varchar("source", { length: 32 }).notNull().default("client_patch"),
}, (t) => ({
  workspaceClientChangedIdx: index("event_change_logs_ws_client_changed_idx").on(t.workspaceId, t.clientId, t.changedAt),
  workspaceChangedIdx: index("event_change_logs_ws_changed_idx").on(t.workspaceId, t.changedAt),
}));

export const insertEventChangeLogSchema = createInsertSchema(eventChangeLogs).omit({ id: true, changedAt: true });
export type InsertEventChangeLog = z.infer<typeof insertEventChangeLogSchema>;
export type EventChangeLog = typeof eventChangeLogs.$inferSelect;

// Invoice Followup Reminders (for setting reminders on invoices)
export const invoiceFollowups = pgTable("invoice_followups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  ownerUserId: varchar("owner_user_id").notNull(),
  invoiceId: varchar("invoice_id").notNull(),
  dueDate: timestamp("due_date").notNull(),
  note: text("note"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceOwnerIdx: index("invoice_followups_ws_owner_idx").on(t.workspaceId, t.ownerUserId, t.completedAt),
  invoiceIdx: index("invoice_followups_invoice_idx").on(t.invoiceId),
  workspaceDueIdx: index("invoice_followups_ws_due_idx").on(t.workspaceId, t.dueDate, t.completedAt),
}));

export const invoiceFollowupsRelations = relations(invoiceFollowups, ({ one }) => ({
  invoice: one(invoices, {
    fields: [invoiceFollowups.invoiceId],
    references: [invoices.id],
  }),
}));

export const insertInvoiceFollowupSchema = createInsertSchema(invoiceFollowups).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  completedAt: true,
});

export type InsertInvoiceFollowup = z.infer<typeof insertInvoiceFollowupSchema>;
export type InvoiceFollowup = typeof invoiceFollowups.$inferSelect;

// Event Reviews - talent feedback after each show
export const eventReviews = pgTable("event_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  invoiceId: varchar("invoice_id").notNull().references(() => invoices.id, { onDelete: "cascade" }),
  rating: integer("rating").notNull(), // 1-11 scale (10 = great, 11 = extra special)
  notes: text("notes"),
  submittedAt: timestamp("submitted_at").defaultNow(),
});

export const eventReviewsRelations = relations(eventReviews, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [eventReviews.workspaceId],
    references: [workspaces.id],
  }),
  talent: one(talent, {
    fields: [eventReviews.talentId],
    references: [talent.id],
  }),
  invoice: one(invoices, {
    fields: [eventReviews.invoiceId],
    references: [invoices.id],
  }),
}));

export const insertEventReviewSchema = createInsertSchema(eventReviews).omit({
  id: true,
  submittedAt: true,
});

export type InsertEventReview = z.infer<typeof insertEventReviewSchema>;
export type EventReview = typeof eventReviews.$inferSelect;

// AI Catch-Me-Up cache — last summary per (workspace, entityType, entityId).
// Avoids re-burning AI credits when nothing has changed since the previous
// summary; invalidated by writes to lead activities, invoice activities,
// calls, payments, and event reviews for the same entity.
export const aiCatchMeUpCache = pgTable("ai_catch_me_up_cache", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  entityType: varchar("entity_type").notNull(), // "lead" | "client"
  entityId: varchar("entity_id").notNull(),
  bullets: jsonb("bullets").notNull().$type<string[]>(),
  eventCount: integer("event_count").notNull().default(0),
  generatedByUserId: varchar("generated_by_user_id"),
  modelKey: varchar("model_key"),
  costCents: integer("cost_cents").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  uniqEntity: uniqueIndex("ai_catch_me_up_cache_unique").on(t.workspaceId, t.entityType, t.entityId),
}));

export const insertAiCatchMeUpCacheSchema = createInsertSchema(aiCatchMeUpCache).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAiCatchMeUpCache = z.infer<typeof insertAiCatchMeUpCacheSchema>;
export type AiCatchMeUpCache = typeof aiCatchMeUpCache.$inferSelect;

// Insert schemas
export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertRegionSchema = createInsertSchema(regions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertLeadSchema = createInsertSchema(leads).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  isSuppressed: true,
  suppressedAt: true,
  suppressedReason: true,
  suppressionSourceMessageId: true,
});

// Task #112 — event-date columns are Postgres `date` (`YYYY-MM-DD`
// strings). Override the drizzle-zod inference (which would still see a
// nullable string) to accept either the canonical string or `Date`/ISO
// from legacy callers, and normalize via `toEventDate`. Callers that
// send a raw `YYYY-MM-DD` get a no-op; legacy callers that wrap in
// `new Date(...).toISOString()` get safely coerced back to a calendar
// day instead of drifting.
export const eventDateInputSchema = z
  .union([z.string(), z.date()])
  .nullish()
  .transform((v) => {
    if (v == null) return null;
    if (v instanceof Date) {
      if (Number.isNaN(v.getTime())) return null;
      const y = v.getUTCFullYear();
      const m = String(v.getUTCMonth() + 1).padStart(2, "0");
      const d = String(v.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    const s = v.trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const head = s.includes("T") ? s.slice(0, s.indexOf("T")) : s.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : null;
  });

export const insertClientSchema = createInsertSchema(clients, {
  dateOfEvent: eventDateInputSchema,
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertInvoiceSchema = createInsertSchema(invoices, {
  dateOfEvent: eventDateInputSchema,
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailTemplateSchema = createInsertSchema(emailTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailAutomationSchema = createInsertSchema(emailAutomations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertAutomationLogSchema = createInsertSchema(automationLogs).omit({
  id: true,
  createdAt: true,
});

export const insertInvoiceReminderSchema = createInsertSchema(invoiceReminders).omit({
  id: true,
  createdAt: true,
});

// Master Schedule entries
export type ScheduleStatus = "Travel Day" | "SL Shows" | "Public Shows" | "Day Off";

export const scheduleEntries = pgTable("schedule_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").references(() => talent.id, { onDelete: "cascade" }),
  year: integer("year").notNull(),
  // Task #112 — canonical calendar-day storage. See shared/event-date.ts.
  date: date("date", { mode: "string" }).notNull(),
  status: varchar("status", { length: 50 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  regionId: varchar("region_id"),
  optionB: text("option_b"),
  notes: text("notes"),
  driveTime: varchar("drive_time", { length: 50 }),
  leadPages: integer("lead_pages"),
  totalBookingAmount: integer("total_booking_amount"),
  estDays: integer("est_days"),
  optionC: text("option_c"),
  optionD: text("option_d"),
  population: integer("population"),
  createdBy: varchar("created_by").notNull(),
  // Task #59 — field-level source tracking. Each editable field maps to
  // 'user' | 'ai' | 'empty'. AI write tools skip 'user'-owned fields.
  fieldSources: jsonb("field_sources").default({}),
  // Task #59 — AI draft staging. AI-written rows are staged (isDraft=true)
  // under a shared draftGroupId until the user commits or discards.
  isDraft: boolean("is_draft").notNull().default(false),
  draftGroupId: varchar("draft_group_id", { length: 36 }),
  // Snapshot of previous field values at commit time — used by undo.
  draftSnapshot: jsonb("draft_snapshot"),
  // Task #59 — generic JSONB store for workspace-configured custom columns.
  // Keys match workspace_schedule_columns.key; values are strings/numbers.
  customFields: jsonb("custom_fields").default({}),
  // Task #59 — AI-flagged pending region name for the cautious-creation flow.
  // Set by guess_region tool when no match found; cleared on commit/resolve.
  regionHint: text("region_hint"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceDateIdx: index("schedule_entries_ws_date_idx").on(t.workspaceId, t.date),
  workspaceYearIdx: index("schedule_entries_ws_year_idx").on(t.workspaceId, t.year),
  talentDateIdx: index("schedule_entries_talent_date_idx").on(t.talentId, t.date),
  regionIdx: index("schedule_entries_region_idx").on(t.regionId),
  workspaceStatusIdx: index("schedule_entries_ws_status_idx").on(t.workspaceId, t.status),
}));

// Notnull variant of `eventDateInputSchema` for `schedule_entries.date`.
const requiredEventDateInputSchema = z
  .union([z.string(), z.date()])
  .transform((v, ctx) => {
    if (v instanceof Date) {
      if (Number.isNaN(v.getTime())) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid date" });
        return z.NEVER;
      }
      const y = v.getUTCFullYear();
      const m = String(v.getUTCMonth() + 1).padStart(2, "0");
      const d = String(v.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    const s = v.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const head = s.includes("T") ? s.slice(0, s.indexOf("T")) : s.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Expected YYYY-MM-DD" });
    return z.NEVER;
  });

export const insertScheduleEntrySchema = createInsertSchema(scheduleEntries, {
  // Task #112 — accept Date / ISO / YYYY-MM-DD and normalize.
  date: requiredEventDateInputSchema,
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertScheduleEntry = z.infer<typeof insertScheduleEntrySchema>;
export type ScheduleEntry = typeof scheduleEntries.$inferSelect;

// ── Task #611 — Tour Mode planner ────────────────────────────────────
// A "tour" is a planning artifact that groups proposed dates into a
// routed multi-city run. Lives alongside scheduleEntries: when an admin
// "commits" a tour, each tour_stop becomes (or updates) a scheduleEntry.
// Until then a tour is purely speculative and never appears in /schedule.
export type TourStatus = "draft" | "routing" | "committed" | "archived";

export const tours = pgTable("tours", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("draft").$type<TourStatus>(),
  // Task #112 — canonical calendar-day storage. See shared/event-date.ts.
  startDate: date("start_date", { mode: "string" }),
  endDate: date("end_date", { mode: "string" }),
  // Optional anchor city/state used as the optimizer's start+end origin
  // (e.g. talent's home base). When NULL the optimizer starts at stop[0].
  originCity: varchar("origin_city", { length: 100 }),
  originState: varchar("origin_state", { length: 50 }),
  notes: text("notes"),
  // Cached optimizer summary so the UI can show totals without re-running.
  totalMiles: integer("total_miles"),
  totalDriveMinutes: integer("total_drive_minutes"),
  optimizedAt: timestamp("optimized_at"),
  // Set once an admin commits the tour to scheduleEntries. Re-committing
  // bumps this; downstream UI uses it to decide whether re-commit will
  // overwrite existing schedule rows.
  committedAt: timestamp("committed_at"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceTalentIdx: index("tours_ws_talent_idx").on(t.workspaceId, t.talentId),
  workspaceStatusIdx: index("tours_ws_status_idx").on(t.workspaceId, t.status),
}));

export const tourStops = pgTable("tour_stops", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tourId: varchar("tour_id").notNull().references(() => tours.id, { onDelete: "cascade" }),
  // 0-indexed position within the tour. Optimizer rewrites these in bulk.
  sequence: integer("sequence").notNull(),
  city: varchar("city", { length: 100 }).notNull(),
  state: varchar("state", { length: 50 }).notNull(),
  // ISO date the stop is proposed for. Tour is purely a plan until commit.
  // Task #112 — canonical calendar-day storage. See shared/event-date.ts.
  proposedDate: date("proposed_date", { mode: "string" }),
  // Cached geocode (lat/lng) so the optimizer doesn't re-hit Geocoding API
  // on every reorder. NULL until first geocoded.
  lat: real("lat"),
  lng: real("lng"),
  population: integer("population"),
  // Distance/time from the previous stop in this tour. NULL on stop[0]
  // and any stops added after the last optimization run.
  driveMilesFromPrev: integer("drive_miles_from_prev"),
  driveMinutesFromPrev: integer("drive_minutes_from_prev"),
  // Free-form per-stop logistics blocks. We don't model hotels/flights
  // as first-class tables yet (out of scope); just notes for the routing sheet.
  hotelNotes: text("hotel_notes"),
  transportNotes: text("transport_notes"),
  venueNotes: text("venue_notes"),
  // Once the tour is committed, this points at the upserted scheduleEntries row
  // so re-commits update in-place instead of duplicating.
  scheduleEntryId: varchar("schedule_entry_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  tourSequenceIdx: index("tour_stops_tour_seq_idx").on(t.tourId, t.sequence),
}));

export const insertTourSchema = createInsertSchema(tours, {
  // Task #112 — canonical date-only storage.
  startDate: eventDateInputSchema,
  endDate: eventDateInputSchema,
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  optimizedAt: true,
  committedAt: true,
  totalMiles: true,
  totalDriveMinutes: true,
});

export const insertTourStopSchema = createInsertSchema(tourStops, {
  // Task #112 — canonical date-only storage.
  proposedDate: eventDateInputSchema,
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  lat: true,
  lng: true,
  driveMilesFromPrev: true,
  driveMinutesFromPrev: true,
  scheduleEntryId: true,
});

export type InsertTour = z.infer<typeof insertTourSchema>;
export type Tour = typeof tours.$inferSelect;
export type InsertTourStop = z.infer<typeof insertTourStopSchema>;
export type TourStop = typeof tourStops.$inferSelect;

// Task #640 — record of every buyer-outreach blast a tour has launched
// so the Tour Planner can show per-campaign delivery stats (sent /
// opened / failed / suppressed) and per-city failure breakdowns without
// the planner having to dig through the bulk-email reports tab. The
// blast itself still flows through the existing mass-email runner +
// scheduler; this is purely a join-table for surfacing.
export const tourOutreachCampaigns = pgTable("tour_outreach_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  tourId: varchar("tour_id").notNull().references(() => tours.id, { onDelete: "cascade" }),
  campaignId: varchar("campaign_id").notNull().references(() => campaigns.id, { onDelete: "cascade" }),
  audience: text("audience").array().notNull().default(sql`ARRAY[]::text[]`),
  citiesCount: integer("cities_count").notNull().default(0),
  recipientsCount: integer("recipients_count").notNull().default(0),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  tourIdx: index("tour_outreach_tour_idx").on(t.tourId, t.createdAt),
  campaignUniq: uniqueIndex("tour_outreach_campaign_uniq").on(t.campaignId),
}));

export const insertTourOutreachCampaignSchema = createInsertSchema(tourOutreachCampaigns).omit({
  id: true,
  createdAt: true,
});
export type InsertTourOutreachCampaign = z.infer<typeof insertTourOutreachCampaignSchema>;
export type TourOutreachCampaign = typeof tourOutreachCampaigns.$inferSelect;

// Calendar Hub Settings - stores status picklist and column configuration
export const calendarHubSettings = pgTable("calendar_hub_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  statusOptions: jsonb("status_options").default(sql`'["Booked", "Hold", "Travel Day", "Day Off", "SL Shows", "Public Show", "Cancelled", "Pending"]'::jsonb`),
  columnConfig: jsonb("column_config").default(sql`'[{"key":"date","label":"Date","visible":true,"editable":false},{"key":"status","label":"Status","visible":true,"editable":true},{"key":"location","label":"Location","visible":true,"editable":true},{"key":"region","label":"Region","visible":true,"editable":true},{"key":"totalBooking","label":"Total Booking $","visible":true,"editable":false},{"key":"leadPages","label":"Lead Pages","visible":true,"editable":true},{"key":"estDays","label":"Est. Days","visible":true,"editable":false},{"key":"notes","label":"Notes","visible":true,"editable":true}]'::jsonb`),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
});

export const insertCalendarHubSettingsSchema = createInsertSchema(calendarHubSettings).omit({
  id: true,
  updatedAt: true,
});

export type InsertCalendarHubSettings = z.infer<typeof insertCalendarHubSettingsSchema>;
export type CalendarHubSettings = typeof calendarHubSettings.$inferSelect;

// Google Sheets Sync Configuration - one per workspace
export const googleSheetsSyncConfig = pgTable("google_sheets_sync_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().unique().references(() => workspaces.id, { onDelete: "cascade" }),
  spreadsheetId: varchar("spreadsheet_id", { length: 255 }).notNull(),
  spreadsheetName: varchar("spreadsheet_name", { length: 255 }),
  tabName: varchar("tab_name", { length: 255 }).notNull(),
  // Column mapping: maps sheet column letters/indices to CRM field names
  columnMapping: jsonb("column_mapping").default(sql`'{}'::jsonb`),
  // Which row has headers (1-indexed, default 1)
  headerRow: integer("header_row").notNull().default(1),
  // Which column has the date (sheet column letter or header name)
  dateColumn: varchar("date_column", { length: 50 }),
  lastSyncAt: timestamp("last_sync_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertGoogleSheetsSyncConfigSchema = createInsertSchema(googleSheetsSyncConfig).omit({
  id: true, createdAt: true, updatedAt: true, lastSyncAt: true,
});
export type GoogleSheetsSyncConfig = typeof googleSheetsSyncConfig.$inferSelect;
export type InsertGoogleSheetsSyncConfig = z.infer<typeof insertGoogleSheetsSyncConfigSchema>;

// User Gmail tokens for per-user email sending
export const userGmailTokens = pgTable("user_gmail_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  // ── Task #244 — granted-scope tracking ───────────────────────────────
  // The exact space-joined scope string Google returned on the most recent
  // token exchange (validated on the callback). We never assume the read
  // scope was granted just because we asked for it — `hasReadScope` is the
  // authoritative gate for any code path that reads mailbox contents.
  grantedScopes: text("granted_scopes"),
  hasReadScope: boolean("has_read_scope").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserGmailTokenSchema = createInsertSchema(userGmailTokens).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ── Task #248 — Platform (Super Admin) personal Google Calendar tokens ───
// Platform users are not members of any workspace, and `user_calendar_tokens`
// carries a NOT NULL FK to `workspaces`, so a platform admin's personal
// calendar connection cannot live there. This table mirrors the user-calendar
// token shape but is keyed solely by the platform user id. The OAuth round-trip
// reuses the already-registered `/api/user/calendar/callback` redirect URI and
// branches on the state token type `platform_calendar`.
export const platformCalendarTokens = pgTable("platform_calendar_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platformUserId: varchar("platform_user_id").notNull().unique(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  calendarId: varchar("calendar_id", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type PlatformCalendarToken = typeof platformCalendarTokens.$inferSelect;

// ── Task #244 — Gmail restricted-scope access requests + seat grants ─────
// Google's gmail.readonly is a RESTRICTED scope: it requires a yearly CASA
// security assessment and counts against a hard 100-user LIFETIME cap. Each
// USER who completes the read OAuth flow permanently consumes one slot — it
// is never refunded, even if the grant is later revoked. These two tables
// run the beta-rollout funnel: a tenant user requests read access, a
// super-admin approves/rejects (with appeal), and an approval allocates a
// per-user seat grant that unlocks the read OAuth flow for exactly that user.
export type GmailReadAccessRequestStatus = "pending" | "approved" | "rejected";

export const gmailReadAccessRequests = pgTable("gmail_read_access_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  userEmail: varchar("user_email", { length: 255 }).notNull(),
  // Free-text justification the requester supplies ("why do you need to read
  // your inbox inside Clarik").
  reason: text("reason"),
  status: varchar("status", { length: 20 })
    .notNull()
    .default("pending")
    .$type<GmailReadAccessRequestStatus>(),
  // Platform user (super-admin) who reviewed it, plus their note. On reject
  // the note is surfaced to the requester so they can appeal.
  reviewedByPlatformUserId: varchar("reviewed_by_platform_user_id"),
  reviewedAt: timestamp("reviewed_at"),
  decisionNote: text("decision_note"),
  // Appeal trail — a rejected requester may appeal once; the appeal flips the
  // row back to pending and records the appeal text + timestamp.
  appealText: text("appeal_text"),
  appealedAt: timestamp("appealed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  wsIdx: index("gmail_read_access_requests_ws_idx").on(table.workspaceId),
  userIdx: index("gmail_read_access_requests_user_idx").on(table.userId),
  statusIdx: index("gmail_read_access_requests_status_idx").on(table.status),
}));

export const insertGmailReadAccessRequestSchema = createInsertSchema(gmailReadAccessRequests).omit({
  id: true,
  status: true,
  reviewedByPlatformUserId: true,
  reviewedAt: true,
  decisionNote: true,
  appealText: true,
  appealedAt: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertGmailReadAccessRequest = z.infer<typeof insertGmailReadAccessRequestSchema>;
export type GmailReadAccessRequest = typeof gmailReadAccessRequests.$inferSelect;

// ── Task #388 — Outlook / Office 365 / Hotmail support request log ──────
// A demand-signal feature only: users on Outlook can't connect their mailbox
// yet (Gmail-only). This logs each "I want Outlook support" request so Super
// Admins can gauge demand before investing in a real Outlook integration.
// Unlike gmailReadAccessRequests this is a PASSIVE log — there is no
// review/approve/appeal workflow and no status transitions.
export const outlookAccessRequests = pgTable("outlook_access_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  userEmail: varchar("user_email", { length: 255 }).notNull(),
  // Optional free-text reason ("why do you want Outlook support").
  reason: text("reason"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  wsIdx: index("outlook_access_requests_ws_idx").on(table.workspaceId),
  userIdx: index("outlook_access_requests_user_idx").on(table.userId),
}));

export const insertOutlookAccessRequestSchema = createInsertSchema(outlookAccessRequests).omit({
  id: true,
  createdAt: true,
});
export type InsertOutlookAccessRequest = z.infer<typeof insertOutlookAccessRequestSchema>;
export type OutlookAccessRequest = typeof outlookAccessRequests.$inferSelect;

// ── Task #409 — Inbox read "Notify me when it's live" interest log ──────
// A passive per-user demand signal (mirrors outlookAccessRequests): a user can
// ask to be notified when in-app inbox reading ships, without submitting a
// formal early-access request. NO email is sent now — this is a tracked list
// only so Super Admins can later reach out manually. One row per user is kept
// (the status endpoint reads the latest), distinct from the review/approve
// gmailReadAccessRequests funnel.
export const gmailReadNotifyInterest = pgTable("gmail_read_notify_interest", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  userEmail: varchar("user_email", { length: 255 }).notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  wsIdx: index("gmail_read_notify_interest_ws_idx").on(table.workspaceId),
  userIdx: index("gmail_read_notify_interest_user_idx").on(table.userId),
}));

export const insertGmailReadNotifyInterestSchema = createInsertSchema(gmailReadNotifyInterest).omit({
  id: true,
  createdAt: true,
});
export type InsertGmailReadNotifyInterest = z.infer<typeof insertGmailReadNotifyInterestSchema>;
export type GmailReadNotifyInterest = typeof gmailReadNotifyInterest.$inferSelect;

// One row per (workspace, user) that has been granted a restricted read seat.
// `oauthCompletedAt` is the irreversible event — it stamps the moment the user
// actually finished the gmail.readonly OAuth flow and thus consumed a permanent
// Google lifetime slot. Revoking sets status='revoked' but does NOT free the
// consumed slot (Google never refunds it), so seat-cap math counts every row
// that ever reached `consumed`.
export type GmailReadGrantStatus = "granted" | "consumed" | "revoked";

export const gmailReadGrants = pgTable("gmail_read_grants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  userEmail: varchar("user_email", { length: 255 }).notNull(),
  status: varchar("status", { length: 20 })
    .notNull()
    .default("granted")
    .$type<GmailReadGrantStatus>(),
  grantedByPlatformUserId: varchar("granted_by_platform_user_id"),
  requestId: varchar("request_id"),
  grantedAt: timestamp("granted_at").notNull().defaultNow(),
  // Stamped when the user completes the read OAuth flow → permanent slot spent.
  oauthCompletedAt: timestamp("oauth_completed_at"),
  revokedAt: timestamp("revoked_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  userUniq: uniqueIndex("gmail_read_grants_user_uniq").on(table.userId),
  wsIdx: index("gmail_read_grants_ws_idx").on(table.workspaceId),
  statusIdx: index("gmail_read_grants_status_idx").on(table.status),
}));

export const insertGmailReadGrantSchema = createInsertSchema(gmailReadGrants).omit({
  id: true,
  oauthCompletedAt: true,
  revokedAt: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertGmailReadGrant = z.infer<typeof insertGmailReadGrantSchema>;
export type GmailReadGrant = typeof gmailReadGrants.$inferSelect;

// Workspace Google Calendar tokens - per-workspace calendar connections
export const workspaceCalendarTokens = pgTable("workspace_calendar_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().unique(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertWorkspaceCalendarTokenSchema = createInsertSchema(workspaceCalendarTokens).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const workspaceDriveTokens = pgTable("workspace_drive_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().unique(),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Types
export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type UserProfile = typeof userProfiles.$inferSelect;

export type InsertRegion = z.infer<typeof insertRegionSchema>;
export type Region = typeof regions.$inferSelect;

export type InsertLead = z.infer<typeof insertLeadSchema>;
export type Lead = typeof leads.$inferSelect;

export type InsertClient = z.infer<typeof insertClientSchema>;
export type Client = typeof clients.$inferSelect;

export type InsertInvoice = z.infer<typeof insertInvoiceSchema>;
export type Invoice = typeof invoices.$inferSelect;

export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;
export type EmailTemplate = typeof emailTemplates.$inferSelect;

export type InsertEmailAutomation = z.infer<typeof insertEmailAutomationSchema>;
export type EmailAutomation = typeof emailAutomations.$inferSelect;

export type InsertAutomationLog = z.infer<typeof insertAutomationLogSchema>;
export type AutomationLog = typeof automationLogs.$inferSelect;

export type InsertInvoiceReminder = z.infer<typeof insertInvoiceReminderSchema>;
export type InvoiceReminder = typeof invoiceReminders.$inferSelect;

export type InsertUserGmailToken = z.infer<typeof insertUserGmailTokenSchema>;
export type UserGmailToken = typeof userGmailTokens.$inferSelect;

export type InsertWorkspaceCalendarToken = z.infer<typeof insertWorkspaceCalendarTokenSchema>;
export type WorkspaceCalendarToken = typeof workspaceCalendarTokens.$inferSelect;

// User-level calendar tokens (for individual user calendars)
export const userCalendarTokens = pgTable("user_calendar_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  calendarId: varchar("calendar_id", { length: 255 }), // Optional specific calendar to use
  // Task #405 — true once this connection has additionally granted the Google
  // Meet REST scopes (meetings.space.created + .readonly) via the incremental
  // re-consent flow. Stays false for accounts connected before Meet support
  // existed, so the create-meeting flow knows to fall back gracefully.
  meetScopesGranted: boolean("meet_scopes_granted").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserCalendarTokenSchema = createInsertSchema(userCalendarTokens).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertUserCalendarToken = z.infer<typeof insertUserCalendarTokenSchema>;
export type UserCalendarToken = typeof userCalendarTokens.$inferSelect;

// ── Task #405 — Google Meet meetings + transcripts + AI notes ─────────────
// One row per calendar "meeting" event a user creates through Calendar Hub
// when transcription is in play. Holds the bridge between the Google Calendar
// event, the Google Meet space, and the post-meeting conference record, plus
// the imported transcript and the AI-generated notes. Designed to degrade:
// when transcription is off/unavailable a row may still be created in
// `status='disabled'` for surfacing the meeting, or no row at all.
export const MEETING_TYPES = ["internal", "client"] as const;
export type MeetingType = typeof MEETING_TYPES[number];
// Lifecycle:
//  disabled      — meeting created, transcription not requested/available
//  scheduled     — transcription requested; space configured, awaiting the call
//  polling       — meeting ended; poller is looking for the transcript
//  imported      — transcript pulled successfully
//  no_transcript — call happened but Google produced no transcript
//  unavailable   — gave up after max poll attempts / scope lost / API error
//  stopped       — host hit the per-meeting stop control before/after the call
export const MEETING_STATUSES = [
  "disabled",
  "scheduled",
  "polling",
  "imported",
  "no_transcript",
  "unavailable",
  "stopped",
] as const;
export type MeetingStatus = typeof MEETING_STATUSES[number];
// AI notes lifecycle.
export const MEETING_NOTES_STATUSES = ["pending", "generating", "ready", "failed", "skipped"] as const;
export type MeetingNotesStatus = typeof MEETING_NOTES_STATUSES[number];

export const meetings = pgTable("meetings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // The host who created the meeting (and whose Google tokens we poll with).
  userId: varchar("user_id").notNull(),
  // Google Calendar identifiers (the event that carries the Meet link).
  calendarEventId: varchar("calendar_event_id", { length: 255 }),
  calendarId: varchar("calendar_id", { length: 255 }),
  // Google Meet space + post-call conference record (Meet REST API v2).
  meetSpaceName: varchar("meet_space_name", { length: 255 }), // "spaces/xxxx"
  meetCode: varchar("meet_code", { length: 64 }),
  meetingUri: text("meeting_uri"),
  conferenceRecordName: varchar("conference_record_name", { length: 255 }), // "conferenceRecords/xxx"
  // Optional client this meeting is about (drives client-record surfacing +
  // the internal/client policy split).
  clientId: varchar("client_id"),
  meetingType: varchar("meeting_type", { length: 16 }).notNull().default("internal"),
  title: text("title"),
  startAt: timestamp("start_at"),
  endAt: timestamp("end_at"),
  // Consent / control.
  transcribeRequested: boolean("transcribe_requested").notNull().default(false),
  consentAcknowledged: boolean("consent_acknowledged").notNull().default(false),
  transcriptionConfigured: boolean("transcription_configured").notNull().default(false),
  status: varchar("status", { length: 24 }).notNull().default("disabled"),
  // Imported transcript.
  transcriptText: text("transcript_text"),
  transcriptEntries: jsonb("transcript_entries"), // [{ speaker, text, startMs, endMs }]
  // AI notes.
  summary: text("summary"),
  keyTakeaways: jsonb("key_takeaways"), // string[]
  actionItems: jsonb("action_items"),   // string[]
  notesStatus: varchar("notes_status", { length: 16 }).notNull().default("pending"),
  notesModelKey: varchar("notes_model_key", { length: 64 }),
  // Poller bookkeeping.
  pollAttempts: integer("poll_attempts").notNull().default(0),
  nextPollAt: timestamp("next_poll_at"),
  lastPolledAt: timestamp("last_polled_at"),
  importedAt: timestamp("imported_at"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceIdx: index("meetings_workspace_idx").on(t.workspaceId),
  userIdx: index("meetings_user_idx").on(t.userId),
  clientIdx: index("meetings_client_idx").on(t.clientId),
  eventIdx: index("meetings_calendar_event_idx").on(t.calendarEventId),
  // Poller hot path: find rows due for a poll.
  pollIdx: index("meetings_poll_idx").on(t.status, t.nextPollAt),
}));

export const insertMeetingSchema = createInsertSchema(meetings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertMeeting = z.infer<typeof insertMeetingSchema>;
export type Meeting = typeof meetings.$inferSelect;

// Talent-level calendar tokens (for talent calendars connected via invitation)
// ── Task #404 — multi-person talent (bands / troupes) ────────────────
// A talent profile can have any number of members. Existing single-person
// talent simply has zero rows here and behaves identically. Each member can
// optionally connect their own Google Calendar (see the nullable
// talentMemberId on talent_calendar_tokens / talent_calendar_invites below)
// so booking pushes fan out to the whole group.
export const talentMembers = pgTable("talent_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 50 }),
  // Task #421 — per-member portal login fields
  userId: varchar("user_id"),                       // null until invite accepted
  accessLevel: varchar("access_level", { length: 10 }).notNull().default("read"), // 'none' | 'read' | 'write'
  isPrimaryContact: boolean("is_primary_contact").notNull().default(false),
  portalInviteToken: varchar("portal_invite_token"),
  portalInviteTokenExpiresAt: timestamp("portal_invite_token_expires_at"),
  portalInviteStatus: varchar("portal_invite_status", { length: 10 }).notNull().default("none"), // 'none'|'pending'|'accepted'
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  talentIdx: index("talent_members_talent_idx").on(t.talentId),
  workspaceIdx: index("talent_members_workspace_idx").on(t.workspaceId),
  userIdx: index("talent_members_user_id_idx").on(t.userId),
}));

export const insertTalentMemberSchema = createInsertSchema(talentMembers, {
  accessLevel: z.enum(["none", "read", "write"]).optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertTalentMember = z.infer<typeof insertTalentMemberSchema>;
export type TalentMember = typeof talentMembers.$inferSelect;

export const talentCalendarTokens = pgTable("talent_calendar_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  // Task #404 — when set, this token belongs to a specific band/troupe member
  // rather than the primary talent. NULL = the primary talent's own calendar
  // (the original single-person behavior). Existing per-talent queries filter
  // `talentMemberId IS NULL` so member rows never leak into the primary flow.
  talentMemberId: varchar("talent_member_id").references((): any => talentMembers.id, { onDelete: "cascade" }),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  calendarId: varchar("calendar_id", { length: 255 }), // Optional specific calendar to use
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertTalentCalendarTokenSchema = createInsertSchema(talentCalendarTokens).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertTalentCalendarToken = z.infer<typeof insertTalentCalendarTokenSchema>;
export type TalentCalendarToken = typeof talentCalendarTokens.$inferSelect;

// Talent calendar connection invitations
export const talentCalendarInvites = pgTable("talent_calendar_invites", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  // Task #404 — when set, this invite is for a specific band/troupe member's
  // calendar. NULL = the primary talent (original behavior).
  talentMemberId: varchar("talent_member_id").references((): any => talentMembers.id, { onDelete: "cascade" }),
  email: varchar("email", { length: 255 }).notNull(),
  token: varchar("token", { length: 64 }).notNull().unique(),
  invitedByUserId: varchar("invited_by_user_id").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, used, expired, revoked
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertTalentCalendarInviteSchema = createInsertSchema(talentCalendarInvites).omit({
  id: true,
  usedAt: true,
  createdAt: true,
});

export type InsertTalentCalendarInvite = z.infer<typeof insertTalentCalendarInviteSchema>;
export type TalentCalendarInvite = typeof talentCalendarInvites.$inferSelect;

// OAuth State Tokens - for persisting OAuth state across server restarts
export const oauthStateTokens = pgTable("oauth_state_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  token: varchar("token", { length: 128 }).notNull().unique(),
  type: varchar("type", { length: 50 }).notNull(), // calendar, user_calendar, talent_calendar, gmail
  workspaceId: varchar("workspace_id"),
  userId: varchar("user_id"),
  talentId: varchar("talent_id"),
  inviteToken: varchar("invite_token", { length: 64 }),
  returnUrl: text("return_url"),
  // JSON-encoded extra context carried through the OAuth round-trip.
  // Read in server/routes.ts (OAuth callback handlers).
  metadata: text("metadata"),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertOAuthStateTokenSchema = createInsertSchema(oauthStateTokens).omit({
  id: true,
  createdAt: true,
});

export type InsertOAuthStateToken = z.infer<typeof insertOAuthStateTokenSchema>;
export type OAuthStateToken = typeof oauthStateTokens.$inferSelect;

// Access Requests - for managing user access
export type AccessRequestStatus = "pending" | "approved" | "denied";

export const accessRequests = pgTable("access_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  firstName: varchar("first_name", { length: 100 }).notNull(),
  lastName: varchar("last_name", { length: 100 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  phone: varchar("phone", { length: 50 }),
  company: varchar("company", { length: 200 }),
  message: text("message"),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  assignedRole: varchar("assigned_role", { length: 50 }),
  processedBy: varchar("processed_by"),
  processedAt: timestamp("processed_at"),
  createdUserId: varchar("created_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertAccessRequestSchema = createInsertSchema(accessRequests).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAccessRequest = z.infer<typeof insertAccessRequestSchema>;
export type AccessRequest = typeof accessRequests.$inferSelect;

// Goal Metric Keys
export type GoalMetricKey = 
  | "EMAILS_SENT_DAILY"
  | "EMAILS_SENT_WEEKLY"
  | "BOOKINGS_DOLLARS_MONTHLY"
  | "BOOKINGS_COUNT_MONTHLY"
  | "SHOWS_COUNT_MONTHLY"
  | "FOLLOWUPS_COMPLETED_DAILY"
  | "LEADS_TOUCHED_DAILY";

export type GoalPeriod = "DAILY" | "WEEKLY" | "MONTHLY";

// Organization-wide goal defaults by role
export const orgGoals = pgTable("org_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  role: varchar("role", { length: 50 }).notNull(), // sales_rep, artist, billing, admin
  metricKey: varchar("metric_key", { length: 50 }).notNull(),
  period: varchar("period", { length: 20 }).notNull(), // DAILY, WEEKLY, MONTHLY
  targetNumber: decimal("target_number", { precision: 12, scale: 2 }).notNull(),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by").notNull(),
  updatedBy: varchar("updated_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertOrgGoalSchema = createInsertSchema(orgGoals).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertOrgGoal = z.infer<typeof insertOrgGoalSchema>;
export type OrgGoal = typeof orgGoals.$inferSelect;

// Per-user goal overrides
export const userGoalOverrides = pgTable("user_goal_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  metricKey: varchar("metric_key", { length: 50 }).notNull(),
  period: varchar("period", { length: 20 }).notNull(), // DAILY, WEEKLY, MONTHLY
  targetNumber: decimal("target_number", { precision: 12, scale: 2 }).notNull(),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by").notNull(),
  updatedBy: varchar("updated_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertUserGoalOverrideSchema = createInsertSchema(userGoalOverrides).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertUserGoalOverride = z.infer<typeof insertUserGoalOverrideSchema>;
export type UserGoalOverride = typeof userGoalOverrides.$inferSelect;

// Custom holiday overrides for the organization
export const orgHolidayOverrides = pgTable("org_holiday_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  date: timestamp("date").notNull(),
  name: varchar("name", { length: 100 }).notNull(),
  isHoliday: boolean("is_holiday").notNull().default(true), // true = add as holiday, false = remove default holiday
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertOrgHolidayOverrideSchema = createInsertSchema(orgHolidayOverrides).omit({
  id: true,
  createdAt: true,
});

export type InsertOrgHolidayOverride = z.infer<typeof insertOrgHolidayOverrideSchema>;
export type OrgHolidayOverride = typeof orgHolidayOverrides.$inferSelect;

// Followup Reminders for sales rep dashboard
export const followupReminders = pgTable("followup_reminders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  ownerUserId: varchar("owner_user_id").notNull(),
  leadId: varchar("lead_id").notNull(),
  dueDate: timestamp("due_date").notNull(),
  note: text("note"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const followupRemindersRelations = relations(followupReminders, ({ one }) => ({
  lead: one(leads, {
    fields: [followupReminders.leadId],
    references: [leads.id],
  }),
}));

export const insertFollowupReminderSchema = createInsertSchema(followupReminders).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  completedAt: true,
});

export type InsertFollowupReminder = z.infer<typeof insertFollowupReminderSchema>;
export type FollowupReminder = typeof followupReminders.$inferSelect;

export const approvalWorkflowSettings = pgTable("approval_workflow_settings", {
  id: serial("id").primaryKey(),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  emailRecipients: text("email_recipients").array().default([]),
  approverUserIds: text("approver_user_ids").array().default([]),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: text("updated_by"),
});

export const insertApprovalWorkflowSettingsSchema = createInsertSchema(approvalWorkflowSettings).omit({
  id: true,
  updatedAt: true,
});

export type InsertApprovalWorkflowSettings = z.infer<typeof insertApprovalWorkflowSettingsSchema>;
export type ApprovalWorkflowSettings = typeof approvalWorkflowSettings.$inferSelect;

// Personal Profiles - "Meet The Team" profile data (optional per user)
export const personalProfiles = pgTable("personal_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(), // One personal profile per user max
  profilePicture: varchar("profile_picture", { length: 500 }), // URL to profile picture in object storage
  title: varchar("title", { length: 100 }), // Custom job title
  location: varchar("location", { length: 100 }),
  bioShort: varchar("bio_short", { length: 255 }), // Used on tiles/cards
  bioLong: text("bio_long"), // Full about section
  contactEmail: varchar("contact_email", { length: 255 }),
  contactPhone: varchar("contact_phone", { length: 50 }),
  socialLinks: text("social_links"), // JSON: {instagram, tiktok, youtube, website, linkedin}
  hobbies: text("hobbies"),
  favoriteQuote: text("favorite_quote"),
  favoriteMovieShow: varchar("favorite_movie_show", { length: 255 }),
  coffeeOrder: varchar("coffee_order", { length: 100 }),
  favoriteSnack: varchar("favorite_snack", { length: 100 }),
  isPublished: boolean("is_published").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPersonalProfileSchema = createInsertSchema(personalProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPersonalProfile = z.infer<typeof insertPersonalProfileSchema>;
export type PersonalProfile = typeof personalProfiles.$inferSelect;

// Profile Settings - Admin controls for profile editing
export const profileSettings = pgTable("profile_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  allowTitleEdit: boolean("allow_title_edit").notNull().default(true),
  titleEditWhitelist: text("title_edit_whitelist").array().default([]), // Array of user_ids who can always edit title
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
});

export const insertProfileSettingsSchema = createInsertSchema(profileSettings).omit({
  id: true,
  updatedAt: true,
});

export type InsertProfileSettings = z.infer<typeof insertProfileSettingsSchema>;
export type ProfileSettings = typeof profileSettings.$inferSelect;

// Profile Field Settings - Role-based visibility controls for personal profile fields
export const profileFieldSettings = pgTable("profile_field_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  role: varchar("role", { length: 50 }).notNull(), // sales_rep, artist, billing, admin
  // Each field can be: visible (shown), editable (can edit), hidden (not shown)
  profilePicture: varchar("profile_picture_visibility", { length: 20 }).notNull().default("editable"),
  title: varchar("title_visibility", { length: 20 }).notNull().default("editable"),
  location: varchar("location_visibility", { length: 20 }).notNull().default("editable"),
  bioShort: varchar("bio_short_visibility", { length: 20 }).notNull().default("editable"),
  bioLong: varchar("bio_long_visibility", { length: 20 }).notNull().default("editable"),
  contactEmail: varchar("contact_email_visibility", { length: 20 }).notNull().default("editable"),
  contactPhone: varchar("contact_phone_visibility", { length: 20 }).notNull().default("editable"),
  socialLinks: varchar("social_links_visibility", { length: 20 }).notNull().default("editable"),
  hobbies: varchar("hobbies_visibility", { length: 20 }).notNull().default("editable"),
  favoriteQuote: varchar("favorite_quote_visibility", { length: 20 }).notNull().default("editable"),
  favoriteMovieShow: varchar("favorite_movie_show_visibility", { length: 20 }).notNull().default("editable"),
  coffeeOrder: varchar("coffee_order_visibility", { length: 20 }).notNull().default("editable"),
  favoriteSnack: varchar("favorite_snack_visibility", { length: 20 }).notNull().default("editable"),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
});

export const insertProfileFieldSettingsSchema = createInsertSchema(profileFieldSettings).omit({
  id: true,
  updatedAt: true,
});

export type InsertProfileFieldSettings = z.infer<typeof insertProfileFieldSettingsSchema>;
export type ProfileFieldSettings = typeof profileFieldSettings.$inferSelect;

// Valid visibility options for profile fields
export const PROFILE_FIELD_VISIBILITY = ["hidden", "visible", "editable"] as const;
export type ProfileFieldVisibility = typeof PROFILE_FIELD_VISIBILITY[number];

// Branding Settings - for custom logo on invoices and email signatures
export const brandingSettings = pgTable("branding_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  logoPath: text("logo_path"),
  logoFileName: varchar("logo_file_name", { length: 255 }),
  logoContentType: varchar("logo_content_type", { length: 100 }),
  signatureLogoPath: text("signature_logo_path"),
  signatureLogoFileName: varchar("signature_logo_file_name", { length: 255 }),
  signatureLogoContentType: varchar("signature_logo_content_type", { length: 100 }),
  forceLogoInSignature: boolean("force_logo_in_signature").notNull().default(false),
  faviconPath: text("favicon_path"),
  faviconFileName: varchar("favicon_file_name", { length: 255 }),
  faviconContentType: varchar("favicon_content_type", { length: 100 }),
  sidebarIconPath: text("sidebar_icon_path"),
  sidebarIconFileName: varchar("sidebar_icon_file_name", { length: 255 }),
  sidebarIconContentType: varchar("sidebar_icon_content_type", { length: 100 }),
  additionalCalendarIds: text("additional_calendar_ids").array().default(sql`ARRAY[]::text[]`),
  // ── Task #405 — Google Meet transcription policy (hybrid, admin-controlled) ──
  // Master toggle for the whole feature in this workspace. Default OFF because
  // it requires a paid Google Workspace + the user-granted Meet REST scopes;
  // when off (or scopes/plan unavailable) the create-meeting flow silently
  // falls back to a plain Calendar-minted Meet link and records nothing.
  meetTranscriptionEnabled: boolean("meet_transcription_enabled").notNull().default(false),
  // Internal meetings (no client attached / only org members) auto-transcribe
  // when the master toggle is on. Client-facing meetings never auto-transcribe;
  // the host must opt in per meeting (consent-by-design).
  meetTranscribeInternalAuto: boolean("meet_transcribe_internal_auto").notNull().default(true),
  // Whether the host is even ALLOWED to opt a client meeting into transcription.
  // Admins can disable client-meeting transcription entirely for compliance.
  meetTranscribeClientAllowed: boolean("meet_transcribe_client_allowed").notNull().default(true),
  // ── Custom brand colors (white-label, Phase 1) ─────────────────────
  // Hex strings including the leading '#'. NULL = use platform default.
  // Applied to public-facing surfaces: invoice page, client portal,
  // booking forms, e-sign page, payment portal.
  primaryColor: varchar("primary_color", { length: 9 }),
  secondaryColor: varchar("secondary_color", { length: 9 }),
  accentColor: varchar("accent_color", { length: 9 }),
  // ── Custom font overrides (Clarik rebrand, Task #13) ──────────────
  // CSS font-family stack strings (e.g. "'Inter', system-ui, sans-serif").
  // NULL = inherit the platform default from `platform_settings`. Applied
  // to public-facing client surfaces (invoice page, client portal,
  // booking forms, e-sign page, payment portal) the same way as the
  // color overrides above.
  bodyFontFamily: text("body_font_family"),
  displayFontFamily: text("display_font_family"),
  monoFontFamily: text("mono_font_family"),
  // ── Custom wordmark + powered-by string (Clarik rebrand) ──────────
  // White-label resellers can override the rendered wordmark and the
  // attribution caption. NULL = use the platform default ("clarik" /
  // "Powered by Clarik"). `wordmarkText` is the lowercase label; the
  // signal dot is rendered separately so it stays brand-consistent.
  wordmarkText: varchar("wordmark_text", { length: 120 }),
  poweredByText: varchar("powered_by_text", { length: 120 }),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
});

export const insertBrandingSettingsSchema = createInsertSchema(brandingSettings).omit({
  id: true,
  updatedAt: true,
});

export type InsertBrandingSettings = z.infer<typeof insertBrandingSettingsSchema>;
export type BrandingSettings = typeof brandingSettings.$inferSelect;

// Payment Provider Settings - for white-label users to connect their own payment accounts
export const paymentSettings = pgTable("payment_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  // Stripe Connect
  stripeConnectAccountId: varchar("stripe_connect_account_id", { length: 255 }),
  stripeConnectStatus: varchar("stripe_connect_status", { length: 50 }).default("not_connected"), // not_connected, pending, active, restricted
  stripeConnectOnboardingComplete: boolean("stripe_connect_onboarding_complete").default(false),
  stripeConnectPayoutsEnabled: boolean("stripe_connect_payouts_enabled").default(false),
  stripeConnectChargesEnabled: boolean("stripe_connect_charges_enabled").default(false),
  stripeConnectedAt: timestamp("stripe_connected_at"),
  // PayPal
  paypalMerchantId: varchar("paypal_merchant_id", { length: 255 }),
  paypalEmail: varchar("paypal_email", { length: 255 }),
  paypalMeUsername: varchar("paypal_me_username", { length: 255 }), // PayPal.me username (different from email)
  paypalClientId: varchar("paypal_client_id", { length: 255 }),
  paypalClientSecret: varchar("paypal_client_secret", { length: 500 }),
  paypalStatus: varchar("paypal_status", { length: 50 }).default("not_connected"), // not_connected, pending, active
  paypalConnectedAt: timestamp("paypal_connected_at"),
  // General settings
  preferredProvider: varchar("preferred_provider", { length: 50 }).default("stripe"), // stripe, paypal
  enableInvoicePayments: boolean("enable_invoice_payments").default(false),
  enableDepositCollection: boolean("enable_deposit_collection").default(false),
  depositPercentage: integer("deposit_percentage").default(50),
  // Audit
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
});

export const insertPaymentSettingsSchema = createInsertSchema(paymentSettings).omit({
  id: true,
  updatedAt: true,
});

export type InsertPaymentSettings = z.infer<typeof insertPaymentSettingsSchema>;
export type PaymentSettings = typeof paymentSettings.$inferSelect;

// Approval Assignments - who can approve show requests by region/talent
export const approvalAssignments = pgTable("approval_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  regionId: varchar("region_id"), // null means all regions
  talentId: varchar("talent_id"), // null means all talent
  canApprove: boolean("can_approve").notNull().default(true),
  receiveNotifications: boolean("receive_notifications").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  createdBy: varchar("created_by"),
});

export const insertApprovalAssignmentSchema = createInsertSchema(approvalAssignments).omit({
  id: true,
  createdAt: true,
});

export type InsertApprovalAssignment = z.infer<typeof insertApprovalAssignmentSchema>;
export type ApprovalAssignment = typeof approvalAssignments.$inferSelect;

// Campaign status for recipients
export type CampaignRecipientStatus = "PENDING" | "SENT" | "SKIPPED" | "SUPPRESSED";

// Email Campaigns - for tracking mass email sends
export const campaigns = pgTable("campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  createdByUserId: varchar("created_by_user_id").notNull(),
  mailboxId: varchar("mailbox_id"), // Gmail connected mailbox
  regionId: varchar("region_id"),
  audienceTypes: text("audience_types").array().default(sql`ARRAY[]::text[]`), // all_clients, all_leads, etc.
  subject: varchar("subject", { length: 500 }),
  body: text("body"),
  cc: varchar("cc", { length: 1000 }), // Optional CC recipients (comma-separated) for bulk sends
  optOutPhraseIncluded: boolean("opt_out_phrase_included").notNull().default(false),
  totalRecipients: integer("total_recipients").notNull().default(0),
  sentCount: integer("sent_count").notNull().default(0),
  failedCount: integer("failed_count").notNull().default(0),
  skippedCount: integer("skipped_count").notNull().default(0),
  suppressedCount: integer("suppressed_count").notNull().default(0),
  status: varchar("status", { length: 50 }).notNull().default("DRAFT"), // DRAFT, SENDING, PAUSED, COMPLETED
  sendMode: varchar("send_mode", { length: 20 }).notNull().default("CAMPAIGN"), // CAMPAIGN or INDIVIDUAL
  scheduledAt: timestamp("scheduled_at"), // Scheduled start time
  estimatedCompletionAt: timestamp("estimated_completion_at"), // Estimated end time based on caps
  // Task #683 — Cold-send safety
  riskTier: varchar("risk_tier", { length: 20 }).notNull().default("warm"), // 'cold' | 'warm'
  preflightBounceRisk: real("preflight_bounce_risk"), // Estimated bounce rate from validator
  preflightInvalidCount: integer("preflight_invalid_count").notNull().default(0),
  preflightDecayedCount: integer("preflight_decayed_count").notNull().default(0),
  preflightRoleAccountCount: integer("preflight_role_account_count").notNull().default(0),
  autoPausedReason: varchar("auto_paused_reason", { length: 255 }),
  autoPausedAt: timestamp("auto_paused_at"),
  createdAt: timestamp("created_at").defaultNow(),
  sentAt: timestamp("sent_at"),
  completedAt: timestamp("completed_at"),
}, (t) => ({
  workspaceCreatedIdx: index("campaigns_ws_created_idx").on(t.workspaceId, t.createdAt),
  workspaceStatusIdx: index("campaigns_ws_status_idx").on(t.workspaceId, t.status),
}));

export const insertCampaignSchema = createInsertSchema(campaigns).omit({
  id: true,
  createdAt: true,
});

export type InsertCampaign = z.infer<typeof insertCampaignSchema>;
export type Campaign = typeof campaigns.$inferSelect;

// Campaign Recipients - individual recipients for each campaign
export const campaignRecipients = pgTable("campaign_recipients", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull(),
  mailboxId: varchar("mailbox_id"), // Which mailbox will send this
  leadId: varchar("lead_id"),
  clientId: varchar("client_id"),
  email: varchar("email", { length: 255 }).notNull(),
  recipientName: varchar("recipient_name", { length: 255 }),
  status: varchar("status", { length: 50 }).notNull().default("PENDING"), // PENDING, SENT, FAILED, SKIPPED, SUPPRESSED, PAUSED
  skipReason: varchar("skip_reason", { length: 255 }),
  failedReason: varchar("failed_reason", { length: 500 }),
  nextSendAt: timestamp("next_send_at"), // Scheduled send time for queue processing
  sentAt: timestamp("sent_at"),
  trackingId: varchar("tracking_id").unique(), // UUID used for open/click pixel tracking
  // Task #683 — pre-flight validation snapshot
  validationStatus: varchar("validation_status", { length: 20 }), // 'ok' | 'syntax_invalid' | 'no_mx_record' | 'role_account' | 'disposable_domain'
  validationReason: varchar("validation_reason", { length: 120 }),
  engagementStatus: varchar("engagement_status", { length: 20 }), // 'engaged' | 'decayed' | 'cold'
  validatedAt: timestamp("validated_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  campaignStatusIdx: index("campaign_recipients_campaign_status_idx").on(t.campaignId, t.status),
  campaignValidationIdx: index("campaign_recipients_validation_idx").on(t.campaignId, t.validationStatus),
  mailboxStatusNextIdx: index("campaign_recipients_mailbox_next_idx").on(t.mailboxId, t.status, t.nextSendAt),
  leadIdx: index("campaign_recipients_lead_idx").on(t.leadId),
  clientIdx: index("campaign_recipients_client_idx").on(t.clientId),
}));

export const campaignRecipientsRelations = relations(campaignRecipients, ({ one }) => ({
  campaign: one(campaigns, {
    fields: [campaignRecipients.campaignId],
    references: [campaigns.id],
  }),
  lead: one(leads, {
    fields: [campaignRecipients.leadId],
    references: [leads.id],
  }),
  client: one(clients, {
    fields: [campaignRecipients.clientId],
    references: [clients.id],
  }),
}));

export const insertCampaignRecipientSchema = createInsertSchema(campaignRecipients).omit({
  id: true,
  createdAt: true,
});

export type InsertCampaignRecipient = z.infer<typeof insertCampaignRecipientSchema>;
export type CampaignRecipient = typeof campaignRecipients.$inferSelect;

// Mass Email Settings - admin-configurable settings for mass email opt-out phrase
export const massEmailSettings = pgTable("mass_email_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id),
  optOutPhrase: text("opt_out_phrase").notNull().default("If this isn't relevant, just reply \"stop\" and I won't reach out again in the future."),
  // Which audience types get the opt-out phrase (stored as JSON array of strings)
  optOutAudienceTypes: text("opt_out_audience_types").array().default(sql`ARRAY['all_leads', 'new_leads', 'not_now_leads']::text[]`),
  lockOptOutForNonAdmins: boolean("lock_opt_out_for_non_admins").notNull().default(true),
  // Compliance / sender identity (CAN-SPAM + Gmail/Yahoo bulk-sender rules)
  senderName: varchar("sender_name", { length: 255 }),
  senderEmail: varchar("sender_email", { length: 255 }),
  replyToEmail: varchar("reply_to_email", { length: 255 }),
  physicalAddress: text("physical_address"),
  unsubscribeFooterHtml: text("unsubscribe_footer_html"),
  // Task #683 — Cold-send safety defaults
  defaultRiskTier: varchar("default_risk_tier", { length: 20 }).notNull().default("warm"),
  coldAudienceTypes: text("cold_audience_types").array().default(sql`ARRAY['all_leads', 'new_leads', 'not_now_leads', 'interested_leads', 'lead_gen', 'imported_leads']::text[]`),
  preflightMaxBounceRisk: real("preflight_max_bounce_risk").notNull().default(0.05),
  midflightPauseBounceRate: real("midflight_pause_bounce_rate").notNull().default(0.04),
  listUnsubscribeEnabled: boolean("list_unsubscribe_enabled").notNull().default(true),
  // Cold Email Policy (admin-set, applied invisibly to reps).
  // When true, force `riskTier='cold'` for the corresponding audience
  // regardless of what the rep selects in the composer/drip UI. Reps
  // never see "cold/warm" — admin policy + audience type decides routing.
  forceColdForLeadMassEmail: boolean("force_cold_for_lead_mass_email").notNull().default(true),
  forceColdForLeadDrip: boolean("force_cold_for_lead_drip").notNull().default(true),
  forceColdForDormantClientMassEmail: boolean("force_cold_for_dormant_client_mass_email").notNull().default(false),
  // Days since a client's last booking before mass-emailing them is treated as cold.
  dormantClientDays: integer("dormant_client_days").notNull().default(180),
  // Cold/Warm user-facing toggle (mass email + drip):
  // - warmVolumeWarnThreshold: when a WARM send/enroll reaches this many
  //   recipients, the composer shows a deliberate acknowledge-gate
  //   explaining the deliverability risk of warm bulk sending through the
  //   user's own inbox. Admin-configurable per workspace; default 20.
  warmVolumeWarnThreshold: integer("warm_volume_warn_threshold").notNull().default(20),
  // - lockColdOverrideForNonAdmins: when true, only admins may override a
  //   prospect/lead audience's cold default to warm. Reps still see the
  //   toggle but cannot flip a lead list off cold. Default OFF (anyone can
  //   override with the warning).
  lockColdOverrideForNonAdmins: boolean("lock_cold_override_for_non_admins").notNull().default(false),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
});

export const insertMassEmailSettingsSchema = createInsertSchema(massEmailSettings).omit({
  id: true,
  updatedAt: true,
});

export type InsertMassEmailSettings = z.infer<typeof insertMassEmailSettingsSchema>;
export type MassEmailSettings = typeof massEmailSettings.$inferSelect;

// Inbound Email Log - for tracking inbound emails from leads
export const inboundEmails = pgTable("inbound_emails", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Was UNIQUE pre-#663; partitioning by processed_at requires UNIQUE
  // indexes to include the partition key, which would break dedup.
  // App-level dedup at inbound-email-processor.ts replaces it.
  providerMessageId: varchar("provider_message_id", { length: 255 }).notNull(),
  threadId: varchar("thread_id", { length: 255 }),
  workspaceId: varchar("workspace_id"), // Workspace scoping (nullable for legacy rows / sent-self mirror)
  fromEmail: varchar("from_email", { length: 255 }).notNull(),
  fromName: varchar("from_name", { length: 255 }),
  subject: varchar("subject", { length: 500 }),
  bodyPlainText: text("body_plain_text"),
  // Threading + inbox-display metadata
  messageIdHeader: varchar("message_id_header", { length: 998 }), // RFC Message-Id of the inbound message
  inReplyTo: varchar("in_reply_to", { length: 998 }), // RFC In-Reply-To header
  referencesChain: text("references_chain"), // RFC References header (space-separated chain of Message-Ids)
  toEmails: text("to_emails").array(), // Full To: list parsed from headers
  ccEmails: text("cc_emails").array(), // Full Cc: list
  bodyHtml: text("body_html"), // Rendered HTML body when present (preferred for inbox display)
  // Inbox state
  isRead: boolean("is_read").notNull().default(false),
  readAt: timestamp("read_at"),
  matchedLeadId: varchar("matched_lead_id"),
  triggeredSuppression: boolean("triggered_suppression").notNull().default(false),
  processedAt: timestamp("processed_at").notNull().defaultNow(),
  receivedAt: timestamp("received_at"),
  // ── Task #601 — AI Reply Management triage (per-message) ───────────
  // Auto-applied label from the AI categorize pass. NULL = not yet
  // categorized (either categorization is disabled or this row predates
  // the feature). Values are the seven labels in
  // INBOX_REPLY_CATEGORIES below; never invent new strings.
  aiCategory: varchar("ai_category", { length: 32 }).$type<InboxReplyCategory>(),
  // One-line model rationale shown on the chip tooltip so reps can
  // sanity-check the label before approving any draft.
  aiCategoryReason: text("ai_category_reason"),
  // 0–100 confidence the model returned with the label. Used to gate
  // whether a draft is auto-prepared (only high-confidence + actionable
  // categories get a pre-built draft).
  aiCategoryConfidence: integer("ai_category_confidence"),
  // When the categorize pass last ran (NULL = never).
  aiCategoryAt: timestamp("ai_category_at"),
  // Set when a rep manually overrides the AI label. We keep the original
  // AI value in `aiCategoryReason` (prefixed) so we can audit override
  // rates without a separate audit table for v1.
  aiCategoryOverriddenBy: varchar("ai_category_overridden_by"),
  // Which user's Gmail OAuth token was used to pull this message.
  // NULL for legacy rows and orphaned/connector polls.
  receivedByUserId: varchar("received_by_user_id", { length: 255 }),
}, (t) => ({
  fromEmailIdx: index("inbound_emails_from_email_idx").on(t.fromEmail),
  matchedLeadIdx: index("inbound_emails_matched_lead_idx").on(t.matchedLeadId),
  processedAtIdx: index("inbound_emails_processed_at_idx").on(t.processedAt),
  workspaceThreadIdx: index("inbound_emails_workspace_thread_idx").on(t.workspaceId, t.threadId),
  workspaceUnreadIdx: index("inbound_emails_workspace_unread_idx").on(t.workspaceId, t.isRead),
  // Index for the Priority Queue query (workspace × category × unread).
  workspaceCategoryIdx: index("inbound_emails_workspace_category_idx").on(t.workspaceId, t.aiCategory),
  // Workspace+time-DESC drives the inbox panel's hot read at scale.
  workspaceProcessedIdx: index("inbound_emails_workspace_processed_idx").on(t.workspaceId, t.processedAt),
  // Replacement for the dropped global UNIQUE on provider_message_id.
  messageIdHeaderIdx: index("inbound_emails_message_id_header_idx").on(t.messageIdHeader),
  providerMessageIdIdx: index("inbound_emails_provider_message_id_idx").on(t.providerMessageId),
}));

// ── Task #601 — AI Reply Management category labels ────────────────
// Seven canonical labels the categorize pass picks from. Order matters
// for the Priority Queue sort (scheduling > objection > negotiation >
// logistics_question > confirmation > other > spam). Keep this list in
// lockstep with the prompt in server/lib/ai-gateway.ts and the chip
// labels in client/src/components/inbox-panel.tsx.
export const INBOX_REPLY_CATEGORIES = [
  "logistics_question",
  "negotiation",
  "objection",
  "scheduling",
  "confirmation",
  "spam",
  "other",
] as const;
export type InboxReplyCategory = (typeof INBOX_REPLY_CATEGORIES)[number];

// Categories that should NEVER auto-draft + are filtered out of the
// Priority Queue. Spam/other = noise; confirmation = no reply needed.
export const INBOX_NON_ACTIONABLE_CATEGORIES: InboxReplyCategory[] = ["spam", "other"];

// Sort order for the Priority Queue. Lower = higher priority. The route
// reads this constant so a future tweak (e.g. swap negotiation +
// scheduling) is a one-line change.
export const INBOX_PRIORITY_RANK: Record<InboxReplyCategory, number> = {
  scheduling: 0,
  objection: 1,
  negotiation: 2,
  logistics_question: 3,
  confirmation: 4,
  other: 5,
  spam: 6,
};

export const inboundEmailsRelations = relations(inboundEmails, ({ one }) => ({
  matchedLead: one(leads, {
    fields: [inboundEmails.matchedLeadId],
    references: [leads.id],
  }),
}));

export const insertInboundEmailSchema = createInsertSchema(inboundEmails).omit({
  id: true,
  processedAt: true,
});

export type InsertInboundEmail = z.infer<typeof insertInboundEmailSchema>;
export type InboundEmail = typeof inboundEmails.$inferSelect;

// ── Task #601 — AI Reply Management: drafted replies ───────────────
// One draft per inbound message (latest wins — re-drafting overwrites
// the row). Keying on inboundEmailId means we never accumulate orphaned
// drafts for messages that get deleted, and the inbox UI only ever has
// to render "the current draft for THIS message". When SMS/IG/FB DM
// channels light up they'll get their own message tables but reuse this
// draft table by switching the `channel` column + adding a foreign
// inbound message id column (the table is intentionally narrow today
// to keep the v1 migration small).
export const INBOX_DRAFT_STATUSES = ["pending", "approved", "dismissed"] as const;
export type InboxDraftStatus = (typeof INBOX_DRAFT_STATUSES)[number];
export const INBOX_DRAFT_TONES = ["professional", "friendly", "concise"] as const;
export type InboxDraftTone = (typeof INBOX_DRAFT_TONES)[number];

export const inboxMessageDrafts = pgTable("inbox_message_drafts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // Exactly ONE of inboundEmailId / inboundSmsLogId / inboundSocialMessageId
  // must be set. Channel column tells you which source table to look at.
  // Migration 0032 added the SMS + social FKs and replaced the single
  // unique idx with three partial unique indexes (one per source) so
  // re-drafting overwrites in place per source.
  inboundEmailId: varchar("inbound_email_id").references(() => inboundEmails.id, { onDelete: "cascade" }),
  inboundSmsLogId: varchar("inbound_sms_log_id").references(() => smsMessageLogs.id, { onDelete: "cascade" }),
  inboundSocialMessageId: varchar("inbound_social_message_id").references(() => leadCatcherMessages.id, { onDelete: "cascade" }),
  channel: varchar("channel", { length: 16 }).notNull().default("email"), // email | sms | ig_dm | fb_dm
  tone: varchar("tone", { length: 24 }).notNull().default("professional").$type<InboxDraftTone>(),
  body: text("body").notNull(), // HTML for email, plaintext for SMS/DM
  status: varchar("status", { length: 16 }).notNull().default("pending").$type<InboxDraftStatus>(),
  // Cents the draft burned at the AI gateway. Surfaced in admin AI usage
  // breakdown so workspaces can see how much triage costs them.
  costCents: integer("cost_cents").notNull().default(0),
  modelKey: varchar("model_key", { length: 64 }),
  // Per-draft AI triage labels. For email these mirror inbound_emails.ai_category
  // (kept on the source row for the priority queue); for SMS/IG/FB DM these are
  // the canonical store since those source tables have no category column.
  aiCategory: varchar("ai_category", { length: 32 }).$type<InboxReplyCategory>(),
  aiCategoryReason: text("ai_category_reason"),
  aiCategoryConfidence: integer("ai_category_confidence"),
  generatedAt: timestamp("generated_at").notNull().defaultNow(),
  generatedByUserId: varchar("generated_by_user_id"), // null = auto-generated by inbound processor
  approvedByUserId: varchar("approved_by_user_id"),
  approvedAt: timestamp("approved_at"),
  dismissedByUserId: varchar("dismissed_by_user_id"),
  dismissedAt: timestamp("dismissed_at"),
  // Task #633 — why this draft was dismissed. `stale` is set by the
  // hourly stale-draft sweeper; user dismissals leave this NULL.
  dismissedReason: varchar("dismissed_reason", { length: 32 }),
}, (t) => ({
  // One ACTIVE draft per inbound source row. Three partial unique indexes
  // — one per source FK — let a single drafts table hold every channel
  // without forcing a synthetic compound key. Re-drafting overwrites in
  // place via ON CONFLICT against whichever index matches the channel.
  inboundEmailIdx: uniqueIndex("inbox_drafts_inbound_email_id_idx")
    .on(t.inboundEmailId)
    .where(sql`${t.inboundEmailId} IS NOT NULL`),
  inboundSmsIdx: uniqueIndex("inbox_drafts_inbound_sms_log_id_idx")
    .on(t.inboundSmsLogId)
    .where(sql`${t.inboundSmsLogId} IS NOT NULL`),
  inboundSocialIdx: uniqueIndex("inbox_drafts_inbound_social_msg_id_idx")
    .on(t.inboundSocialMessageId)
    .where(sql`${t.inboundSocialMessageId} IS NOT NULL`),
  workspaceStatusIdx: index("inbox_drafts_workspace_status_idx").on(t.workspaceId, t.status),
}));

export const insertInboxMessageDraftSchema = createInsertSchema(inboxMessageDrafts).omit({
  id: true,
  generatedAt: true,
});
export type InsertInboxMessageDraft = z.infer<typeof insertInboxMessageDraftSchema>;
export type InboxMessageDraft = typeof inboxMessageDrafts.$inferSelect;

// ── Task #601 — Per-user inbox channel preferences ─────────────────
// One row per (userId, channel). Lets a rep silence specific channels
// (e.g. "I never want to see Instagram DMs in my queue") without
// affecting other reps. Only `enabled=false` rows are persisted
// strictly speaking, but we eagerly write rows on first toggle so the
// UI can show explicit on/off state without inferring defaults.
export const INBOX_CHANNELS = ["email", "sms", "ig_dm", "fb_dm"] as const;
export type InboxChannel = (typeof INBOX_CHANNELS)[number];

export const inboxChannelPrefs = pgTable("inbox_channel_prefs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  channel: varchar("channel", { length: 16 }).notNull().$type<InboxChannel>(),
  enabled: boolean("enabled").notNull().default(true),
  // When true, AI drafts ARE generated for this channel for this user.
  // Independent from `enabled` so a rep can keep email visible but turn
  // off auto-drafts for it.
  aiDraftEnabled: boolean("ai_draft_enabled").notNull().default(true),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  uniqueUserChannel: uniqueIndex("inbox_channel_prefs_user_channel_idx").on(t.userId, t.workspaceId, t.channel),
}));

export const insertInboxChannelPrefSchema = createInsertSchema(inboxChannelPrefs).omit({
  id: true,
  updatedAt: true,
});
export type InsertInboxChannelPref = z.infer<typeof insertInboxChannelPrefSchema>;
export type InboxChannelPref = typeof inboxChannelPrefs.$inferSelect;

// Task #632 — non-email inbox thread summary. SMS threads are grouped by
// (workspace, phone); IG/FB DM threads by lead_catcher_conversations.id.
// `latestDraft`, when present, is the AI Reply Management draft attached
// to the most recent inbound message in the thread.
export type NonEmailInboxThread = {
  channel: "sms" | "ig_dm" | "fb_dm";
  // Stable thread key — phone for SMS, conversation id for social.
  threadKey: string;
  // Source row id of the latest inbound message — used to load the draft / approve.
  latestSourceId: string;
  fromHandle: string; // E.164 phone for SMS; senderId/handle for social
  preview: string;
  body: string; // full inbound body (untruncated) — detail pane needs this
  lastReceivedAt: Date | null;
  unreadCount: number; // not yet wired for SMS/social — always 0 for now
  latestDraft: {
    id: string;
    status: "pending" | "approved" | "dismissed";
    bodyPreview: string; // truncated for list rows
    body: string; // full untruncated draft text — detail pane / Approve / Edit / Send use this
    aiCategory: InboxReplyCategory | null;
  } | null;
};

// Thread-grouped summary for the Inbox UI. Backed by aggregating inbound rows by Gmail
// `threadId` (or by row id when no threadId). Used by `listInboxThreads`.
export type InboxThreadSummary = {
  // Stable thread key — Gmail threadId when present, otherwise the inbound row id
  // (so threadless inbound rows still appear as their own conversation).
  threadKey: string;
  threadId: string | null;
  latestMessageId: string; // The latest inbound message in this thread (used by GET /api/inbox/:id)
  subject: string | null;
  // Distinct senders who have written in this thread (deduped, latest-first)
  participants: string[];
  lastFromEmail: string | null;
  lastFromName: string | null;
  preview: string | null; // First ~120 chars of plain-text body of the latest message
  lastReceivedAt: Date | null;
  messageCount: number;
  unreadCount: number;
};

// ── Task #408 — Cold campaign folder DTOs ──────────────────────────
// Resolved cold sending identity for a workspace. agentUserIds = synthetic
// cold-agent backingUserIds that own cold inbound; mailboxAddresses = the
// workspace's provisioned cold mailbox addresses. mailboxById maps a
// provisioned-item id → its address for mailbox-of-origin display.
export type ColdScope = {
  agentUserIds: string[];
  mailboxAddresses: string[];
  mailboxById: Record<string, string>;
};

export type ColdThreadItem = {
  type: "inbound" | "sent";
  id: string;
  from: string | null;
  to: string[] | null;
  cc: string[] | null;
  subject: string | null;
  bodyHtml: string | null;
  bodyPlainText: string | null;
  sentAt: string | null;
};

export type ColdSentItem = {
  id: string;
  recipientEmail: string;
  subject: string | null;
  threadId: string | null;
  sentAt: string | null;
  mailbox: string | null; // mailbox-of-origin address (when resolvable)
  campaignName: string | null;
  opened: boolean;
  clicked: boolean;
};

export type ColdOutboxItem = {
  id: string;
  email: string;
  recipientName: string | null;
  subject: string | null;
  status: string; // PENDING | SENDING | FAILED
  failedReason: string | null;
  nextSendAt: string | null;
  mailbox: string | null;
  campaignName: string | null;
};

export type ColdDraftItem = {
  id: string; // campaign id
  campaignName: string;
  subject: string | null;
  totalRecipients: number;
  createdAt: string | null;
};

// Mailbox Deliverability Settings - per-mailbox warmup and sending configuration
export const mailboxDeliverabilitySettings = pgTable("mailbox_deliverability_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  mailboxId: varchar("mailbox_id", { length: 255 }).notNull().unique(), // e.g., user email or mailbox identifier
  warmupStartDate: timestamp("warmup_start_date").defaultNow(), // When campaign sending started for warmup calculation
  graceBuffer: integer("grace_buffer").notNull().default(20), // Extra emails allowed beyond daily cap
  sendWindowStartLocal: varchar("send_window_start_local", { length: 10 }).notNull().default("09:00"), // HH:mm format
  sendWindowEndLocal: varchar("send_window_end_local", { length: 10 }).notNull().default("17:30"), // HH:mm format
  sendWindowTimezone: varchar("send_window_timezone", { length: 50 }).notNull().default("America/Chicago"),
  maxPerTenMinutes: integer("max_per_ten_minutes").notNull().default(6), // Rate limiting
  minDelaySeconds: integer("min_delay_seconds").notNull().default(45), // Min jitter between sends
  maxDelaySeconds: integer("max_delay_seconds").notNull().default(140), // Max jitter between sends
  campaignEnabled: boolean("campaign_enabled").notNull().default(true),
  status: varchar("status", { length: 20 }).notNull().default("ACTIVE"), // ACTIVE, PAUSED
  pausedReason: varchar("paused_reason", { length: 255 }),
  pausedAt: timestamp("paused_at"),
  // Auto-pause thresholds (configurable per mailbox)
  bounceThreshold24h: real("bounce_threshold_24h").notNull().default(0.05), // 5%
  bounceThreshold7d: real("bounce_threshold_7d").notNull().default(0.08), // 8%
  optOutThreshold7d: real("opt_out_threshold_7d").notNull().default(0.03), // 3%
  // Task #683 — Reputation-aware daily cap (replaces flat 20→200 ramp)
  reputationScore: real("reputation_score").notNull().default(1.0), // 0..1
  reputationUpdatedAt: timestamp("reputation_updated_at"),
  warmupTargetPerDay: integer("warmup_target_per_day").notNull().default(200),
  warmupStartingPerDay: integer("warmup_starting_per_day").notNull().default(20),
  midflightPauseBounceRate: real("midflight_pause_bounce_rate").notNull().default(0.04),
  instantlyEnrolled: boolean("instantly_enrolled").notNull().default(false),
  instantlyEnrolledAt: timestamp("instantly_enrolled_at"),
  instantlyHealthScore: real("instantly_health_score"),
  instantlyHealthCheckedAt: timestamp("instantly_health_checked_at"),
  instantlySent: integer("instantly_sent"),
  instantlyLandedInbox: integer("instantly_landed_inbox"),
  instantlyLandedSpam: integer("instantly_landed_spam"),
  instantlyReceived: integer("instantly_received"),
  warmupPaid: boolean("warmup_paid").notNull().default(false),
  warmupPaidAt: timestamp("warmup_paid_at"),
  warmupStripePaymentIntentId: varchar("warmup_stripe_payment_intent_id", { length: 255 }),
  warmupStripeSubscriptionId: varchar("warmup_stripe_subscription_id", { length: 255 }),
  warmupStripeCustomerId: varchar("warmup_stripe_customer_id", { length: 255 }),
  warmupSubscriptionStatus: varchar("warmup_subscription_status", { length: 50 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertMailboxDeliverabilitySettingsSchema = createInsertSchema(mailboxDeliverabilitySettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertMailboxDeliverabilitySettings = z.infer<typeof insertMailboxDeliverabilitySettingsSchema>;
export type MailboxDeliverabilitySettings = typeof mailboxDeliverabilitySettings.$inferSelect;

// Mailbox Deliverability Stats - daily/rolling stats per mailbox
export const mailboxDeliverabilityStats = pgTable("mailbox_deliverability_stats", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  mailboxId: varchar("mailbox_id", { length: 255 }).notNull(),
  date: timestamp("date").notNull(), // Date for this stats record
  campaignSentCount: integer("campaign_sent_count").notNull().default(0),
  individualSentCount: integer("individual_sent_count").notNull().default(0),
  hardBounceCount: integer("hard_bounce_count").notNull().default(0),
  softBounceCount: integer("soft_bounce_count").notNull().default(0),
  optOutCount: integer("opt_out_count").notNull().default(0),
  replyCount: integer("reply_count").notNull().default(0),
  failedSendCount: integer("failed_send_count").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertMailboxDeliverabilityStatsSchema = createInsertSchema(mailboxDeliverabilityStats).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertMailboxDeliverabilityStats = z.infer<typeof insertMailboxDeliverabilityStatsSchema>;
export type MailboxDeliverabilityStats = typeof mailboxDeliverabilityStats.$inferSelect;

// Email Send Errors - track recent send errors for admin visibility
export const emailSendErrors = pgTable("email_send_errors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  mailboxId: varchar("mailbox_id", { length: 255 }).notNull(),
  campaignId: varchar("campaign_id"),
  recipientEmail: varchar("recipient_email", { length: 255 }),
  errorType: varchar("error_type", { length: 50 }).notNull(), // BOUNCE, RATE_LIMIT, AUTH, OTHER
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertEmailSendErrorSchema = createInsertSchema(emailSendErrors).omit({
  id: true,
  createdAt: true,
});

export type InsertEmailSendError = z.infer<typeof insertEmailSendErrorSchema>;
export type EmailSendError = typeof emailSendErrors.$inferSelect;

// Email Sending Domains - workspace-scoped sending domains discovered from
// connected mailboxes plus any custom domains added by an admin. Used by the
// Domain Authentication tab to run live SPF / DKIM / DMARC lookups.
export const emailSendingDomains = pgTable("email_sending_domains", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  domain: varchar("domain", { length: 255 }).notNull(),
  source: varchar("source", { length: 20 }).notNull().default("manual"), // 'auto' | 'manual'
  dkimSelector: varchar("dkim_selector", { length: 64 }).notNull().default("google"),
  notes: text("notes"),
  // Task #683 — Cold-send safety: secondary cold-sending domain support
  purpose: varchar("purpose", { length: 20 }).notNull().default("primary"), // 'primary' | 'cold'
  // Task #391 — cold-domain management mode: 'lease' | 'buy' | 'byod'.
  // Denormalized from the originating purchase so per-domain gating (e.g.
  // managed-Postmaster eligibility) can read it without a join. Only
  // meaningful for purpose='cold' rows; primary domains stay 'lease'.
  managementMode: varchar("management_mode", { length: 10 }).notNull().default("lease"),
  provider: varchar("provider", { length: 20 }), // 'postmark' | 'manual'
  providerDomainId: varchar("provider_domain_id", { length: 64 }),
  spfVerified: boolean("spf_verified").notNull().default(false),
  dkimVerified: boolean("dkim_verified").notNull().default(false),
  returnPathVerified: boolean("return_path_verified").notNull().default(false),
  dkimHost: varchar("dkim_host", { length: 255 }),
  dkimTextValue: text("dkim_text_value"),
  returnPathHost: varchar("return_path_host", { length: 255 }),
  returnPathCname: varchar("return_path_cname", { length: 255 }),
  verificationStatus: varchar("verification_status", { length: 20 }).notNull().default("pending"), // 'pending' | 'verified' | 'failed'
  verifiedAt: timestamp("verified_at"),
  lastVerificationCheckAt: timestamp("last_verification_check_at"),
  fromAddress: varchar("from_address", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  wsDomainIdx: uniqueIndex("email_sending_domains_ws_domain_idx").on(t.workspaceId, t.domain),
  wsPurposeIdx: index("email_sending_domains_ws_purpose_idx").on(t.workspaceId, t.purpose),
}));

export const insertEmailSendingDomainSchema = createInsertSchema(emailSendingDomains).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEmailSendingDomain = z.infer<typeof insertEmailSendingDomainSchema>;
export type EmailSendingDomain = typeof emailSendingDomains.$inferSelect;

// =====================================================================
// ESP Relay Configuration (Task #573 — Bulk-Email ESP Relay)
// =====================================================================
// Optional per-workspace relay through a warmed Email Service Provider
// (Postmark / Resend / SendGrid / AWS SES). When configured + enabled,
// `dispatchEmail({intent: 'bulk'})` routes through the configured ESP
// instead of Gmail; transactional sends stay on Gmail to preserve the
// reply-thread continuity and the connector OAuth.
//
// `apiKeyEncrypted` is stored via server/lib/crypto/fields.encryptField
// (envelope-AES-256-GCM). `apiKeyLast4` is stored separately for the UI
// to render a fingerprint without ever needing to decrypt the full key.
//
// `webhookSecret` + `webhookToken` are random per-workspace values used
// to authenticate inbound webhook callbacks from the ESP — the URL is
// `/api/webhooks/email-relay/:provider/:workspaceId/:webhookToken` and
// HMAC validation uses `webhookSecret` for providers that sign payloads
// (Postmark optionally; SES via SNS signature). Token-in-URL alone is
// the minimum bar so an attacker cannot forge bounce events for a
// workspace they don't know the token for.
export const workspaceEmailRelayConfigs = pgTable("workspace_email_relay_configs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }).unique(),
  provider: varchar("provider", { length: 20 }).notNull(), // 'postmark' | 'resend' | 'sendgrid' | 'ses'
  apiKeyEncrypted: text("api_key_encrypted").notNull(),
  apiKeyLast4: varchar("api_key_last4", { length: 8 }),
  fromEmail: varchar("from_email", { length: 255 }).notNull(),
  fromName: varchar("from_name", { length: 255 }),
  replyToEmail: varchar("reply_to_email", { length: 255 }),
  // The verified sending domain on the provider side (e.g. "send.example.com").
  // Stored separately from fromEmail because providers verify domains
  // (subdomains) rather than individual addresses.
  sendingDomain: varchar("sending_domain", { length: 255 }),
  // 'off'        — relay is configured but inactive (Gmail handles everything)
  // 'bulk_only'  — bulk + drip routes through ESP; transactional stays on Gmail
  routingMode: varchar("routing_mode", { length: 20 }).notNull().default("off"),
  enabled: boolean("enabled").notNull().default(false),
  // Task #391 — managed cold-reply forwarding target. When set, an inbound
  // cold reply on a MANAGED (lease/buy) mailbox is auto-forwarded as a copy
  // to this human mailbox (e.g. the rep's Gmail) so a person actually sees
  // it. Per-campaign opt-out lives on drip_campaigns.forward_cold_replies.
  coldReplyForwardEmail: varchar("cold_reply_forward_email", { length: 255 }),
  webhookToken: varchar("webhook_token", { length: 64 }).notNull(),
  webhookSecret: varchar("webhook_secret", { length: 128 }).notNull(),
  // Set once a "test connection" or first successful send confirms the API key.
  connectionVerifiedAt: timestamp("connection_verified_at"),
  lastSendAt: timestamp("last_send_at"),
  lastError: text("last_error"),
  // Lightweight running counters; reset on operator action only. Heavier
  // analytics will live in a dedicated stats table in a follow-up task.
  sendCount: integer("send_count").notNull().default(0),
  bounceCount: integer("bounce_count").notNull().default(0),
  spamComplaintCount: integer("spam_complaint_count").notNull().default(0),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  webhookTokenIdx: uniqueIndex("workspace_email_relay_webhook_token_idx").on(t.webhookToken),
}));

export const insertWorkspaceEmailRelayConfigSchema = createInsertSchema(workspaceEmailRelayConfigs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  sendCount: true,
  bounceCount: true,
  spamComplaintCount: true,
});

export type InsertWorkspaceEmailRelayConfig = z.infer<typeof insertWorkspaceEmailRelayConfigSchema>;
export type WorkspaceEmailRelayConfig = typeof workspaceEmailRelayConfigs.$inferSelect;

// DMARC Aggregate Reports — parsed RUA XML reports from receivers (Google,
// Yahoo, Microsoft, etc). Each row is one report from one org for one
// reporting window. Per-source rows are folded into the `sources` JSON for
// quick rendering. Used by the Domain Authentication tab to surface
// real-world pass/fail rates alongside the synthetic DNS check.

// Shape of one row in the `sources` JSONB column. Mirrors a single
// <record> in a DMARC aggregate XML report. Defined here so storage and
// parser code can share it without `any` casts.
export interface DmarcReportSource {
  sourceIp: string;
  host: string | null;
  count: number;
  disposition: string;             // "none" | "quarantine" | "reject" | other
  dkim: string;                    // "pass" | "fail" | other
  spf: string;                     // "pass" | "fail" | other
  dmarc: "pass" | "fail";
  headerFrom: string | null;
  dkimDomains: string[];
  spfDomain: string | null;
}

// Shape of the `policy_published` JSONB column.
export interface DmarcReportPolicyPublished {
  p: string | null;
  sp: string | null;
  pct: string | null;
  adkim: string | null;
  aspf: string | null;
}

export const dmarcAggregateReports = pgTable("dmarc_aggregate_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  domain: varchar("domain", { length: 255 }).notNull(),
  orgName: varchar("org_name", { length: 255 }).notNull(),
  orgEmail: varchar("org_email", { length: 255 }),
  reportId: varchar("report_id", { length: 255 }).notNull(),
  dateRangeBegin: timestamp("date_range_begin").notNull(),
  dateRangeEnd: timestamp("date_range_end").notNull(),
  policyPublished: jsonb("policy_published").$type<DmarcReportPolicyPublished>(),
  totalMessages: integer("total_messages").notNull().default(0),
  // dmarcPass = at least one of dkim/spf passed AND aligned (per row policy_evaluated)
  dmarcPassCount: integer("dmarc_pass_count").notNull().default(0),
  dmarcFailCount: integer("dmarc_fail_count").notNull().default(0),
  dkimPassCount: integer("dkim_pass_count").notNull().default(0),
  spfPassCount: integer("spf_pass_count").notNull().default(0),
  sources: jsonb("sources").$type<DmarcReportSource[]>().notNull().default(sql`'[]'::jsonb`),
  receivedAt: timestamp("received_at").defaultNow(),
}, (t) => ({
  uniqueReport: uniqueIndex("dmarc_aggregate_reports_unique_idx").on(
    t.workspaceId, t.domain, t.orgName, t.reportId,
  ),
  domainIdx: index("dmarc_aggregate_reports_domain_idx").on(
    t.workspaceId, t.domain, t.dateRangeEnd,
  ),
}));

export const insertDmarcAggregateReportSchema = createInsertSchema(dmarcAggregateReports).omit({
  id: true,
  receivedAt: true,
});

export type InsertDmarcAggregateReport = z.infer<typeof insertDmarcAggregateReportSchema>;
export type DmarcAggregateReport = typeof dmarcAggregateReports.$inferSelect;

// Aggregated summary per domain that the API surfaces to the UI. Computed
// from the most recent N reports — see server/dmarc-rua-parser.ts.
export interface DmarcAggregateSummary {
  domain: string;
  reportCount: number;
  windowBegin: string | null; // ISO
  windowEnd: string | null;   // ISO
  totalMessages: number;
  dmarcPassCount: number;
  dmarcFailCount: number;
  dmarcPassRate: number; // 0..1
  dkimPassRate: number;
  spfPassRate: number;
  topSources: Array<{
    sourceIp: string;
    host: string | null;
    count: number;
    dmarcPass: number;
    dmarcFail: number;
    dkimPass: number;
    spfPass: number;
    dispositions: Record<string, number>;
  }>;
  reporters: string[]; // org names that contributed
  latestReceivedAt: string | null;
}

// Email Alert Settings - workspace-level configuration for the early-warning
// engine. Watch thresholds are intentionally stricter than the per-mailbox
// auto-pause thresholds in mailbox_deliverability_settings so the system can
// flag a problem before it actually pauses the mailbox.
export const emailAlertSettings = pgTable("email_alert_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }).unique(),
  // Watch thresholds (decimal rates: 0.02 = 2%)
  watchBounceRate24h: real("watch_bounce_rate_24h").notNull().default(0.02),
  watchBounceRate7d: real("watch_bounce_rate_7d").notNull().default(0.04),
  watchSpamComplaintRate: real("watch_spam_complaint_rate").notNull().default(0.001), // 0.1%
  watchOptOutRate7d: real("watch_opt_out_rate_7d").notNull().default(0.02),
  watchOpenRateFloor: real("watch_open_rate_floor").notNull().default(0.10), // alert if below 10%
  alertOnReputationDrop: boolean("alert_on_reputation_drop").notNull().default(true),
  alertOnDmarcMissing: boolean("alert_on_dmarc_missing").notNull().default(true),
  alertOnMailboxPaused: boolean("alert_on_mailbox_paused").notNull().default(true),
  // Delivery preferences
  inAppBannerEnabled: boolean("in_app_banner_enabled").notNull().default(true),
  emailWorkspaceOwner: boolean("email_workspace_owner").notNull().default(true),
  // Optional list of additional admin user ids to notify
  additionalRecipientUserIds: text("additional_recipient_user_ids").array().default(sql`ARRAY[]::text[]`),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertEmailAlertSettingsSchema = createInsertSchema(emailAlertSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertEmailAlertSettings = z.infer<typeof insertEmailAlertSettingsSchema>;
export type EmailAlertSettings = typeof emailAlertSettings.$inferSelect;

// Email Health Alerts - individual alert events written by the evaluator. The
// Overview tab reads "active" (non-resolved) alerts to populate the
// "What's worrying us right now" callout.
export const emailHealthAlerts = pgTable("email_health_alerts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  alertType: varchar("alert_type", { length: 50 }).notNull(), // bounce_rate_24h, bounce_rate_7d, spam_complaint, opt_out_rate, open_rate_low, reputation_drop, dmarc_missing, mailbox_paused
  severity: varchar("severity", { length: 20 }).notNull().default("watch"), // watch | atRisk
  mailboxId: varchar("mailbox_id", { length: 255 }),
  domain: varchar("domain", { length: 255 }),
  message: text("message").notNull(),
  observedValue: real("observed_value"),
  thresholdValue: real("threshold_value"),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  wsActiveIdx: index("email_health_alerts_ws_active_idx").on(t.workspaceId, t.resolvedAt),
}));

export const insertEmailHealthAlertSchema = createInsertSchema(emailHealthAlerts).omit({
  id: true,
  createdAt: true,
});

export type InsertEmailHealthAlert = z.infer<typeof insertEmailHealthAlertSchema>;
export type EmailHealthAlert = typeof emailHealthAlerts.$inferSelect;

// Postmaster Tools Connections - stores the workspace-level OAuth refresh token
// for read-only access to Google Postmaster Tools. Cached metrics are kept on
// the connection row so the UI can render without re-hitting Google's API.
export const postmasterToolsConnections = pgTable("postmaster_tools_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }).unique(),
  accountEmail: varchar("account_email", { length: 255 }),
  refreshToken: text("refresh_token").notNull(),
  scope: text("scope"),
  cachedDomainsJson: text("cached_domains_json"), // JSON: [{ domain, reputation, spamRate, ... }]
  lastSyncedAt: timestamp("last_synced_at"),
  lastSyncError: text("last_sync_error"),
  connectedBy: varchar("connected_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPostmasterToolsConnectionSchema = createInsertSchema(postmasterToolsConnections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPostmasterToolsConnection = z.infer<typeof insertPostmasterToolsConnectionSchema>;
export type PostmasterToolsConnection = typeof postmasterToolsConnections.$inferSelect;

// Postmaster reputation samples — time-series rows captured by the daily
// scheduler. One row per (workspace, domain, sampleDate). Google's traffic
// stats are reported one day at a time, so we use a date column rather than
// a timestamp and dedupe on (workspaceId, domain, sampleDate).
export const postmasterReputationSamples = pgTable("postmaster_reputation_samples", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  domain: varchar("domain", { length: 255 }).notNull(),
  sampleDate: date("sample_date").notNull(), // YYYY-MM-DD (the day Google reports stats for)
  domainReputation: varchar("domain_reputation", { length: 16 }), // HIGH | MEDIUM | LOW | BAD | UNKNOWN
  ipReputationsJson: text("ip_reputations_json"), // raw IP reputation buckets from Google
  spamRate: real("spam_rate"),                    // userReportedSpamRatio
  spfPassRate: real("spf_pass_rate"),
  dkimPassRate: real("dkim_pass_rate"),
  dmarcPassRate: real("dmarc_pass_rate"),
  inboundEncryptionRatio: real("inbound_encryption_ratio"),
  outboundEncryptionRatio: real("outbound_encryption_ratio"),
  deliveryErrorsJson: text("delivery_errors_json"), // [{errorType, errorClass, errorRatio}, ...]
  feedbackLoopJson: text("feedback_loop_json"),     // [{id, spamRatio}, ...]
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  wsDomainDateUniq: uniqueIndex("postmaster_reputation_samples_ws_domain_date_uniq").on(t.workspaceId, t.domain, t.sampleDate),
  wsDomainIdx: index("postmaster_reputation_samples_ws_domain_idx").on(t.workspaceId, t.domain),
}));

export const insertPostmasterReputationSampleSchema = createInsertSchema(postmasterReputationSamples).omit({
  id: true,
  createdAt: true,
});

export type InsertPostmasterReputationSample = z.infer<typeof insertPostmasterReputationSampleSchema>;
export type PostmasterReputationSample = typeof postmasterReputationSamples.$inferSelect;

// Invoice Settings - configurable invoice display options (per client type)
export const invoiceSettings = pgTable("invoice_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  // Client type - null means "All Client Types" (default settings)
  clientTypeId: varchar("client_type_id"),
  // Deposit toggle (whether the deposit checkbox even renders for this
  // client type; admins can hide deposits per-client-type, e.g. for
  // "Regulars" who never pay deposits).
  enableDeposits: boolean("enable_deposits").default(true),
  // Deposit default — sales rep sees this pre-filled when checking the
  // "Deposit required" box on a new booking. Mode picks how the value
  // is interpreted; the rep can override unless `depositForcedByAdmin`
  // is on, in which case both the checkbox and the amount are read-only.
  depositMode: varchar("deposit_mode", { length: 16 }).notNull().default("percent"),
  depositPercentage: integer("deposit_percentage").default(50),
  depositFlatCents: integer("deposit_flat_cents"),
  depositForcedByAdmin: boolean("deposit_forced_by_admin").notNull().default(false),
  // Late fees toggle - whether late fees apply to this client type
  applyLateFees: boolean("apply_late_fees").default(true),
  // Payment terms
  paymentTerms: text("payment_terms").default("Payment is due upon receipt. Please remit payment within 30 days of invoice date."),
  // Disclaimer text
  disclaimerText: text("disclaimer_text").default("Invoices unpaid 60 days after the performance date may incur a late fee of $50, with additional fees applied monthly as allowed by law."),
  // Company address — left blank by default. The API falls back to the
  // workspace's own name + an empty address when this row hasn't been
  // configured yet, so we never silently substitute another agency's
  // brand into a tenant's invoices. (task #494)
  companyAddress: text("company_address"),
  // Invoice header/title — see note above for why no default value.
  invoiceTitle: text("invoice_title"),
  // Audit
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
});

export const insertInvoiceSettingsSchema = createInsertSchema(invoiceSettings).omit({
  id: true,
  updatedAt: true,
});

export type InsertInvoiceSettings = z.infer<typeof insertInvoiceSettingsSchema>;
export type InvoiceSettings = typeof invoiceSettings.$inferSelect;

// ========================================
// DRIP CAMPAIGNS
// ========================================

export type DripCampaignStatus = "draft" | "active" | "paused";
export type DripEnrollmentStatus = "active" | "completed" | "paused" | "suppressed" | "auth_paused";
export type DripMessageStatus = "queued" | "sent" | "failed" | "suppressed";

// Drip Campaigns - automated email sequences
export const dripCampaigns = pgTable("drip_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("draft"), // draft, active, paused
  scope: varchar("scope", { length: 20 }).notNull().default("USER"), // MASTER (admin-created for all users), USER (personal)
  ownerUserId: varchar("owner_user_id"), // null for MASTER, user id for USER
  assignedToUserId: varchar("assigned_to_user_id"), // For MASTER: specific user, "ALL_SALES_REPS", or null
  createdByUserId: varchar("created_by_user_id").notNull(),
  fromName: varchar("from_name", { length: 255 }),
  fromEmail: varchar("from_email", { length: 255 }),
  autoEnrollNewLeads: boolean("auto_enroll_new_leads").default(false), // MASTER only: auto-enroll new leads
  autoEnrollDelayMinutes: integer("auto_enroll_delay_minutes").default(0), // Delay before auto-enrolling

  // ── M1 (Drip Foundation) — event-based trigger config ──────────────
  // `triggerType`: how the campaign starts. `manual` keeps existing
  // behavior (manual enrollment + auto-enroll-new-lead via the legacy
  // flag above). All other values are subscribed to by the central
  // `emitDripEvent` bus in server/lib/drip-event-bus.ts.
  triggerType: varchar("trigger_type", { length: 40 }).notNull().default("manual"),
  // Per-trigger configuration. Shape varies by triggerType:
  //   show.scheduled        → { daysBefore: 7 }
  //   show.completed        → { daysAfter: 1 }
  //   tour.started          → { daysAfter: 0 }   (0 = day-of)
  //   tour.ended            → { daysAfter: 7 }
  //   booking.deposit_paid  → { daysAfter: 0 }
  //   booking.balance_paid  → { daysAfter: 0 }
  //   contract.unsigned     → { daysSinceSent: 5 }
  //   invoice.overdue       → { daysOverdue: 3 }   (post-show + unpaid)
  //   lead.status_changed   → { toStatus: "qualified" }
  //   lead.inactive         → { daysSinceActivity: 30 }
  triggerConfig: jsonb("trigger_config"),

  // ── M1 — Campaign-level behavior settings ──────────────────────────
  // These take precedence over the legacy per-step `pauseOnReply` flag
  // when set on the campaign row.
  autoPauseOnReply: boolean("auto_pause_on_reply").notNull().default(true),
  autoExitOnBooked: boolean("auto_exit_on_booked").notNull().default(true),
  autoExitOnReplied: boolean("auto_exit_on_replied").notNull().default(false),
  // Task #391 — per-campaign toggle for managed cold-reply forwarding. When
  // true (default), an inbound cold reply on a MANAGED (lease/buy) mailbox is
  // auto-forwarded to workspace_email_relay_configs.cold_reply_forward_email.
  // Has no effect on BYOD mailboxes (managed forwarding is off entirely).
  forwardColdReplies: boolean("forward_cold_replies").notNull().default(true),
  autoExitOnDisqualified: boolean("auto_exit_on_disqualified").notNull().default(true),
  // When true, sends never fall outside the start/end window in CT.
  respectBusinessHours: boolean("respect_business_hours").notNull().default(false),
  businessHoursStartLocal: varchar("business_hours_start_local", { length: 5 }).notNull().default("09:00"),
  businessHoursEndLocal: varchar("business_hours_end_local", { length: 5 }).notNull().default("17:00"),
  // When true, the From identity at send time is the contact's assigned
  // rep (lead.assignedToUserId / client.assignedToUserId). If the
  // contact has no rep AND `sendAsRoundRobin` is true, the round-robin
  // helper picks the next sales rep using the workspace pointer below.
  sendAsAssignedRep: boolean("send_as_assigned_rep").notNull().default(false),
  sendAsRoundRobin: boolean("send_as_round_robin").notNull().default(false),
  // Campaign-wide "RE: trick": if a step goes unopened beyond the
  // threshold, the next step is sent as a reply to that step's Gmail
  // thread so it appears as "RE: …" in the recipient's inbox.
  // (Per-step opt-in still possible via `dripCampaignSteps.sendAsReply`.)
  replyTrickEnabled: boolean("reply_trick_enabled").notNull().default(false),
  // Smear sends across N minutes when a batch fires at the same minute
  // (Gmail rate-limit avoidance). 0 = no spread.
  sendTimeSpreadMinutes: integer("send_time_spread_minutes").notNull().default(0),

  // Campaign-level daily send cap. When set, the drip scheduler will not
  // send more than this many emails for this campaign in a UTC calendar day.
  // Pending enrollments are NOT dropped — they remain queued and continue
  // sending on subsequent days. Null = no campaign-level cap; fall through
  // to workspace policy (existing behavior, unchanged).
  maxSendsPerDay: integer("max_sends_per_day"),

  // Task #683 — Cold-send safety
  riskTier: varchar("risk_tier", { length: 20 }).notNull().default("warm"), // 'cold' | 'warm'
  // True only when a user EXPLICITLY overrode a prospect/lead audience's
  // cold default to warm through the composer's warm-volume gate. Lets the
  // drip scheduler honor an intentional warm choice WITHOUT flipping legacy
  // lead drips (riskTier='warm' by column default) off the force-cold path.
  warmOverrideApproved: boolean("warm_override_approved").notNull().default(false),
  autoPausedReason: varchar("auto_paused_reason", { length: 255 }),
  autoPausedAt: timestamp("auto_paused_at"),

  // ── M6 — Audience segment ──────────────────────────────────────────
  // Optional whitelist-predicate filter (same shape as the M3 skip-if
  // filter — see server/lib/drip-skip-filter.ts). When set, the polled
  // segment enroller sweeps every 5 min, evaluates the filter against
  // every contact in the configured entity scope (lead | client), and
  // enrolls any matches that aren't already enrolled. Idempotent via
  // the existing `getEnrollmentByCampaignAndContact` dedupe so re-sweeps
  // never create duplicate enrollments.
  //
  // We store our own jsonb here rather than referencing
  // `saved_smart_searches` because that table holds NL sentences (not
  // structured filters), and re-parsing the NL string on every sweep
  // would drag the AI gateway into a hot path. Keeping the predicate
  // first-class also means the evaluator can stay pure + unit-testable.
  targetSegmentJson: jsonb("target_segment_json"),
  targetSegmentEntity: varchar("target_segment_entity", { length: 16 }).notNull().default("lead"),

  // Stable identifier for rows inserted by `server/seed-automations.ts`
  // (task #129). Mirrors `email_templates.seed_key` so each seeded
  // campaign shares the same idempotency key as the master template
  // it was paired with — renaming a seeded campaign in the UI no
  // longer causes a duplicate to appear on the next boot. Null for
  // every non-seeded campaign (USER/MASTER created by admins).
  // Unique per workspace via `drip_campaigns_ws_seed_key_uniq`.
  seedKey: varchar("seed_key", { length: 100 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceStatusIdx: index("drip_campaigns_ws_status_idx").on(t.workspaceId, t.status),
  workspaceScopeIdx: index("drip_campaigns_ws_scope_idx").on(t.workspaceId, t.scope),
  ownerUserIdx: index("drip_campaigns_owner_user_idx").on(t.ownerUserId),
  // Unique-per-workspace so seed inserts can't race-create duplicates.
  // Postgres treats NULLs as distinct in unique indexes, so non-seeded
  // rows (which leave seed_key null) are unaffected.
  workspaceSeedKeyUniq: uniqueIndex("drip_campaigns_ws_seed_key_uniq").on(t.workspaceId, t.seedKey),
}));

export const insertDripCampaignSchema = createInsertSchema(dripCampaigns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ────────────────────────────────────────────────────────────────────
// M1 (Drip Foundation) — Drip event emissions ledger
// ────────────────────────────────────────────────────────────────────
// Every event the drip event bus emits is recorded here, keyed by a
// caller-supplied `dedupeKey` so the same logical event (e.g. "show
// 7 days before for booking X") never fires twice. The bus uses
// INSERT … ON CONFLICT DO NOTHING; if the insert is suppressed, the
// event is skipped entirely (no campaigns subscribed are evaluated).
//
// dedupeKey conventions:
//   show.scheduled       → `${bookingId}:before:${daysBefore}`
//   show.completed       → `${bookingId}:after:${daysAfter}`
//   tour.started         → `${runId}:started`
//   tour.ended           → `${runId}:ended:${daysAfter}`
//   booking.deposit_paid → `${bookingId}:deposit`
//   booking.balance_paid → `${bookingId}:balance`
//   contract.unsigned    → `${contractId}:unsigned:${daysSinceSent}`
//   invoice.overdue      → `${invoiceId}:overdue:${daysOverdue}`
//   lead.status_changed  → `${leadId}:to:${toStatus}:${changedAt-iso}`
//   lead.inactive        → `${leadId}:inactive:${daysSinceActivity}`
//
// Multi-tenant: every emission is workspace-scoped; the unique index
// includes workspaceId so cross-tenant collisions are impossible.
export const dripEventEmissions = pgTable("drip_event_emissions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  eventType: varchar("event_type", { length: 40 }).notNull(),
  // Logical entity the event is about. lead | client | booking | invoice
  // | contract | run. Used by the bus to resolve which contact (lead/
  // client) to enroll into matched campaigns.
  entityType: varchar("entity_type", { length: 20 }).notNull(),
  entityId: varchar("entity_id").notNull(),
  // Caller-supplied idempotency key (see conventions above). Combined
  // with workspaceId+eventType for the unique index so the same event
  // is never re-emitted for the same logical occurrence.
  dedupeKey: varchar("dedupe_key", { length: 200 }).notNull(),
  // Optional opaque payload the bus passes through to subscribers.
  payload: jsonb("payload"),
  emittedAt: timestamp("emitted_at").notNull().defaultNow(),
}, (t) => ({
  uniqEmission: uniqueIndex("drip_event_emissions_uniq").on(t.workspaceId, t.eventType, t.dedupeKey),
  workspaceTypeIdx: index("drip_event_emissions_ws_type_idx").on(t.workspaceId, t.eventType, t.emittedAt),
}));

export type DripEventEmission = typeof dripEventEmissions.$inferSelect;

export type InsertDripCampaign = z.infer<typeof insertDripCampaignSchema>;
export type DripCampaign = typeof dripCampaigns.$inferSelect;

// Drip Campaign Steps - individual emails in the sequence
export const dripCampaignSteps = pgTable("drip_campaign_steps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull().references(() => dripCampaigns.id, { onDelete: "cascade" }),
  stepOrder: integer("step_order").notNull().default(1),
  delayMinutes: integer("delay_minutes").notNull().default(0), // 0 = immediate, 1440 = 1 day
  sendAtTime: varchar("send_at_time", { length: 5 }), // Optional HH:MM time in CT to send on the target day
  // M2: channel for this step. 'email' (default) routes through Gmail
  // and uses subjectTemplate + bodyTemplate. 'sms' routes through the
  // Twilio SMS provider and uses smsBody (subject is ignored). Existing
  // rows get 'email' so behavior is identical pre/post-migration.
  stepType: varchar("step_type", { length: 16 }).notNull().default("email"),
  // M2: SMS body template (only used when stepType === 'sms'). Kept
  // separate from bodyTemplate so creators can flip a step between
  // email and SMS without losing the other channel's draft.
  smsBody: text("sms_body"),
  subjectTemplate: varchar("subject_template", { length: 500 }).notNull().default(""),
  bodyTemplate: text("body_template").notNull().default(""),
  pauseOnReply: boolean("pause_on_reply").notNull().default(true), // Pause enrollment if lead replies before next step
  // Send this step as a true reply to the previous step's delivered message
  // (same Gmail thread, "Re:" subject, In-Reply-To/References headers).
  // Only meaningful for steps where stepOrder >= 2.
  sendAsReply: boolean("send_as_reply").notNull().default(false),
  // ── M3: conditional branching & step-level filters ─────────────────
  // When set, after this step finishes the scheduler looks back over
  // engagement events for the message just sent and decides which step
  // to jump to next. Falsy condition = legacy linear behavior.
  //
  //   branchOnCondition: 'opened' | 'clicked' | 'replied' | 'booked'
  //   branchOnLookbackHours: how far back to consider events (default 24)
  //   branchTrueGoToStepOrder: 1-based stepOrder to jump to if true
  //   branchFalseGoToStepOrder: 1-based stepOrder to jump to if false
  //                              (null = fall through to next step)
  //
  // Self-reference / out-of-range targets are clamped server-side to
  // avoid infinite loops or "jump past the end" silently completing.
  branchOnCondition: varchar("branch_on_condition", { length: 32 }),
  branchOnLookbackHours: integer("branch_on_lookback_hours").notNull().default(24),
  branchTrueGoToStepOrder: integer("branch_true_go_to_step_order"),
  branchFalseGoToStepOrder: integer("branch_false_go_to_step_order"),
  // M3: skip-if filter. JSON-encoded predicate evaluated against the
  // contact (lead/client) right before send. If matched, the step is
  // recorded as 'skipped' and the engine advances to the next step.
  // Predicates are whitelisted in `server/lib/drip-skip-filter.ts`;
  // anything off the whitelist is treated as a non-match (fail-open
  // delivery, never silently suppress).
  skipIfFilter: jsonb("skip_if_filter"),
  // DEPRECATED legacy column — workspace scoping is inherited from the parent campaign.
  // Declared here only so Drizzle does not drop it on deploy. Do not read or write.
  workspaceId: varchar("workspace_id"),
  // Stable identifier for rows inserted by `server/seed-automations.ts`
  // (task #333). Mirrors the pattern on `drip_campaigns.seed_key`: each
  // starter step in an `AutomationSeed.defaultSteps` carries a stable
  // `${seed.seedKey}:${step.stepKey}` value so the seeder can find the
  // exact row on the next boot and avoid double-inserting it. Null for
  // any step admins create themselves in the Email Hub UI.
  // Unique per campaign via `drip_campaign_steps_camp_seed_key_uniq`.
  seedKey: varchar("seed_key", { length: 150 }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  // Unique-per-campaign so the boot seeder can't race-create duplicate
  // starter steps. Postgres treats NULLs as distinct in unique indexes,
  // so admin-created steps (which leave seed_key null) are unaffected
  // and a single campaign can hold any number of them.
  campaignSeedKeyUniq: uniqueIndex("drip_campaign_steps_camp_seed_key_uniq").on(t.campaignId, t.seedKey),
}));

export const dripCampaignStepsRelations = relations(dripCampaignSteps, ({ one }) => ({
  campaign: one(dripCampaigns, {
    fields: [dripCampaignSteps.campaignId],
    references: [dripCampaigns.id],
  }),
}));

export const insertDripCampaignStepSchema = createInsertSchema(dripCampaignSteps).omit({
  id: true,
  createdAt: true,
});

export type InsertDripCampaignStep = z.infer<typeof insertDripCampaignStepSchema>;
export type DripCampaignStep = typeof dripCampaignSteps.$inferSelect;

// Drip Campaign Enrollments - contacts enrolled in a campaign
export const dripCampaignEnrollments = pgTable("drip_campaign_enrollments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull().references(() => dripCampaigns.id, { onDelete: "cascade" }),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id").notNull(), // Lead or Client ID
  contactType: varchar("contact_type", { length: 20 }).notNull().default("lead"), // lead or client
  contactEmail: varchar("contact_email", { length: 255 }).notNull(),
  contactName: varchar("contact_name", { length: 255 }),
  currentStepIndex: integer("current_step_index").notNull().default(0),
  nextSendAt: timestamp("next_send_at"),
  status: varchar("status", { length: 20 }).notNull().default("active"), // active, completed, paused, suppressed, auth_paused
  authPausedAt: timestamp("auth_paused_at"),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
}, (t) => ({
  campaignStatusIdx: index("drip_enrollments_campaign_status_idx").on(t.campaignId, t.status),
  workspaceStatusNextIdx: index("drip_enrollments_ws_status_next_idx").on(t.workspaceId, t.status, t.nextSendAt),
  contactIdx: index("drip_enrollments_contact_idx").on(t.contactType, t.contactId),
  // M6 (idempotency hardening): one enrollment per contact per campaign.
  // The polled segment sweep relies on this to make concurrent ticks
  // safe — without it, two sweeps racing past `getEnrollmentByCampaignAndContact`
  // would both insert. With it, the loser hits 23505 and we swallow.
  campaignContactUniq: uniqueIndex("drip_enrollments_campaign_contact_uniq").on(
    t.campaignId, t.contactType, t.contactId,
  ),
}));

export const dripCampaignEnrollmentsRelations = relations(dripCampaignEnrollments, ({ one }) => ({
  campaign: one(dripCampaigns, {
    fields: [dripCampaignEnrollments.campaignId],
    references: [dripCampaigns.id],
  }),
}));

export const insertDripCampaignEnrollmentSchema = createInsertSchema(dripCampaignEnrollments).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});

export type InsertDripCampaignEnrollment = z.infer<typeof insertDripCampaignEnrollmentSchema>;
export type DripCampaignEnrollment = typeof dripCampaignEnrollments.$inferSelect;

// Drip Campaign Messages - individual emails sent
export const dripCampaignMessages = pgTable("drip_campaign_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull().references(() => dripCampaigns.id, { onDelete: "cascade" }),
  enrollmentId: varchar("enrollment_id").notNull().references(() => dripCampaignEnrollments.id, { onDelete: "cascade" }),
  stepId: varchar("step_id").notNull().references(() => dripCampaignSteps.id, { onDelete: "cascade" }),
  // M2: which channel actually delivered this message. 'email' is the
  // historical default; 'sms' was added with M2 SMS channel parity.
  channel: varchar("channel", { length: 16 }).notNull().default("email"),
  // M2: nullable because SMS sends do not have an email recipient.
  // Existing rows backfill to their original to_email value.
  toEmail: varchar("to_email", { length: 255 }),
  // M2: phone (E.164) used for SMS sends; null for email sends.
  toPhone: varchar("to_phone", { length: 32 }),
  // M2: provider message id returned by Twilio. Used for delivery
  // status reconciliation and for marking duplicate sends as already-
  // delivered without re-billing the SMS.
  smsSid: varchar("sms_sid", { length: 64 }),
  status: varchar("status", { length: 20 }).notNull().default("queued"), // queued, sent, failed, suppressed
  scheduledAt: timestamp("scheduled_at"),
  sentAt: timestamp("sent_at"),
  error: text("error"),
  notes: text("notes"), // Optional admin-visible notes (e.g., "Reply step fell back to fresh thread because step 1 never sent")
  // Threading metadata so a later step in the same enrollment can reply on this thread
  messageIdHeader: varchar("message_id_header", { length: 998 }), // RFC Message-Id of the delivered email
  threadId: varchar("thread_id", { length: 255 }), // Gmail thread id
  inReplyTo: varchar("in_reply_to", { length: 998 }), // The Message-Id this message replied to (when sendAsReply is on)
  referencesChain: text("references_chain"), // RFC 5322 References header (full ancestry, space-separated Message-Ids)
  trackingId: varchar("tracking_id").unique(), // UUID used for open/click pixel tracking
  // M5: A/B variants — when the step has variants, the picker records
  // which variant was sent so analytics can break the funnel down per arm.
  variantId: varchar("variant_id"),
  // Set immediately BEFORE the provider (Gmail/Twilio/ESP) call. Used
  // by the scheduler to refuse re-sending a row whose previous attempt
  // is still inside the kill window — a process kill between the
  // provider POST and the status='sent' UPDATE would otherwise let
  // the next tick re-send the same email/SMS. The scheduler treats
  // any row with last_send_attempted_at within DRIP_KILL_WINDOW_MS
  // as "delivery uncertain" and skips it (see drip-scheduler.ts).
  lastSendAttemptedAt: timestamp("last_send_attempted_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  enrollmentIdx: index("drip_messages_enrollment_idx").on(t.enrollmentId),
  statusScheduledIdx: index("drip_messages_status_scheduled_idx").on(t.status, t.scheduledAt),
  enrollmentStepIdx: index("drip_messages_enrollment_step_idx").on(t.enrollmentId, t.stepId),
  // Idempotency hardening: one message row per (enrollment, step). The
  // scheduler's check-then-insert path relies on this to make concurrent
  // ticks (or scheduler instances) safe — losers hit 23505 and we treat
  // it as "already queued/sent".
  enrollmentStepUniq: uniqueIndex("drip_messages_enrollment_step_uniq").on(
    t.enrollmentId, t.stepId,
  ),
}));

export const dripCampaignMessagesRelations = relations(dripCampaignMessages, ({ one }) => ({
  campaign: one(dripCampaigns, {
    fields: [dripCampaignMessages.campaignId],
    references: [dripCampaigns.id],
  }),
  enrollment: one(dripCampaignEnrollments, {
    fields: [dripCampaignMessages.enrollmentId],
    references: [dripCampaignEnrollments.id],
  }),
  step: one(dripCampaignSteps, {
    fields: [dripCampaignMessages.stepId],
    references: [dripCampaignSteps.id],
  }),
}));

export const insertDripCampaignMessageSchema = createInsertSchema(dripCampaignMessages).omit({
  id: true,
  createdAt: true,
});

export type InsertDripCampaignMessage = z.infer<typeof insertDripCampaignMessageSchema>;
export type DripCampaignMessage = typeof dripCampaignMessages.$inferSelect;

// M5: A/B variants on a step. A step with zero variants behaves exactly
// as before (sends its subjectTemplate/bodyTemplate). A step with N
// variants performs a deterministic weighted pick at send time, keyed by
// `enrollmentId+stepId` so the same enrollment always gets the same arm
// even on retry. Up to 4 variants in the UI to keep statistical power
// per arm reasonable on typical workspace volumes.
export const dripCampaignStepVariants = pgTable("drip_campaign_step_variants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  stepId: varchar("step_id")
    .notNull()
    .references(() => dripCampaignSteps.id, { onDelete: "cascade" }),
  label: varchar("label", { length: 64 }).notNull().default("Variant"),
  weight: integer("weight").notNull().default(1),
  subjectTemplate: text("subject_template").notNull().default(""),
  bodyTemplate: text("body_template").notNull().default(""),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  stepIdx: index("drip_step_variants_step_idx").on(t.stepId),
}));

export const dripCampaignStepVariantsRelations = relations(dripCampaignStepVariants, ({ one }) => ({
  step: one(dripCampaignSteps, {
    fields: [dripCampaignStepVariants.stepId],
    references: [dripCampaignSteps.id],
  }),
}));

export const insertDripCampaignStepVariantSchema = createInsertSchema(dripCampaignStepVariants).omit({
  id: true,
  createdAt: true,
});
export type InsertDripCampaignStepVariant = z.infer<typeof insertDripCampaignStepVariantSchema>;
export type DripCampaignStepVariant = typeof dripCampaignStepVariants.$inferSelect;

// Drip Campaign Permissions - role-based access control
export const dripCampaignPermissions = pgTable("drip_campaign_permissions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  role: varchar("role", { length: 50 }).notNull().unique(), // sales_rep, artist, billing, admin
  canViewCampaigns: boolean("can_view_campaigns").notNull().default(true),
  canCreateCampaigns: boolean("can_create_campaigns").notNull().default(false),
  canEditCampaigns: boolean("can_edit_campaigns").notNull().default(false),
  canDeleteCampaigns: boolean("can_delete_campaigns").notNull().default(false),
  canEnrollContacts: boolean("can_enroll_contacts").notNull().default(false),
  canPauseCampaigns: boolean("can_pause_campaigns").notNull().default(false),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertDripCampaignPermissionSchema = createInsertSchema(dripCampaignPermissions).omit({
  id: true,
  updatedAt: true,
});

export type InsertDripCampaignPermission = z.infer<typeof insertDripCampaignPermissionSchema>;
export type DripCampaignPermission = typeof dripCampaignPermissions.$inferSelect;

// ========================================
// SMS CAMPAIGNS
// ========================================

export type SmsCampaignStatus = "draft" | "scheduled" | "sending" | "paused" | "completed";
export type SmsSendMode = "now" | "scheduled";
export type SmsAudienceType = "lead" | "client" | "mixed";
export type SmsSendStatus = "queued" | "sent" | "failed" | "skipped" | "dry_run";
export type SmsMessageDirection = "outbound" | "inbound";
export type SmsSuppressionReason = "STOP" | "manual" | "invalid";
export type SmsSuppressionSource = "inbound" | "admin" | "system";

// SMS Campaigns - main campaign table
export const smsCampaigns = pgTable("sms_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  name: varchar("name", { length: 255 }).notNull(),
  audienceType: varchar("audience_type", { length: 20 }).notNull().default("mixed"), // lead, client, mixed
  filtersJson: text("filters_json"), // JSON blob for all filter criteria
  messageTemplate: text("message_template").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("draft"), // draft, scheduled, sending, paused, completed
  sendMode: varchar("send_mode", { length: 20 }).notNull().default("now"), // now, scheduled
  scheduledFor: timestamp("scheduled_for"),
  createdByUserId: varchar("created_by_user_id").notNull(),
  sentCount: integer("sent_count").default(0),
  failedCount: integer("failed_count").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  // Per-workspace listing in admin UI
  workspaceCreatedIdx: index("sms_campaigns_ws_created_idx").on(t.workspaceId, t.createdAt),
  // Global cron tick: SELECT WHERE status='scheduled' AND scheduled_for <= NOW()
  // (no workspace filter — single query across all tenants).
  statusScheduledIdx: index("sms_campaigns_status_scheduled_idx").on(t.status, t.scheduledFor),
}));

export const insertSmsCampaignSchema = createInsertSchema(smsCampaigns).omit({
  id: true,
  sentCount: true,
  failedCount: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSmsCampaign = z.infer<typeof insertSmsCampaignSchema>;
export type SmsCampaign = typeof smsCampaigns.$inferSelect;

// SMS Campaign Recipients - snapshot of recipients for a campaign
export const smsCampaignRecipients = pgTable("sms_campaign_recipients", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull(),
  entityType: varchar("entity_type", { length: 20 }).notNull(), // lead, client
  entityId: varchar("entity_id").notNull(),
  phoneE164: varchar("phone_e164", { length: 20 }).notNull(),
  displayName: varchar("display_name", { length: 255 }),
  regionId: varchar("region_id"),
  clientType: varchar("client_type", { length: 50 }),
  sendStatus: varchar("send_status", { length: 20 }).notNull().default("queued"), // queued, sent, failed, skipped, dry_run
  errorMessage: text("error_message"),
  sentAt: timestamp("sent_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const smsCampaignRecipientsRelations = relations(smsCampaignRecipients, ({ one }) => ({
  campaign: one(smsCampaigns, {
    fields: [smsCampaignRecipients.campaignId],
    references: [smsCampaigns.id],
  }),
}));

export const insertSmsCampaignRecipientSchema = createInsertSchema(smsCampaignRecipients).omit({
  id: true,
  sentAt: true,
  createdAt: true,
});

export type InsertSmsCampaignRecipient = z.infer<typeof insertSmsCampaignRecipientSchema>;
export type SmsCampaignRecipient = typeof smsCampaignRecipients.$inferSelect;

// SMS Message Logs - all inbound/outbound messages
export const smsMessageLogs = pgTable("sms_message_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id"),
  campaignId: varchar("campaign_id"),
  recipientId: varchar("recipient_id"),
  provider: varchar("provider", { length: 50 }).notNull().default("twilio"),
  providerMessageId: varchar("provider_message_id", { length: 255 }),
  direction: varchar("direction", { length: 20 }).notNull(), // outbound, inbound
  phoneE164: varchar("phone_e164", { length: 20 }).notNull(),
  body: text("body").notNull(),
  status: varchar("status", { length: 50 }).notNull(),
  rawProviderPayload: text("raw_provider_payload"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const smsMessageLogsRelations = relations(smsMessageLogs, ({ one }) => ({
  campaign: one(smsCampaigns, {
    fields: [smsMessageLogs.campaignId],
    references: [smsCampaigns.id],
  }),
  recipient: one(smsCampaignRecipients, {
    fields: [smsMessageLogs.recipientId],
    references: [smsCampaignRecipients.id],
  }),
}));

export const insertSmsMessageLogSchema = createInsertSchema(smsMessageLogs).omit({
  id: true,
  createdAt: true,
});

export type InsertSmsMessageLog = z.infer<typeof insertSmsMessageLogSchema>;
export type SmsMessageLog = typeof smsMessageLogs.$inferSelect;

// SMS Suppressions - phones that should not receive SMS
export const smsSuppressions = pgTable("sms_suppressions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  phoneE164: varchar("phone_e164", { length: 20 }).notNull(),
  reason: varchar("reason", { length: 20 }).notNull(), // STOP, manual, invalid
  source: varchar("source", { length: 20 }).notNull(), // inbound, admin, system
  createdByUserId: varchar("created_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSmsSuppressionSchema = createInsertSchema(smsSuppressions).omit({
  id: true,
  createdAt: true,
});

export type InsertSmsSuppression = z.infer<typeof insertSmsSuppressionSchema>;
export type SmsSuppression = typeof smsSuppressions.$inferSelect;

// SMS Provider Configurations - stores provider credentials (encrypted)
export const smsProviderConfigs = pgTable("sms_provider_configs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  providerType: varchar("provider_type", { length: 50 }).notNull().default("twilio"), // twilio, vonage, etc.
  name: varchar("name", { length: 100 }).notNull(), // Display name for the provider config
  connectedByUserId: varchar("connected_by_user_id").notNull(), // User who connected this provider
  accountSid: text("account_sid"), // Encrypted
  authToken: text("auth_token"), // Encrypted
  fromNumber: varchar("from_number", { length: 20 }),
  isActive: boolean("is_active").notNull().default(true),
  isPrimary: boolean("is_primary").notNull().default(false), // The primary provider used for campaigns
  // Browser dialer credentials (Task #57) — stored on the primary SMS config row.
  // apiKeySecret is encrypted; apiKeySid and voiceAppSid are non-secret identifiers.
  apiKeySid: text("api_key_sid"),       // Twilio API Key SID (non-secret)
  apiKeySecret: text("api_key_secret"), // Twilio API Key Secret (encrypted)
  voiceAppSid: varchar("voice_app_sid", { length: 64 }), // TwiML App SID (non-secret)
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  // Hot path: every inbound Twilio webhook (SMS + voice) needs to map a
  // dialed/sender number → workspace. Without this index, the lookup is
  // O(N workspaces × providers) on every webhook. Partial on isActive
  // because retired configs should never claim ownership.
  fromNumberActiveIdx: index("sms_provider_configs_from_number_active_idx")
    .on(t.fromNumber)
    .where(sql`is_active = true`),
}));

export const insertSmsProviderConfigSchema = createInsertSchema(smsProviderConfigs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSmsProviderConfig = z.infer<typeof insertSmsProviderConfigSchema>;
export type SmsProviderConfig = typeof smsProviderConfigs.$inferSelect;

// ── Platform-provisioned Twilio phone config ──────────────────────────────────
// One row per workspace. Created by platform admins via the Platform →
// Workspaces → "Provision Phone" action. Stores the Twilio subaccount SID,
// encrypted auth token, purchased phone number, and the TwiML App + API Key
// credentials for the browser dialer. Takes precedence over the legacy BYOA
// sms_provider_configs row when status='active'.
export const workspacePhoneConfig = pgTable("workspace_phone_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().unique().references(() => workspaces.id, { onDelete: "cascade" }),
  subaccountSid: varchar("subaccount_sid", { length: 64 }),
  authToken: text("auth_token"),          // encrypted at rest (Clarik DEK)
  phoneNumberSid: varchar("phone_number_sid", { length: 64 }),
  phoneNumber: varchar("phone_number", { length: 20 }), // E.164, e.g. +13125551234
  voiceAppSid: varchar("voice_app_sid", { length: 64 }), // TwiML App SID for browser dialer
  apiKeySid: varchar("api_key_sid", { length: 64 }),    // non-secret, for Access Tokens
  apiKeySecret: text("api_key_secret"),   // encrypted at rest
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending|active|suspended|deprovisioned
  areaCode: varchar("area_code", { length: 10 }),
  provisionedByUserId: varchar("provisioned_by_user_id"),
  provisionedAt: timestamp("provisioned_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceUniqueIdx: uniqueIndex("workspace_phone_config_workspace_idx").on(t.workspaceId),
  statusIdx: index("workspace_phone_config_status_idx").on(t.status),
}));

export const insertWorkspacePhoneConfigSchema = createInsertSchema(workspacePhoneConfig).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertWorkspacePhoneConfig = z.infer<typeof insertWorkspacePhoneConfigSchema>;
export type WorkspacePhoneConfig = typeof workspacePhoneConfig.$inferSelect;

// ─── Workspace IVR Config ────────────────────────────────────────────────────
// Per-workspace inbound call routing configuration.
// routingMode:
//   "ring_all"       — ring every rep's browser client (+ bridge phone if set) simultaneously
//   "round_robin"    — ring reps in rotation (stateless: pick rep by (call_count % rep_count))
//   "auto_attendant" — play greeting → ring workspace owner / round-robin reps
//   "menu"           — play greeting, gather keypress, route to a specific rep or voicemail
// greetingText: TTS text played before routing (all modes). Null = skip greeting.
// menuItems: JSON array of { key: "1", label: "Sales", action: "ring_user"|"voicemail", userId?: string }
//            Only used when routingMode = "menu".
// missedCallAction: what to do after timeout / no answer:
//   "voicemail" — record a message (default)
//   "hangup"    — disconnect silently
// voicemailGreetingText: TTS text played before the voicemail beep.
export const workspaceIvrConfig = pgTable("workspace_ivr_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().unique().references(() => workspaces.id, { onDelete: "cascade" }),
  routingMode: varchar("routing_mode", { length: 20 }).notNull().default("ring_all"),
  greetingText: text("greeting_text"),
  menuItems: jsonb("menu_items"),            // WorkspaceIvrMenuItem[]
  ringTimeoutSeconds: integer("ring_timeout_seconds").notNull().default(30),
  missedCallAction: varchar("missed_call_action", { length: 20 }).notNull().default("voicemail"),
  voicemailGreetingText: text("voicemail_greeting_text"),
  voicemailGreetingAudioPath: text("voicemail_greeting_audio_path"),
  // fallbackForwardNumber: workspace-level "if nobody picks up" PSTN number.
  // Dialed as its own <Dial> leg after the ring group finishes unanswered and
  // before the missed-call / voicemail block. NULL = no fallback leg (legacy
  // behaviour: ring group then voicemail).
  fallbackForwardNumber: varchar("fallback_forward_number", { length: 32 }),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const insertWorkspaceIvrConfigSchema = createInsertSchema(workspaceIvrConfig).omit({ id: true, updatedAt: true });
export type InsertWorkspaceIvrConfig = z.infer<typeof insertWorkspaceIvrConfigSchema>;
export type WorkspaceIvrConfig = typeof workspaceIvrConfig.$inferSelect;

// A single "Press N for …" option (Task #613 — real departments).
//
// Four actions, all valid:
//   "ring_user"        — ring exactly one rep (legacy shape, unchanged)
//   "ring_group"       — ring several reps simultaneously (userIds[])
//   "external_forward" — forward straight to a PSTN number (forwardNumber)
//   "voicemail"        — go to voicemail
//
// Every field beyond `action` is optional, so menu items stored before this
// task ({key,label,action:"ring_user",userId}) parse unchanged.
export type WorkspaceIvrMenuAction =
  | "ring_user"
  | "ring_group"
  | "external_forward"
  | "voicemail";

export interface WorkspaceIvrMenuItem {
  key: string;          // "1"–"9"
  label: string;        // "Sales"
  action: WorkspaceIvrMenuAction;
  userId?: string;      // required when action = "ring_user"
  userIds?: string[];   // required when action = "ring_group"
  forwardNumber?: string; // required when action = "external_forward" (E.164)
}

// E.164-ish validator shared by the IVR forwarding fields. Mirrors the rule
// already used for bridge phones / the workspace fallback number: optional
// leading +, 7–19 digits, no leading zero, punctuation stripped first.
export const IVR_E164_RE = /^\+?[1-9]\d{6,18}$/;
export function isIvrE164(value: string): boolean {
  return IVR_E164_RE.test(String(value).trim().replace(/[\s().-]/g, ""));
}

export const workspaceIvrMenuItemSchema = z
  .object({
    key: z.string().regex(/^[1-9]$/, "Menu key must be a digit 1–9"),
    label: z.string().trim().min(1).max(60),
    action: z.enum(["ring_user", "ring_group", "external_forward", "voicemail"]),
    userId: z.string().trim().min(1).optional(),
    userIds: z.array(z.string().trim().min(1)).max(25).optional(),
    forwardNumber: z.string().trim().min(1).optional(),
  })
  .superRefine((item, ctx) => {
    if (item.action === "ring_user" && !item.userId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["userId"], message: `Press ${item.key}: pick a rep to ring.` });
    }
    if (item.action === "ring_group" && !(item.userIds && item.userIds.length > 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["userIds"], message: `Press ${item.key}: pick at least one rep for the group.` });
    }
    if (item.action === "external_forward") {
      if (!item.forwardNumber) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["forwardNumber"], message: `Press ${item.key}: enter a phone number to forward to.` });
      } else if (!isIvrE164(item.forwardNumber)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["forwardNumber"], message: `Press ${item.key}: forwarding number must be E.164 (e.g. +15551234567).` });
      }
    }
  });

// ─── Dialer Voicemails ────────────────────────────────────────────────────────
// One row per voicemail recording captured by the Twilio <Record> callback.
// assignedUserId is null when the workspace shared number falls through to
// voicemail (no rep routing). Transcription column reserved for future work.
export const dialerVoicemails = pgTable("dialer_voicemails", {
  id: uuid("id").primaryKey().defaultRandom(),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  assignedUserId: varchar("assigned_user_id", { length: 255 }),
  fromNumber: varchar("from_number", { length: 32 }),
  twilioCallSid: varchar("twilio_call_sid", { length: 64 }),
  twilioRecordingSid: varchar("twilio_recording_sid", { length: 64 }),
  recordingUrl: text("recording_url"),
  durationSeconds: integer("duration_seconds").notNull().default(0),
  transcription: text("transcription"),
  listenedAt: timestamp("listened_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  wsCreatedIdx: index("dialer_voicemails_ws_created_idx").on(table.workspaceId, table.createdAt),
  wsUserIdx: index("dialer_voicemails_ws_user_idx").on(table.workspaceId, table.assignedUserId),
}));
export const insertDialerVoicemailSchema = createInsertSchema(dialerVoicemails).omit({ id: true, createdAt: true });
export type InsertDialerVoicemail = z.infer<typeof insertDialerVoicemailSchema>;
export type DialerVoicemail = typeof dialerVoicemails.$inferSelect;

// ─── Phone Billing Config ────────────────────────────────────────────────────
// One row per permission-tier slug (e.g. "lite", "pro", "enterprise").
// All monetary values in cents. Managed entirely through the platform admin.
export const phoneBillingConfig = pgTable("phone_billing_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tier: varchar("tier", { length: 20 }).notNull().unique(), // matches permissionTiers.name
  phoneAllowed: boolean("phone_allowed").notNull().default(false),
  // When true, the dialer is bundled free into the tier's base subscription and
  // monthlyFlatCents is NOT separately charged as an add-on. When false, the
  // dialer is a paid add-on priced at monthlyFlatCents. The add-on reconciler
  // (server/lib/addon-reconcile.ts) cancels a workspace's paid dialer add-on on
  // upgrade to a tier where dialerIncluded = true.
  dialerIncluded: boolean("dialer_included").notNull().default(false),
  monthlyFlatCents: integer("monthly_flat_cents").notNull().default(0),    // e.g. 2900 = $29/mo
  includedMinutes: integer("included_minutes").notNull().default(0),
  includedSms: integer("included_sms").notNull().default(0),
  overagePerMinuteCents: integer("overage_per_minute_cents").notNull().default(0),  // e.g. 2 = $0.02/min
  overagePerSmsCents: integer("overage_per_sms_cents").notNull().default(0),        // e.g. 1 = $0.01/sms
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const insertPhoneBillingConfigSchema = createInsertSchema(phoneBillingConfig).omit({ id: true, updatedAt: true });
export type InsertPhoneBillingConfig = z.infer<typeof insertPhoneBillingConfigSchema>;
export type PhoneBillingConfig = typeof phoneBillingConfig.$inferSelect;

// ─── Cold Email (Carousel) Billing Config ────────────────────────────────────
// One row per permission-tier slug. Mirrors phoneBillingConfig but for the cold
// outreach carousel add-on. A "carousel" is a managed cold-sending domain +
// mailbox cluster. All monetary values in cents, managed entirely from the
// platform admin (platform-tiers.tsx). includedCarousels are bundled free into
// the tier; anything beyond that is a paid add-on priced per the lease/buy rate.
export const coldEmailBillingConfig = pgTable("cold_email_billing_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tier: varchar("tier", { length: 20 }).notNull().unique(), // matches permissionTiers.name
  coldEmailAllowed: boolean("cold_email_allowed").notNull().default(false),
  // Carousels bundled free into the tier's base subscription.
  includedCarousels: integer("included_carousels").notNull().default(0),
  // Per-carousel monthly price for additional (beyond-included) carousels.
  // lease = Clarik owns the domain; buy = tenant owns it (priced higher).
  carouselLeaseMonthlyCents: integer("carousel_lease_monthly_cents").notNull().default(0),
  carouselBuyMonthlyCents: integer("carousel_buy_monthly_cents").notNull().default(0),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const insertColdEmailBillingConfigSchema = createInsertSchema(coldEmailBillingConfig).omit({ id: true, updatedAt: true });
export type InsertColdEmailBillingConfig = z.infer<typeof insertColdEmailBillingConfigSchema>;
export type ColdEmailBillingConfig = typeof coldEmailBillingConfig.$inferSelect;

// ─── Workspace Add-ons ───────────────────────────────────────────────────────
// Tracks every active *paid* add-on subscription a workspace carries on top of
// its base tier. This is the source of truth the upgrade reconciler reads when
// deciding which add-ons are now bundled into a newly-upgraded tier (and must
// therefore stop billing). One row per active add-on (carousels/users/talent
// carry a quantity; dialer is a single boolean add-on so quantity stays 1).
//
//   addonType:  'dialer' | 'cold_email_carousel' | 'extra_user' | 'extra_talent'
//   variant:    'lease' | 'buy' for carousels; null otherwise
//   status:     'active' | 'cancelled'
//   unitPriceCents: snapshot of the per-unit price at purchase time (cents)
export const workspaceAddons = pgTable("workspace_addons", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  addonType: varchar("addon_type", { length: 30 }).notNull(),
  quantity: integer("quantity").notNull().default(1),
  unitPriceCents: integer("unit_price_cents").notNull().default(0),
  variant: varchar("variant", { length: 20 }),
  status: varchar("status", { length: 20 }).notNull().default("active"),
  stripeSubscriptionItemId: varchar("stripe_subscription_item_id", { length: 255 }),
  // Audit — who/what created or ended the add-on.
  createdByUserId: varchar("created_by_user_id", { length: 255 }),
  cancelledAt: timestamp("cancelled_at"),
  // 'tier_upgrade_included' | 'admin_cancelled' | 'tenant_cancelled'
  cancelledReason: varchar("cancelled_reason", { length: 50 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  wsIdx: index("workspace_addons_ws_idx").on(t.workspaceId),
  wsTypeStatusIdx: index("workspace_addons_ws_type_status_idx").on(t.workspaceId, t.addonType, t.status),
}));
export const insertWorkspaceAddonSchema = createInsertSchema(workspaceAddons).omit({
  id: true, cancelledAt: true, createdAt: true, updatedAt: true,
});
export type InsertWorkspaceAddon = z.infer<typeof insertWorkspaceAddonSchema>;
export type WorkspaceAddon = typeof workspaceAddons.$inferSelect;

export const WORKSPACE_ADDON_TYPES = ["dialer", "cold_email_carousel", "extra_user", "extra_talent"] as const;
export type WorkspaceAddonType = (typeof WORKSPACE_ADDON_TYPES)[number];

// ─── Workspace Phone Usage ───────────────────────────────────────────────────
// One row per workspace × billing month. Updated in real-time as calls/SMS flow.
// The EOM billing job reads this, charges Stripe, then sets billedAt.
export const workspacePhoneUsage = pgTable("workspace_phone_usage", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  billingMonth: varchar("billing_month", { length: 7 }).notNull(), // "YYYY-MM"
  tierAtPeriodStart: varchar("tier_at_period_start", { length: 20 }), // snapshot when month opened
  minutesUsed: integer("minutes_used").notNull().default(0),
  smsUsed: integer("sms_used").notNull().default(0),
  billedAt: timestamp("billed_at"),           // null = not yet billed
  stripeInvoiceId: varchar("stripe_invoice_id", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  wsMonthIdx: uniqueIndex("workspace_phone_usage_ws_month_idx").on(t.workspaceId, t.billingMonth),
}));
export const insertWorkspacePhoneUsageSchema = createInsertSchema(workspacePhoneUsage).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertWorkspacePhoneUsage = z.infer<typeof insertWorkspacePhoneUsageSchema>;
export type WorkspacePhoneUsage = typeof workspacePhoneUsage.$inferSelect;

// SMS User Permissions - controls who can send SMS
export type SmsPermissionType = "can_send_sms" | "can_create_campaigns" | "can_use_in_automations";

export const smsUserPermissions = pgTable("sms_user_permissions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  permissionType: varchar("permission_type", { length: 50 }).notNull(), // can_send_sms, can_create_campaigns, can_use_in_automations
  grantedByUserId: varchar("granted_by_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSmsUserPermissionSchema = createInsertSchema(smsUserPermissions).omit({
  id: true,
  createdAt: true,
});
export type InsertSmsUserPermission = z.infer<typeof insertSmsUserPermissionSchema>;
export type SmsUserPermission = typeof smsUserPermissions.$inferSelect;

// SMS Connection Invites - secure links for users to connect their credentials
export const smsConnectionInvites = pgTable("sms_connection_invites", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  token: varchar("token", { length: 64 }).notNull().unique(), // Secure random token
  invitedByUserId: varchar("invited_by_user_id").notNull(),
  targetUserId: varchar("target_user_id"), // Optional - if targeting a specific user
  targetEmail: varchar("target_email", { length: 255 }), // Optional - email to send invite to
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"), // When the invite was used
  usedByUserId: varchar("used_by_user_id"), // User who used the invite
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertSmsConnectionInviteSchema = createInsertSchema(smsConnectionInvites).omit({
  id: true,
  usedAt: true,
  usedByUserId: true,
  createdAt: true,
});
export type InsertSmsConnectionInvite = z.infer<typeof insertSmsConnectionInviteSchema>;
export type SmsConnectionInvite = typeof smsConnectionInvites.$inferSelect;

// ============================================================================
// MULTI-TENANT PERMISSION SYSTEM
// ============================================================================

// User workspace memberships - links users to workspaces with a role
export const userWorkspaceMemberships = pgTable("user_workspace_memberships", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  role: varchar("role", { length: 50 }).notNull().default("sales_rep"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  userIdx: index("user_workspace_memberships_user_idx").on(t.userId),
  workspaceIdx: index("user_workspace_memberships_ws_idx").on(t.workspaceId),
  userWorkspaceUnique: uniqueIndex("user_workspace_memberships_user_ws_unique").on(t.userId, t.workspaceId),
}));

export const userWorkspaceMembershipsRelations = relations(userWorkspaceMemberships, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [userWorkspaceMemberships.workspaceId],
    references: [workspaces.id],
  }),
}));

export const insertUserWorkspaceMembershipSchema = createInsertSchema(userWorkspaceMemberships).omit({ id: true, createdAt: true });
export type InsertUserWorkspaceMembership = z.infer<typeof insertUserWorkspaceMembershipSchema>;
export type UserWorkspaceMembership = typeof userWorkspaceMemberships.$inferSelect;

// Permission definitions - global source of truth for all permission keys
export type PermissionType = "module" | "feature";

export const permissionDefinitions = pgTable("permission_definitions", {
  key: varchar("key", { length: 100 }).primaryKey(), // e.g., "leads.feature.create"
  module: varchar("module", { length: 50 }).notNull(), // e.g., "leads"
  type: varchar("type", { length: 20 }).notNull(), // "module" or "feature"
  label: varchar("label", { length: 100 }).notNull(),
  description: text("description"),
  sortOrder: integer("sort_order").notNull().default(0),
});

export const insertPermissionDefinitionSchema = createInsertSchema(permissionDefinitions);
export type InsertPermissionDefinition = z.infer<typeof insertPermissionDefinitionSchema>;
export type PermissionDefinition = typeof permissionDefinitions.$inferSelect;

// Workspace role permissions - default permissions per role within a workspace
export const workspaceRolePermissions = pgTable("workspace_role_permissions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  role: varchar("role", { length: 50 }).notNull(),
  permissionKey: varchar("permission_key", { length: 100 }).notNull(),
  allowed: boolean("allowed").notNull().default(false),
}, (t) => ({
  // Storage layer relies on ON CONFLICT against (workspaceId, role, permissionKey).
  workspaceRolePermissionUnique: uniqueIndex("workspace_role_permissions_unique_idx")
    .on(t.workspaceId, t.role, t.permissionKey),
}));

export const workspaceRolePermissionsRelations = relations(workspaceRolePermissions, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [workspaceRolePermissions.workspaceId],
    references: [workspaces.id],
  }),
  permissionDef: one(permissionDefinitions, {
    fields: [workspaceRolePermissions.permissionKey],
    references: [permissionDefinitions.key],
  }),
}));

export const insertWorkspaceRolePermissionSchema = createInsertSchema(workspaceRolePermissions).omit({ id: true });
export type InsertWorkspaceRolePermission = z.infer<typeof insertWorkspaceRolePermissionSchema>;
export type WorkspaceRolePermission = typeof workspaceRolePermissions.$inferSelect;

// Workspace user permission overrides - per-user overrides within a workspace (3-state: inherit/allow/deny)
export const workspaceUserPermissionOverrides = pgTable("workspace_user_permission_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  permissionKey: varchar("permission_key", { length: 100 }).notNull(),
  allowed: boolean("allowed").notNull(), // true = force allow, false = force deny (null state = inherit, represented by row absence)
}, (t) => ({
  // Storage layer relies on ON CONFLICT against (workspaceId, userId, permissionKey).
  workspaceUserPermissionOverrideUnique: uniqueIndex("workspace_user_permission_overrides_unique_idx")
    .on(t.workspaceId, t.userId, t.permissionKey),
}));

export const workspaceUserPermissionOverridesRelations = relations(workspaceUserPermissionOverrides, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [workspaceUserPermissionOverrides.workspaceId],
    references: [workspaces.id],
  }),
  permissionDef: one(permissionDefinitions, {
    fields: [workspaceUserPermissionOverrides.permissionKey],
    references: [permissionDefinitions.key],
  }),
}));

export const insertWorkspaceUserPermissionOverrideSchema = createInsertSchema(workspaceUserPermissionOverrides).omit({ id: true });
export type InsertWorkspaceUserPermissionOverride = z.infer<typeof insertWorkspaceUserPermissionOverrideSchema>;
export type WorkspaceUserPermissionOverride = typeof workspaceUserPermissionOverrides.$inferSelect;

// Workspace permission defaults - "All Users" layer for workspace-wide defaults
export const workspacePermissionDefaults = pgTable("workspace_permission_defaults", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  permissionKey: varchar("permission_key", { length: 100 }).notNull(),
  allowed: boolean("allowed").notNull(),
}, (t) => ({
  // Storage layer relies on ON CONFLICT against (workspaceId, permissionKey).
  workspacePermissionDefaultUnique: uniqueIndex("workspace_permission_defaults_unique_idx")
    .on(t.workspaceId, t.permissionKey),
}));

export const workspacePermissionDefaultsRelations = relations(workspacePermissionDefaults, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [workspacePermissionDefaults.workspaceId],
    references: [workspaces.id],
  }),
  permissionDef: one(permissionDefinitions, {
    fields: [workspacePermissionDefaults.permissionKey],
    references: [permissionDefinitions.key],
  }),
}));

export const insertWorkspacePermissionDefaultSchema = createInsertSchema(workspacePermissionDefaults).omit({ id: true });
export type InsertWorkspacePermissionDefault = z.infer<typeof insertWorkspacePermissionDefaultSchema>;
export type WorkspacePermissionDefault = typeof workspacePermissionDefaults.$inferSelect;

// Platform user permission overrides - super admin layer for cross-workspace control.
//
// Uniqueness is enforced via two PARTIAL unique indexes (managed by the
// hand-written migration 0020 — drizzle-kit's `push` keeps them in sync
// with these declarations). The split is required because Postgres treats
// NULL as distinct in a normal multi-column UNIQUE — a single
// `(user_id, workspace_id, permission_key)` unique would still permit
// duplicate rows in the "applies to ALL workspaces" (workspace_id IS NULL)
// case. The storage `setPlatformUserPermissionOverride` upsert targets one
// of these two indexes via `targetWhere` to perform an atomic write.
export const platformUserPermissionOverrides = pgTable("platform_user_permission_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }), // NULL = applies to ALL workspaces
  permissionKey: varchar("permission_key", { length: 100 }).notNull(),
  allowed: boolean("allowed").notNull(),
  // Bumped by `setPlatformUserPermissionOverride` on every upsert (Task #577)
  // so a future cleanup pass can pick the most-recently-touched row
  // deterministically and the platform admin UI can show "last changed".
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  scopedUniq: uniqueIndex("platform_user_permission_overrides_scoped_uniq")
    .on(t.userId, t.workspaceId, t.permissionKey)
    .where(sql`${t.workspaceId} IS NOT NULL`),
  globalUniq: uniqueIndex("platform_user_permission_overrides_global_uniq")
    .on(t.userId, t.permissionKey)
    .where(sql`${t.workspaceId} IS NULL`),
}));

export const platformUserPermissionOverridesRelations = relations(platformUserPermissionOverrides, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [platformUserPermissionOverrides.workspaceId],
    references: [workspaces.id],
  }),
  permissionDef: one(permissionDefinitions, {
    fields: [platformUserPermissionOverrides.permissionKey],
    references: [permissionDefinitions.key],
  }),
}));

export const insertPlatformUserPermissionOverrideSchema = createInsertSchema(platformUserPermissionOverrides).omit({ id: true, updatedAt: true });
export type InsertPlatformUserPermissionOverride = z.infer<typeof insertPlatformUserPermissionOverrideSchema>;
export type PlatformUserPermissionOverride = typeof platformUserPermissionOverrides.$inferSelect;

// Permission audit log - tracks permission changes (optional but useful for compliance).
//
// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `created_at`. No auto-pruner — kept indefinitely for
// compliance. See `docs/security/audit-log-policy.md`.
export const permissionAuditLog = pgTable("permission_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  changedByUserId: varchar("changed_by_user_id").notNull(),
  targetUserId: varchar("target_user_id"), // NULL for role-level changes
  targetRole: varchar("target_role", { length: 50 }), // NULL for user-level changes
  permissionKey: varchar("permission_key", { length: 100 }).notNull(),
  previousValue: boolean("previous_value"), // NULL if new
  newValue: boolean("new_value"), // NULL if deleted
  changeType: varchar("change_type", { length: 20 }).notNull(), // "role_permission", "user_override", "platform_override"
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPermissionAuditLogSchema = createInsertSchema(permissionAuditLog).omit({ id: true, createdAt: true });
export type InsertPermissionAuditLog = z.infer<typeof insertPermissionAuditLogSchema>;
export type PermissionAuditLog = typeof permissionAuditLog.$inferSelect;

// ============================================================================
// FORMS & CONTRACTS MODULE
// ============================================================================

// Template types
export type DocumentTemplateType = "form" | "contract";
export type DeliveryMethod = "internal" | "docusign";
export type TemplateStatus = "draft" | "published" | "archived";
export type RecipientType = "signer" | "cc" | "approver" | "witness";
export type FieldType = "signature" | "initial" | "date_signed" | "text" | "email" | "phone" | "number" | "date" | "checkbox" | "dropdown" | "radio";
export type DataSource = "client_field" | "manual" | "static";
export type PrefillBehavior = "prefill_editable" | "prefill_locked" | "blank_required";
export type DocumentInstanceStatus = "draft" | "sent" | "completed" | "declined" | "voided";

// Document Templates - reusable form/contract templates
export const documentTemplates = pgTable("document_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  templateType: varchar("template_type", { length: 20 }).notNull().$type<DocumentTemplateType>(),
  deliveryMethod: varchar("delivery_method", { length: 20 }).notNull().default("internal").$type<DeliveryMethod>(),
  status: varchar("status", { length: 20 }).notNull().default("draft").$type<TemplateStatus>(),
  allowedRolesJson: text("allowed_roles_json"), // JSON array of allowed user roles
  fileObjectKey: text("file_object_key"),
  fileName: varchar("file_name", { length: 255 }),
  fileMime: varchar("file_mime", { length: 100 }),
  version: integer("version").notNull().default(1),
  createdByUserId: varchar("created_by_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const documentTemplatesRelations = relations(documentTemplates, ({ many }) => ({
  roles: many(templateRoles),
  fields: many(templateFields),
  instances: many(documentInstances),
}));

export const insertDocumentTemplateSchema = createInsertSchema(documentTemplates).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDocumentTemplate = z.infer<typeof insertDocumentTemplateSchema>;
export type DocumentTemplate = typeof documentTemplates.$inferSelect;

// Template Roles - define who participates in a document
export const templateRoles = pgTable("template_roles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  templateId: varchar("template_id").notNull().references(() => documentTemplates.id, { onDelete: "cascade" }),
  roleName: varchar("role_name", { length: 100 }).notNull(), // Client, BillingContact, VenueRep, etc.
  recipientType: varchar("recipient_type", { length: 20 }).notNull().default("signer").$type<RecipientType>(),
  signingOrder: integer("signing_order"),
  isRequired: boolean("is_required").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
});

export const templateRolesRelations = relations(templateRoles, ({ one }) => ({
  template: one(documentTemplates, {
    fields: [templateRoles.templateId],
    references: [documentTemplates.id],
  }),
}));

export const insertTemplateRoleSchema = createInsertSchema(templateRoles).omit({ id: true, createdAt: true });
export type InsertTemplateRole = z.infer<typeof insertTemplateRoleSchema>;
export type TemplateRole = typeof templateRoles.$inferSelect;

// Template Fields - define data fields for a template (list-based, no PDF coordinates)
export const templateFields = pgTable("template_fields", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  templateId: varchar("template_id").notNull().references(() => documentTemplates.id, { onDelete: "cascade" }),
  fieldKey: varchar("field_key", { length: 100 }).notNull(), // Unique per template
  label: varchar("label", { length: 255 }).notNull(),
  fieldType: varchar("field_type", { length: 20 }).notNull().$type<FieldType>(),
  assignedRoleName: varchar("assigned_role_name", { length: 100 }), // Which template role fills this
  isRequired: boolean("is_required").notNull().default(true),
  dataSource: varchar("data_source", { length: 20 }).notNull().default("manual").$type<DataSource>(),
  clientFieldKey: varchar("client_field_key", { length: 100 }), // For data_source = client_field
  staticValue: text("static_value"), // For data_source = static
  prefillBehavior: varchar("prefill_behavior", { length: 30 }).default("prefill_editable").$type<PrefillBehavior>(),
  optionsJson: text("options_json"), // For dropdown/radio
  // Visual placement (DocuSign-style overlay editor). Nullable for legacy/list-only fields.
  pageNumber: integer("page_number"),
  xPct: decimal("x_pct"),       // 0..1 from left of page
  yPct: decimal("y_pct"),       // 0..1 from top of page
  widthPct: decimal("width_pct"),
  heightPct: decimal("height_pct"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const templateFieldsRelations = relations(templateFields, ({ one }) => ({
  template: one(documentTemplates, {
    fields: [templateFields.templateId],
    references: [documentTemplates.id],
  }),
}));

export const insertTemplateFieldSchema = createInsertSchema(templateFields).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTemplateField = z.infer<typeof insertTemplateFieldSchema>;
export type TemplateField = typeof templateFields.$inferSelect;

// Document Instances - sent/completed documents (queue spine)
export const documentInstances = pgTable("document_instances", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  templateId: varchar("template_id").references(() => documentTemplates.id),
  clientId: varchar("client_id").references(() => clients.id),
  displayName: varchar("display_name", { length: 255 }),
  createdByUserId: varchar("created_by_user_id").notNull(),
  deliveryMethod: varchar("delivery_method", { length: 20 }).notNull().default("internal").$type<DeliveryMethod>(),
  status: varchar("status", { length: 20 }).notNull().default("draft").$type<DocumentInstanceStatus>(),
  sentAt: timestamp("sent_at"),
  lastUpdatedAt: timestamp("last_updated_at").defaultNow(),
  internalDocumentObjectKey: text("internal_document_object_key"),
  internalDocumentFileName: varchar("internal_document_file_name", { length: 255 }),
  docusignEnvelopeId: varchar("docusign_envelope_id", { length: 100 }),
  recipientsJson: text("recipients_json"), // JSON array of resolved recipients
  fieldValuesJson: text("field_values_json"), // JSON object of field values
  templateSnapshotJson: text("template_snapshot_json"), // Snapshot of template at send time
  // Native e-signature fields. Used when delivery method is "esign" (native);
  // existing upload/DocuSign paths leave these null.
  signingToken: varchar("signing_token", { length: 64 }).unique(),
  signingTokenExpiresAt: timestamp("signing_token_expires_at"),
  signatureMethod: varchar("signature_method", { length: 20 }), // "drawn" | "typed"
  signedSignatureDataUrl: text("signed_signature_data_url"), // PNG data URL
  signedTypedName: varchar("signed_typed_name", { length: 255 }),
  signedByName: varchar("signed_by_name", { length: 255 }),
  signedByEmail: varchar("signed_by_email", { length: 255 }),
  signedAtIp: varchar("signed_at_ip", { length: 64 }),
  signedAtUserAgent: text("signed_at_user_agent"),
  signedAuditHash: varchar("signed_audit_hash", { length: 64 }), // SHA-256 hex
  signedCompletedAt: timestamp("signed_completed_at"),
  // Task #243 — durable archive of the executed/signed document. On
  // signature completion we render a Certificate-of-Completion artifact and
  // store it in per-workspace object storage with private ACL, then link the
  // object key + display filename here so the executed copy survives
  // independently of the live signing flow.
  signedDocumentObjectKey: text("signed_document_object_key"),
  signedDocumentFileName: varchar("signed_document_file_name", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow(),
});

// Task #243 — append-only change log for document instances. Records every
// tracked text/number field edit (field, old → new value, who, when),
// workspace-scoped, so the contract/document settings UI can surface a
// most-recent-first audit trail of what changed on a contract over time.
export const documentInstanceChanges = pgTable("document_instance_changes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  documentInstanceId: varchar("document_instance_id").notNull().references(() => documentInstances.id, { onDelete: "cascade" }),
  fieldKey: varchar("field_key", { length: 120 }).notNull(),
  fieldLabel: varchar("field_label", { length: 255 }),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  changedByUserId: varchar("changed_by_user_id"),
  changedByName: varchar("changed_by_name", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  instanceCreatedIdx: index("document_instance_changes_instance_created_idx").on(t.documentInstanceId, t.createdAt),
}));

export const documentInstanceChangesRelations = relations(documentInstanceChanges, ({ one }) => ({
  instance: one(documentInstances, {
    fields: [documentInstanceChanges.documentInstanceId],
    references: [documentInstances.id],
  }),
}));

export const insertDocumentInstanceChangeSchema = createInsertSchema(documentInstanceChanges).omit({ id: true, createdAt: true });
export type InsertDocumentInstanceChange = z.infer<typeof insertDocumentInstanceChangeSchema>;
export type DocumentInstanceChange = typeof documentInstanceChanges.$inferSelect;

export const documentInstancesRelations = relations(documentInstances, ({ one }) => ({
  template: one(documentTemplates, {
    fields: [documentInstances.templateId],
    references: [documentTemplates.id],
  }),
  client: one(clients, {
    fields: [documentInstances.clientId],
    references: [clients.id],
  }),
}));

export const insertDocumentInstanceSchema = createInsertSchema(documentInstances).omit({ id: true, createdAt: true, lastUpdatedAt: true });
export type InsertDocumentInstance = z.infer<typeof insertDocumentInstanceSchema>;
export type DocumentInstance = typeof documentInstances.$inferSelect;

// Client portal sessions: token-gated public portal for clients (no Replit Auth).
// Each client may have one or more active sessions; we keep history but only the
// most-recent unexpired one is meaningful for "send portal link" UX.
export const clientPortalSessions = pgTable(
  "client_portal_sessions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
    clientId: varchar("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
    accessToken: varchar("access_token", { length: 64 }).notNull().unique(),
    createdByUserId: varchar("created_by_user_id"),
    createdAt: timestamp("created_at").defaultNow(),
    lastUsedAt: timestamp("last_used_at"),
    expiresAt: timestamp("expires_at"),
    revokedAt: timestamp("revoked_at"),
  },
  (table) => ({
    workspaceClientIdx: index("idx_portal_sessions_workspace_client").on(table.workspaceId, table.clientId),
  }),
);

export const insertClientPortalSessionSchema = createInsertSchema(clientPortalSessions).omit({ id: true, createdAt: true });
export type InsertClientPortalSession = z.infer<typeof insertClientPortalSessionSchema>;
export type ClientPortalSession = typeof clientPortalSessions.$inferSelect;

// Client portal messages: lightweight thread between client (via portal token)
// and the assigned rep. Routed into existing email + activity timeline systems.
export const clientPortalMessages = pgTable(
  "client_portal_messages",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
    clientId: varchar("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
    sessionId: varchar("session_id").references(() => clientPortalSessions.id, { onDelete: "set null" }),
    senderType: varchar("sender_type", { length: 20 }).notNull(), // "client" | "rep"
    senderName: varchar("sender_name", { length: 255 }),
    senderEmail: varchar("sender_email", { length: 255 }),
    senderUserId: varchar("sender_user_id"),
    body: text("body").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    workspaceClientIdx: index("idx_portal_messages_workspace_client").on(table.workspaceId, table.clientId, table.createdAt),
  }),
);

export const insertClientPortalMessageSchema = createInsertSchema(clientPortalMessages).omit({ id: true, createdAt: true });
export type InsertClientPortalMessage = z.infer<typeof insertClientPortalMessageSchema>;
export type ClientPortalMessage = typeof clientPortalMessages.$inferSelect;

// DocuSign Connection - workspace-level OAuth connection (optional)
export const docusignConnections = pgTable("docusign_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().unique().references(() => workspaces.id, { onDelete: "cascade" }),
  isEnabled: boolean("is_enabled").notNull().default(false),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  accountId: varchar("account_id", { length: 100 }),
  baseUri: text("base_uri"),
  tokenExpiresAt: timestamp("token_expires_at"),
  // Task #57 — Per-workspace DocuSign Connect HMAC key for inbound webhook
  // signature verification. Encrypted at rest. NULL means not configured;
  // the webhook falls back to the platform DOCUSIGN_CONNECT_HMAC_KEY env var.
  connectHmacKey: text("connect_hmac_key"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertDocusignConnectionSchema = createInsertSchema(docusignConnections).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDocusignConnection = z.infer<typeof insertDocusignConnectionSchema>;
export type DocusignConnection = typeof docusignConnections.$inferSelect;

// Task #57 — Per-workspace ElevenLabs configuration.
// One row per workspace. Stores the workspace's own ElevenLabs API key
// encrypted at rest. Independent of workspace_voice_profiles so admins
// can configure the key before creating a voice profile.
export const workspaceElevenLabsConfigs = pgTable("workspace_elevenlabs_configs", {
  workspaceId: varchar("workspace_id").primaryKey().references(() => workspaces.id, { onDelete: "cascade" }),
  elevenLabsApiKey: text("elevenlabs_api_key"), // Encrypted
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertWorkspaceElevenLabsConfigSchema = createInsertSchema(workspaceElevenLabsConfigs).omit({ createdAt: true, updatedAt: true });
export type InsertWorkspaceElevenLabsConfig = z.infer<typeof insertWorkspaceElevenLabsConfigSchema>;
export type WorkspaceElevenLabsConfig = typeof workspaceElevenLabsConfigs.$inferSelect;

// Eventbrite Connection — workspace-level OAuth (Task #694).
// Mirrors the docusign_connections shape: one row per workspace,
// holds the OAuth access/refresh tokens plus the connected EB
// account id/name for display in the integrations dialog. There is
// NO platform-wide Eventbrite key — every workspace BYOA.
export const eventbriteConnections = pgTable("eventbrite_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().unique().references(() => workspaces.id, { onDelete: "cascade" }),
  isEnabled: boolean("is_enabled").notNull().default(false),
  // Workspace-supplied EB OAuth app credentials — there is NO platform-
  // wide Eventbrite key. The workspace admin registers their own EB API
  // app at https://www.eventbrite.com/account-settings/apps and pastes
  // the client_id / client_secret here BEFORE they trigger /oauth/start.
  // The OAuth start route refuses to mint an authorize URL when these
  // are missing, and the callback / token-refresh paths read them from
  // this row (never from process.env).
  clientId: text("client_id"),
  clientSecret: text("client_secret"),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  // Eventbrite OAuth tokens are long-lived (no `expires_in` returned);
  // we still record `tokenExpiresAt` defensively in case EB starts
  // returning one. Token-refresh path is wired in eventbrite/client.ts.
  tokenExpiresAt: timestamp("token_expires_at"),
  // Eventbrite User/Organization ids needed by /events list calls.
  accountId: varchar("account_id", { length: 100 }),
  organizationId: varchar("organization_id", { length: 100 }),
  accountName: varchar("account_name", { length: 255 }),
  email: varchar("email", { length: 255 }),
  connectedBy: varchar("connected_by"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertEventbriteConnectionSchema = createInsertSchema(eventbriteConnections).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertEventbriteConnection = z.infer<typeof insertEventbriteConnectionSchema>;
export type EventbriteConnection = typeof eventbriteConnections.$inferSelect;

// ============================================================================
// CALENDAR HUB MODULE TABLES
// ============================================================================

// Calendar Task Types
export type CalendarTaskType = "calls" | "emails" | "booking_focus" | "campaign" | "generic";
export type CalendarTaskStatus = "pending" | "in_progress" | "completed" | "cancelled";

// Calendar Tasks - daily assignments/tasks for sales reps
export const calendarTasks = pgTable("calendar_tasks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  date: timestamp("date").notNull(),
  assignedToUserId: varchar("assigned_to_user_id").notNull(),
  type: varchar("type", { length: 50 }).notNull().default("generic").$type<CalendarTaskType>(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  targetNumber: integer("target_number"), // For calls/emails: target count
  completedNumber: integer("completed_number").default(0),
  status: varchar("status", { length: 20 }).notNull().default("pending").$type<CalendarTaskStatus>(),
  regionId: varchar("region_id"), // Optional focus region
  clientTypeId: varchar("client_type_id"), // Optional focus client type
  talentId: varchar("talent_id"), // Optional focus talent
  notes: text("notes"),
  createdByUserId: varchar("created_by_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const calendarTasksRelations = relations(calendarTasks, ({ one }) => ({
  region: one(regions, {
    fields: [calendarTasks.regionId],
    references: [regions.id],
  }),
  talent: one(talent, {
    fields: [calendarTasks.talentId],
    references: [talent.id],
  }),
}));

export const insertCalendarTaskSchema = createInsertSchema(calendarTasks).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertCalendarTask = z.infer<typeof insertCalendarTaskSchema>;
export type CalendarTask = typeof calendarTasks.$inferSelect;

// ============================================================================
// Calendar Hub — Slice 1: per-user time blocks with three-layer model
// ============================================================================
//
// A "block" is anything that consumes calendar time for a single user — a
// doctor's appointment, a focus block, a drive leg, a decompression buffer.
// The three layers drive the future cascade engine and the chatbot's idea
// of how aggressively to reshuffle:
//   * fixed   — never moved by the bot (shows + user-declared appointments)
//   * fluid   — bot may reshuffle (focus blocks, errands, admin)
//   * life    — protected by default (sleep, meals, decompression)
//
// `visibility` lets the owner decide what teammates see on a shared
// calendar. Shows + drive time conventionally stay 'org' so the team
// always knows where the artist is; personal blocks default to whatever
// the owner picked in user_profiles.default_block_visibility.

export type CalendarBlockLayer = "fixed" | "fluid" | "life";
export type CalendarBlockKind =
  | "show"
  | "drive"
  | "decompression"
  | "user_fixed"
  | "focus"
  | "errand"
  | "meal"
  | "sleep"
  | "buffer"
  | "coach_check_in"
  | "other";
export type CalendarBlockVisibility = "org" | "role" | "private";
export type CalendarSharingMode = "hidden" | "availability_only" | "shared";
export type RecurringBlockFrequency = "daily" | "weekly" | "biweekly" | "monthly";
export type CoachStake = "promise" | "try" | "wish";
// Lifecycle of an aspiration-scheduled coach block, driving the
// green/yellow progress bar. null === "planned" (upcoming, not yet due).
export type CoachBlockCompletion = "planned" | "assumed" | "confirmed" | "missed";

export const personalCalendarBlocks = pgTable("personal_calendar_blocks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // whose calendar this block belongs to
  startAt: timestamp("start_at").notNull(),
  endAt: timestamp("end_at").notNull(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  layer: varchar("layer", { length: 16 }).notNull().default("fluid").$type<CalendarBlockLayer>(),
  kind: varchar("kind", { length: 32 }).notNull().default("other").$type<CalendarBlockKind>(),
  visibility: varchar("visibility", { length: 16 }).notNull().default("private").$type<CalendarBlockVisibility>(),
  locationAddress: text("location_address"),
  // Linkage back to the system event that produced this block (when bot-generated).
  // E.g. a drive block has sourceType='schedule_entry' sourceId=<entry id>.
  sourceType: varchar("source_type", { length: 32 }),
  sourceId: varchar("source_id"),
  // For auto-generated cascade rows: snapshot of the source row's updated_at
  // at the moment we derived this block. Used by the cascade UPSERT to
  // reject stale writes (older snapshot vs newer existing row), giving
  // monotonic convergence under concurrent schedule moves.
  sourceUpdatedAt: timestamp("source_updated_at"),
  isAutoGenerated: boolean("is_auto_generated").notNull().default(false),
  // ── Coach Hub v2 — calendar write loop ───────────────────────────
  // is_coach_created marks blocks the AI coach proposed and the user
  // accepted. coach_goal_id (soft pointer, no FK) links the block to
  // the goal it serves. coach_stake is the firmness with which the
  // coach defends the block when something hard collides; values
  // mirror coach_goals.stake. coach_reschedulable=false opts a coach
  // block out of auto-bumping. coach_original_start_at remembers the
  // first placement so the coach can explain a move.
  coachGoalId: varchar("coach_goal_id"),
  coachStake: varchar("coach_stake", { length: 16 }).$type<CoachStake>(),
  isCoachCreated: boolean("is_coach_created").notNull().default(false),
  coachReschedulable: boolean("coach_reschedulable").notNull().default(true),
  coachOriginalStartAt: timestamp("coach_original_start_at"),
  // ── Task #411 — dedicated "Clarik Coach" Google Calendar mirror ───
  // One-way push only (Clarik is the source of truth). When a coach
  // block is mirrored into the user's dedicated "Clarik Coach"
  // secondary calendar, the Google event id is stored here so later
  // moves/deletes can patch/delete the same event. null = not (yet)
  // mirrored (e.g. no connected calendar, coach off, or a transient
  // Google failure that will be retried on the next mutation).
  coachGoogleEventId: varchar("coach_google_event_id"),
  // ── Aspiration auto-scheduling ────────────────────────────────────
  // aspiration_id soft-links a coach block to the aspiration it serves
  // (no FK — aspirations can be deleted without orphaning calendar
  // history). coach_completion tracks the block's lifecycle for the
  // green/yellow progress bar: null/'planned' = upcoming, 'assumed' =
  // the time passed so we *infer* the work happened (yellow, counts
  // toward progress provisionally), 'confirmed' = the user told the
  // coach it really happened (green), 'missed' = the user said it
  // didn't (excluded from progress).
  aspirationId: varchar("aspiration_id"),
  coachCompletion: varchar("coach_completion", { length: 16 }).$type<CoachBlockCompletion>(),
  // ── Recurring blocks ──────────────────────────────────────────────────
  // Simple client-side expansion: the block represents a recurring series.
  // The client renders virtual instances for each occurrence within the
  // viewed window. No server-side expansion is performed.
  isRecurring: boolean("is_recurring").notNull().default(false),
  recurringFrequency: varchar("recurring_frequency", { length: 16 }).$type<RecurringBlockFrequency>(),
  recurringUntil: date("recurring_until"),
  // For a weekly/biweekly "on selected days" pattern: which weekdays the
  // block repeats on, as 0–6 day indices (Sunday=0 … Saturday=6, matching
  // JS Date.getDay()). NULL/empty = repeat on the block's own start weekday
  // (i.e. plain weekly). Ignored for daily/monthly frequencies.
  recurringDaysOfWeek: integer("recurring_days_of_week").array(),
  createdByUserId: varchar("created_by_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPersonalCalendarBlockSchema = createInsertSchema(personalCalendarBlocks, {
  // The DB CHECK constraint also enforces these, but failing fast in Zod
  // gives a clean 400 instead of a 500 with a Postgres constraint violation.
  layer: z.enum(["fixed", "fluid", "life"]),
  kind: z.enum([
    "show",
    "drive",
    "decompression",
    "user_fixed",
    "focus",
    "errand",
    "meal",
    "sleep",
    "buffer",
    "coach_check_in",
    "other",
  ]),
  visibility: z.enum(["org", "role", "private"]),
  coachStake: z.enum(["promise", "try", "wish"]).optional(),
  recurringFrequency: z.enum(["daily", "weekly", "biweekly", "monthly"]).optional(),
  recurringDaysOfWeek: z.array(z.number().int().min(0).max(6)).nullish(),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPersonalCalendarBlock = z.infer<typeof insertPersonalCalendarBlockSchema>;
export type PersonalCalendarBlock = typeof personalCalendarBlocks.$inferSelect;

// ── Calendar Hub Slice 3 — lodging stays ─────────────────────────────────
// Per-stay record of where the artist is sleeping while on tour. Used by
// the drive-time cascade as a higher-priority origin than "previous
// schedule entry's city" so we model the realistic case where the artist
// drove to a hotel after the show, slept there, and is leaving from the
// hotel — not from the previous venue. Per-stay (not per-night) so it
// matches both how hotels work and how Gmail confirmations parse 1:1.
export type LodgingSource = "manual" | "gmail" | "ical" | "apple-mail";

export const lodgingStays = pgTable("lodging_stays", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  // DATE columns (no time-of-day) — hotels work in nights, not minutes.
  // Drizzle returns date columns as ISO strings (YYYY-MM-DD), which is
  // what the cascade lookup wants anyway.
  checkInDate: date("check_in_date").notNull(),
  checkOutDate: date("check_out_date").notNull(),
  address: text("address").notNull(),
  city: varchar("city", { length: 120 }),
  state: varchar("state", { length: 8 }),
  name: varchar("name", { length: 200 }),
  notes: text("notes"),
  source: varchar("source", { length: 16 }).notNull().default("manual").$type<LodgingSource>(),
  createdByUserId: varchar("created_by_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertLodgingStaySchema = createInsertSchema(lodgingStays, {
  // checkOutDate > checkInDate is enforced by the DB CHECK constraint, but
  // failing fast in Zod gives a clean 400 instead of a 500.
  checkInDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "checkInDate must be YYYY-MM-DD"),
  checkOutDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "checkOutDate must be YYYY-MM-DD"),
  source: z.enum(["manual", "gmail", "ical", "apple-mail"]).optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertLodgingStay = z.infer<typeof insertLodgingStaySchema>;
export type LodgingStay = typeof lodgingStays.$inferSelect;

// Calendar Hub Slice 4 — lodging suggestions queue.
//
// AI-extracted hotel confirmations from inbound Gmail (Marriott / Hilton
// / Hyatt / IHG / Booking / Airbnb / Expedia / Hotels.com / etc.) live
// here in `pending` state until the user clicks Approve on the Lodging
// tab. Approve copies fields into lodging_stays with source='gmail' and
// triggers the cascade re-emit. We do NOT auto-write because an AI
// false-positive would silently break drive-time calculations.
export type LodgingSuggestionStatus = "pending" | "approved" | "rejected" | "superseded";
export type LodgingSuggestionSourceType = "gmail" | "ical" | "apple-mail";

export const lodgingSuggestions = pgTable("lodging_suggestions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  sourceType: varchar("source_type", { length: 16 }).notNull().default("gmail").$type<LodgingSuggestionSourceType>(),
  // Provider-side message id. Combined with userId to dedupe re-polled mail.
  sourceMessageId: varchar("source_message_id", { length: 998 }).notNull(),
  // Loose pointer (no FK — inbound_emails is partitioned per migration 0038).
  inboundEmailId: varchar("inbound_email_id"),
  hotelName: varchar("hotel_name", { length: 200 }),
  address: text("address"),
  city: varchar("city", { length: 120 }),
  state: varchar("state", { length: 8 }),
  checkInDate: date("check_in_date").notNull(),
  checkOutDate: date("check_out_date").notNull(),
  confirmationNumber: varchar("confirmation_number", { length: 120 }),
  parsedJson: jsonb("parsed_json"),
  confidence: integer("confidence").notNull().default(0),
  status: varchar("status", { length: 16 }).notNull().default("pending").$type<LodgingSuggestionStatus>(),
  createdLodgingStayId: varchar("created_lodging_stay_id"),
  decidedAt: timestamp("decided_at"),
  decidedByUserId: varchar("decided_by_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertLodgingSuggestionSchema = createInsertSchema(lodgingSuggestions, {
  checkInDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "checkInDate must be YYYY-MM-DD"),
  checkOutDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "checkOutDate must be YYYY-MM-DD"),
  sourceType: z.enum(["gmail", "ical", "apple-mail"]).optional(),
  status: z.enum(["pending", "approved", "rejected", "superseded"]).optional(),
  confidence: z.number().int().min(0).max(100).optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertLodgingSuggestion = z.infer<typeof insertLodgingSuggestionSchema>;
export type LodgingSuggestion = typeof lodgingSuggestions.$inferSelect;

// Sales Goals - targets by period for users
export const salesGoals = pgTable("sales_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  targetCalls: integer("target_calls").default(0),
  targetEmails: integer("target_emails").default(0),
  targetBookingDollars: decimal("target_booking_dollars", { precision: 12, scale: 2 }).default("0"),
  targetBookingCount: integer("target_booking_count").default(0),
  notes: text("notes"),
  createdByUserId: varchar("created_by_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSalesGoalSchema = createInsertSchema(salesGoals).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertSalesGoal = z.infer<typeof insertSalesGoalSchema>;
export type SalesGoal = typeof salesGoals.$inferSelect;

// Daily Metrics - actual metrics tracked per user per day
export const dailyMetrics = pgTable("daily_metrics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  date: timestamp("date").notNull(),
  userId: varchar("user_id").notNull(),
  callsDone: integer("calls_done").default(0),
  emailsDone: integer("emails_done").default(0),
  dollarsBooked: decimal("dollars_booked", { precision: 12, scale: 2 }).default("0"),
  bookingCount: integer("booking_count").default(0),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceUserDateIdx: index("daily_metrics_ws_user_date_idx").on(t.workspaceId, t.userId, t.date),
  workspaceDateIdx: index("daily_metrics_ws_date_idx").on(t.workspaceId, t.date),
}));

export const insertDailyMetricSchema = createInsertSchema(dailyMetrics).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDailyMetric = z.infer<typeof insertDailyMetricSchema>;
export type DailyMetric = typeof dailyMetrics.$inferSelect;

// Sales Focus - admin-defined focus periods for targeting regions/client types/talent
export const salesFocus = pgTable("sales_focus", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date").notNull(),
  regionId: varchar("region_id"), // Focus region
  clientTypeId: varchar("client_type_id"), // Focus client type
  talentId: varchar("talent_id"), // Focus talent
  assignedToUserId: varchar("assigned_to_user_id"), // Specific user or null for all
  title: varchar("title", { length: 255 }),
  description: text("description"),
  createdByUserId: varchar("created_by_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const salesFocusRelations = relations(salesFocus, ({ one }) => ({
  region: one(regions, {
    fields: [salesFocus.regionId],
    references: [regions.id],
  }),
  talent: one(talent, {
    fields: [salesFocus.talentId],
    references: [talent.id],
  }),
}));

export const insertSalesFocusSchema = createInsertSchema(salesFocus).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertSalesFocus = z.infer<typeof insertSalesFocusSchema>;
export type SalesFocus = typeof salesFocus.$inferSelect;

// ============================================================================
// SUPER ADMIN PLATFORM LAYER
// ============================================================================

// Platform Settings - platform-level configuration and integrations
export const platformSettings = pgTable("platform_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key", { length: 255 }).notNull().unique(),
  value: text("value"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPlatformSettingsSchema = createInsertSchema(platformSettings).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPlatformSettings = z.infer<typeof insertPlatformSettingsSchema>;
export type PlatformSettings = typeof platformSettings.$inferSelect;

// Platform Enhancements — editable "parked feature" cards shown on the
// Super Admin → Platform Config → Enhancements tab. Previously the card
// content lived hardcoded in client/src/lib/enhancements-data.ts and only
// the show-in-workspaces toggle + sort order were persisted (as a JSON blob
// under platform_settings key "enhancement_settings"). This table folds the
// content AND the visibility/order into a single editable source of truth so
// platform admins can edit/add/delete cards entirely from the UI.
export const platformEnhancements = pgTable("platform_enhancements", {
  // Stable string id (seeded rows reuse their historical slug, e.g.
  // "ai-lead-catcher"; new rows get a uuid).
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description").notNull().default(""),
  reenable: text("reenable").notNull().default(""),
  // Icon is a string key resolved against a client-side Lucide registry
  // (icons are React components and can't live in the DB).
  iconKey: varchar("icon_key", { length: 64 }).notNull().default("BookOpen"),
  status: varchar("status", { length: 16 }).notNull().default("hidden"),
  previewHref: varchar("preview_href", { length: 512 }),
  note: text("note"),
  showInWorkspaces: boolean("show_in_workspaces").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPlatformEnhancementSchema = createInsertSchema(platformEnhancements).omit({ createdAt: true, updatedAt: true });
export type InsertPlatformEnhancement = z.infer<typeof insertPlatformEnhancementSchema>;
export type PlatformEnhancement = typeof platformEnhancements.$inferSelect;

// Platform Users - completely separate from workspace users
// Task #102 — Platform user role matrix.
// super_admin: full control + impersonation; can manage every platform user
// support:     read-only cross-workspace + impersonation (for L1/L2 support)
// sales:       no /platform/* access — uses the Clarik tenant workspace
// billing:     subscriptions/refunds/invoices, NO impersonation
// Task #113 — Extended matrix: admin and marketing are seat types that
// receive the Clarik workspace shell (no /platform/* access) the same
// way 'sales' and 'support' (non-super) do.
export type PlatformUserRole =
  | "super_admin"
  | "admin"
  | "manager"
  | "support"
  | "billing"
  | "sales"
  | "developer"
  | "auditor"
  | "compliance"
  // Legacy role kept so historical rows remain valid. Not offered when
  // creating new platform staff (see ALL_PLATFORM_ROLES in
  // shared/platform/role-bundles.ts).
  | "marketing";

export const platformUsers = pgTable("platform_users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email", { length: 255 }).notNull().unique(),
  passwordHash: text("password_hash").notNull(),
  name: varchar("name", { length: 255 }).notNull(),
  isActive: boolean("is_active").notNull().default(true),
  // Task #102 — Role gating for /platform/* surfaces. Existing rows
  // backfill to 'super_admin' to preserve current behavior.
  role: varchar("role", { length: 20 }).notNull().default("super_admin").$type<PlatformUserRole>(),
  // Task #249 — when true the user must set a new password before they
  // can use the platform. Set on staff creation (temp password) and
  // cleared by the in-session change-password flow.
  mustResetPassword: boolean("must_reset_password").notNull().default(false),
  lastLoginAt: timestamp("last_login_at"),
  // ── MFA — same column set as workspace `users` for consistency.
  // Wired up in Phase 3 (platform-admin login flow). Schema lives here now
  // to avoid a second migration when that work lands.
  mfaEnabled: boolean("mfa_enabled").notNull().default(false),
  mfaEnrolledAt: timestamp("mfa_enrolled_at"),
  mfaLastVerifiedAt: timestamp("mfa_last_verified_at"),
  mfaTotpSecretEncrypted: text("mfa_totp_secret_encrypted"),
  mfaPhoneE164: varchar("mfa_phone_e164", { length: 32 }),
  mfaPhoneVerifiedAt: timestamp("mfa_phone_verified_at"),
  mfaEmailVerifiedAt: timestamp("mfa_email_verified_at"),
  mfaBackupCodesHashedJson: jsonb("mfa_backup_codes_hashed_json"),
  mfaDefaultMethod: varchar("mfa_default_method", { length: 16 }),
  // Secondary backup email — a distinct address (e.g. booking@joshfarley.com)
  // usable as an independent break-glass recovery factor. Verified via an
  // emailed code before it counts toward the any-2-of-N recovery flow.
  secondaryEmail: varchar("secondary_email", { length: 255 }),
  secondaryEmailVerifiedAt: timestamp("secondary_email_verified_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPlatformUserSchema = createInsertSchema(platformUsers).omit({ id: true, createdAt: true, updatedAt: true, lastLoginAt: true });
export type InsertPlatformUser = z.infer<typeof insertPlatformUserSchema>;
export type PlatformUser = typeof platformUsers.$inferSelect;

// Platform Sessions - separate session management for platform users
export const platformSessions = pgTable("platform_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platformUserId: varchar("platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  tokenHash: text("token_hash").notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  // Impersonation tracking
  impersonatingWorkspaceId: varchar("impersonating_workspace_id"),
  impersonatingUserId: varchar("impersonating_user_id"),
  impersonationStartedAt: timestamp("impersonation_started_at"),
  // Task #583 — link the session back to the trusted device row that
  // approved this login so "Revoke device" can cascade-kill its sessions.
  // Nullable for backwards compatibility with sessions created before
  // device binding shipped (those just keep working until they expire).
  trustedDeviceId: varchar("trusted_device_id"),
});

export const platformSessionsRelations = relations(platformSessions, ({ one }) => ({
  platformUser: one(platformUsers, {
    fields: [platformSessions.platformUserId],
    references: [platformUsers.id],
  }),
  impersonatingWorkspace: one(workspaces, {
    fields: [platformSessions.impersonatingWorkspaceId],
    references: [workspaces.id],
  }),
}));

export const insertPlatformSessionSchema = createInsertSchema(platformSessions).omit({ id: true, createdAt: true });
export type InsertPlatformSession = z.infer<typeof insertPlatformSessionSchema>;
export type PlatformSession = typeof platformSessions.$inferSelect;

// Permission Tiers - platform-level permission templates for workspace tiers
export const permissionTiers = pgTable("permission_tiers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 100 }).notNull().unique(), // "lite", "pro", "business", "enterprise" - internal key
  displayName: varchar("display_name", { length: 100 }), // Editable display name shown to users
  description: text("description"),
  // Pricing fields
  monthlyPrice: integer("monthly_price"), // Price in cents
  yearlyPrice: integer("yearly_price"), // Price in cents
  // JSON object with permission visibility settings: { [permissionKey]: "allow" | "deny" | "hide" }
  // "allow" = normal access, "deny" = disabled but visible, "hide" = completely hidden from UI
  permissionsJson: text("permissions_json").notNull().default("{}"),
  // Hard limits for this tier
  maxUsers: integer("max_users"),
  maxTalentProfiles: integer("max_talent_profiles"), // Max talent profiles for workspace
  maxEmailsPerDay: integer("max_emails_per_day"),
  maxCampaigns: integer("max_campaigns"),
  maxLeads: integer("max_leads"),
  maxEmailTemplates: integer("max_email_templates"), // Max email templates for workspace
  // Price per additional user beyond maxUsers (in cents)
  pricePerAdditionalUser: integer("price_per_additional_user"),
  // Price per additional talent profile beyond maxTalentProfiles (in cents).
  // Mirrors pricePerAdditionalUser. Used by the add-on layer so admins can sell
  // extra talent records on tiers whose included maxTalentProfiles is exceeded.
  pricePerAdditionalTalent: integer("price_per_additional_talent"),
  // Revenue share percent charged to workspaces on this tier (e.g. 1.5 = 1.5%).
  // Overrides the global platform_settings `lite_revenue_share_percent` when set.
  // Per-workspace `litePlatformFeePercent` still takes highest precedence.
  revenueSharePercent: decimal("revenue_share_percent", { precision: 5, scale: 2 }),
  // Tier UI Preview & Branding (platform-level, governs how this tier looks
  // to its workspaces). All optional — null falls back to defaults.
  previewBadgeText: varchar("preview_badge_text", { length: 50 }),
  previewBadgeColor: varchar("preview_badge_color", { length: 30 }), // tailwind color key e.g. "blue", "amber", "red"
  previewAccentColor: varchar("preview_accent_color", { length: 30 }),
  previewLockedBehavior: varchar("preview_locked_behavior", { length: 20 }), // "hide" | "show_locked"
  previewUpgradeCopy: text("preview_upgrade_copy"),
  previewWelcomeMessage: text("preview_welcome_message"),
  // Free-form notes captured from the Tier Preview modal — the design queue
  // for this tier that the agent can pick up next pass.
  designNotes: text("design_notes"),
  // Monthly Lead Gen Credits granted to each workspace on this tier (use-it-or-lose-it).
  // Reset on the 1st of each month. Purchased credits are tracked separately and never reset.
  monthlyLeadCredits: integer("monthly_lead_credits").notNull().default(0),
  // Sign-on bonus Lead Gen credits awarded one-time when a workspace is created on this tier.
  // Lands in the purchased bucket (rolls over). 0 = no bonus.
  signupBonusLeadCredits: integer("signup_bonus_lead_credits").notNull().default(0),
  // Grace period (in days) after a billing failure before the workspace on this
  // tier is downgraded/suspended. 0 = no grace period (immediate). Enforcement
  // is a follow-up; this column only stores the configured value.
  gracePeriodDays: integer("grace_period_days").notNull().default(0),
  // Audience tag — segments tiers on the public Signup page tab toggle.
  // "individual" | "team_enterprise" | NULL (untagged — appears under both tabs).
  audience: varchar("audience", { length: 20 }),
  // Marketing copy that drives the public Signup page card for this tier.
  // When marketingShowOnSignup is true, the tier appears on the public Signup
  // page under the audience tab above. Admins can edit each field from the
  // Configure dialog so brand-new tiers can be exposed without code changes.
  marketingIcon: varchar("marketing_icon", { length: 50 }), // lucide-react icon key, e.g. "Zap"
  marketingFeaturesJson: text("marketing_features_json").notNull().default("[]"), // JSON array of {name, included, isHeader?, indent?}
  marketingHighlight: varchar("marketing_highlight", { length: 50 }), // e.g. "Most Popular", "Best Value"
  marketingPopular: boolean("marketing_popular").notNull().default(false), // applies the "popular" gradient styling
  marketingShowOnSignup: boolean("marketing_show_on_signup").notNull().default(false),
  // Headroom for per-tier entitlement flags that don't warrant their own column
  // (e.g. allowsWhiteLabel, aiAgentsEnabled). The platform tier editor merges
  // into this jsonb so existing keys are preserved across edits.
  extraConfig: jsonb("extra_config").default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Lead Credit Ledger — full audit trail of every credit movement for every workspace.
// type: 'grant_reset' | 'admin_award' | 'purchase' | 'spend' | 'adjustment'
// Append-only audit table; see `docs/security/audit-log-policy.md`.
export const leadCreditLedger = pgTable("lead_credit_ledger", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  type: varchar("type", { length: 30 }).notNull(),
  // Positive = credits added, negative = credits spent. Splits between buckets recorded below.
  grantDelta: integer("grant_delta").notNull().default(0),
  purchasedDelta: integer("purchased_delta").notNull().default(0),
  grantBalanceAfter: integer("grant_balance_after").notNull(),
  purchasedBalanceAfter: integer("purchased_balance_after").notNull(),
  // Context — at most one of these will be set depending on event type.
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
  actorPlatformUserId: varchar("actor_platform_user_id", { length: 255 }),
  actorUserId: varchar("actor_user_id", { length: 255 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  // Stripe-webhook replay safety. A duplicate delivery for the same
  // PaymentIntent must NOT create a second 'purchase' row (would double-
  // credit the workspace). recordPurchase() relies on this index to make
  // INSERT … ON CONFLICT DO NOTHING dedupe atomically. Partial because
  // non-purchase rows legitimately have NULL stripe_payment_intent_id.
  uniqStripePiPurchase: uniqueIndex("lead_credit_ledger_stripe_pi_purchase_uniq")
    .on(table.workspaceId, table.stripePaymentIntentId)
    .where(sql`${table.stripePaymentIntentId} IS NOT NULL AND ${table.type} = 'purchase'`),
}));

export const insertLeadCreditLedgerSchema = createInsertSchema(leadCreditLedger).omit({ id: true, createdAt: true });
export type InsertLeadCreditLedger = z.infer<typeof insertLeadCreditLedgerSchema>;
export type LeadCreditLedger = typeof leadCreditLedger.$inferSelect;

// Coupon codes for subscription discounts
export const coupons = pgTable("coupons", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: varchar("code", { length: 50 }).notNull().unique(), // Unique coupon code
  description: text("description"),
  discountType: varchar("discount_type", { length: 20 }).notNull().default("percentage"), // "percentage" or "fixed"
  discountValue: integer("discount_value").notNull(), // Percentage (0-100) or cents for fixed
  appliesTo: varchar("applies_to", { length: 20 }), // null = all tiers, or specific tier name (DEPRECATED: use appliesToTiers)
  appliesToTiers: text("applies_to_tiers").array(), // Array of tier IDs this coupon applies to, null = all tiers
  maxUses: integer("max_uses"), // null = unlimited
  usedCount: integer("used_count").notNull().default(0),
  validFrom: timestamp("valid_from"),
  validUntil: timestamp("valid_until"),
  isActive: boolean("is_active").notNull().default(true),
  // One-time bonus Lead Gen credits granted at signup when this coupon is used.
  // Lands in the purchased bucket (rolls over). 0 = no bonus credits.
  bonusLeadCredits: integer("bonus_lead_credits").notNull().default(0),
  // One-time bonus AI credits (in cents) granted at signup when this coupon is used.
  bonusAiCreditsCents: integer("bonus_ai_credits_cents").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Coupon discount rules - supports multi-phase discounts (e.g., "100% off for 3 months, then 50% off forever")
export const couponRules = pgTable("coupon_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  couponId: varchar("coupon_id").notNull().references(() => coupons.id, { onDelete: "cascade" }),
  orderIndex: integer("order_index").notNull().default(0), // Order of rule application (0 = first)
  durationMonths: integer("duration_months"), // null = forever/remaining duration
  discountType: varchar("discount_type", { length: 20 }).notNull().default("percentage"), // "percentage" or "fixed"
  discountValue: integer("discount_value").notNull(), // Percentage (0-100) or cents for fixed
  createdAt: timestamp("created_at").defaultNow(),
});

export const couponRulesRelations = relations(couponRules, ({ one }) => ({
  coupon: one(coupons, {
    fields: [couponRules.couponId],
    references: [coupons.id],
  }),
}));

export const couponsRelations = relations(coupons, ({ many }) => ({
  rules: many(couponRules),
}));

export const insertCouponSchema = createInsertSchema(coupons).omit({ id: true, usedCount: true, createdAt: true, updatedAt: true });
export type InsertCoupon = z.infer<typeof insertCouponSchema>;
export type Coupon = typeof coupons.$inferSelect;

export const insertCouponRuleSchema = createInsertSchema(couponRules).omit({ id: true, createdAt: true });
export type InsertCouponRule = z.infer<typeof insertCouponRuleSchema>;
export type CouponRule = typeof couponRules.$inferSelect;

export const insertPermissionTierSchema = createInsertSchema(permissionTiers).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPermissionTier = z.infer<typeof insertPermissionTierSchema>;
export type PermissionTier = typeof permissionTiers.$inferSelect;

// Platform Permission Overrides - platform-level overrides that always win
export const platformPermissionOverrides = pgTable("platform_permission_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  permissionKey: varchar("permission_key", { length: 100 }).notNull(),
  // Override type: "hide" = module completely hidden, "deny" = disabled but visible
  overrideType: varchar("override_type", { length: 20 }).notNull().default("deny"),
  reason: text("reason"),
  createdByPlatformUserId: varchar("created_by_platform_user_id").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const platformPermissionOverridesRelations = relations(platformPermissionOverrides, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [platformPermissionOverrides.workspaceId],
    references: [workspaces.id],
  }),
  createdBy: one(platformUsers, {
    fields: [platformPermissionOverrides.createdByPlatformUserId],
    references: [platformUsers.id],
  }),
}));

export const insertPlatformPermissionOverrideSchema = createInsertSchema(platformPermissionOverrides).omit({ id: true, createdAt: true });
export type InsertPlatformPermissionOverride = z.infer<typeof insertPlatformPermissionOverrideSchema>;
export type PlatformPermissionOverride = typeof platformPermissionOverrides.$inferSelect;

// Platform Audit Log - immutable log of all platform actions
export type PlatformAuditAction = 
  | "workspace_created"
  | "workspace_tier_changed"
  | "workspace_activated"
  | "workspace_deactivated"
  | "workspace_locked"
  | "workspace_unlocked"
  | "permission_override_created"
  | "permission_override_deleted"
  | "impersonation_started"
  | "impersonation_ended"
  | "platform_user_created"
  | "platform_user_deactivated"
  | "tier_created"
  | "tier_updated"
  | "tier_deleted"
  | "seed_failure_retry_triggered"
  // Two-person approval workflow (task #608) — request → decision → execute.
  // The request and decision rows are written by the approval-engine and
  // identify the gated action via `metadata.actionType`. The execute row is
  // written by the gated endpoint after the engine hands control back.
  | "approval_request_created"
  | "approval_request_approved"
  | "approval_request_rejected"
  | "approval_request_expired"
  | "approval_request_executed"
  | "approval_request_execution_failed"
  // Task #583 — super-admin device binding
  | "platform_new_device_login_attempt"
  | "platform_mfa_email_sent"
  | "platform_mfa_verified"
  | "platform_device_approval_requested"
  | "platform_device_approved"
  | "platform_device_denied"
  | "platform_device_approval_expired"
  | "platform_device_revoked"
  | "platform_device_bootstrap_trusted"
  // Task #614 — quarterly PII column scanner
  | "pii_scan_run_triggered"
  | "pii_scan_findings_summary"
  | "pii_scan_run_failed"
  | "backup_restore_test_passed"
  | "backup_restore_test_failed"
  | "backup_restore_test_skipped"
  | "backup_pruned"
  | "backup_prune_failed"
  | "backup_failure_alerted"
  | "backup_health_warning"
  // Task #638 — SBOM vulnerability scanner alert fan-out.
  | "sbom_vulnerability_alerted"
  // Task #8 — Notification Hub fan-out gate for platformOnly categories.
  // When a workspace-side call site fires a category that is platform-only
  // (e.g. system.backup_failed), notify() records it here instead so
  // platform admins still have an authoritative trail.
  | "notification_hub_platform_event"
  // Platform-default branding (super-admin) — favicon + workspace icon
  // every workspace inherits unless its tier allows custom branding.
  | "platform_branding_favicon_set"
  | "platform_branding_favicon_cleared"
  | "platform_branding_sidebar_icon_set"
  | "platform_branding_sidebar_icon_cleared"
  // One-shot workspace restore (gated by RESTORE_IMPORT_ENABLED env flag).
  | "restore_workspace";

// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `created_at`. No auto-pruner — platform-admin actions are
// kept indefinitely for compliance. See
// `docs/security/audit-log-policy.md`.
export const platformAuditLog = pgTable("platform_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  action: varchar("action", { length: 50 }).notNull().$type<PlatformAuditAction>(),
  actorPlatformUserId: varchar("actor_platform_user_id").notNull(),
  actorEmail: varchar("actor_email", { length: 255 }).notNull(), // Denormalized for immutability
  targetWorkspaceId: varchar("target_workspace_id"),
  targetWorkspaceName: varchar("target_workspace_name", { length: 255 }), // Denormalized
  targetUserId: varchar("target_user_id"),
  targetUserEmail: varchar("target_user_email", { length: 255 }), // Denormalized
  metadataJson: text("metadata_json"), // Additional context as JSON
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const platformAuditLogRelations = relations(platformAuditLog, ({ one }) => ({
  actor: one(platformUsers, {
    fields: [platformAuditLog.actorPlatformUserId],
    references: [platformUsers.id],
  }),
  targetWorkspace: one(workspaces, {
    fields: [platformAuditLog.targetWorkspaceId],
    references: [workspaces.id],
  }),
}));

export const insertPlatformAuditLogSchema = createInsertSchema(platformAuditLog).omit({ id: true, createdAt: true });
export type InsertPlatformAuditLog = z.infer<typeof insertPlatformAuditLogSchema>;
export type PlatformAuditLog = typeof platformAuditLog.$inferSelect;

// ============================================================================
// PLATFORM ADMIN APPROVAL REQUESTS (task #608)
// ============================================================================
// Two-person approval workflow for the most destructive platform-admin
// actions (workspace hard-delete, permission-tier mutations, permission
// override mutations). The requesting admin POSTs the gated endpoint as
// usual; the handler routes the request through the approval-engine
// instead of executing inline. A second admin (not the requester)
// approves or rejects from the Pending Approvals page, and only on
// approval does the original work actually run.
//
// Rows are append-only in spirit (status transitions are guarded in
// storage), expire 24h after `requestedAt`, and the audit log carries a
// full request → decision → execute chain via `metadata.approvalRequestId`.
export type PlatformAdminApprovalActionType =
  | "workspace_hard_delete"
  | "tier_create"
  | "tier_update"
  | "tier_delete"
  | "permission_override_create"
  | "permission_override_delete";

export type PlatformAdminApprovalStatus =
  | "pending"
  | "approved"
  | "executing"
  | "rejected"
  | "expired"
  | "executed"
  | "execution_failed";

export const platformAdminApprovalRequests = pgTable(
  "platform_admin_approval_requests",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    actionType: varchar("action_type", { length: 64 })
      .notNull()
      .$type<PlatformAdminApprovalActionType>(),
    // The validated, action-specific payload the engine replays on
    // approval. Stored as JSON text so the shape can evolve per action
    // without a schema migration. Engine validates again before exec.
    payloadJson: text("payload_json").notNull(),
    // Free-form summary the requester saw at confirmation time (e.g.
    // "Hard-delete workspace 'Acme'") — surfaced verbatim on the
    // approver's pending-list so the second pair of eyes doesn't have
    // to reconstruct what was asked for.
    summary: text("summary").notNull(),
    requesterPlatformUserId: varchar("requester_platform_user_id").notNull(),
    requesterEmail: varchar("requester_email", { length: 255 }).notNull(),
    requesterReason: text("requester_reason"),
    // Optional denormalized target context — copied from the payload so
    // the pending-list can render workspace/tier names without any
    // joins, and so the row stays meaningful even after the underlying
    // workspace or tier is renamed/deleted.
    targetWorkspaceId: varchar("target_workspace_id"),
    targetWorkspaceName: varchar("target_workspace_name", { length: 255 }),
    targetLabel: varchar("target_label", { length: 255 }),
    status: varchar("status", { length: 32 })
      .notNull()
      .default("pending")
      .$type<PlatformAdminApprovalStatus>(),
    approverPlatformUserId: varchar("approver_platform_user_id"),
    approverEmail: varchar("approver_email", { length: 255 }),
    decisionReason: text("decision_reason"),
    requestedAt: timestamp("requested_at").defaultNow().notNull(),
    decidedAt: timestamp("decided_at"),
    executedAt: timestamp("executed_at"),
    expiresAt: timestamp("expires_at").notNull(),
    // Captured execution result/error for the audit chain. Trimmed by
    // the engine to keep the row small even when the underlying
    // workspace had a million rows.
    executionResultJson: text("execution_result_json"),
    executionError: text("execution_error"),
  },
);

export const platformAdminApprovalRequestsRelations = relations(
  platformAdminApprovalRequests,
  ({ one }) => ({
    requester: one(platformUsers, {
      fields: [platformAdminApprovalRequests.requesterPlatformUserId],
      references: [platformUsers.id],
    }),
    approver: one(platformUsers, {
      fields: [platformAdminApprovalRequests.approverPlatformUserId],
      references: [platformUsers.id],
    }),
    targetWorkspace: one(workspaces, {
      fields: [platformAdminApprovalRequests.targetWorkspaceId],
      references: [workspaces.id],
    }),
  }),
);

export const insertPlatformAdminApprovalRequestSchema = createInsertSchema(
  platformAdminApprovalRequests,
).omit({
  id: true,
  status: true,
  approverPlatformUserId: true,
  approverEmail: true,
  decisionReason: true,
  decidedAt: true,
  executedAt: true,
  executionResultJson: true,
  executionError: true,
  requestedAt: true,
});
export type InsertPlatformAdminApprovalRequest = z.infer<
  typeof insertPlatformAdminApprovalRequestSchema
>;
export type PlatformAdminApprovalRequest =
  typeof platformAdminApprovalRequests.$inferSelect;

// ============================================================================
// PLATFORM TRUSTED DEVICES (Task #583)
// ============================================================================
//
// Device-binding for super-admin sessions. A super-admin can only get a
// session cookie from a device that:
//   (a) already appears in `platform_trusted_devices` for this user, OR
//   (b) passed an emailed MFA challenge AND was explicitly approved by
//       another active super-admin (via a row in
//       `platform_device_approval_requests`), OR
//   (c) is the very first device on the very first login when no
//       trusted devices exist anywhere — the bootstrap auto-trust path.
//
// The `deviceCookieHash` is sha256(`platform_device_id` cookie value).
// We never store the raw cookie so a DB read does not let an attacker
// forge a trusted device.

export type PlatformDeviceApprovalStatus = "pending" | "approved" | "denied" | "expired";

export const platformTrustedDevices = pgTable("platform_trusted_devices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platformUserId: varchar("platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  // sha256 of the long-lived `platform_device_id` cookie. Unique per
  // (user, hash) — the same physical browser may be trusted by two
  // different admins, which is fine and intentional.
  deviceCookieHash: text("device_cookie_hash").notNull(),
  // Operator-friendly label, e.g. "Chrome on MacBook Pro". Defaulted
  // from the UA on first creation; user can rename later.
  label: varchar("label", { length: 255 }).notNull(),
  firstSeenAt: timestamp("first_seen_at").notNull().defaultNow(),
  lastSeenAt: timestamp("last_seen_at").notNull().defaultNow(),
  lastIp: varchar("last_ip", { length: 64 }),
  lastUserAgent: text("last_user_agent"),
  approvedAt: timestamp("approved_at").notNull().defaultNow(),
  // platform_user_id of the admin who approved this device. NULL for
  // bootstrap (no human approver — auto-trusted as the first device).
  approvedByPlatformUserId: varchar("approved_by_platform_user_id"),
  // Email of the approver, denormalised for the audit / device list UI
  // when the approver row gets deactivated/renamed later.
  approvedByEmail: varchar("approved_by_email", { length: 255 }),
  // Set when the admin (or another admin via "Revoke all other devices")
  // explicitly removed trust for this device. We keep the row instead of
  // deleting it so the audit history is preserved.
  revokedAt: timestamp("revoked_at"),
  revokedByPlatformUserId: varchar("revoked_by_platform_user_id"),
  // Inactivity expiry. If `lastSeenAt < now() - inactivityExpiresAt`,
  // the device-trust resolver treats this row as untrusted and a new
  // approval will be required. Default = approvedAt + 90d when the row
  // is created; revoking sets this to `now()`.
  inactivityExpiresAt: timestamp("inactivity_expires_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const platformTrustedDevicesRelations = relations(platformTrustedDevices, ({ one }) => ({
  platformUser: one(platformUsers, {
    fields: [platformTrustedDevices.platformUserId],
    references: [platformUsers.id],
  }),
}));

export const insertPlatformTrustedDeviceSchema = createInsertSchema(platformTrustedDevices).omit({
  id: true,
  createdAt: true,
  firstSeenAt: true,
  lastSeenAt: true,
  approvedAt: true,
});
export type InsertPlatformTrustedDevice = z.infer<typeof insertPlatformTrustedDeviceSchema>;
export type PlatformTrustedDevice = typeof platformTrustedDevices.$inferSelect;

export const platformDeviceApprovalRequests = pgTable("platform_device_approval_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // pending → approved | denied | expired (terminal). Once terminal, the
  // requesting browser is told via the polling endpoint and the row
  // stays for the audit trail.
  status: varchar("status", { length: 16 }).notNull().$type<PlatformDeviceApprovalStatus>().default("pending"),
  // The super-admin trying to log in.
  requestingPlatformUserId: varchar("requesting_platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  requestingEmail: varchar("requesting_email", { length: 255 }).notNull(),
  // sha256 of the candidate device cookie. Promoted to a real
  // `platform_trusted_devices` row only on approval.
  deviceCookieHash: text("device_cookie_hash").notNull(),
  ip: varchar("ip", { length: 64 }),
  userAgent: text("user_agent"),
  // Reference to the email-MFA challenge this request was created
  // against. Lets the audit log link MFA → approval cleanly.
  mfaChallengeId: varchar("mfa_challenge_id"),
  requestedAt: timestamp("requested_at").notNull().defaultNow(),
  decidedAt: timestamp("decided_at"),
  decidedByPlatformUserId: varchar("decided_by_platform_user_id"),
  decidedByEmail: varchar("decided_by_email", { length: 255 }),
  // Approval window — past this point a sweeper marks the row expired
  // and the requester has to start over.
  expiresAt: timestamp("expires_at").notNull(),
  // Set once the approval has been "consumed" by the requesting browser
  // (i.e. session cookie issued + trusted-device row written). Prevents
  // the same approval from being replayed if the requester polls again
  // after a successful login.
  consumedAt: timestamp("consumed_at"),
});

export const platformDeviceApprovalRequestsRelations = relations(platformDeviceApprovalRequests, ({ one }) => ({
  requestingUser: one(platformUsers, {
    fields: [platformDeviceApprovalRequests.requestingPlatformUserId],
    references: [platformUsers.id],
  }),
}));

export const insertPlatformDeviceApprovalRequestSchema = createInsertSchema(platformDeviceApprovalRequests).omit({
  id: true,
  requestedAt: true,
  decidedAt: true,
  consumedAt: true,
});
export type InsertPlatformDeviceApprovalRequest = z.infer<typeof insertPlatformDeviceApprovalRequestSchema>;
export type PlatformDeviceApprovalRequest = typeof platformDeviceApprovalRequests.$inferSelect;

// ============================================================================
// BETA FEEDBACK MODULE
// ============================================================================

export type FeedbackType = "enhancement" | "bug" | "critical";
export type FeedbackPriority = "low" | "medium" | "high" | "critical";
export type FeedbackUrgency = "low" | "medium" | "high";
export type FeedbackStatus = "new" | "acknowledged" | "in_progress" | "resolved" | "wont_fix";
// Status of the AI-generated "Replit fix prompt" attached to a feedback item.
// null = never generated (e.g. an enhancement the admin hasn't asked about yet).
export type FixPromptStatus = "pending" | "ready" | "failed";

export type FeedbackAttachment = {
  filename: string;
  contentType: string;
  contentBase64: string;
  size: number;
};

export const betaFeedback = pgTable("beta_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // The user who submitted the feedback
  userEmail: varchar("user_email", { length: 255 }).notNull(), // Denormalized for easy viewing
  userName: varchar("user_name", { length: 255 }), // Denormalized
  feedbackType: varchar("feedback_type", { length: 50 }).notNull().$type<FeedbackType>(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description").notNull(),
  pageUrl: varchar("page_url", { length: 500 }), // Which page they were on
  urgency: varchar("urgency", { length: 20 }).$type<FeedbackUrgency>(), // User-supplied urgency at submit time
  attachments: jsonb("attachments").$type<FeedbackAttachment[]>().default(sql`'[]'::jsonb`), // User-uploaded screenshots/files (base64-encoded)
  priority: varchar("priority", { length: 50 }).$type<FeedbackPriority>(), // Assigned by super admin
  status: varchar("status", { length: 50 }).notNull().default("new").$type<FeedbackStatus>(),
  adminNotes: text("admin_notes"), // Super admin private notes
  // AI Fix-Prompt Generator (Task #229) — Platform-Workspace-only fields.
  // The generated paste-ready "Replit prompt", its generation lifecycle status,
  // a diagnostic note when generation fails (or what the agent couldn't access),
  // and when the prompt was last produced. Only the Platform surface reads/writes
  // these; the tenant Feedback page never touches them.
  fixPrompt: text("fix_prompt"),
  fixPromptStatus: varchar("fix_prompt_status", { length: 20 }).$type<FixPromptStatus>(),
  fixPromptError: text("fix_prompt_error"),
  fixPromptGeneratedAt: timestamp("fix_prompt_generated_at"),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const betaFeedbackRelations = relations(betaFeedback, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [betaFeedback.workspaceId],
    references: [workspaces.id],
  }),
}));

export const insertBetaFeedbackSchema = createInsertSchema(betaFeedback).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertBetaFeedback = z.infer<typeof insertBetaFeedbackSchema>;
export type BetaFeedback = typeof betaFeedback.$inferSelect;

// ============================================================================
// BILLING MODULE TABLES
// ============================================================================

// Accounts Payable - expenses and contractor/talent payments
export type PayeeType = "user" | "contractor" | "talent" | "vendor" | "other";
export type PayableTransactionType = "expense" | "contractor_payment" | "talent_payment";
export type PaymentMethod = "manual" | "paypal" | "check" | "bank_transfer" | "cash" | "other";
export type PayableStatus = "pending" | "paid" | "overdue" | "failed" | "cancelled";
export type RecurringFrequency = "weekly" | "biweekly" | "monthly" | "quarterly";

export const accountsPayable = pgTable("accounts_payable", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  transactionType: varchar("transaction_type", { length: 50 }).notNull().$type<PayableTransactionType>(),
  payeeType: varchar("payee_type", { length: 50 }).notNull().$type<PayeeType>(),
  payeeId: varchar("payee_id"), // Reference to user/talent ID (nullable for external contractors)
  payeeName: varchar("payee_name", { length: 255 }).notNull(),
  payeeEmail: varchar("payee_email", { length: 255 }), // For PayPal payments
  // Optional FK back to the talent row when payeeType === "talent" so the AP entry
  // stays linked to the chosen talent (and not just a copied-down name string)
  // even after the payee is renamed. Nullable: null for non-talent payees and
  // for legacy rows created before this column was added. Task #309.
  talentId: varchar("talent_id").references(() => talent.id, { onDelete: "set null" }),
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(),
  description: text("description"),
  category: varchar("category", { length: 100 }),
  invoiceReference: varchar("invoice_reference", { length: 100 }), // Optional linked invoice #
  paymentMethod: varchar("payment_method", { length: 50 }).$type<PaymentMethod>(),
  // Free-text reference number for the actual payment (check #, wire confirm,
  // bank transaction id, etc.). Surfaced in the AP edit dialog when status
  // becomes "paid". Nullable for entries that haven't been paid yet. Task #309.
  paymentReference: varchar("payment_reference", { length: 255 }),
  paypalTransactionId: varchar("paypal_transaction_id", { length: 255 }),
  status: varchar("status", { length: 50 }).notNull().default("pending").$type<PayableStatus>(),
  paidAt: timestamp("paid_at"),
  isRecurring: boolean("is_recurring").notNull().default(false),
  recurringFrequency: varchar("recurring_frequency", { length: 50 }).$type<RecurringFrequency>(),
  nextPaymentDate: timestamp("next_payment_date"),
  // Free-form bookkeeping notes typed into the AP form. Nullable. Task #309.
  notes: text("notes"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceStatusIdx: index("accounts_payable_ws_status_idx").on(t.workspaceId, t.status),
  workspaceCreatedIdx: index("accounts_payable_ws_created_idx").on(t.workspaceId, t.createdAt),
  workspacePayeeIdx: index("accounts_payable_ws_payee_idx").on(t.workspaceId, t.payeeType, t.payeeId),
}));

export const accountsPayableRelations = relations(accountsPayable, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [accountsPayable.workspaceId],
    references: [workspaces.id],
  }),
}));

export const insertAccountsPayableSchema = createInsertSchema(accountsPayable).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertAccountsPayable = z.infer<typeof insertAccountsPayableSchema>;
export type AccountsPayable = typeof accountsPayable.$inferSelect;

// Accounts Receivable - auto-logged from paid invoices
export const accountsReceivable = pgTable("accounts_receivable", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  invoiceId: varchar("invoice_id").notNull().references(() => invoices.id, { onDelete: "cascade" }),
  invoiceNumber: varchar("invoice_number", { length: 50 }).notNull(),
  accountName: varchar("account_name", { length: 255 }),
  paymentAmount: decimal("payment_amount", { precision: 10, scale: 2 }).notNull(),
  paymentDate: timestamp("payment_date").notNull(),
  paymentNotes: text("payment_notes"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspacePaymentDateIdx: index("accounts_receivable_ws_payment_date_idx").on(t.workspaceId, t.paymentDate),
  invoiceIdx: index("accounts_receivable_invoice_idx").on(t.invoiceId),
}));

export const accountsReceivableRelations = relations(accountsReceivable, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [accountsReceivable.workspaceId],
    references: [workspaces.id],
  }),
  invoice: one(invoices, {
    fields: [accountsReceivable.invoiceId],
    references: [invoices.id],
  }),
}));

export const insertAccountsReceivableSchema = createInsertSchema(accountsReceivable).omit({ id: true, createdAt: true });
export type InsertAccountsReceivable = z.infer<typeof insertAccountsReceivableSchema>;
export type AccountsReceivable = typeof accountsReceivable.$inferSelect;

// ── Browser dialer (Wave 2) ──────────────────────────────────────────────
// One row per call (inbound or outbound). Manual call entries are also
// allowed (twilioCallSid NULL) for users without the browser dialer.
export type CallDirection = "inbound" | "outbound";
export type CallStatus =
  | "queued"
  | "ringing"
  | "in-progress"
  | "completed"
  | "failed"
  | "busy"
  | "no-answer";

export const calls = pgTable("calls", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // Owning user (sales rep / admin who placed or received the call). On inbound
  // calls this is the user we route to; on manual logs it's whoever logged it.
  userId: varchar("user_id"),
  // Optional FK to a lead or client. Both nullable so the call can be linked
  // later from the call log UI.
  leadId: varchar("lead_id"),
  clientId: varchar("client_id"),
  // Task #705 — set when an inbound call lands on an ad-tracking phone number
  // so call-volume reports can pivot by ad campaign. Nullable for old rows.
  campaignId: varchar("campaign_id"),
  direction: varchar("direction", { length: 16 }).notNull().$type<CallDirection>(),
  twilioCallSid: varchar("twilio_call_sid", { length: 64 }),
  fromNumber: varchar("from_number", { length: 32 }),
  toNumber: varchar("to_number", { length: 32 }),
  status: varchar("status", { length: 16 }).notNull().default("queued").$type<CallStatus>(),
  startedAt: timestamp("started_at"),
  endedAt: timestamp("ended_at"),
  durationSeconds: integer("duration_seconds").default(0),
  // Twilio-hosted recording URL (when recordingMode allows). Reserved.
  recordingUrl: varchar("recording_url", { length: 1024 }),
  // Reserved for the upcoming Google Drive recording-store + quota work.
  // When set, the recording has been mirrored to the workspace's Drive and
  // the Twilio-hosted copy is safe to delete.
  recordingDriveFileId: varchar("recording_drive_file_id", { length: 255 }),
  recordingDeletedAt: timestamp("recording_deleted_at"),
  // True when the consent disclosure prompt was played to the called party.
  // Required in two-party-consent states; admins can disable in single-party
  // states with the recordingMode setting.
  disclosurePlayed: boolean("disclosure_played").notNull().default(false),
  notes: text("notes"),
  // Tracks when a missed call was acknowledged in the Dialer Hub. NULL = unread.
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceCreatedIdx: index("calls_ws_created_idx").on(t.workspaceId, t.createdAt),
  leadIdx: index("calls_lead_idx").on(t.leadId),
  clientIdx: index("calls_client_idx").on(t.clientId),
  twilioSidIdx: uniqueIndex("calls_twilio_sid_unique_idx").on(t.twilioCallSid),
}));

export const insertCallSchema = createInsertSchema(calls).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertCall = z.infer<typeof insertCallSchema>;
export type Call = typeof calls.$inferSelect;

// Commission Structures - defines how commissions are calculated
export type CommissionStructureType = "flat" | "tiered" | "per_run";
export type CalculationBasis = "day_of_event" | "day_booked";
export type RecalculationMode = "retroactive" | "forward_only";

export const commissionStructures = pgTable("commission_structures", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  structureType: varchar("structure_type", { length: 50 }).notNull().$type<CommissionStructureType>(),
  flatPercentage: decimal("flat_percentage", { precision: 5, scale: 2 }), // For flat type
  calculationBasis: varchar("calculation_basis", { length: 50 }).$type<CalculationBasis>(), // For tiered
  recalculationMode: varchar("recalculation_mode", { length: 50 }).default("retroactive").$type<RecalculationMode>(),
  clientTypeId: varchar("client_type_id"), // legacy single — kept for compat; prefer clientTypeIds
  clientTypeIds: text("client_type_ids").array().notNull().default(sql`'{}'::text[]`), // multi-pick; empty = all types
  triggerMode: varchar("trigger_mode", { length: 20 }), // null = use workspace commissionTriggerMode default
  isDefault: boolean("is_default").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const commissionStructuresRelations = relations(commissionStructures, ({ one, many }) => ({
  workspace: one(workspaces, {
    fields: [commissionStructures.workspaceId],
    references: [workspaces.id],
  }),
  tiers: many(commissionTiers),
}));

export const insertCommissionStructureSchema = createInsertSchema(commissionStructures).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertCommissionStructure = z.infer<typeof insertCommissionStructureSchema>;
export type CommissionStructure = typeof commissionStructures.$inferSelect;

// Commission Tiers - tier thresholds for tiered structures
export const commissionTiers = pgTable("commission_tiers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  commissionStructureId: varchar("commission_structure_id").notNull().references(() => commissionStructures.id, { onDelete: "cascade" }),
  minThreshold: decimal("min_threshold", { precision: 10, scale: 2 }).notNull(), // Min avg daily booking $
  maxThreshold: decimal("max_threshold", { precision: 10, scale: 2 }), // Max avg (null = unlimited)
  percentage: decimal("percentage", { precision: 5, scale: 2 }).notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const commissionTiersRelations = relations(commissionTiers, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [commissionTiers.workspaceId],
    references: [workspaces.id],
  }),
  structure: one(commissionStructures, {
    fields: [commissionTiers.commissionStructureId],
    references: [commissionStructures.id],
  }),
}));

export const insertCommissionTierSchema = createInsertSchema(commissionTiers).omit({ id: true, createdAt: true });
export type InsertCommissionTier = z.infer<typeof insertCommissionTierSchema>;
export type CommissionTier = typeof commissionTiers.$inferSelect;

// User Commission Assignments - assigns structures to users
export const userCommissionAssignments = pgTable("user_commission_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // User profile ID
  commissionStructureId: varchar("commission_structure_id").notNull().references(() => commissionStructures.id, { onDelete: "cascade" }),
  customFlatOverride: decimal("custom_flat_override", { precision: 5, scale: 2 }), // Per-user override
  effectiveDate: timestamp("effective_date").defaultNow(),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceUserIdx: index("user_commission_assignments_ws_user_idx").on(t.workspaceId, t.userId),
  structureIdx: index("user_commission_assignments_structure_idx").on(t.commissionStructureId),
}));

export const userCommissionAssignmentsRelations = relations(userCommissionAssignments, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [userCommissionAssignments.workspaceId],
    references: [workspaces.id],
  }),
  structure: one(commissionStructures, {
    fields: [userCommissionAssignments.commissionStructureId],
    references: [commissionStructures.id],
  }),
}));

export const insertUserCommissionAssignmentSchema = createInsertSchema(userCommissionAssignments).omit({ id: true, createdAt: true });
export type InsertUserCommissionAssignment = z.infer<typeof insertUserCommissionAssignmentSchema>;
export type UserCommissionAssignment = typeof userCommissionAssignments.$inferSelect;

// Commission Payments - log of calculated/paid commissions
export type CommissionPaymentStatus = "pending" | "paid" | "adjusted";

export const commissionPayments = pgTable("commission_payments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // Sales rep user ID
  invoiceId: varchar("invoice_id").notNull().references(() => invoices.id, { onDelete: "cascade" }),
  invoiceNumber: varchar("invoice_number", { length: 50 }).notNull(),
  accountName: varchar("account_name", { length: 255 }),
  invoiceAmount: decimal("invoice_amount", { precision: 10, scale: 2 }).notNull(),
  regionId: varchar("region_id"), // Region for tiered calc
  calculatedTierPercentage: decimal("calculated_tier_percentage", { precision: 5, scale: 2 }),
  commissionAmount: decimal("commission_amount", { precision: 10, scale: 2 }).notNull(),
  manualOverride: boolean("manual_override").notNull().default(false),
  overrideAmount: decimal("override_amount", { precision: 10, scale: 2 }),
  overrideBy: varchar("override_by"),
  overrideReason: text("override_reason"),
  paymentDate: timestamp("payment_date").notNull(), // Date invoice was paid
  status: varchar("status", { length: 50 }).notNull().default("pending").$type<CommissionPaymentStatus>(),
  paidAt: timestamp("paid_at"), // When commission was paid out
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const commissionPaymentsRelations = relations(commissionPayments, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [commissionPayments.workspaceId],
    references: [workspaces.id],
  }),
  invoice: one(invoices, {
    fields: [commissionPayments.invoiceId],
    references: [invoices.id],
  }),
  region: one(regions, {
    fields: [commissionPayments.regionId],
    references: [regions.id],
  }),
}));

export const insertCommissionPaymentSchema = createInsertSchema(commissionPayments).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertCommissionPayment = z.infer<typeof insertCommissionPaymentSchema>;
export type CommissionPayment = typeof commissionPayments.$inferSelect;


// ============================================================================
// PLATFORM BACKUPS
// ============================================================================

export type BackupStatus = "pending" | "in_progress" | "completed" | "failed";

// `encrypted` = envelope+BACKUP_KEK (only mode new writers emit);
// `plaintext_legacy` = historical rows from before envelope encryption.
export type BackupEncryptionStatus = "encrypted" | "plaintext_legacy";

// Task #386 — a backup row is either the platform-wide bundle (all workspaces
// + platform data) or an independently-restorable single-workspace backup.
export type BackupKind = "platform" | "workspace";

export const platformBackups = pgTable("platform_backups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  status: varchar("status", { length: 50 }).notNull().default("pending").$type<BackupStatus>(),
  // Task #386 — distinguishes the platform-wide bundle from a per-workspace
  // standalone backup. Legacy rows default to 'platform'.
  kind: varchar("kind", { length: 16 }).notNull().default("platform").$type<BackupKind>(),
  // Set only when kind = 'workspace'. FK-less (mirrors other platform tables)
  // so restore/prune never trips over a missing workspace row.
  workspaceId: varchar("workspace_id"),
  workspaceCount: integer("workspace_count").notNull().default(0),
  totalRecords: integer("total_records").notNull().default(0),
  driveFolderId: varchar("drive_folder_id", { length: 255 }),
  driveFolderUrl: varchar("drive_folder_url", { length: 500 }),
  errorMessage: text("error_message"),
  encryptionStatus: varchar("encryption_status", { length: 32 })
    .notNull()
    .default("plaintext_legacy")
    .$type<BackupEncryptionStatus>(),
  // Schema version embedded in the encrypted bundle. Restore refuses if
  // this doesn't match the running DB's expected version.
  backupSchemaVersion: integer("backup_schema_version").notNull().default(1),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
});

export const insertPlatformBackupSchema = createInsertSchema(platformBackups).omit({ id: true, createdAt: true });
export type InsertPlatformBackup = z.infer<typeof insertPlatformBackupSchema>;
export type PlatformBackup = typeof platformBackups.$inferSelect;

// One row per restore attempt against the scratch DB; powers the BC/DR
// restore-test log on the platform Backups page.
export type BackupRestoreTestStatus =
  | "pending"
  | "in_progress"
  | "passed"
  | "failed"
  | "skipped";

export const backupRestoreTests = pgTable("backup_restore_tests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Optional FK to the backup we restored. Nullable because a "skipped"
  // tick (no Drive creds, no recent backup) records the attempt with no
  // backup to point at.
  backupId: varchar("backup_id"),
  status: varchar("status", { length: 16 }).notNull().default("pending").$type<BackupRestoreTestStatus>(),
  // 'scheduled' (monthly cron) or 'manual' (admin-triggered).
  triggerType: varchar("trigger_type", { length: 16 }).notNull(),
  startedAt: timestamp("started_at").notNull().defaultNow(),
  finishedAt: timestamp("finished_at"),
  durationMs: integer("duration_ms"),
  // JSON-serialized array of assertion results so the UI can render them
  // without another join. Shape: [{ name, ok, detail? }].
  assertionsJson: text("assertions_json"),
  errorMessage: text("error_message"),
});

export const insertBackupRestoreTestSchema = createInsertSchema(backupRestoreTests).omit({ id: true, startedAt: true });
export type InsertBackupRestoreTest = z.infer<typeof insertBackupRestoreTestSchema>;
export type BackupRestoreTest = typeof backupRestoreTests.$inferSelect;

// ============================================================================
// AUDIT-LOG FORENSIC EXPORT
// ============================================================================
// One row per (table, utc_date, run_id) attempt of the audit-export sweep.
// `non_compliant` = uploaded under the mutable-bucket override; not durable.

export type AuditExportStatus =
  | "pending"
  | "succeeded"
  | "failed"
  | "skipped"
  | "non_compliant";

export const auditExportRuns = pgTable("audit_export_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id", { length: 64 }).notNull(),
  tableName: varchar("table_name", { length: 100 }).notNull(),
  utcDate: varchar("utc_date", { length: 10 }).notNull(),
  status: varchar("status", { length: 16 }).notNull().$type<AuditExportStatus>(),
  rowCount: integer("row_count").notNull().default(0),
  bytesWritten: integer("bytes_written").notNull().default(0),
  objectPath: text("object_path"),
  durationMs: integer("duration_ms").notNull().default(0),
  errorMessage: text("error_message"),
  startedAt: timestamp("started_at").notNull().defaultNow(),
  finishedAt: timestamp("finished_at"),
});

export const insertAuditExportRunSchema = createInsertSchema(auditExportRuns).omit({
  id: true,
  startedAt: true,
});
export type InsertAuditExportRun = z.infer<typeof insertAuditExportRunSchema>;
export type AuditExportRun = typeof auditExportRuns.$inferSelect;

// ============================================================================
// BUDGET APP INTEGRATION
// ============================================================================

export type BudgetAppSyncStatus = "connected" | "disconnected" | "error";

export const budgetAppIntegrations = pgTable("budget_app_integrations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  apiUrl: varchar("api_url", { length: 500 }).notNull(), // Budget app base URL
  apiKey: varchar("api_key", { length: 255 }).notNull(), // API key for auth
  status: varchar("status", { length: 50 }).notNull().default("disconnected").$type<BudgetAppSyncStatus>(),
  lastSyncAt: timestamp("last_sync_at"),
  lastSyncError: text("last_sync_error"),
  syncEnabled: boolean("sync_enabled").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const budgetAppIntegrationsRelations = relations(budgetAppIntegrations, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [budgetAppIntegrations.workspaceId],
    references: [workspaces.id],
  }),
}));

export const insertBudgetAppIntegrationSchema = createInsertSchema(budgetAppIntegrations).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertBudgetAppIntegration = z.infer<typeof insertBudgetAppIntegrationSchema>;
export type BudgetAppIntegration = typeof budgetAppIntegrations.$inferSelect;

// Balance Sheet Transactions - synced from budget app
export type BalanceSheetTransactionType = "income" | "expense";
export type BalanceSheetTransactionSource = "budget_app" | "invoice_payment" | "commission_payment" | "accounts_payable";

export const balanceSheetTransactions = pgTable("balance_sheet_transactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  externalId: varchar("external_id", { length: 255 }), // ID from budget app for deduplication
  date: timestamp("date").notNull(),
  description: varchar("description", { length: 500 }).notNull(),
  amount: decimal("amount", { precision: 12, scale: 2 }).notNull(),
  type: varchar("type", { length: 50 }).notNull().$type<BalanceSheetTransactionType>(),
  category: varchar("category", { length: 255 }),
  source: varchar("source", { length: 50 }).notNull().$type<BalanceSheetTransactionSource>(),
  referenceId: varchar("reference_id", { length: 255 }), // Invoice ID, commission ID, etc.
  referenceType: varchar("reference_type", { length: 50 }), // "invoice", "commission", "payable"
  syncedAt: timestamp("synced_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const balanceSheetTransactionsRelations = relations(balanceSheetTransactions, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [balanceSheetTransactions.workspaceId],
    references: [workspaces.id],
  }),
}));

export const insertBalanceSheetTransactionSchema = createInsertSchema(balanceSheetTransactions).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertBalanceSheetTransaction = z.infer<typeof insertBalanceSheetTransactionSchema>;
export type BalanceSheetTransaction = typeof balanceSheetTransactions.$inferSelect;

// User Sessions - track site sessions for activity analytics
export const userSessions = pgTable("user_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  sessionId: varchar("session_id", { length: 100 }).notNull(), // Unique browser session identifier
  startedAt: timestamp("started_at").defaultNow(),
  lastActiveAt: timestamp("last_active_at").defaultNow(),
  userAgent: text("user_agent"),
  ipAddress: varchar("ip_address", { length: 100 }),
});

export const userSessionsRelations = relations(userSessions, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [userSessions.workspaceId],
    references: [workspaces.id],
  }),
}));

export const insertUserSessionSchema = createInsertSchema(userSessions).omit({ id: true, startedAt: true, lastActiveAt: true });
export type InsertUserSession = z.infer<typeof insertUserSessionSchema>;
export type UserSession = typeof userSessions.$inferSelect;

// Session Heartbeat Stats - per-workspace, per-day counters of the outcome of
// POST /api/sessions/heartbeat. The heartbeat endpoint returns one of
// `created`, `updated`, `rolled`, or `throttled`. Tracking those outcomes as
// daily aggregate counters lets admins notice if the 30-minute idle roll is
// firing way more (or way less) than expected — a leading indicator that
// engagement-time numbers are drifting. The `throttled` outcome is the
// 30-second per-session rate limit and is intentionally NOT counted: it is a
// noisy signal that says nothing about real user behavior.
export const sessionHeartbeatStats = pgTable("session_heartbeat_stats", {
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  day: date("day").notNull(),
  createdCount: integer("created_count").notNull().default(0),
  updatedCount: integer("updated_count").notNull().default(0),
  rolledCount: integer("rolled_count").notNull().default(0),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  pk: primaryKey({ columns: [table.workspaceId, table.day] }),
}));

export const sessionHeartbeatStatsRelations = relations(sessionHeartbeatStats, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [sessionHeartbeatStats.workspaceId],
    references: [workspaces.id],
  }),
}));

export type SessionHeartbeatStat = typeof sessionHeartbeatStats.$inferSelect;

// Per-workspace, per-day dedupe row for the heartbeat-anomaly alerter
// (task #312). The alerter checks once or twice per day whether today's
// rolled_count is materially out of band vs. the trailing 7-day average
// and emails admins when it is. The (workspaceId, day) PK + `direction`
// column guarantees that a single anomaly can't email twice in one day:
// the row is INSERTed up-front and any conflicting subsequent send is
// silently skipped. `direction` is "spike" (today > 2x avg) or "drop"
// (today < 0.25x avg with prior-week traffic), and is stored alongside
// the snapshot numbers so the diagnostics view can answer "which way
// did it move?" without re-running the comparison.
export const sessionHeartbeatAnomalyAlerts = pgTable("session_heartbeat_anomaly_alerts", {
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  day: date("day").notNull(),
  direction: varchar("direction", { length: 8 }).notNull(), // 'spike' | 'drop'
  todayCount: integer("today_count").notNull(),
  trailingAverage: decimal("trailing_average", { precision: 12, scale: 2 }).notNull(),
  notifiedAt: timestamp("notified_at").defaultNow().notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.workspaceId, table.day] }),
}));

export type SessionHeartbeatAnomalyAlert = typeof sessionHeartbeatAnomalyAlerts.$inferSelect;

// ── Anomaly detection v2 (task #593) ─────────────────────────────────
// Append-only ledger of every successful login, captured by the auth
// hooks (password + Replit OIDC). Each row carries the resolved IP,
// user-agent, and best-effort geolocation so the impossible-travel
// evaluator can compute great-circle distance between consecutive
// logins. workspaceId may be NULL when a freshly-OIDC'd user hasn't
// been bound to a workspace yet — the impossible-travel check still
// uses (userId) as its key, so a missing workspace doesn't disable
// detection.
export const userLoginEvents = pgTable("user_login_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  ip: varchar("ip", { length: 64 }),
  userAgent: text("user_agent"),
  geoCity: varchar("geo_city", { length: 128 }),
  geoCountry: varchar("geo_country", { length: 8 }),
  geoLat: real("geo_lat"),
  geoLon: real("geo_lon"),
  // 'password' | 'replit_oauth' — bookkeeping for forensics, not
  // currently used by the evaluator.
  authMethod: varchar("auth_method", { length: 32 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userCreatedIdx: index("user_login_events_user_created_idx").on(table.userId, table.createdAt),
  workspaceCreatedIdx: index("user_login_events_ws_created_idx").on(table.workspaceId, table.createdAt),
}));
export type UserLoginEvent = typeof userLoginEvents.$inferSelect;
export const insertUserLoginEventSchema = createInsertSchema(userLoginEvents).omit({ id: true, createdAt: true });
export type InsertUserLoginEvent = z.infer<typeof insertUserLoginEventSchema>;

// Append-only ledger of every bulk-export call (any of:
// activity-tracker CSV, leads/clients/invoices CSV, workspace
// XLSX/ZIP, platform workspace export). The volumetric evaluator
// reads the trailing-30d window per (userId, kind) to compute the
// median row-count baseline and decide whether `rowCount` warrants
// an alert.
export const userExportEvents = pgTable("user_export_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  // Stable, code-defined identifier for the export endpoint, e.g.
  // 'activity_csv', 'crm_csv', 'workspace_zip', 'workspace_xlsx'.
  // Same value used as the volumetric dedupe key.
  kind: varchar("kind", { length: 64 }).notNull(),
  rowCount: integer("row_count").notNull(),
  ip: varchar("ip", { length: 64 }),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userKindCreatedIdx: index("user_export_events_user_kind_created_idx").on(table.userId, table.kind, table.createdAt),
  workspaceCreatedIdx: index("user_export_events_ws_created_idx").on(table.workspaceId, table.createdAt),
}));
export type UserExportEvent = typeof userExportEvents.$inferSelect;
export const insertUserExportEventSchema = createInsertSchema(userExportEvents).omit({ id: true, createdAt: true });
export type InsertUserExportEvent = z.infer<typeof insertUserExportEventSchema>;

// Per-(user, hour) dedupe slot for impossible-travel alerts. The PK
// is the dedupe primitive — `INSERT … ON CONFLICT DO NOTHING
// RETURNING` lets exactly one concurrent caller win the claim and
// page admins, while losers see the existing row and short-circuit.
// Using a 1-hour bucket (rather than per-event) means a user who
// genuinely is bouncing between two access tokens within the same
// hour still produces at most one alert. workspaceId is captured for
// reporting but is NOT part of the PK — the same physical user may
// belong to multiple workspaces and we still want to silence their
// account globally for the hour.
export const impossibleTravelAlerts = pgTable("impossible_travel_alerts", {
  userId: varchar("user_id").notNull(),
  // Truncated to start-of-hour UTC by the alerter, not by the DB —
  // keeps the dedupe behavior independent of the server's local
  // timezone setting.
  hourBucket: timestamp("hour_bucket").notNull(),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  prevEventId: varchar("prev_event_id"),
  currEventId: varchar("curr_event_id"),
  distanceKm: decimal("distance_km", { precision: 10, scale: 2 }).notNull(),
  hoursElapsed: decimal("hours_elapsed", { precision: 10, scale: 4 }).notNull(),
  speedKmh: decimal("speed_kmh", { precision: 10, scale: 2 }).notNull(),
  thresholdKmh: integer("threshold_kmh").notNull(),
  notifiedAt: timestamp("notified_at").defaultNow().notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.userId, table.hourBucket] }),
}));
export type ImpossibleTravelAlert = typeof impossibleTravelAlerts.$inferSelect;

// Per-(user, day, kind) dedupe slot for volumetric-export alerts.
// Same claim-first contract as impossible-travel above. Using day
// rather than hour because export bursts are usually a single
// session of mass-pulling, so collapsing to one alert per day per
// kind matches what an admin would actually want to see.
export const volumetricExportAlerts = pgTable("volumetric_export_alerts", {
  userId: varchar("user_id").notNull(),
  day: date("day").notNull(),
  kind: varchar("kind", { length: 64 }).notNull(),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  eventId: varchar("event_id"),
  rowCount: integer("row_count").notNull(),
  medianRowCount: decimal("median_row_count", { precision: 12, scale: 2 }).notNull(),
  multiplier: decimal("multiplier", { precision: 10, scale: 2 }).notNull(),
  thresholdMultiplier: integer("threshold_multiplier").notNull(),
  notifiedAt: timestamp("notified_at").defaultNow().notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.userId, table.day, table.kind] }),
}));
export type VolumetricExportAlert = typeof volumetricExportAlerts.$inferSelect;

// Customer-facing audit log surface. Lives separately from the
// existing `workspaceSettingAudits` (which only tracks setting
// changes) and from the platform-side `permissionAuditLog`. The
// upcoming customer audit-log UI reads from this table to show
// security events — impossible-travel hits, volumetric-export
// anomalies, and any future per-tenant security signal — alongside
// other workspace activity. eventType is a dotted path
// (`security.impossible_travel`, `security.volumetric_export`)
// matching the notification category for cross-referencing.
//
// AUDIT-LOG TABLE — append-only enforcement is handled by the
// shared `audit_log_append_only` trigger inventory in
// `server/lib/audit-log-trigger-sync.ts`.
export const workspaceAuditEvents = pgTable("workspace_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id"),
  eventType: varchar("event_type", { length: 64 }).notNull(),
  // 'info' | 'warning' | 'critical' — mirrors notify().severity.
  severity: varchar("severity", { length: 16 }).notNull().default("info"),
  summary: text("summary").notNull(),
  metadataJson: jsonb("metadata_json"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  workspaceCreatedIdx: index("workspace_audit_events_ws_created_idx").on(table.workspaceId, table.createdAt),
  workspaceTypeCreatedIdx: index("workspace_audit_events_ws_type_created_idx").on(table.workspaceId, table.eventType, table.createdAt),
}));
export type WorkspaceAuditEvent = typeof workspaceAuditEvents.$inferSelect;
export const insertWorkspaceAuditEventSchema = createInsertSchema(workspaceAuditEvents).omit({ id: true, createdAt: true });
export type InsertWorkspaceAuditEvent = z.infer<typeof insertWorkspaceAuditEventSchema>;

// Activity Goals - admin-configurable goals for activity metrics
export const activityGoals = pgTable("activity_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  metricKey: varchar("metric_key", { length: 100 }).notNull(), // leads, clients, calls, emails, etc.
  dailyGoal: integer("daily_goal"),
  weeklyGoal: integer("weekly_goal"),
  monthlyGoal: integer("monthly_goal"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const activityGoalsRelations = relations(activityGoals, ({ one }) => ({
  workspace: one(workspaces, {
    fields: [activityGoals.workspaceId],
    references: [workspaces.id],
  }),
}));

export const insertActivityGoalSchema = createInsertSchema(activityGoals).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertActivityGoal = z.infer<typeof insertActivityGoalSchema>;
export type ActivityGoal = typeof activityGoals.$inferSelect;

// ============================================================================
// LEAD GEN MODULE
// ============================================================================

// Lead Gen Searches — tracks background Google Places grid search jobs
export const leadGenSearches = pgTable("lead_gen_searches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  createdByUserId: varchar("created_by_user_id").notNull(),
  keyword: varchar("keyword", { length: 255 }).notNull(), // e.g. "senior living" or AI brief
  location: varchar("location", { length: 255 }).notNull(), // e.g. "Denver, CO" or AI scope label
  radiusMiles: integer("radius_miles").notNull().default(25),
  status: varchar("status", { length: 20 }).notNull().default("PENDING"), // PENDING, RUNNING, COMPLETE, FAILED
  resultCount: integer("result_count").notNull().default(0), // unique results added to sandbox
  totalSubSearches: integer("total_sub_searches").notNull().default(0),
  completedSubSearches: integer("completed_sub_searches").notNull().default(0),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  source: varchar("source", { length: 10 }).notNull().default("grid"), // 'grid' | 'ai'
}, (t) => ({
  workspaceStatusCreatedIdx: index("lead_gen_searches_ws_status_created_idx").on(t.workspaceId, t.status, t.createdAt),
}));

export const insertLeadGenSearchSchema = createInsertSchema(leadGenSearches).omit({ id: true, createdAt: true });
export type InsertLeadGenSearch = z.infer<typeof insertLeadGenSearchSchema>;
export type LeadGenSearch = typeof leadGenSearches.$inferSelect;

// Lead Gen Sandbox — temporary workspace for reviewing leads before import
export const leadGenSandbox = pgTable("lead_gen_sandbox", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // which user's sandbox this belongs to (per-user isolation)
  searchId: varchar("search_id"), // which search job produced this; NULL for AI-generated rows
  googlePlaceId: varchar("google_place_id", { length: 255 }).notNull(), // unique per (workspace, user) for dedup
  businessName: varchar("business_name", { length: 255 }).notNull(),
  address: varchar("address", { length: 500 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 100 }),
  zipCode: varchar("zip_code", { length: 20 }),
  phone: varchar("phone", { length: 50 }),
  website: varchar("website", { length: 500 }),
  googleRating: real("google_rating"),
  googleRatingsCount: integer("google_ratings_count"),
  googleMapsUrl: varchar("google_maps_url", { length: 1000 }),
  photoReference: varchar("photo_reference", { length: 1000 }), // For hover photo preview
  latitude: real("latitude"),
  longitude: real("longitude"),
  distanceMiles: real("distance_miles"), // Straight-line distance from search center
  quadrant: varchar("quadrant", { length: 2 }), // NE | NW | SE | SW relative to search center
  isDuplicate: boolean("is_duplicate").notNull().default(false), // Matches existing lead/client phone
  isOutOfRegion: boolean("is_out_of_region").notNull().default(false),
  ignored: boolean("ignored").notNull().default(false), // User manually removed from import
  // ── AI Search additions ────────────────────────────────────────────────
  // True when this row originated from the AI Discovery flow (vs. Google grid search).
  isAiGenerated: boolean("is_ai_generated").notNull().default(false),
  // 'verified' = Google Places confirmed the venue exists and we have a real placeId.
  // 'unverified' = Claude proposed it, Google could not confirm. NULL for non-AI rows.
  verificationStatus: varchar("verification_status", { length: 20 }),
  // 1-5 fit score against the user's brief. 1 = best per product spec.
  // Used to auto-sort the sandbox and to filter by minFitScore.
  aiFitScore: integer("ai_fit_score"),
  // Soft contact fields populated by AI / email-finder / Apollo. Carried forward to
  // the lead row at import time. Kept separate from `phone`/`website` (which are the
  // Google-verified values) so admins can see the AI proposal vs. the verified value.
  aiContactName: varchar("ai_contact_name", { length: 255 }),
  aiContactRole: varchar("ai_contact_role", { length: 100 }), // "Talent Buyer", "Programming Director", "GM", etc.
  aiContactEmail: varchar("ai_contact_email", { length: 255 }),
  aiVenueType: varchar("ai_venue_type", { length: 100 }), // free-text per user spec — not enum-constrained
  // Was varchar(50). The Analyst now returns descriptive capacity rather than a
  // bare number ("multi-venue complex; Overture Hall ~2,250 seats plus Capitol
  // Theater ~1,100 seats..."), which overflowed the column and failed the whole
  // multi-row sandbox INSERT — losing every lead in the run. See migration 0209.
  aiVenueCapacity: text("ai_venue_capacity"),
  aiNotes: text("ai_notes"), // programming style, recent acts, season window, why it fits the brief
  // Where this row came from: "AI Discovery", "AI Enrich", "Apollo", "Email Finder".
  // Used as the lead's `source` value at import time and for filtering reports.
  aiSourceLabel: varchar("ai_source_label", { length: 50 }),
  // Per-field confidence map { field: 'high'|'med'|'low' } — drives the badge UI in the sandbox.
  aiConfidenceJson: jsonb("ai_confidence_json"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceSearchIdx: index("lead_gen_sandbox_ws_search_idx").on(t.workspaceId, t.searchId),
  workspaceUserPlaceUnique: uniqueIndex("lead_gen_sandbox_ws_user_place_unique").on(t.workspaceId, t.userId, t.googlePlaceId),
  workspaceUserCreatedIdx: index("lead_gen_sandbox_ws_user_created_idx").on(t.workspaceId, t.userId, t.createdAt),
  workspaceUserAiFitIdx: index("lead_gen_sandbox_ws_user_ai_fit_idx").on(t.workspaceId, t.userId, t.aiFitScore),
}));

export const insertLeadGenSandboxSchema = createInsertSchema(leadGenSandbox).omit({ id: true, createdAt: true });
export type InsertLeadGenSandbox = z.infer<typeof insertLeadGenSandboxSchema>;
export type LeadGenSandboxItem = typeof leadGenSandbox.$inferSelect;

// ── Charged place IDs — permanent billing dedup log ───────────────────
// Decoupled from sandbox contents so clearing the sandbox never resets
// dedup protection. Every place_id billed to a workspace is recorded
// here; a re-search after sandbox clear will never re-charge the same
// place_id within the same workspace.
export const leadGenChargedPlaceIds = pgTable("lead_gen_charged_place_ids", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  googlePlaceId: varchar("google_place_id", { length: 255 }).notNull(),
  chargedAt: timestamp("charged_at").defaultNow(),
}, (t) => ({
  workspacePlaceUniq: uniqueIndex("lead_gen_charged_ws_place_uniq").on(t.workspaceId, t.googlePlaceId),
  workspaceIdx: index("lead_gen_charged_ws_idx").on(t.workspaceId),
}));

export type LeadGenChargedPlaceId = typeof leadGenChargedPlaceIds.$inferSelect;

// ── AI Search saved searches + run audit ──────────────────────────────
// Saved searches let the user pin a brief + scope + filters and re-run on demand
// or on a recurring monthly schedule. New finds land in the sandbox tagged with the
// saved-search name and (when recurring) trigger a digest email to the creator.
export const leadGenSavedSearches = pgTable("lead_gen_saved_searches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  createdByUserId: varchar("created_by_user_id").notNull(),
  name: varchar("name", { length: 255 }).notNull(),
  brief: text("brief").notNull(), // natural-language description of the ideal prospect
  // scope: { kind: 'city' | 'state' | 'region' | 'national' | 'cities' | 'custom', value: string|string[] }
  scopeJson: jsonb("scope_json").notNull(),
  // filters: { venueTypes?: string[], capacityMin?, capacityMax?, genres?: string[], exclude?: string[],
  //   minFitScore?: 1..5, resultCap?, modelKey?, requireVerified?: boolean, runEmailFinder?: boolean,
  //   runApollo?: boolean }
  filtersJson: jsonb("filters_json").notNull(),
  recurring: boolean("recurring").notNull().default(false),
  recurringFrequency: varchar("recurring_frequency", { length: 20 }), // 'monthly' | null
  lastRunAt: timestamp("last_run_at"),
  nextRunAt: timestamp("next_run_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceIdx: index("lead_gen_saved_searches_ws_idx").on(t.workspaceId),
  recurringDueIdx: index("lead_gen_saved_searches_due_idx").on(t.recurring, t.nextRunAt),
}));

export const insertLeadGenSavedSearchSchema = createInsertSchema(leadGenSavedSearches).omit({ id: true, createdAt: true });
export type InsertLeadGenSavedSearch = z.infer<typeof insertLeadGenSavedSearchSchema>;
export type LeadGenSavedSearch = typeof leadGenSavedSearches.$inferSelect;

// Audit log of every AI run — usage tracking, cost tracking, debugging.
// Append-only audit table; see `docs/security/audit-log-policy.md`.
export const leadGenAiRuns = pgTable("lead_gen_ai_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  savedSearchId: varchar("saved_search_id"), // null for ad-hoc runs
  runByUserId: varchar("run_by_user_id"),
  kind: varchar("kind", { length: 30 }).notNull(), // 'discover' | 'enrich' | 'email_finder' | 'apollo' | 'recurring'
  modelKey: varchar("model_key", { length: 64 }),
  inputTokens: integer("input_tokens").notNull().default(0),
  outputTokens: integer("output_tokens").notNull().default(0),
  costCents: integer("cost_cents").notNull().default(0), // our actual provider cost (LLM + API)
  candidatesGenerated: integer("candidates_generated").notNull().default(0),
  verifiedCount: integer("verified_count").notNull().default(0),
  importedCount: integer("imported_count").notNull().default(0),
  creditsCharged: integer("credits_charged").notNull().default(0),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceCreatedIdx: index("lead_gen_ai_runs_ws_created_idx").on(t.workspaceId, t.createdAt),
}));

export const insertLeadGenAiRunSchema = createInsertSchema(leadGenAiRuns).omit({ id: true, createdAt: true });
export type InsertLeadGenAiRun = z.infer<typeof insertLeadGenAiRunSchema>;
export type LeadGenAiRun = typeof leadGenAiRuns.$inferSelect;

// ── AI Search Sessions — memory + feedback ────────────────────────────────────
// One row per AI search run. Used for workspace-level memory and quality tracking.
// Super admin can filter by workspace; leads_kept/deleted tracked at import time.
export const aiSearchSessions = pgTable("ai_search_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  prompt: text("prompt").notNull(),
  modelKey: varchar("model_key", { length: 64 }).notNull(), // 'scout' | 'analyst'
  scopeJson: jsonb("scope_json"),
  filtersJson: jsonb("filters_json"),
  resultsCount: integer("results_count").notNull().default(0),
  creditsCharged: integer("credits_charged").notNull().default(0),
  savedName: varchar("saved_name", { length: 255 }),
  leadsKept: integer("leads_kept").notNull().default(0),
  leadsDeleted: integer("leads_deleted").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceIdx: index("ai_search_sessions_ws_idx").on(t.workspaceId, t.createdAt),
}));

export const insertAiSearchSessionSchema = createInsertSchema(aiSearchSessions).omit({ id: true, createdAt: true });
export type InsertAiSearchSession = z.infer<typeof insertAiSearchSessionSchema>;
export type AiSearchSession = typeof aiSearchSessions.$inferSelect;

// ============================================================================
// MARKETING MODULE
// ============================================================================

// Marketing Contacts — separate from leads/clients. type discriminator: media (press/industry) | audience (fans/subscribers)
export type MarketingContactType = "media" | "audience";
export type EngagementTier = "active" | "lapsed" | "cold" | "vip";
// Renamed from `SuppressionReason` to avoid colliding with the
// lead-suppression `SuppressionReason` declared earlier in this file
// (the lead one is the one consumed by `server/storage.ts`). Stricter
// TS loaders (e.g. babel) flag the duplicate; tsx silently uses the
// last declaration. This type is local to the marketing module and
// isn't imported elsewhere, so the rename is internal-only.
export type MarketingContactSuppressionReason =
  | "bounce"
  | "complaint"
  | "unsubscribe"
  | "manual";

export const marketingContacts = pgTable("marketing_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  type: varchar("type", { length: 20 }).notNull(), // media | audience
  // Universal fields
  firstName: varchar("first_name", { length: 100 }),
  lastName: varchar("last_name", { length: 100 }),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 50 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  tags: text("tags").array().default(sql`ARRAY[]::text[]`),
  notes: text("notes"),
  source: varchar("source", { length: 100 }), // import, form, manual, contest, etc.
  // MEDIA-only fields
  outlet: varchar("outlet", { length: 255 }), // Publication / station / podcast name
  beat: varchar("beat", { length: 100 }), // comedy, music, family, corporate, etc.
  mediaType: varchar("media_type", { length: 50 }), // print, podcast, radio, tv, blog, newsletter, influencer
  reach: integer("reach"), // Circulation/audience size estimate
  preferredPitchFormat: varchar("preferred_pitch_format", { length: 100 }), // EPK, exclusive, embargo, etc.
  marketsCovered: text("markets_covered").array().default(sql`ARRAY[]::text[]`),
  lastPitchedAt: timestamp("last_pitched_at"),
  lastPlacementAt: timestamp("last_placement_at"),
  // AUDIENCE-only fields
  optInEmail: boolean("opt_in_email").notNull().default(false),
  optInSms: boolean("opt_in_sms").notNull().default(false),
  optInSource: varchar("opt_in_source", { length: 100 }), // form, manual, import, contest, etc.
  optInTimestamp: timestamp("opt_in_timestamp"),
  favoriteTalentIds: text("favorite_talent_ids").array().default(sql`ARRAY[]::text[]`),
  engagementTier: varchar("engagement_tier", { length: 20 }), // active | lapsed | cold | vip
  engagementScore: integer("engagement_score").notNull().default(0),
  lastOpenAt: timestamp("last_open_at"),
  lastClickAt: timestamp("last_click_at"),
  // Suppression — global enforcement on every send
  suppressed: boolean("suppressed").notNull().default(false),
  suppressedAt: timestamp("suppressed_at"),
  suppressedReason: varchar("suppressed_reason", { length: 50 }),
  // Audit
  ownerUserId: varchar("owner_user_id"), // Relationship owner (esp. for media)
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
});

export const insertMarketingContactSchema = createInsertSchema(marketingContacts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});
export type InsertMarketingContact = z.infer<typeof insertMarketingContactSchema>;
export type MarketingContact = typeof marketingContacts.$inferSelect;

// Marketing contact ↔ region (many-to-many). A journalist can cover
// multiple markets; an audience subscriber may consent to news from
// several regions. Region assignment is optional — no row here just
// means "no region tagged".
export const marketingContactRegions = pgTable("marketing_contact_regions", {
  contactId: varchar("contact_id").notNull().references(() => marketingContacts.id, { onDelete: "cascade" }),
  regionId: varchar("region_id").notNull().references(() => regions.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  pk: primaryKey({ columns: [t.contactId, t.regionId] }),
  byContact: index("idx_mc_regions_contact").on(t.contactId),
  byRegion: index("idx_mc_regions_region").on(t.regionId),
}));

// Marketing contact ↔ client (many-to-many). Captures historical
// relationships — e.g. "Rolling Stone has covered Artist A, Artist B,
// Artist C" — so the sales team can see who has worked with whom when
// pitching the next show.
export const marketingContactClients = pgTable("marketing_contact_clients", {
  contactId: varchar("contact_id").notNull().references(() => marketingContacts.id, { onDelete: "cascade" }),
  clientId: varchar("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  pk: primaryKey({ columns: [t.contactId, t.clientId] }),
  byContact: index("idx_mc_clients_contact").on(t.contactId),
  byClient: index("idx_mc_clients_client").on(t.clientId),
}));

export type MarketingContactRegion = typeof marketingContactRegions.$inferSelect;
export type MarketingContactClient = typeof marketingContactClients.$inferSelect;

// Newsletters — issue-based publishing (per-talent or house-wide)
export type NewsletterScope = "house" | "talent";
export type NewsletterStatus = "draft" | "scheduled" | "sent" | "archived";

export const newsletters = pgTable("newsletters", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  scope: varchar("scope", { length: 20 }).notNull().default("house"), // house | talent
  talentId: varchar("talent_id"), // null when scope=house
  title: varchar("title", { length: 255 }).notNull(),
  volume: varchar("volume", { length: 50 }), // e.g. "Vol 12" / "Issue 3"
  subject: varchar("subject", { length: 255 }), // Email subject
  fromName: varchar("from_name", { length: 100 }),
  coverImageUrl: varchar("cover_image_url", { length: 1000 }),
  // Block-based content stored as JSON: [{ type, props }]
  contentBlocks: jsonb("content_blocks").default(sql`'[]'::jsonb`),
  // Audience filter — segment definition (jsonb): { type: "media"|"audience", tags?, markets?, optInChannel?, engagementTier? }
  audienceFilter: jsonb("audience_filter").default(sql`'{}'::jsonb`),
  status: varchar("status", { length: 20 }).notNull().default("draft"), // draft | scheduled | sent | archived
  scheduledAt: timestamp("scheduled_at"),
  sentAt: timestamp("sent_at"),
  // Stats
  recipientCount: integer("recipient_count").notNull().default(0),
  openCount: integer("open_count").notNull().default(0),
  clickCount: integer("click_count").notNull().default(0),
  unsubscribeCount: integer("unsubscribe_count").notNull().default(0),
  bounceCount: integer("bounce_count").notNull().default(0),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
});

export const insertNewsletterSchema = createInsertSchema(newsletters).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  sentAt: true,
  recipientCount: true,
  openCount: true,
  clickCount: true,
  unsubscribeCount: true,
  bounceCount: true,
});
export type InsertNewsletter = z.infer<typeof insertNewsletterSchema>;
export type Newsletter = typeof newsletters.$inferSelect;

// Distribution Hub Campaigns — multi-channel push of an talent/show
export type DistributionStatus = "draft" | "scheduled" | "launched" | "complete" | "archived";
export type DistributionChannelKey =
  | "press_release"
  | "fan_newsletter"
  | "buyer_newsletter"
  | "social_facebook"
  | "social_instagram"
  | "social_twitter"
  | "social_linkedin"
  | "social_tiktok"
  | "buyer_one_sheet"
  | "booking_page_refresh"
  | "youtube"
  | "listings";

export const distributionCampaigns = pgTable("distribution_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  eventDates: text("event_dates").array().default(sql`ARRAY[]::text[]`), // ISO date strings
  markets: text("markets").array().default(sql`ARRAY[]::text[]`),
  // Channel configuration: { [channelKey]: { enabled: bool, status: 'pending'|'queued'|'sent'|'failed', payload?: {...}, sentAt? } }
  channelsConfig: jsonb("channels_config").default(sql`'{}'::jsonb`),
  // Asset references — pulled from talent profile / object storage
  assetRefs: jsonb("asset_refs").default(sql`'{}'::jsonb`), // { photos: [], videos: [], bios: {short,medium,long}, oneSheetUrl, ... }
  utmCampaign: varchar("utm_campaign", { length: 100 }), // Auto-generated for link tagging
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  scheduledAt: timestamp("scheduled_at"),
  launchedAt: timestamp("launched_at"),
  completedAt: timestamp("completed_at"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
});

export const insertDistributionCampaignSchema = createInsertSchema(distributionCampaigns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  launchedAt: true,
  completedAt: true,
});
export type InsertDistributionCampaign = z.infer<typeof insertDistributionCampaignSchema>;
export type DistributionCampaign = typeof distributionCampaigns.$inferSelect;

// ============================================================================
// SOCIAL MEDIA MODULE
// ============================================================================
// Buffer/PaddyPost-quality social media management. Phase 1 ships full UI + persistence
// with platform connections stubbed (waitlist) until per-platform OAuth approvals land.
// All tables are workspace-scoped and enforced at the API layer per replit.md.

export type SocialPlatform =
  | "youtube"
  | "instagram"
  | "facebook"
  | "tiktok"
  | "linkedin"
  | "twitter"
  | "threads"
  | "pinterest"
  | "google_business"
  | "bluesky";

export type SocialAccountStatus = "pending" | "active" | "expired" | "revoked" | "waitlist";
export type SocialPostStatus = "draft" | "pending_approval" | "scheduled" | "publishing" | "published" | "failed" | "archived";
export type SocialTargetStatus = "queued" | "publishing" | "published" | "failed" | "skipped";
export type SocialAssetStorageMode = "drive" | "hosted";
export type SocialAssetTier = "hot" | "nearline" | "coldline" | "purged";

// Connected platform accounts. One row per (workspace, talent?, platform, account).
// talentId is nullable to support workspace-level shared accounts (e.g., the booking agency's own page).
export const socialAccounts = pgTable("social_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id"),
  platform: varchar("platform", { length: 30 }).notNull().$type<SocialPlatform>(),
  displayName: varchar("display_name", { length: 255 }).notNull(),
  externalAccountId: varchar("external_account_id", { length: 255 }), // Platform's own ID (e.g., FB page id)
  externalUsername: varchar("external_username", { length: 255 }),
  profileImageUrl: text("profile_image_url"),
  // OAuth tokens — encrypted at rest in production. NULL while status='waitlist'.
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  tokenExpiry: timestamp("token_expiry"),
  scopes: text("scopes").array().notNull().default(sql`'{}'::text[]`),
  status: varchar("status", { length: 20 }).notNull().default("waitlist").$type<SocialAccountStatus>(),
  connectedByUserId: varchar("connected_by_user_id"),
  lastSyncAt: timestamp("last_sync_at"),
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceIdx: index("social_accounts_workspace_idx").on(t.workspaceId),
  workspaceTalentIdx: index("social_accounts_workspace_talent_idx").on(t.workspaceId, t.talentId),
  // Hot path: inbound Meta (FB/IG) webhook dispatch maps page_id → workspace.
  // Without this index, the lookup is O(N workspaces × accounts) on every
  // inbound DM. The partial filter narrows to active connections only.
  platformExternalIdx: index("social_accounts_platform_external_idx")
    .on(t.platform, t.externalAccountId)
    .where(sql`external_account_id IS NOT NULL`),
}));
export const insertSocialAccountSchema = createInsertSchema(socialAccounts).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertSocialAccount = z.infer<typeof insertSocialAccountSchema>;
export type SocialAccount = typeof socialAccounts.$inferSelect;

// Master post record. One per logical "thing the user is sharing", regardless of how many platforms.
export const socialPosts = pgTable("social_posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id"),
  status: varchar("status", { length: 30 }).notNull().default("draft").$type<SocialPostStatus>(),
  scheduledAt: timestamp("scheduled_at"),
  publishedAt: timestamp("published_at"),
  createdByUserId: varchar("created_by_user_id").notNull(),
  approvedByUserId: varchar("approved_by_user_id"),
  approvedAt: timestamp("approved_at"),
  internalNote: text("internal_note"),
  campaignTag: varchar("campaign_tag", { length: 100 }),
  // For evergreen / recycled posts — points back to the source post.
  recycledFromPostId: varchar("recycled_from_post_id"),
  // Default media assets attached at the post level. Used to seed `mediaAssetIds`
  // on each per-platform target created later (e.g. an AI-generated thumbnail
  // produced inside the composer that should auto-attach to every platform).
  // Targets can still override with their own `mediaAssetIds` list.
  defaultMediaAssetIds: text("default_media_asset_ids").array().notNull().default(sql`'{}'::text[]`),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"), // 7-day soft delete window
}, (t) => ({
  // Calendar/queue: list posts in a workspace by status + scheduled time.
  workspaceStatusScheduledIdx: index("social_posts_ws_status_sched_idx").on(t.workspaceId, t.status, t.scheduledAt),
  // Per-talent post lists.
  workspaceTalentIdx: index("social_posts_ws_talent_idx").on(t.workspaceId, t.talentId),
  // Soft-delete cleanup scans.
  deletedAtIdx: index("social_posts_deleted_at_idx").on(t.deletedAt),
}));
export const insertSocialPostSchema = createInsertSchema(socialPosts).omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true, publishedAt: true });
export type InsertSocialPost = z.infer<typeof insertSocialPostSchema>;
export type SocialPost = typeof socialPosts.$inferSelect;

// Per-platform variant of a post. Captions, hashtags, media selection, and platform-specific options
// (e.g., IG first-comment, TikTok privacy, YouTube category) all live here so each platform can be
// tuned independently.
export const socialPostTargets = pgTable("social_post_targets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  postId: varchar("post_id").notNull().references(() => socialPosts.id, { onDelete: "cascade" }),
  accountId: varchar("account_id").notNull().references(() => socialAccounts.id, { onDelete: "cascade" }),
  platform: varchar("platform", { length: 30 }).notNull().$type<SocialPlatform>(),
  caption: text("caption"),
  firstComment: text("first_comment"), // IG hashtag-stuffing pattern
  hashtags: text("hashtags").array().notNull().default(sql`'{}'::text[]`),
  // Ordered array of social_media_assets ids used for this target.
  mediaAssetIds: text("media_asset_ids").array().notNull().default(sql`'{}'::text[]`),
  // Free-form per-platform options: alt text, link, location, IG carousel order, TikTok privacy, YouTube title/category, etc.
  platformOptions: jsonb("platform_options").default(sql`'{}'::jsonb`),
  publishStatus: varchar("publish_status", { length: 20 }).notNull().default("queued").$type<SocialTargetStatus>(),
  externalPostId: varchar("external_post_id", { length: 255 }), // Platform's id once published
  externalUrl: text("external_url"), // Live URL on the platform
  errorMessage: text("error_message"),
  scheduledAt: timestamp("scheduled_at"), // May differ from parent if staggering
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  // Publisher worker scans by (status, scheduled_at) for due targets, scoped per workspace.
  workspaceStatusScheduledIdx: index("social_post_targets_ws_status_sched_idx").on(t.workspaceId, t.publishStatus, t.scheduledAt),
  // Joining targets to their parent post is the most common access path.
  postIdx: index("social_post_targets_post_idx").on(t.postId),
  // For per-account analytics aggregation.
  accountIdx: index("social_post_targets_account_idx").on(t.accountId),
}));
export const insertSocialPostTargetSchema = createInsertSchema(socialPostTargets).omit({ id: true, createdAt: true, updatedAt: true, publishedAt: true });
export type InsertSocialPostTarget = z.infer<typeof insertSocialPostTargetSchema>;
export type SocialPostTarget = typeof socialPostTargets.$inferSelect;

// Media library — supports both Google Drive references (free) and hosted object storage (paid).
// `tier` is managed by the storage tiering service and lifecycles hot → nearline → coldline → purged
// based on age and last access. See replit.md "Storage Backbone".
export const socialMediaAssets = pgTable("social_media_assets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  ownerUserId: varchar("owner_user_id").notNull(), // Whoever uploaded it (internal or external)
  storageMode: varchar("storage_mode", { length: 20 }).notNull().$type<SocialAssetStorageMode>(),
  // For storageMode='drive':
  driveFileId: varchar("drive_file_id", { length: 255 }),
  driveFolderId: varchar("drive_folder_id", { length: 255 }),
  driveOwnerEmail: varchar("drive_owner_email", { length: 255 }),
  // For storageMode='hosted':
  objectKey: text("object_key"),
  // Common metadata (best-effort populated for both modes):
  fileName: varchar("file_name", { length: 500 }).notNull(),
  mimeType: varchar("mime_type", { length: 100 }).notNull(),
  sizeBytes: integer("size_bytes").notNull().default(0),
  width: integer("width"),
  height: integer("height"),
  durationSeconds: integer("duration_seconds"),
  altText: text("alt_text"),
  thumbnailUrl: text("thumbnail_url"),
  // Lifecycle/tiering state:
  tier: varchar("tier", { length: 20 }).notNull().default("hot").$type<SocialAssetTier>(),
  lastAccessedAt: timestamp("last_accessed_at").defaultNow(),
  purgeAfter: timestamp("purge_after"), // Auto-purge target; null = keep indefinitely
  // For grouping/search:
  tags: text("tags").array().notNull().default(sql`'{}'::text[]`),
  // ── Wave 9 — AI image generation provenance ────────────────────────
  // True when this asset was produced by the AI thumbnail generator. The
  // library UI shows an "AI" badge and surfaces the prompt so users can
  // re-run / remix.
  aiGenerated: boolean("ai_generated").notNull().default(false),
  // Provider key — 'nano_banana' (Gemini 2.5 Flash Image) or 'runway' (Gen-4 Image).
  aiProvider: varchar("ai_provider", { length: 32 }),
  // Provider-specific model id used (e.g. 'gemini-2.5-flash-image-preview',
  // 'gen4_image'). Stored separately from `aiProvider` so we can tell apart
  // model upgrades within a provider for analytics later.
  aiModelKey: varchar("ai_model_key", { length: 64 }),
  // Original prompt the user typed. Truncated server-side to 4 KB.
  aiPrompt: text("ai_prompt"),
  // '1:1' | '4:5' | '9:16' | '16:9'. Free-form so future ratios just work.
  aiAspectRatio: varchar("ai_aspect_ratio", { length: 16 }),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  // Media library lists, scoped per workspace.
  workspaceUploadedIdx: index("social_media_assets_ws_uploaded_idx").on(t.workspaceId, t.uploadedAt),
  // Lifecycle/tiering job: find candidates by tier + purgeAfter.
  workspaceTierPurgeIdx: index("social_media_assets_ws_tier_purge_idx").on(t.workspaceId, t.tier, t.purgeAfter),
  // Storage-mode-specific reports (e.g., "all hosted assets in this workspace").
  workspaceModeIdx: index("social_media_assets_ws_mode_idx").on(t.workspaceId, t.storageMode),
}));
export const insertSocialMediaAssetSchema = createInsertSchema(socialMediaAssets).omit({ id: true, createdAt: true, updatedAt: true, uploadedAt: true });
export type InsertSocialMediaAsset = z.infer<typeof insertSocialMediaAssetSchema>;
export type SocialMediaAsset = typeof socialMediaAssets.$inferSelect;

// Comments / change requests on draft posts (used by Commenter access level + general review workflow).
export const socialPostComments = pgTable("social_post_comments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  postId: varchar("post_id").notNull().references(() => socialPosts.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  body: text("body").notNull(),
  resolvedAt: timestamp("resolved_at"),
  resolvedByUserId: varchar("resolved_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  postIdx: index("social_post_comments_post_idx").on(t.postId, t.createdAt),
}));
export const insertSocialPostCommentSchema = createInsertSchema(socialPostComments).omit({ id: true, createdAt: true });
export type InsertSocialPostComment = z.infer<typeof insertSocialPostCommentSchema>;
export type SocialPostComment = typeof socialPostComments.$inferSelect;

// Per-target analytics snapshots. Multiple rows per target across time so we can chart trends.
export const socialPostAnalytics = pgTable("social_post_analytics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  postId: varchar("post_id").notNull().references(() => socialPosts.id, { onDelete: "cascade" }),
  targetId: varchar("target_id").notNull().references(() => socialPostTargets.id, { onDelete: "cascade" }),
  platform: varchar("platform", { length: 30 }).notNull().$type<SocialPlatform>(),
  fetchedAt: timestamp("fetched_at").notNull().defaultNow(),
  impressions: integer("impressions").notNull().default(0),
  reach: integer("reach").notNull().default(0),
  engagement: integer("engagement").notNull().default(0),
  likes: integer("likes").notNull().default(0),
  comments: integer("comments").notNull().default(0),
  shares: integer("shares").notNull().default(0),
  saves: integer("saves").notNull().default(0),
  clicks: integer("clicks").notNull().default(0),
  videoViews: integer("video_views").notNull().default(0),
  watchTimeSeconds: integer("watch_time_seconds").notNull().default(0),
  rawPayload: jsonb("raw_payload").default(sql`'{}'::jsonb`),
}, (t) => ({
  // Trend chart for a single target.
  targetFetchedIdx: index("social_post_analytics_target_fetched_idx").on(t.targetId, t.fetchedAt),
  // Workspace-wide reporting windows.
  workspaceFetchedIdx: index("social_post_analytics_ws_fetched_idx").on(t.workspaceId, t.fetchedAt),
}));
export const insertSocialPostAnalyticsSchema = createInsertSchema(socialPostAnalytics).omit({ id: true });
export type InsertSocialPostAnalytics = z.infer<typeof insertSocialPostAnalyticsSchema>;
export type SocialPostAnalyticsRow = typeof socialPostAnalytics.$inferSelect;

// Audit trail of every action on a social post (who/what/when). Used for accountability across
// internal + external users alike. External-collaborator actions ALSO write to collaborator_audit_log.
//
// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `created_at`. No auto-pruner. See
// `docs/security/audit-log-policy.md`.
export const socialPostAudit = pgTable("social_post_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // ON DELETE SET NULL so the audit row outlives the parent post (task #607).
  postId: varchar("post_id").references(() => socialPosts.id, { onDelete: "set null" }),
  userId: varchar("user_id").notNull(),
  action: varchar("action", { length: 50 }).notNull(), // 'created' | 'edited' | 'submitted_for_approval' | 'approved' | 'scheduled' | 'published' | ...
  details: jsonb("details").default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  postIdx: index("social_post_audit_post_idx").on(t.postId, t.createdAt),
  workspaceIdx: index("social_post_audit_ws_idx").on(t.workspaceId, t.createdAt),
}));
export const insertSocialPostAuditSchema = createInsertSchema(socialPostAudit).omit({ id: true, createdAt: true });
export type InsertSocialPostAudit = z.infer<typeof insertSocialPostAuditSchema>;
export type SocialPostAuditRow = typeof socialPostAudit.$inferSelect;

// Per-workspace storage usage snapshot. Mirrors the lead_credits two-bucket pattern: planGrant = monthly
// included by tier (resets/refreshes when tier changes), purchasedBytes = Stripe one-time pack add-ons
// that roll over indefinitely. usedBytes is the running sum from social_storage_ledger.
export const socialStorageQuota = pgTable("social_storage_quota", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().unique().references(() => workspaces.id, { onDelete: "cascade" }),
  planGrantBytes: integer("plan_grant_bytes").notNull().default(0),
  purchasedBytes: integer("purchased_bytes").notNull().default(0),
  usedBytes: integer("used_bytes").notNull().default(0),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const insertSocialStorageQuotaSchema = createInsertSchema(socialStorageQuota).omit({ id: true, updatedAt: true });
export type InsertSocialStorageQuota = z.infer<typeof insertSocialStorageQuotaSchema>;
export type SocialStorageQuota = typeof socialStorageQuota.$inferSelect;

// Append-only ledger of every storage event. Used both for usage accounting and as an audit trail
// for the lifecycle/tiering service.
//
// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `created_at`. No auto-pruner. See
// `docs/security/audit-log-policy.md`.
export const socialStorageLedger = pgTable("social_storage_ledger", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  assetId: varchar("asset_id"), // May be null after asset is purged
  event: varchar("event", { length: 30 }).notNull(), // 'upload' | 'purge' | 'tier_change' | 'overage_warn' | 'manual_adjust'
  bytesDelta: integer("bytes_delta").notNull().default(0), // Signed: +N on upload, -N on purge
  tierFrom: varchar("tier_from", { length: 20 }),
  tierTo: varchar("tier_to", { length: 20 }),
  reason: text("reason"),
  performedByUserId: varchar("performed_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceCreatedIdx: index("social_storage_ledger_ws_created_idx").on(t.workspaceId, t.createdAt),
  assetIdx: index("social_storage_ledger_asset_idx").on(t.assetId),
}));
export const insertSocialStorageLedgerSchema = createInsertSchema(socialStorageLedger).omit({ id: true, createdAt: true });
export type InsertSocialStorageLedger = z.infer<typeof insertSocialStorageLedgerSchema>;
export type SocialStorageLedgerRow = typeof socialStorageLedger.$inferSelect;

// Inbound webhook event log. Every callback we receive from Meta/TikTok/YouTube is recorded here
// raw (signature, body, headers) BEFORE we trust anything in it. We process async after the
// initial 200 OK so the platforms don't think we're slow + retry-storm us.
export type SocialWebhookStatus = "received" | "verified" | "processed" | "rejected" | "error";

export const socialWebhookEvents = pgTable("social_webhook_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platform: varchar("platform", { length: 30 }).notNull().$type<SocialPlatform>(),
  // workspaceId is null until we figure out which workspace this event belongs to (e.g., by FB page id lookup)
  workspaceId: varchar("workspace_id"),
  eventType: varchar("event_type", { length: 100 }), // e.g., 'page_post', 'instagram_comment', 'video_status'
  externalEventId: varchar("external_event_id", { length: 255 }), // platform's own event id if available — used for idempotency
  signatureHeader: text("signature_header"), // raw signature header for audit
  signatureValid: boolean("signature_valid").notNull().default(false),
  rawHeaders: jsonb("raw_headers").default(sql`'{}'::jsonb`),
  rawBody: text("raw_body"), // stored as text so we can re-verify signatures later if the secret rotates
  status: varchar("status", { length: 20 }).notNull().default("received").$type<SocialWebhookStatus>(),
  errorMessage: text("error_message"),
  processedAt: timestamp("processed_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  platformCreatedIdx: index("social_webhook_events_plat_created_idx").on(t.platform, t.createdAt),
  workspaceCreatedIdx: index("social_webhook_events_ws_created_idx").on(t.workspaceId, t.createdAt),
  // Idempotency lookup: same platform + external_event_id should never be processed twice.
  externalIdIdx: index("social_webhook_events_ext_id_idx").on(t.platform, t.externalEventId),
}));
export const insertSocialWebhookEventSchema = createInsertSchema(socialWebhookEvents).omit({ id: true, createdAt: true });
export type InsertSocialWebhookEvent = z.infer<typeof insertSocialWebhookEventSchema>;
export type SocialWebhookEvent = typeof socialWebhookEvents.$inferSelect;

// Data-deletion compliance log. Meta REQUIRES a deletion callback URL; users can also submit
// requests directly via the public /data-deletion page. We give them a confirmation_code they
// can check against /api/data-deletion/status/:code.
export type DataDeletionSource = "meta_callback" | "tiktok_callback" | "user_request" | "internal_admin";
export type DataDeletionStatus = "received" | "in_progress" | "completed" | "rejected";

export const socialDataDeletionRequests = pgTable("social_data_deletion_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Public-facing short code we give the requester so they can check status without an account.
  confirmationCode: varchar("confirmation_code", { length: 32 }).notNull().unique(),
  source: varchar("source", { length: 30 }).notNull().$type<DataDeletionSource>(),
  platform: varchar("platform", { length: 30 }).$type<SocialPlatform>(), // null for non-platform user requests
  // External user identifier from the platform (Meta/TikTok user_id) — used to find their data.
  externalUserId: varchar("external_user_id", { length: 255 }),
  // Internal user / workspace if known.
  workspaceId: varchar("workspace_id"),
  userId: varchar("user_id"),
  requesterEmail: varchar("requester_email", { length: 255 }),
  requesterNotes: text("requester_notes"),
  status: varchar("status", { length: 20 }).notNull().default("received").$type<DataDeletionStatus>(),
  affectedAccountIds: text("affected_account_ids").array().notNull().default(sql`'{}'::text[]`),
  affectedPostIds: text("affected_post_ids").array().notNull().default(sql`'{}'::text[]`),
  affectedAssetIds: text("affected_asset_ids").array().notNull().default(sql`'{}'::text[]`),
  completedAt: timestamp("completed_at"),
  rejectedReason: text("rejected_reason"),
  rawCallbackPayload: jsonb("raw_callback_payload").default(sql`'{}'::jsonb`), // for Meta signed_request decode etc.
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  confirmationCodeIdx: index("social_data_deletion_code_idx").on(t.confirmationCode),
  externalUserIdx: index("social_data_deletion_ext_user_idx").on(t.platform, t.externalUserId),
  workspaceIdx: index("social_data_deletion_ws_idx").on(t.workspaceId),
}));
export const insertSocialDataDeletionRequestSchema = createInsertSchema(socialDataDeletionRequests).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertSocialDataDeletionRequest = z.infer<typeof insertSocialDataDeletionRequestSchema>;
export type SocialDataDeletionRequest = typeof socialDataDeletionRequests.$inferSelect;

// ============================================================================
// MODULE GRANTS — external collaborator access (Google Drive-style sharing)
// ============================================================================
// Lets a workspace invite outside users (e.g., a freelance video editor) to a specific module
// (currently 'social_media') scoped to specific talent, at one of 5 access levels.
// External users (users.userType='external') hold ONLY module_grants — they have zero access to
// the rest of the CRM. Internal users (users.userType='internal') use the standard role+permission
// system; module_grants do not apply to them.

export type ModuleGrantModule = "social_media"; // Extensible: 'lead_gen' | 'invoices' | ... in future
export type ModuleGrantAccessLevel = "viewer" | "commenter" | "contributor" | "editor" | "manager";
export type ModuleGrantStatus = "pending" | "active" | "revoked" | "expired";

export const moduleGrants = pgTable("module_grants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // userId is null while status='pending' (until invite is accepted); set to users.id afterward.
  userId: varchar("user_id"),
  inviteEmail: varchar("invite_email", { length: 255 }).notNull(),
  inviteToken: varchar("invite_token", { length: 80 }).notNull().unique(),
  module: varchar("module", { length: 50 }).notNull().$type<ModuleGrantModule>(),
  accessLevel: varchar("access_level", { length: 20 }).notNull().$type<ModuleGrantAccessLevel>(),
  // Talent scope: { mode: 'all' } OR { mode: 'specific', ids: ['ent-id-1', 'ent-id-2'] }
  // Lists/queries inside the module are auto-filtered by this scope at the API layer.
  talentScope: jsonb("talent_scope").notNull().default(sql`'{"mode":"all"}'::jsonb`),
  // Optional fine-grained permission overrides on top of the access-level defaults.
  // Schema: { canPublish?: bool, canDeleteMedia?: bool, canInviteOthers?: bool, ... }
  permissions: jsonb("permissions").default(sql`'{}'::jsonb`),
  expiresAt: timestamp("expires_at"), // null = no expiration
  status: varchar("status", { length: 20 }).notNull().default("pending").$type<ModuleGrantStatus>(),
  invitedByUserId: varchar("invited_by_user_id").notNull(),
  invitedAt: timestamp("invited_at").defaultNow(),
  acceptedAt: timestamp("accepted_at"),
  lastAccessAt: timestamp("last_access_at"),
  revokedAt: timestamp("revoked_at"),
  revokedByUserId: varchar("revoked_by_user_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  // Workspace admin views: list active grants per module.
  workspaceModuleStatusIdx: index("module_grants_ws_module_status_idx").on(t.workspaceId, t.module, t.status),
  // Per-user grant lookup at login (resolve which workspaces an external user can access).
  userIdx: index("module_grants_user_idx").on(t.userId),
  // Expiration sweeper.
  expiresAtIdx: index("module_grants_expires_at_idx").on(t.expiresAt),
  // Invite-acceptance lookup uses inviteToken (already unique) so no extra index needed there.
}));
export const insertModuleGrantSchema = createInsertSchema(moduleGrants).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  acceptedAt: true,
  lastAccessAt: true,
  revokedAt: true,
  revokedByUserId: true,
});
export type InsertModuleGrant = z.infer<typeof insertModuleGrantSchema>;
export type ModuleGrant = typeof moduleGrants.$inferSelect;

// Append-only audit of every action by external collaborators. Workspace admins see this
// in Social Media → Settings → Collaborator Activity. Distinct from socialPostAudit so it
// can capture cross-module activity (uploads, settings changes, etc.) in one place.
//
// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `created_at`. No auto-pruner. See
// `docs/security/audit-log-policy.md`.
export const collaboratorAuditLog = pgTable("collaborator_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  grantId: varchar("grant_id").references(() => moduleGrants.id, { onDelete: "set null" }),
  module: varchar("module", { length: 50 }).notNull(),
  action: varchar("action", { length: 80 }).notNull(),
  resourceType: varchar("resource_type", { length: 50 }), // 'post' | 'asset' | 'comment' | ...
  resourceId: varchar("resource_id"),
  ipAddress: varchar("ip_address", { length: 64 }),
  userAgent: text("user_agent"),
  details: jsonb("details").default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceCreatedIdx: index("collaborator_audit_log_ws_created_idx").on(t.workspaceId, t.createdAt),
  userIdx: index("collaborator_audit_log_user_idx").on(t.userId, t.createdAt),
  grantIdx: index("collaborator_audit_log_grant_idx").on(t.grantId),
}));
export const insertCollaboratorAuditLogSchema = createInsertSchema(collaboratorAuditLog).omit({ id: true, createdAt: true });
export type InsertCollaboratorAuditLog = z.infer<typeof insertCollaboratorAuditLogSchema>;
export type CollaboratorAuditLogRow = typeof collaboratorAuditLog.$inferSelect;

// ============================================================================
// PLAN TIER FEATURES — super-admin-controlled per-tier capability flags
// ============================================================================
// One row per tier. Workspaces inherit these defaults; per-workspace overrides live on the
// workspaces table (e.g., socialMediaStorageGbLimit). Editable in the super admin panel.

export type CollaboratorWhitelabelMode = "never" | "optional" | "always";

export const planTierFeatures = pgTable("plan_tier_features", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tier: varchar("tier", { length: 20 }).notNull().unique().$type<WorkspaceTier>(),
  // Social Media module
  socialStorageGbIncluded: integer("social_storage_gb_included").notNull().default(0),
  socialMaxCollaborators: integer("social_max_collaborators").notNull().default(0),
  socialAllowsCollaboratorWhitelabel: varchar("social_allows_collaborator_whitelabel", { length: 20 })
    .notNull()
    .default("never")
    .$type<CollaboratorWhitelabelMode>(),
  // Headroom for future module-specific tier flags (jsonb so we don't migrate every time we add one).
  extraConfig: jsonb("extra_config").default(sql`'{}'::jsonb`),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const insertPlanTierFeatureSchema = createInsertSchema(planTierFeatures).omit({ id: true, updatedAt: true });
export type InsertPlanTierFeature = z.infer<typeof insertPlanTierFeatureSchema>;
export type PlanTierFeature = typeof planTierFeatures.$inferSelect;

// ============================================================================
// KNOWLEDGE CENTER
// In-app docs, public help/security/trust/policies mirrors, and DRAFT policies
// ============================================================================
export type KnowledgeSection =
  | "getting-started"
  | "feature-guides"
  | "security-training"
  | "compliance-policies"
  // Internal-only operational runbooks. Articles in this section are admin-only
  // (always seeded with isPublic=false) and intentionally not listed in the
  // Knowledge sidebar — admins reach them by direct slug link from the README,
  // replit.md, or the security-training tracker reminder. See
  // server/seed-incident-response.ts. Articles in this section are gated
  // by `articleAppliesToUserRole` in server/security-training/routes.ts so
  // mandatory acks only nag admins, never non-admins who 404 on the row.
  | "incident-response";

// Subset of KnowledgeSection that is allowed to surface on the public mirror
// (`/help`, `/security`, `/trust`, `/policies`) and in OG / SEO surfaces.
// Public-only label/theme maps should be typed as `Record<PublicKnowledgeSection, …>`
// so admin-only operational sections (`incident-response`) are excluded at the
// type level — a forgotten label can never silently fall through to a public
// surface.
export type PublicKnowledgeSection = Exclude<KnowledgeSection, "incident-response">;

export const knowledgeArticles = pgTable("knowledge_articles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  section: varchar("section", { length: 40 }).notNull(),
  slug: varchar("slug", { length: 200 }).notNull(),
  title: varchar("title", { length: 255 }).notNull(),
  summary: text("summary"),
  bodyMarkdown: text("body_markdown").notNull().default(""),
  videoUrl: varchar("video_url", { length: 1000 }),
  // Optional per-article preview image used for Open Graph / Twitter Card
  // social-share unfurls on /help/<slug>, /security/<slug>, /trust/<slug>,
  // /policies/<slug>. Stored as a `/objects/uploads/<id>` path that is
  // streamed publicly via /api/public/knowledge/:slug/og-image. When unset,
  // the OG image route falls back to the workspace branding logo.
  heroImageUrl: varchar("hero_image_url", { length: 1000 }),
  // Original (uncropped) source image the admin uploaded, retained alongside
  // the rendered `heroImageUrl` so the article editor can re-open the crop
  // dialog later and let the admin nudge the framing without re-uploading
  // the source. Stored as a `/objects/uploads/<id>` path on the same
  // public knowledge-hero ACL marker as `heroImageUrl` (so the admin's
  // editor `<img>` can load it through the standard authenticated objects
  // route). Older articles whose hero was uploaded before this column
  // existed will leave this null and the editor falls back to the
  // legacy "no source available — re-upload to recrop" behavior.
  heroSourceImageUrl: varchar("hero_source_image_url", { length: 1000 }),
  // The crop rectangle (in source-image pixel coordinates) the admin
  // chose in the crop dialog the last time the hero was rendered.
  // Shape: `{ x, y, width, height }` matching react-easy-crop's
  // `croppedAreaPixels`. Null = legacy center-crop or no source available.
  heroCropArea: jsonb("hero_crop_area"),
  tags: text("tags").array().default(sql`ARRAY[]::text[]`),
  // Public mirror flag — when true, article is visible at /help, /security,
  // /trust or /policies depending on its section
  isPublic: boolean("is_public").notNull().default(false),
  isPublished: boolean("is_published").notNull().default(true),
  // Optional draft flag — surfaces a "DRAFT" banner on the article view.
  // Useful for compliance policies awaiting review by counsel.
  isDraft: boolean("is_draft").notNull().default(false),
  // Mandatory-reading flag. When true, the article is included in the
  // per-user training-status report at /admin (Training Status panel) and
  // any logged-in user who hasn't acknowledged the article's current
  // `updatedAt` version is treated as overdue/never-read by the weekly
  // reminder scheduler. Independent from workspace `securityTrainingRequiredSlugs`
  // — that list controls the hard "you cannot use the app until you ack"
  // gate; `mandatory` controls long-term annual-attestation tracking.
  mandatory: boolean("mandatory").notNull().default(false),
  orderIndex: integer("order_index").notNull().default(0),
  createdBy: varchar("created_by"),
  publishedAt: timestamp("published_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  // Generated tsvector column populated at runtime by
  // ensureKnowledgeSearchIndex() in server/ensure-knowledge-search-index.ts.
  // Declared here only so Drizzle doesn't try to drop it on push; search
  // queries continue to reference it via raw SQL.
  searchVector: tsvector("search_vector"),
}, (t) => ({
  workspaceSectionIdx: index("knowledge_articles_ws_section_idx").on(t.workspaceId, t.section),
  workspaceSlugIdx: index("knowledge_articles_ws_slug_idx").on(t.workspaceId, t.slug),
  publicSectionIdx: index("knowledge_articles_public_section_idx").on(t.isPublic, t.section),
}));

// ============================================================================
// SALES REP GAMIFICATION
// ============================================================================
// Workspace-level gamification settings (one row per workspace)
export const gamificationSettings = pgTable("gamification_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().unique().references(() => workspaces.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(false),
  defaultTheme: varchar("default_theme", { length: 30 }).notNull().default("equalizer"),
  soundEnabled: boolean("sound_enabled").notNull().default(true),
  // Quality guards
  callQualityGuard: boolean("call_quality_guard").notNull().default(true),
  emailQualityGuard: boolean("email_quality_guard").notNull().default(true),
  bookingConfirmedOnly: boolean("booking_confirmed_only").notNull().default(true),
  // Default goals shape: { activity:{daily,weekly,monthly}, pipeline:{...}, wins:{...}, revenueBooked:{...} }
  defaultGoals: jsonb("default_goals").notNull().default(
    sql`'{"activity":{"daily":20,"weekly":100,"monthly":400},"pipeline":{"daily":3,"weekly":15,"monthly":60},"wins":{"daily":1,"weekly":5,"monthly":20},"revenueBooked":{"daily":500,"weekly":2500,"monthly":10000}}'::jsonb`
  ),
  stretchFactor: decimal("stretch_factor", { precision: 4, scale: 2 }).notNull().default("1.15"),
  tenureRampEnabled: boolean("tenure_ramp_enabled").notNull().default(true),
  summaryCutoffHour: integer("summary_cutoff_hour").notNull().default(18),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertGamificationSettingsSchema = createInsertSchema(gamificationSettings).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertGamificationSettings = z.infer<typeof insertGamificationSettingsSchema>;
export type GamificationSettings = typeof gamificationSettings.$inferSelect;

// Per-rep gamification preferences and per-rep goal overrides
export const repGamificationPrefs = pgTable("rep_gamification_prefs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  widgetEnabled: boolean("widget_enabled").notNull().default(true),
  // null = inherit workspace default theme
  theme: varchar("theme", { length: 30 }),
  reduceMotion: boolean("reduce_motion").notNull().default(false),
  silentMode: boolean("silent_mode").notNull().default(false),
  endOfDaySummaryEnabled: boolean("end_of_day_summary_enabled").notNull().default(true),
  // Opt-in: email a recap of yesterday's Daily Drive at the workspace cutoff hour each day.
  endOfDayRecapEmailEnabled: boolean("end_of_day_recap_email_enabled").notNull().default(false),
  // null = inherit workspace defaults; otherwise same shape as defaultGoals
  goalOverrides: jsonb("goal_overrides"),
  // For tenure ramp (50% < 30d, 75% 30-60d, 100% after). null = treat as fully tenured.
  tenureStartDate: timestamp("tenure_start_date"),
  // YYYY-MM-DD of the most-recent end-of-day summary the rep dismissed
  lastSummaryDismissedFor: varchar("last_summary_dismissed_for", { length: 20 }),
  // YYYY-MM-DD of the most-recent end-of-day recap email we sent — used by the
  // scheduler to avoid duplicate sends within the same local day.
  lastRecapEmailSentFor: varchar("last_recap_email_sent_for", { length: 20 }),
  // Opt-in: email a 7-day Daily Drive digest each Monday morning at the
  // workspace `summaryCutoffHour`. Independent of the daily recap toggle so
  // reps can pick the cadence that fits them.
  weeklyRecapEmailEnabled: boolean("weekly_recap_email_enabled").notNull().default(false),
  // ISO-week string ("YYYY-Www", e.g. "2026-W17") of the most-recent week
  // we sent a weekly digest for. The scheduler uses this as the per-rep
  // idempotency key so the 15-minute cron tick can't duplicate sends.
  lastWeeklyRecapEmailSentFor: varchar("last_weekly_recap_email_sent_for", { length: 20 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertKnowledgeArticleSchema = createInsertSchema(knowledgeArticles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  publishedAt: true,
});
export type InsertKnowledgeArticle = z.infer<typeof insertKnowledgeArticleSchema>;
export type KnowledgeArticle = typeof knowledgeArticles.$inferSelect;

// Shape of a single "Did you mean…?" suggestion returned by the knowledge
// search API. `term` is the search term to apply when the chip is clicked
// (the closest-matching word from a title or tag). `articleTitle` is the
// title of an article that contains that term, so the chip can render a
// friendlier label like "How permissions and region access work" instead
// of the bare word "permissions". `articleTitle` is null only in the rare
// case the matched candidate had no associated title (defensive — every
// candidate is sourced from an article row today).
export type KnowledgeSuggestion = {
  term: string;
  articleTitle: string | null;
};

// Telemetry on "Did you mean…?" suggestion-chip clicks. Helps the team
// know which fuzzy suggestions actually drive recovery (vs. which queries
// dead-end with the user still typing). Stored per-workspace so we can
// surface "top no-result queries that converted into a click" in admin
// reporting later. Lightweight — one row per chip click — and deliberately
// unindexed beyond (workspace, clickedAt) since the volume is low.
export const knowledgeSuggestionClicks = pgTable("knowledge_suggestion_clicks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Null for the public mirror (/help, /security, /trust, /policies) since
  // the public corpus isn't workspace-scoped — the click is still useful
  // for "which fuzzy suggestions are working" analytics globally.
  workspaceId: varchar("workspace_id"),
  // The user who clicked the chip. Null for unauthenticated public-mirror
  // clicks since we don't have a session for them.
  userId: varchar("user_id"),
  // The original search term that produced the suggestions list (i.e. the
  // typo / no-match query the user typed). Always populated.
  originalQuery: varchar("original_query", { length: 500 }).notNull(),
  // The suggested term the user clicked on. Always populated.
  acceptedTerm: varchar("accepted_term", { length: 500 }).notNull(),
  // The article title displayed alongside the chip (so we can later see
  // whether the user clicked the term-level chip or an article-level
  // chip). Optional — chips without an associated article store null.
  acceptedArticleTitle: varchar("accepted_article_title", { length: 500 }),
  clickedAt: timestamp("clicked_at").notNull().defaultNow(),
}, (t) => ({
  workspaceClickedIdx: index("knowledge_suggestion_clicks_ws_clicked_idx").on(t.workspaceId, t.clickedAt),
}));

export const insertKnowledgeSuggestionClickSchema = createInsertSchema(knowledgeSuggestionClicks).omit({
  id: true, clickedAt: true,
});
export type InsertKnowledgeSuggestionClick = z.infer<typeof insertKnowledgeSuggestionClickSchema>;
export type KnowledgeSuggestionClick = typeof knowledgeSuggestionClicks.$inferSelect;

// Admin-controlled draft state for legal documents (terms, privacy).
// The DRAFT banner on /terms and /privacy is driven by these rows — admins
// flip isDraft to false once counsel has reviewed.
export const legalDocumentStatus = pgTable("legal_document_status", {
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  documentSlug: varchar("document_slug", { length: 50 }).notNull(),
  isDraft: boolean("is_draft").notNull().default(true),
  lastReviewedAt: timestamp("last_reviewed_at"),
  lastReviewedBy: varchar("last_reviewed_by"),
  bannerNote: text("banner_note"),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  pk: primaryKey({ columns: [table.workspaceId, table.documentSlug] }),
}));
export const insertLegalDocumentStatusSchema = createInsertSchema(legalDocumentStatus).omit({
  updatedAt: true,
});
export type InsertLegalDocumentStatus = z.infer<typeof insertLegalDocumentStatusSchema>;
export type LegalDocumentStatus = typeof legalDocumentStatus.$inferSelect;

// Per-user acknowledgments of required Security Training articles. Append-only — one row per
// (workspace, user, article slug) the first time the user clicks "I have read and understood
// this." A new acknowledgment is recorded on top of any prior one if the article slug appears
// again (we don't overwrite — admins can audit history if needed).
//
// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `acknowledged_at`. No auto-pruner. See
// `docs/security/audit-log-policy.md`.
export const userSecurityTrainingAcks = pgTable("user_security_training_acks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  articleSlug: varchar("article_slug", { length: 200 }).notNull(),
  acknowledgedAt: timestamp("acknowledged_at").notNull().defaultNow(),
  // Snapshot of the article's `updatedAt` at the time of acknowledgement.
  // The training-status report uses this to decide whether the user has
  // attested to the *current* version of the article — if the article is
  // edited after acknowledgement, the row is treated as overdue until
  // the user re-acknowledges. NULL on legacy pre-versioning rows; the
  // status helper falls back to comparing acknowledgedAt vs updatedAt.
  articleVersion: timestamp("article_version"),
}, (t) => ({
  byUserIdx: index("user_security_training_acks_by_user_idx").on(t.workspaceId, t.userId),
  byArticleIdx: index("user_security_training_acks_by_article_idx").on(t.workspaceId, t.articleSlug),
}));
export const insertUserSecurityTrainingAckSchema = createInsertSchema(userSecurityTrainingAcks).omit({
  id: true, acknowledgedAt: true,
});
export type InsertUserSecurityTrainingAck = z.infer<typeof insertUserSecurityTrainingAckSchema>;
export type UserSecurityTrainingAck = typeof userSecurityTrainingAcks.$inferSelect;

// Per-(workspace, user) idempotency tracker for the Monday-9am security
// training reminder cron (task #392). The generic notification dedup window
// in `server/lib/notify.ts` only collapses identical (entity, category)
// inserts within the last 5 minutes, so a manual re-run, a cron retry, or
// any second invocation of `runWorkspaceReminders` more than 5 minutes
// later in the same week would re-notify every overdue user. Storing the
// ISO-week label of the most-recent reminder (mirrors the daily-drive
// digest's `rep_gamification_prefs.lastWeeklyRecapEmailSentFor` pattern)
// gives us a per-week idempotency key the scheduler consults before each
// `notifyUser` call.
export const securityTrainingReminderLog = pgTable("security_training_reminder_log", {
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  // ISO-week string ("YYYY-Www", e.g. "2026-W18") of the most-recent week
  // the scheduler sent this user a training-overdue reminder for.
  lastRemindedIsoWeek: varchar("last_reminded_iso_week", { length: 20 }).notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  pk: primaryKey({ columns: [t.workspaceId, t.userId] }),
}));
export type SecurityTrainingReminderLog = typeof securityTrainingReminderLog.$inferSelect;

export const insertRepGamificationPrefsSchema = createInsertSchema(repGamificationPrefs).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertRepGamificationPrefs = z.infer<typeof insertRepGamificationPrefsSchema>;
export type RepGamificationPrefs = typeof repGamificationPrefs.$inferSelect;

// Append-only log of every recap email send attempt the scheduler makes. Each
// row captures the workspace, recipient, recap-day being summarized, the wall-
// clock time of the attempt, and a status. Reps see their own last-7-days of
// rows on the Daily Drive settings card; admins see a workspace-wide table on
// the gamification admin page (filterable by user + status). This is what
// makes the scheduler observable without grepping server logs.
export type GamificationRecapEmailStatus = "success" | "skipped" | "error";

export type GamificationRecapEmailTrigger = "scheduled" | "test" | "manual_resend";

// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `sent_at`. Pruned by `pruneOldRecapEmailLogs` (env override
// clamped up to 365). See `docs/security/audit-log-policy.md`.
export const gamificationRecapEmailLog = pgTable("gamification_recap_email_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  // YYYY-MM-DD in workspace local timezone of the day being summarized
  // (i.e. "yesterday" relative to when the scheduler fired). Stored as a
  // string to match how the scheduler keys idempotency elsewhere.
  recapDate: varchar("recap_date", { length: 20 }).notNull(),
  sentAt: timestamp("sent_at").notNull().defaultNow(),
  status: varchar("status", { length: 20 }).notNull().$type<GamificationRecapEmailStatus>(),
  // Populated for status="error" (exception message) and status="skipped"
  // (e.g. "no-user-email", "no-snapshot"). Null on success.
  errorMessage: text("error_message"),
  // What triggered this send attempt — "scheduled" (the cron-driven daily
  // sweep — the default and the historical behavior), "test" (an admin
  // clicked the "Send test recap" button to preview the template), or
  // "manual_resend" (an admin clicked "Resend" on a previously failed log
  // row from the recap-history table). Defaulting to "scheduled" keeps
  // pre-existing rows attributed correctly without backfill.
  trigger: varchar("trigger", { length: 20 }).notNull().default("scheduled").$type<GamificationRecapEmailTrigger>(),
  // For trigger="manual_resend", points back at the original failed log row
  // we were retrying. Lets the admin UI render the resend chain ("retried
  // 2024-04-01 11:32 manual resend by alice@…") inline next to the original
  // failure without a join. Null for scheduled and test sends.
  resendOfLogId: varchar("resend_of_log_id"),
  // For trigger="test" or trigger="manual_resend", the user id of the admin
  // who triggered the send. Null for scheduled sends (those are attributed
  // to the cron job itself). Used by the admin recap-history UI to show
  // "test by alice@…" / "resend by bob@…" attribution rows.
  triggeredByUserId: varchar("triggered_by_user_id"),
}, (t) => ({
  workspaceUserIdx: index("gamification_recap_email_log_ws_user_idx").on(t.workspaceId, t.userId, t.sentAt),
  workspaceSentIdx: index("gamification_recap_email_log_ws_sent_idx").on(t.workspaceId, t.sentAt),
}));

export const insertGamificationRecapEmailLogSchema = createInsertSchema(gamificationRecapEmailLog).omit({
  id: true, sentAt: true,
});
export type InsertGamificationRecapEmailLog = z.infer<typeof insertGamificationRecapEmailLogSchema>;
export type GamificationRecapEmailLog = typeof gamificationRecapEmailLog.$inferSelect;

export type GamificationMetricKey = "activity" | "pipeline" | "wins" | "revenueBooked";
export type GamificationPeriodKey = "daily" | "weekly" | "monthly";
export type GamificationGoals = Record<GamificationMetricKey, Record<GamificationPeriodKey, number>>;
export type GamificationTheme = "equalizer" | "test_tubes" | "marquee";

// ============================================================================
// DATA SECURITY HARDENING — DOCUMENT ACCESS AUDIT LOG
// ============================================================================
// Append-only log of every sensitive document access (upload, download, view,
// delete, virus_scan_failed, decrypt_error). The storage interface deliberately
// exposes only insert + read methods — there is no update/delete path.
// See server/lib/crypto/README.md and server/replit_integrations/object_storage/README.md.
//
// Task #607 — `document_access_log` is one of the audit tables in the
// inventory at `docs/security/audit-log-policy.md`. Migration
// `migrations/0022_audit_log_append_only_triggers.sql` installs the
// `audit_log_append_only` trigger on this table to reject UPDATE
// outright and reject DELETE unless the session has set the
// privileged-purge GUC AND the row is older than the 365-day
// retention floor. The application has no DELETE path on this table
// today — the trigger is defense-in-depth.

export type DocumentAccessAction =
  | "upload"
  | "upload_rejected"
  | "download"
  | "view"
  | "preview"
  | "email_attached"
  | "delete"
  | "virus_scan_failed"
  | "decrypt_error"
  | "migration";

// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `created_at`. No auto-pruner — every document access is
// kept indefinitely for compliance. See
// `docs/security/audit-log-policy.md`.
export const documentAccessLog = pgTable("document_access_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id"),                       // null for unauthenticated/system actions
  action: varchar("action", { length: 32 }).notNull().$type<DocumentAccessAction>(),
  documentInstanceId: varchar("document_instance_id"),
  templateId: varchar("template_id"),
  clientId: varchar("client_id"),
  objectKey: text("object_key"),
  fileName: varchar("file_name", { length: 255 }),
  fileSize: integer("file_size"),
  ipAddress: varchar("ip_address", { length: 64 }),
  userAgent: text("user_agent"),
  threat: varchar("threat", { length: 64 }),         // populated when action = virus_scan_failed
  errorMessage: text("error_message"),               // populated when action = decrypt_error / upload_rejected
  metadata: jsonb("metadata"),                       // free-form context, kept small
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertDocumentAccessLogSchema = createInsertSchema(documentAccessLog).omit({ id: true, createdAt: true });
export type InsertDocumentAccessLog = z.infer<typeof insertDocumentAccessLogSchema>;
export type DocumentAccessLog = typeof documentAccessLog.$inferSelect;

// ============================================================================
// OBJECT UPLOAD INTENT — single-use bindings for presigned upload URLs.
// ============================================================================
// When the server hands out a presigned PUT URL for /objects/uploads/<id>, it
// also writes a row here to record (a) which user requested it, (b) which
// workspace they belong to, and (c) which document instance/template the
// upload is meant for. The matching upload-completed call must present the
// same objectKey from the same caller; otherwise the bytes can't be claimed.
// This closes the IDOR where any user with upload permission could call
// upload-completed pointing at another tenant's freshly-uploaded object key.
//
// Rows are single-use (deleted on successful claim) and TTL-pruned.
export const objectUploadIntent = pgTable("object_upload_intent", {
  objectKey: text("object_key").primaryKey(),         // canonical /objects/uploads/<id>
  userId: varchar("user_id").notNull(),
  workspaceId: varchar("workspace_id").notNull(),
  intendedInstanceId: varchar("intended_instance_id"),
  intendedTemplateId: varchar("intended_template_id"),
  purpose: varchar("purpose", { length: 32 }).notNull(),  // e.g. "document_instance", "document_template"
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type ObjectUploadIntent = typeof objectUploadIntent.$inferSelect;
export type InsertObjectUploadIntent = typeof objectUploadIntent.$inferInsert;

// ============================================================================
// SAVED VIEWS — per-user spreadsheet view presets.
// ============================================================================
// A "view" is the captured state of sort + filter + visible-columns for a
// spreadsheet (leads, clients, invoices, schedule). Users save views so they
// can switch back to a frequent layout in one click. v1 is per-user (not
// workspace-shared); a future v2 can add a `shared` flag.
export const savedViews = pgTable("saved_views", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  // Whitelisted at the API layer: leads | clients | invoices | schedule.
  entity: varchar("entity", { length: 32 }).notNull(),
  name: varchar("name", { length: 80 }).notNull(),
  // Free-form blob; the corresponding spreadsheet decides how to apply it.
  // Shape today: { sort?, dir?, filters?: {...}, visibleColumns?: string[] }.
  params: jsonb("params").notNull(),
  pinned: boolean("pinned").default(false).notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  // Tenant-scoped lookups by user + entity dominate.
  workspaceUserEntityIdx: index("saved_views_ws_user_entity_idx").on(
    t.workspaceId, t.userId, t.entity,
  ),
  // Enforce per-user uniqueness on (entity, name) so a user can't have two
  // views with the same name in the same entity.
  uniqueUserEntityName: uniqueIndex("saved_views_user_entity_name_uq").on(
    t.workspaceId, t.userId, t.entity, t.name,
  ),
}));

export const insertSavedViewSchema = createInsertSchema(savedViews).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSavedView = z.infer<typeof insertSavedViewSchema>;
export type SavedView = typeof savedViews.$inferSelect;

// =============================================================================
// Onboarding system (Wave 1)
// -----------------------------------------------------------------------------
// Three pieces:
//  - onboarding_tracks: super-admin-owned playlists ("New Workspace",
//    "Solo Talent", "High-Volume Agency"). One workspace can see one
//    or more tracks based on its plan tier.
//  - onboarding_steps: ordered cards inside a track. Each step links to a
//    walkthrough article in the Knowledge module so users can revisit.
//  - user_onboarding_progress: per-user, per-step completion timestamp.
// Sample data lives outside this system — flagged via leads/clients/invoices
// .isDemoSeed and tracked in workspace_demo_seeds for cleanup auditing.
// =============================================================================

export const onboardingTracks = pgTable("onboarding_tracks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 120 }).notNull(),
  description: text("description"),
  // Optional plan-tier filter. Empty array = visible to all tiers.
  // Otherwise must contain one of "lite" | "starter" | "pro" | "enterprise".
  planTierFilter: text("plan_tier_filter").array().notNull().default(sql`'{}'::text[]`),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  activeSortIdx: index("onboarding_tracks_active_sort_idx").on(t.isActive, t.sortOrder),
}));

export const onboardingSteps = pgTable("onboarding_steps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  trackId: varchar("track_id").notNull().references(() => onboardingTracks.id, { onDelete: "cascade" }),
  title: varchar("title", { length: 200 }).notNull(),
  body: text("body"),
  // Whitelisted: "link" | "video" | "checkbox" | "external_action".
  actionType: varchar("action_type", { length: 24 }).notNull().default("link"),
  // For actionType=link: where the in-app link should send the user (e.g. /leads).
  targetRoute: varchar("target_route", { length: 255 }),
  // For actionType=video: the embedded video URL.
  videoUrl: varchar("video_url", { length: 500 }),
  // Slug into knowledge_articles section='walkthroughs'. Optional — if set, the
  // step links to the Knowledge article so users can re-read after dismissing.
  knowledgeSlug: varchar("knowledge_slug", { length: 200 }),
  isRequired: boolean("is_required").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  trackSortIdx: index("onboarding_steps_track_sort_idx").on(t.trackId, t.sortOrder),
}));

export const userOnboardingProgress = pgTable("user_onboarding_progress", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  stepId: varchar("step_id").notNull().references(() => onboardingSteps.id, { onDelete: "cascade" }),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  completedAt: timestamp("completed_at").defaultNow().notNull(),
}, (t) => ({
  uniqueUserStep: uniqueIndex("user_onboarding_progress_user_step_uq").on(t.userId, t.stepId),
  userIdx: index("user_onboarding_progress_user_idx").on(t.userId),
}));

// Per-workspace marker that the sample-data seeder ran. Lets us skip
// re-seeding and surface a "demo data is present" badge in admin tools.
export const workspaceDemoSeeds = pgTable("workspace_demo_seeds", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }).unique(),
  // True while seeded rows still exist; flips to false when "Done with setup"
  // wipes them out. Kept around as an audit footprint instead of deleting the row.
  isPresent: boolean("is_present").notNull().default(true),
  seededAt: timestamp("seeded_at").defaultNow().notNull(),
  clearedAt: timestamp("cleared_at"),
}, (t) => ({
  workspaceIdx: index("workspace_demo_seeds_workspace_idx").on(t.workspaceId),
}));

export const insertOnboardingTrackSchema = createInsertSchema(onboardingTracks).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertOnboardingTrack = z.infer<typeof insertOnboardingTrackSchema>;
export type OnboardingTrack = typeof onboardingTracks.$inferSelect;

export const insertOnboardingStepSchema = createInsertSchema(onboardingSteps).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertOnboardingStep = z.infer<typeof insertOnboardingStepSchema>;
export type OnboardingStep = typeof onboardingSteps.$inferSelect;

export const insertUserOnboardingProgressSchema = createInsertSchema(userOnboardingProgress).omit({
  id: true, completedAt: true,
});
export type InsertUserOnboardingProgress = z.infer<typeof insertUserOnboardingProgressSchema>;
export type UserOnboardingProgress = typeof userOnboardingProgress.$inferSelect;

export const insertWorkspaceDemoSeedSchema = createInsertSchema(workspaceDemoSeeds).omit({
  id: true, seededAt: true, clearedAt: true,
});
export type InsertWorkspaceDemoSeed = z.infer<typeof insertWorkspaceDemoSeedSchema>;
export type WorkspaceDemoSeed = typeof workspaceDemoSeeds.$inferSelect;

export const ONBOARDING_HAT_OPTIONS = ["booking_rep", "manager", "artist", "admin"] as const;
export type OnboardingHat = (typeof ONBOARDING_HAT_OPTIONS)[number];
export const UI_LENS_OPTIONS = ["admin", "artist"] as const;
export type UiLens = (typeof UI_LENS_OPTIONS)[number];

// ── Wave 3: Workspace setting audits ────────────────────────────────────
// Append-only ledger of who toggled which workspace setting and when. The
// settingKey is a dotted path like `dialer.recordingMode` so the same table
// covers white-label, dialer, and any future admin toggles. Old/new values
// are jsonb so we keep a faithful snapshot regardless of column type.
// Append-only audit table; see `docs/security/audit-log-policy.md`.
export const workspaceSettingAudits = pgTable("workspace_setting_audits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id"),
  settingKey: varchar("setting_key", { length: 128 }).notNull(),
  oldValue: jsonb("old_value"),
  newValue: jsonb("new_value"),
  changedAt: timestamp("changed_at").defaultNow().notNull(),
}, (table) => ({
  workspaceChangedIdx: index("workspace_setting_audits_ws_changed_idx").on(table.workspaceId, table.changedAt),
}));
export const insertWorkspaceSettingAuditSchema = createInsertSchema(workspaceSettingAudits).omit({
  id: true, changedAt: true,
});
export type InsertWorkspaceSettingAudit = z.infer<typeof insertWorkspaceSettingAuditSchema>;
export type WorkspaceSettingAudit = typeof workspaceSettingAudits.$inferSelect;

// ── Wave 4: Referral program ────────────────────────────────────────────
// Each user can mint a single short code; signups that arrive with `?ref=<code>`
// produce an attribution row. The platform later credits the referrer when
// the new workspace converts to a paid plan (creditAmountCents).
export const referralCodes = pgTable("referral_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  code: varchar("code", { length: 32 }).notNull().unique(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  userIdx: index("referral_codes_user_idx").on(table.userId),
}));
export const insertReferralCodeSchema = createInsertSchema(referralCodes).omit({ id: true, createdAt: true });
export type InsertReferralCode = z.infer<typeof insertReferralCodeSchema>;
export type ReferralCode = typeof referralCodes.$inferSelect;

export const referralAttributions = pgTable("referral_attributions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: varchar("code", { length: 32 }).notNull(),
  signupUserId: varchar("signup_user_id"),
  signupWorkspaceId: varchar("signup_workspace_id"),
  convertedAt: timestamp("converted_at"),
  creditAmountCents: integer("credit_amount_cents").default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  codeIdx: index("referral_attributions_code_idx").on(table.code),
  signupWsIdx: index("referral_attributions_signup_ws_idx").on(table.signupWorkspaceId),
}));
export const insertReferralAttributionSchema = createInsertSchema(referralAttributions).omit({
  id: true, createdAt: true, convertedAt: true,
});
export type InsertReferralAttribution = z.infer<typeof insertReferralAttributionSchema>;
export type ReferralAttribution = typeof referralAttributions.$inferSelect;

// ============================================================================
// WAVE 6 — Stripe disputes & refunds tracking (surfaces in Billing > Disputes)
// ============================================================================
export const stripeDisputes = pgTable("stripe_disputes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  stripeDisputeId: varchar("stripe_dispute_id", { length: 255 }).notNull().unique(),
  stripeChargeId: varchar("stripe_charge_id", { length: 255 }),
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
  invoiceId: varchar("invoice_id"),
  amountCents: integer("amount_cents").notNull().default(0),
  currency: varchar("currency", { length: 8 }).notNull().default("usd"),
  reason: varchar("reason", { length: 64 }),
  status: varchar("status", { length: 32 }).notNull().default("warning_needs_response"),
  evidenceDueAt: timestamp("evidence_due_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  wsCreatedIdx: index("stripe_disputes_ws_created_idx").on(table.workspaceId, table.createdAt),
  wsStatusIdx: index("stripe_disputes_ws_status_idx").on(table.workspaceId, table.status),
}));
export const insertStripeDisputeSchema = createInsertSchema(stripeDisputes).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertStripeDispute = z.infer<typeof insertStripeDisputeSchema>;
export type StripeDispute = typeof stripeDisputes.$inferSelect;

export const stripeRefunds = pgTable("stripe_refunds", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  stripeRefundId: varchar("stripe_refund_id", { length: 255 }).notNull().unique(),
  stripeChargeId: varchar("stripe_charge_id", { length: 255 }),
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
  invoiceId: varchar("invoice_id"),
  amountCents: integer("amount_cents").notNull().default(0),
  currency: varchar("currency", { length: 8 }).notNull().default("usd"),
  reason: varchar("reason", { length: 64 }),
  status: varchar("status", { length: 32 }).notNull().default("succeeded"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  wsCreatedIdx: index("stripe_refunds_ws_created_idx").on(table.workspaceId, table.createdAt),
}));
export const insertStripeRefundSchema = createInsertSchema(stripeRefunds).omit({
  id: true, createdAt: true,
});
export type InsertStripeRefund = z.infer<typeof insertStripeRefundSchema>;
export type StripeRefund = typeof stripeRefunds.$inferSelect;

// Optional persistent churn-risk snapshot (computed nightly; client list hits this
// instead of recomputing every page-load).
export const clientChurnRisk = pgTable("client_churn_risk", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  clientId: varchar("client_id").notNull().unique(),
  score: integer("score").notNull().default(0), // 0..100
  reasons: text("reasons").array().default(sql`ARRAY[]::text[]`),
  computedAt: timestamp("computed_at").defaultNow().notNull(),
}, (table) => ({
  wsScoreIdx: index("client_churn_risk_ws_score_idx").on(table.workspaceId, table.score),
}));
export const insertClientChurnRiskSchema = createInsertSchema(clientChurnRisk).omit({
  id: true, computedAt: true,
});
export type InsertClientChurnRisk = z.infer<typeof insertClientChurnRiskSchema>;
export type ClientChurnRisk = typeof clientChurnRisk.$inferSelect;

// ============================================================================
// WAVE 7 — Web Push subscriptions (per-user, per-device)
// ============================================================================
export const pushSubscriptions = pgTable("push_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  endpoint: text("endpoint").notNull().unique(),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  userAgent: varchar("user_agent", { length: 500 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  wsUserIdx: index("push_subscriptions_ws_user_idx").on(table.workspaceId, table.userId),
}));
export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({
  id: true, createdAt: true,
});
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;
export type PushSubscription = typeof pushSubscriptions.$inferSelect;

// ============================================================================
// WAVE 8 — AI assist credit ledger
// kind = 'gifted' rows expire at end of their period (use-it-or-lose-it).
// kind = 'paid' rows are durable (no period).
// ============================================================================
export type AiCreditKind = "gifted" | "paid";
export type AiAssistKind =
  | "marketing"
  | "drip"
  | "mass_email"
  | "standard_email"
  | "social_description"
  | "catch_me_up"
  | "qualification"
  | "qualification_helper"
  | "nl_filter_parse"
  | "lead_gen"
  // Wave 9 — image generation (one usage row per image, provider in modelKey).
  | "image_nano_banana"
  | "image_runway"
  // Task #600 — AI outbound caller (cloned voice email finder).
  // `outbound_email_finder_call` covers the AI-side spend (STT + cloned-
  // voice TTS + chat completion); `telecom_outbound` covers the per-second
  // Twilio voice + SIP cost. Both bill against the AI ledger so admins see
  // total cost per conversation in one place.
  | "outbound_email_finder_call"
  | "telecom_outbound"
  // Task #599 — AI Lead Catcher inbound first-responder turn
  | "lead_catcher_turn"
  // Task #601 — AI Reply Management. `reply_categorize` is the cheap
  // JSON-output triage call that picks one of the seven INBOX_REPLY_CATEGORIES
  // for an inbound message; `reply_draft` is the suggested-reply
  // generator that runs only on actionable categories.
  | "reply_categorize"
  | "reply_draft"
  // Task #11 — in-app Help Assistant. One kind covers both GPT primary
  // pass and Claude escalation; `modelKey` discriminates in the ledger.
  | "help_chat"
  // Voice assistant & AI Life Coach (Whisper pipeline = per-turn,
  // Realtime pipeline = per-minute).
  | "voice_whisper_turn"
  | "voice_realtime_min"
  // Task #204 — Onboarding wizard per-step AI chat.
  | "onboarding_chat"
  // Task #351 — Tour Planner standalone chat turns.
  | "tour_planner_chat"
  // AI Life Coach — one-time Opus "plan of action" generated the first time
  // an aspiration is scheduled. Separate from voice_whisper_turn because it
  // is a single deep-reasoning call, not a conversational turn.
  | "coach_aspiration_plan";

export const AI_IMAGE_KINDS: AiAssistKind[] = ["image_nano_banana", "image_runway"];

export function isAiImageKind(k: string): k is AiAssistKind {
  return k === "image_nano_banana" || k === "image_runway";
}

// Logical feature buckets for the per-feature AI usage breakdown shown
// in Admin → AI Settings. Each row in `ai_credit_usage` rolls up into
// exactly one bucket via `kind`. Keep this list in lockstep with the UI
// labels so a new kind never silently falls into "Other".
export const AI_USAGE_FEATURE_BUCKETS: Record<string, AiAssistKind[]> = {
  Drafting: ["marketing", "drip", "mass_email", "standard_email", "social_description"],
  "Catch Me Up": ["catch_me_up"],
  Qualification: ["qualification", "qualification_helper"],
  "Lead Gen": ["lead_gen"],
  "Smart Search": ["nl_filter_parse"],
  // Wave 9 — image generation rolls up into its own bucket so admins can see
  // image spend separately from text drafting.
  "Image Generation": ["image_nano_banana", "image_runway"],
  // Task #600 — AI outbound caller. Both kinds roll up into one bucket
  // because admins typically reason about it as "what did the cloned-voice
  // outreach cost this month" rather than splitting AI vs telecom.
  "Outbound Caller": ["outbound_email_finder_call", "telecom_outbound"],
  // Task #599 — first-responder turns across web/SMS/IG/Messenger/form.
  "Lead Catcher": ["lead_catcher_turn"],
  // Task #601 — both AI reply management kinds bucket together so admins
  // can see "what did inbox triage cost this month" in one row.
  "Reply Management": ["reply_categorize", "reply_draft"],
  // Task #11 — in-app Help Assistant (GPT primary + Claude escalation).
  "Help Assistant": ["help_chat"],
  "Voice & Coach": ["voice_whisper_turn", "voice_realtime_min", "coach_aspiration_plan"],
  // Task #351 — Tour Planner standalone chat.
  "Tour Planner": ["tour_planner_chat"],
};

// ── Smart-search natural-language filter spec (Task #120) ──
// Parsed by the AI gateway, validated server-side, then turned into the
// same query-string filters the manual filter pills produce. There is
// only one filter spec / one query layer — no AI-vs-manual code branch.
export const nlFilterSpecSchema = z.object({
  // Free-text search (substring) — usually a venue/contact name.
  q: z.string().trim().max(200).optional().nullable(),
  // Region (string ID) or array of region IDs. Either format works.
  regionIds: z.array(z.string().min(1)).max(50).optional().nullable(),
  clientType: z.string().min(1).max(100).optional().nullable(),
  talentId: z.string().min(1).max(64).optional().nullable(),
  // Leads-only.
  status: z.string().min(1).max(50).optional().nullable(),
  quadrants: z.array(z.enum(["NE", "NW", "SE", "SW"])).max(4).optional().nullable(),
  ratingMin: z.number().int().min(1).max(5).optional().nullable(),
  // Days since last contact — UI shows "no contact in N days".
  lastContactedDaysAgo: z.number().int().min(0).max(3650).optional().nullable(),
  // Venue type substring (works for leads + clients which both have a `venueType` column).
  venueType: z.string().min(1).max(100).optional().nullable(),
  // Clients-only date-of-event range. ISO yyyy-mm-dd.
  dateOfEventFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  dateOfEventTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  // Invoices-only.
  paidStatus: z.enum(["paid", "unpaid"]).optional().nullable(),
  dateOfEventYear: z.number().int().min(1900).max(2200).optional().nullable(),
  billingLastContactDaysAgo: z.number().int().min(0).max(3650).optional().nullable(),
});
export type NlFilterSpec = z.infer<typeof nlFilterSpecSchema>;
export type NlFilterPage = "leads" | "clients" | "invoices" | "sponsor_leads" | "sponsor_clients";

// Per-user, per-page named smart-search sentences. We deliberately store
// just the natural-language `sentence` (not the parsed spec) so re-running
// a saved search reuses the same parser path and stays in sync with any
// later parser improvements.
export const savedSmartSearches = pgTable("saved_smart_searches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  page: varchar("page", { length: 20 }).notNull().$type<NlFilterPage>(),
  name: varchar("name", { length: 120 }).notNull(),
  sentence: text("sentence").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  ownerIdx: index("saved_smart_searches_owner_idx").on(table.workspaceId, table.userId, table.page),
}));
export const insertSavedSmartSearchSchema = createInsertSchema(savedSmartSearches).omit({
  id: true, createdAt: true,
});
export type InsertSavedSmartSearch = z.infer<typeof insertSavedSmartSearchSchema>;
export type SavedSmartSearch = typeof savedSmartSearches.$inferSelect;

export const aiCreditGrants = pgTable("ai_credit_grants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  kind: varchar("kind", { length: 10 }).notNull().$type<AiCreditKind>(),
  amountCents: integer("amount_cents").notNull(), // positive = grant, negative = adjustment
  // For gifted grants: first day of the calendar month they apply to (UTC).
  // For paid grants: NULL (no expiry).
  periodStart: date("period_start"),
  note: varchar("note", { length: 255 }),
  grantedByUserId: varchar("granted_by_user_id"),
  // For Stripe purchases — enables replay-safe dedup (partial unique index below).
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  wsKindPeriodIdx: index("ai_credit_grants_ws_kind_period_idx").on(table.workspaceId, table.kind, table.periodStart),
  stripeDedup: uniqueIndex("ai_credit_grants_stripe_pi_uniq")
    .on(table.workspaceId, table.stripePaymentIntentId)
    .where(sql`${table.stripePaymentIntentId} IS NOT NULL AND ${table.kind} = 'paid'`),
}));
export const insertAiCreditGrantSchema = createInsertSchema(aiCreditGrants).omit({
  id: true, createdAt: true,
});
export type InsertAiCreditGrant = z.infer<typeof insertAiCreditGrantSchema>;
export type AiCreditGrant = typeof aiCreditGrants.$inferSelect;

export const aiCreditUsage = pgTable("ai_credit_usage", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id"),
  kind: varchar("kind", { length: 32 }).notNull().$type<AiAssistKind>(),
  modelKey: varchar("model_key", { length: 64 }).notNull(),
  inputTokens: integer("input_tokens").notNull().default(0),
  outputTokens: integer("output_tokens").notNull().default(0),
  costCents: integer("cost_cents").notNull().default(0), // billed against the workspace ledger
  requestId: varchar("request_id", { length: 64 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  wsCreatedIdx: index("ai_credit_usage_ws_created_idx").on(table.workspaceId, table.createdAt),
  wsKindIdx: index("ai_credit_usage_ws_kind_idx").on(table.workspaceId, table.kind),
}));
export const insertAiCreditUsageSchema = createInsertSchema(aiCreditUsage).omit({
  id: true, createdAt: true,
});
export type InsertAiCreditUsage = z.infer<typeof insertAiCreditUsageSchema>;
export type AiCreditUsage = typeof aiCreditUsage.$inferSelect;

// AI Credit Wallet V2 ledger — append-only audit of every balance mutation
// across both buckets (mirrors lead_credit_ledger). All amounts in microcredits
// (1 credit = 1_000_000 µcr; 1 credit = $0.10). `ai_credit_usage` stays as the
// per-feature metering table; THIS table is the balance/audit source of truth.
export const aiCreditLedger = pgTable("ai_credit_ledger", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // grant_reset | admin_award | purchase | spend | refund | adjustment | signup_bonus
  type: varchar("type", { length: 30 }).notNull(),
  grantDeltaMicrocredits: bigint("grant_delta_microcredits", { mode: "number" }).notNull().default(0),
  purchasedDeltaMicrocredits: bigint("purchased_delta_microcredits", { mode: "number" }).notNull().default(0),
  grantBalanceAfterMicrocredits: bigint("grant_balance_after_microcredits", { mode: "number" }).notNull(),
  purchasedBalanceAfterMicrocredits: bigint("purchased_balance_after_microcredits", { mode: "number" }).notNull(),
  // Context (at most one set depending on type).
  kind: varchar("kind", { length: 32 }),
  modelKey: varchar("model_key", { length: 64 }),
  requestId: varchar("request_id", { length: 64 }),
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
  actorUserId: varchar("actor_user_id", { length: 255 }),
  actorPlatformUserId: varchar("actor_platform_user_id", { length: 255 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  wsCreatedIdx: index("ai_credit_ledger_ws_created_idx").on(table.workspaceId, table.createdAt),
  // Stripe replay safety — a duplicate webhook delivery must not double-credit.
  uniqStripePiPurchase: uniqueIndex("ai_credit_ledger_stripe_pi_purchase_uniq")
    .on(table.workspaceId, table.stripePaymentIntentId)
    .where(sql`${table.stripePaymentIntentId} IS NOT NULL AND ${table.type} = 'purchase'`),
  // Spend idempotency — a retried call with the same requestId must not double-debit.
  uniqSpendRequest: uniqueIndex("ai_credit_ledger_spend_request_uniq")
    .on(table.workspaceId, table.requestId)
    .where(sql`${table.requestId} IS NOT NULL AND ${table.type} = 'spend'`),
}));
export const insertAiCreditLedgerSchema = createInsertSchema(aiCreditLedger).omit({ id: true, createdAt: true });
export type InsertAiCreditLedger = z.infer<typeof insertAiCreditLedgerSchema>;
export type AiCreditLedger = typeof aiCreditLedger.$inferSelect;

// ============================================================================
// CREDIT SYSTEM V2 — cost-vs-charge AUDIT (user decision #3: "don't lose money")
// ----------------------------------------------------------------------------
// One row per billable operation recording BOTH our true underlying provider
// cost (USD) AND the credits we charged the customer, so the platform can see
// realized margin and the average true cost per produced lead for each search
// type. Purely additive / observational — it never gates or changes billing.
//   pool        : 'ai' | 'lead' — which wallet was charged.
//   operation   : machine key, e.g. 'ai_discovery','analyst','ai_enrich',
//                 'email_finder','apollo','draft:<kind>','image_nano_banana'.
//   ourCostUsd  : our true cost to us in USD (numeric, high precision).
//   creditsCharged : credits debited from the customer (numeric — AI credits
//                 are fractional under V2; lead credits are whole).
//   creditUnitUsd  : dollar value of one credit at time of charge (audit trail
//                 across the unit migration).
//   unitsProduced  : leads produced (for searches) else null — powers the
//                 "avg true cost per lead" metric.
// ============================================================================
export type CreditPool = "ai" | "lead";

export const creditCostAudit = pgTable("credit_cost_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id"),
  pool: varchar("pool", { length: 8 }).notNull().$type<CreditPool>(),
  operation: varchar("operation", { length: 64 }).notNull(),
  ourCostUsd: decimal("our_cost_usd", { precision: 14, scale: 6 }).notNull().default("0"),
  creditsCharged: decimal("credits_charged", { precision: 14, scale: 4 }).notNull().default("0"),
  creditUnitUsd: decimal("credit_unit_usd", { precision: 8, scale: 4 }).notNull().default("0.10"),
  unitsProduced: integer("units_produced"),
  modelKey: varchar("model_key", { length: 64 }),
  // Correlates a cost row to an AI Agent run for real-time per-run true-USD totals.
  agentRunId: varchar("agent_run_id"),
  meta: jsonb("meta"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  wsCreatedIdx: index("credit_cost_audit_ws_created_idx").on(table.workspaceId, table.createdAt),
  poolOpIdx: index("credit_cost_audit_pool_op_idx").on(table.pool, table.operation),
  agentRunIdx: index("credit_cost_audit_agent_run_idx").on(table.agentRunId),
}));
export const insertCreditCostAuditSchema = createInsertSchema(creditCostAudit).omit({
  id: true, createdAt: true,
});
export type InsertCreditCostAudit = z.infer<typeof insertCreditCostAuditSchema>;
export type CreditCostAudit = typeof creditCostAudit.$inferSelect;

// ============================================================================
// HELP ASSISTANT — KB articles, chat transcripts, feedback, settings (Task #11)
// ----------------------------------------------------------------------------
// Super-admin-curated knowledge base grounding the in-app Help widget.
// Articles are tenant-global (one source of truth across all workspaces).
// Chats + messages persist per workspace+user with configurable retention
// so the super-admin can mine real questions for KB gaps. Spend rolls into
// `ai_credit_usage` with kind='help_chat' — no per-feature table needed.
// ============================================================================

export const helpKbArticles = pgTable("help_kb_articles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: varchar("slug", { length: 160 }).notNull().unique(),
  title: varchar("title", { length: 255 }).notNull(),
  // Markdown body. Rendered raw to the LLM grounding context; never shown
  // directly to end-users (the model paraphrases).
  body: text("body").notNull(),
  // Free-form tag list for keyword/tag scoring (no embeddings in v1).
  tags: text("tags").array().notNull().default(sql`'{}'::text[]`),
  // 'live' articles flow into retrieval; 'draft' articles are visible to
  // super-admins only (used by Claude escalation drafts awaiting review);
  // 'archived' articles are hidden everywhere.
  status: varchar("status", { length: 16 }).notNull().default("live"),
  // Source — 'curated' (hand-authored), 'seed' (boot-time seed batch),
  // 'ai_drafted' (Claude escalation auto-draft).
  source: varchar("source", { length: 16 }).notNull().default("curated"),
  // For ai_drafted rows: the chat that triggered the draft. Lets the
  // super-admin jump back to the source conversation.
  sourceChatId: varchar("source_chat_id"),
  createdByUserId: varchar("created_by_user_id"),
  updatedByUserId: varchar("updated_by_user_id"),
  // Manual ordering for the super-admin KB list. Lower numbers sort
  // first; ties fall back to updatedAt desc so newly-edited rows still
  // float up within an unsorted batch. Migration 0082.
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  statusIdx: index("help_kb_articles_status_idx").on(table.status),
  sourceIdx: index("help_kb_articles_source_idx").on(table.source),
  sortIdx: index("help_kb_articles_sort_idx").on(table.sortOrder),
}));
export const insertHelpKbArticleSchema = createInsertSchema(helpKbArticles).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertHelpKbArticle = z.infer<typeof insertHelpKbArticleSchema>;
export type HelpKbArticle = typeof helpKbArticles.$inferSelect;

export const helpChats = pgTable("help_chats", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  // First user message (short slice) so the super-admin browser can show
  // a list without loading every message row.
  title: varchar("title", { length: 200 }),
  // Aggregate spend across this chat (cents). Updated after every turn.
  totalCostCents: integer("total_cost_cents").notNull().default(0),
  // Last feedback signal captured on any message in this chat — null,
  // 'up', or 'down'. Drives the super-admin "needs attention" filter.
  lastFeedback: varchar("last_feedback", { length: 8 }),
  // Cached scope-flag for the super-admin browser (true once any message
  // tripped the off-topic pre-classifier).
  flaggedOffTopic: boolean("flagged_off_topic").notNull().default(false),
  // Latch-on flag: set to true the first time any message in this chat
  // is rated 'down'. Never reset (even if the user flips to 👍 later).
  // Drives sticky 180-day retention so a later thumbs-up can't collapse
  // a thumbs-down chat back into the 30-day window.
  hasDownFeedback: boolean("has_down_feedback").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  wsCreatedIdx: index("help_chats_ws_created_idx").on(table.workspaceId, table.createdAt),
  userIdx: index("help_chats_user_idx").on(table.workspaceId, table.userId),
  hasDownIdx: index("help_chats_has_down_idx").on(table.hasDownFeedback, table.createdAt),
}));
export const insertHelpChatSchema = createInsertSchema(helpChats).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertHelpChat = z.infer<typeof insertHelpChatSchema>;
export type HelpChat = typeof helpChats.$inferSelect;

export const helpChatMessages = pgTable("help_chat_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  chatId: varchar("chat_id").notNull().references(() => helpChats.id, { onDelete: "cascade" }),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  role: varchar("role", { length: 16 }).notNull(), // user | assistant | system | tool
  content: text("content").notNull(),
  // 'gpt' for the primary GPT pass, 'claude' for an escalation answer,
  // 'classifier' for the pre-classifier verdict. Null on user messages.
  pass: varchar("pass", { length: 16 }),
  // KB article IDs we grounded this turn on (assistant rows only).
  groundedArticleIds: text("grounded_article_ids").array(),
  // Tool calls executed for this turn (assistant rows only): summarised
  // names + arg JSON. We deliberately don't store raw row data here so a
  // super-admin reading a transcript can't bypass workspace permissions.
  toolCalls: jsonb("tool_calls"),
  modelKey: varchar("model_key", { length: 64 }),
  inputTokens: integer("input_tokens").notNull().default(0),
  outputTokens: integer("output_tokens").notNull().default(0),
  costCents: integer("cost_cents").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  chatIdx: index("help_chat_messages_chat_idx").on(table.chatId, table.createdAt),
  wsIdx: index("help_chat_messages_ws_idx").on(table.workspaceId, table.createdAt),
}));
export const insertHelpChatMessageSchema = createInsertSchema(helpChatMessages).omit({
  id: true, createdAt: true,
});
export type InsertHelpChatMessage = z.infer<typeof insertHelpChatMessageSchema>;
export type HelpChatMessage = typeof helpChatMessages.$inferSelect;

export const helpChatFeedback = pgTable("help_chat_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  messageId: varchar("message_id").notNull().references(() => helpChatMessages.id, { onDelete: "cascade" }),
  chatId: varchar("chat_id").notNull().references(() => helpChats.id, { onDelete: "cascade" }),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  rating: varchar("rating", { length: 8 }).notNull(), // 'up' | 'down'
  comment: text("comment"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  messageUserUniq: uniqueIndex("help_chat_feedback_message_user_uniq").on(table.messageId, table.userId),
  ratingIdx: index("help_chat_feedback_rating_idx").on(table.rating, table.createdAt),
}));
export const insertHelpChatFeedbackSchema = createInsertSchema(helpChatFeedback).omit({
  id: true, createdAt: true,
});
export type InsertHelpChatFeedback = z.infer<typeof insertHelpChatFeedbackSchema>;
export type HelpChatFeedback = typeof helpChatFeedback.$inferSelect;

// Singleton row holding the global help-assistant policy. id = 'global'.
export const helpAssistantSettings = pgTable("help_assistant_settings", {
  id: varchar("id").primaryKey().default("global"),
  // Per-workspace monthly spend cap (cents). Default $5.
  perWorkspaceMonthlyCapCents: integer("per_workspace_monthly_cap_cents").notNull().default(500),
  // Global monthly spend cap across all workspaces (cents). Default $50.
  globalMonthlyCapCents: integer("global_monthly_cap_cents").notNull().default(5000),
  // Retention windows. Default 30 days for non-flagged chats, 180 for
  // flagged/feedback chats so the super-admin can mine improvements.
  retentionDaysDefault: integer("retention_days_default").notNull().default(30),
  retentionDaysFlagged: integer("retention_days_flagged").notNull().default(180),
  // Per-cap throttle so we don't email admins every minute when a cap is
  // crossed. Tracks YYYY-MM of the last alert.
  lastGlobalAlertMonth: varchar("last_global_alert_month", { length: 7 }),
  // Toggle so a super-admin can flip the assistant off platform-wide
  // without redeploying.
  helpAssistantEnabled: boolean("help_assistant_enabled").notNull().default(true),
  // Auto-promote policy: if set, ai_drafted articles older than this many
  // days that still have a sourceChatId (came from a real gap) and are still
  // in draft status are automatically promoted to live by the daily scheduler.
  // null = disabled (default OFF).
  autoPromoteAfterDays: integer("auto_promote_after_days"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type HelpAssistantSettings = typeof helpAssistantSettings.$inferSelect;

// ── Help Bot Prompt Config ───────────────────────────────────────────────────
// Single global row (id = 'global') containing the four admin-editable prompts
// and the tool-enable flags for the Claude escalation agent.
// Null values mean "use the hardcoded default in help-chat-routes.ts".
export const helpBotConfig = pgTable("help_bot_config", {
  id: varchar("id").primaryKey().default("global"),
  // System prompt for the front-line GPT chatbot. Null = use hardcoded default.
  chatbotSystemPrompt: text("chatbot_system_prompt"),
  // System prompt for the Claude escalation agent. Null = use hardcoded default.
  escalationSystemPrompt: text("escalation_system_prompt"),
  // Prompt for the GPT structurer that turns Claude's answer into a KB article.
  structurerPrompt: text("structurer_prompt"),
  // Prompt for the background article writer (confident how-to answers).
  articleWriterPrompt: text("article_writer_prompt"),
  // Tool toggles for the Claude escalation agent.
  escalationWebSearchEnabled: boolean("escalation_web_search_enabled").notNull().default(true),
  escalationKbSearchEnabled: boolean("escalation_kb_search_enabled").notNull().default(true),
  escalationDocsSearchEnabled: boolean("escalation_docs_search_enabled").notNull().default(false),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type HelpBotConfig = typeof helpBotConfig.$inferSelect;

// Optional per-workspace overrides on top of the global settings row.
export const helpAssistantWorkspaceOverrides = pgTable("help_assistant_workspace_overrides", {
  workspaceId: varchar("workspace_id").primaryKey().references(() => workspaces.id, { onDelete: "cascade" }),
  // Override the global cap for this single workspace; null = inherit.
  monthlyCapCentsOverride: integer("monthly_cap_cents_override"),
  // Force-disable the assistant for this workspace; default false = inherit
  // global helpAssistantEnabled.
  disabled: boolean("disabled").notNull().default(false),
  // Last YYYY-MM we alerted admins about this workspace's cap crossing.
  lastWorkspaceAlertMonth: varchar("last_workspace_alert_month", { length: 7 }),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
export type HelpAssistantWorkspaceOverride = typeof helpAssistantWorkspaceOverrides.$inferSelect;

// ============================================================================
// AI LEAD CATCHER (Task #599)
// ----------------------------------------------------------------------------
// Channel-agnostic transcript storage for the AI first-responder. One
// `lead_catcher_conversations` row per inbound conversation across web chat,
// SMS, missed-call→SMS, IG DM, Messenger, and contact form. Messages live in
// `lead_catcher_messages` (one row per turn) and any hard-stop violations the
// engine refused to surface land in `lead_catcher_violations` for admin
// review. The engine is intentionally not coupled to any specific channel —
// the channel adapter just records the inbound text, calls the engine, and
// posts the engine's reply back through the originating provider.
// ============================================================================
export type LeadCatcherChannel = "web" | "sms" | "missed_call" | "instagram" | "messenger" | "form";
export type LeadCatcherOutcome =
  | "open"        // still in progress
  | "handoff"     // captured enough; lead created + assigned
  | "closed"      // prospect declined / abandoned
  | "blocked"     // engine refused (kill switch / cap / hard rule)
  | "errored";    // provider error during the conversation

export const leadCatcherConversations = pgTable("lead_catcher_conversations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  channel: varchar("channel", { length: 20 }).notNull().$type<LeadCatcherChannel>(),
  // Per-channel external identifier so duplicates can be merged across
  // page reloads / repeat webhook deliveries. Examples:
  //   web        → opaque visitor cookie
  //   sms        → E.164 from-number
  //   instagram  → IG sender id
  //   messenger  → page-scoped sender id
  //   form       → submitted email address
  externalId: varchar("external_id", { length: 255 }),
  // Best-known contact info captured during the conversation. These are
  // duplicated onto the resulting lead at hand-off time.
  capturedEmail: varchar("captured_email", { length: 255 }),
  capturedName: varchar("captured_name", { length: 255 }),
  capturedPhone: varchar("captured_phone", { length: 64 }),
  capturedEventDate: varchar("captured_event_date", { length: 64 }),
  capturedEventType: varchar("captured_event_type", { length: 100 }),
  // Free-form bag of additional answers keyed by `questions[].key`.
  capturedExtrasJson: jsonb("captured_extras_json").default(sql`'{}'::jsonb`),
  // Final state. Always starts 'open'.
  outcome: varchar("outcome", { length: 16 }).notNull().default("open").$type<LeadCatcherOutcome>(),
  // Set once the engine emits action='handoff' AND the lead row was
  // created. Points back to the resulting `leads.id`.
  handedOffLeadId: varchar("handed_off_lead_id"),
  handedOffAt: timestamp("handed_off_at"),
  // AI-generated summary the rep sees in the lead detail conversation tab.
  // Reuses the Catch Me Up infrastructure shape (3 short bullets).
  aiSummary: text("ai_summary"),
  // Cumulative tokens spent on this conversation (input + output across
  // every turn). Engine refuses further turns past
  // workspaces.aiLeadCatcherMaxTokensPerConversation.
  totalTokens: integer("total_tokens").notNull().default(0),
  totalCostCents: integer("total_cost_cents").notNull().default(0),
  // For widget snippet attribution + spam triage.
  pageUrl: text("page_url"),
  userAgent: text("user_agent"),
  ipAddress: varchar("ip_address", { length: 64 }),
  // Task #705 — Ads Manager Phase 1 attribution. Captured at conversation
  // start (web widget, contact form) and forwarded onto the lead row at
  // handoff time. Channel-agnostic: present on every conversation row even
  // when the channel is SMS / IG / Messenger, but typically only populated
  // for `web` and `form` where we have a browser session to read.
  attributionVisitorId: varchar("attribution_visitor_id", { length: 64 }),
  attributionGclid: varchar("attribution_gclid", { length: 255 }),
  attributionFbclid: varchar("attribution_fbclid", { length: 255 }),
  // Task #706 — Phase 2 click ids (TikTok / LinkedIn).
  attributionTtclid: varchar("attribution_ttclid", { length: 255 }),
  attributionLiFatId: varchar("attribution_li_fat_id", { length: 255 }),
  attributionUtmSource: varchar("attribution_utm_source", { length: 128 }),
  attributionUtmMedium: varchar("attribution_utm_medium", { length: 128 }),
  attributionUtmCampaign: varchar("attribution_utm_campaign", { length: 255 }),
  attributionUtmTerm: varchar("attribution_utm_term", { length: 255 }),
  attributionUtmContent: varchar("attribution_utm_content", { length: 255 }),
  attributionLandingUrl: varchar("attribution_landing_url", { length: 2048 }),
  attributionCampaignId: varchar("attribution_campaign_id"),
  attributionCapturedAt: timestamp("attribution_captured_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  // List-newest-first scan for the admin conversations table.
  wsCreatedIdx: index("lead_catcher_conv_ws_created_idx").on(t.workspaceId, t.createdAt),
  // Per-channel + per-outcome filter for the admin dashboard breakdown.
  wsChannelOutcomeIdx: index("lead_catcher_conv_ws_chan_outcome_idx").on(t.workspaceId, t.channel, t.outcome),
  // Lookup an in-progress conversation by its external id (e.g. resume
  // an SMS thread from the same phone number).
  wsExternalIdx: index("lead_catcher_conv_ws_ext_idx").on(t.workspaceId, t.channel, t.externalId),
}));
export const insertLeadCatcherConversationSchema = createInsertSchema(leadCatcherConversations).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertLeadCatcherConversation = z.infer<typeof insertLeadCatcherConversationSchema>;
export type LeadCatcherConversation = typeof leadCatcherConversations.$inferSelect;

export type LeadCatcherMessageRole = "prospect" | "ai" | "system";

export const leadCatcherMessages = pgTable("lead_catcher_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull().references(() => leadCatcherConversations.id, { onDelete: "cascade" }),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  role: varchar("role", { length: 16 }).notNull().$type<LeadCatcherMessageRole>(),
  body: text("body").notNull(),
  // Per-turn token usage (only set on role='ai' rows). Useful for
  // surfacing per-conversation cost in the admin UI.
  inputTokens: integer("input_tokens"),
  outputTokens: integer("output_tokens"),
  costCents: integer("cost_cents"),
  // Engine's `action` field for the turn ('ask' | 'handoff' | 'close').
  // Null on prospect/system rows.
  action: varchar("action", { length: 16 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  convCreatedIdx: index("lead_catcher_msg_conv_created_idx").on(t.conversationId, t.createdAt),
  wsCreatedIdx: index("lead_catcher_msg_ws_created_idx").on(t.workspaceId, t.createdAt),
}));
export const insertLeadCatcherMessageSchema = createInsertSchema(leadCatcherMessages).omit({
  id: true, createdAt: true,
});
export type InsertLeadCatcherMessage = z.infer<typeof insertLeadCatcherMessageSchema>;
export type LeadCatcherMessage = typeof leadCatcherMessages.$inferSelect;

// Hard-stop violations the engine surfaced (the model attempted to quote a
// price, confirm a date, etc). The output filter strips them from the
// reply and logs the redaction here so admins can audit how often the
// model misbehaves and tune the system prompt.
export type LeadCatcherViolationKind = "price" | "availability" | "negotiation" | "other";

export const leadCatcherViolations = pgTable("lead_catcher_violations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull().references(() => leadCatcherConversations.id, { onDelete: "cascade" }),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  kind: varchar("kind", { length: 20 }).notNull().$type<LeadCatcherViolationKind>(),
  pattern: varchar("pattern", { length: 120 }).notNull(),
  // The exact snippet the filter redacted. Useful for debugging.
  snippet: text("snippet").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  convIdx: index("lead_catcher_viol_conv_idx").on(t.conversationId),
  wsCreatedIdx: index("lead_catcher_viol_ws_created_idx").on(t.workspaceId, t.createdAt),
}));
export const insertLeadCatcherViolationSchema = createInsertSchema(leadCatcherViolations).omit({
  id: true, createdAt: true,
});
export type InsertLeadCatcherViolation = z.infer<typeof insertLeadCatcherViolationSchema>;
export type LeadCatcherViolation = typeof leadCatcherViolations.$inferSelect;

// Per-question shape persisted in `workspaces.aiLeadCatcherQuestionsJson`.
// Validated server-side before save so the admin UI can't push malformed
// rows. Email + date are always implicitly required by the engine; this
// list adds business-specific extras (guest count, venue type, etc).
export const leadCatcherQuestionSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,30}$/i, "Lowercase identifier with letters, numbers, underscores"),
  label: z.string().min(2).max(120),
  required: z.boolean().default(false),
});
export type LeadCatcherQuestion = z.infer<typeof leadCatcherQuestionSchema>;

// ============================================================================
// NOTIFICATION HUB — unified in-app notifications + per-channel preferences
// ----------------------------------------------------------------------------
// `notifications` is the per-user inbox. The bell/inbox UI reads from here.
// Categories are defined in shared/notification-categories.ts (single source
// of truth shared with the settings UI). Severity drives badge color.
// ============================================================================
export type NotificationSeverity = "info" | "success" | "warning" | "error";

export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // recipient
  category: varchar("category", { length: 64 }).notNull(),
  title: varchar("title", { length: 255 }).notNull(),
  body: text("body"),
  linkUrl: varchar("link_url", { length: 500 }),
  entityType: varchar("entity_type", { length: 32 }), // e.g. 'lead', 'invoice'
  entityId: varchar("entity_id", { length: 64 }),
  severity: varchar("severity", { length: 16 }).notNull().default("info").$type<NotificationSeverity>(),
  readAt: timestamp("read_at"),
  dismissedAt: timestamp("dismissed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  // Inbox query: list newest-first per user, optionally filter unread.
  wsUserCreatedIdx: index("notifications_ws_user_created_idx").on(t.workspaceId, t.userId, t.createdAt),
  wsUserReadIdx: index("notifications_ws_user_read_idx").on(t.workspaceId, t.userId, t.readAt),
  // Dedup helper: same entity+category collapses on the inbox query.
  wsEntityIdx: index("notifications_ws_entity_idx").on(t.workspaceId, t.entityType, t.entityId),
}));
export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true, createdAt: true, readAt: true, dismissedAt: true,
});
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;

// Per-user (or workspace-default when userId IS NULL) channel preference
// per category. Falls back through user → workspace default → registry default.
export const notificationPreferences = pgTable("notification_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id"), // null => workspace default
  category: varchar("category", { length: 64 }).notNull(),
  inApp: boolean("in_app").notNull().default(true),
  email: boolean("email").notNull().default(false),
  push: boolean("push").notNull().default(false),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  // Look up "all prefs for user" or "all prefs for workspace defaults".
  wsUserIdx: index("notification_prefs_ws_user_idx").on(t.workspaceId, t.userId),
  // We rely on partial unique indexes built in code (Drizzle doesn't model
  // them cleanly here); upserts use ON CONFLICT on these columns.
  wsUserCategoryIdx: uniqueIndex("notification_prefs_ws_user_category_unique").on(t.workspaceId, t.userId, t.category),
}));
export const insertNotificationPreferenceSchema = createInsertSchema(notificationPreferences).omit({
  id: true, updatedAt: true,
});
export type InsertNotificationPreference = z.infer<typeof insertNotificationPreferenceSchema>;
export type NotificationPreference = typeof notificationPreferences.$inferSelect;

// One row per user-per-workspace storing quiet-hours / working-hours config.
// Quiet hours suppress *push* (and email if a category opted in to it) outside
// the configured window. In-app rows are always written so the inbox is the
// always-on source of truth — quiet hours never silently drop notifications.
export const notificationUserSettings = pgTable("notification_user_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  // "Only deliver outside work hours" toggle. When OFF, push fires anytime.
  quietHoursEnabled: boolean("quiet_hours_enabled").notNull().default(false),
  // 24h "HH:MM" — e.g. "09:00" / "17:00". Treated in `quietHoursTimezone`.
  quietHoursStart: varchar("quiet_hours_start", { length: 5 }).notNull().default("09:00"),
  quietHoursEnd: varchar("quiet_hours_end", { length: 5 }).notNull().default("17:00"),
  quietHoursTimezone: varchar("quiet_hours_timezone", { length: 64 }).notNull().default("America/Chicago"),
  // Days of week the working window applies. 0=Sun..6=Sat.
  quietHoursDays: integer("quiet_hours_days").array().notNull().default(sql`'{1,2,3,4,5}'::int[]`),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  wsUserUnique: uniqueIndex("notification_user_settings_ws_user_unique").on(t.workspaceId, t.userId),
}));
export const insertNotificationUserSettingsSchema = createInsertSchema(notificationUserSettings).omit({
  id: true, updatedAt: true,
});
export type InsertNotificationUserSettings = z.infer<typeof insertNotificationUserSettingsSchema>;
export type NotificationUserSettings = typeof notificationUserSettings.$inferSelect;

// ============================================================================
// FAILED EMAIL/SMS DRAFTS — Task #692
// ----------------------------------------------------------------------------
// When the send guard refuses an outbound (unrendered merge tokens, recipient
// suppression, broken seed template, final-mile sanitizer) we save the full
// payload here so the operator can fix the placeholder and re-send from the
// composer without retyping anything. The Notification Hub gets a row that
// deeplinks to /email-drafts/<id>.
// ============================================================================
export const failedEmailDrafts = pgTable("failed_email_drafts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(), // sender we should notify
  channel: varchar("channel", { length: 16 }).notNull().default("email"), // 'email' | 'sms'
  endpoint: varchar("endpoint", { length: 500 }), // POST endpoint to retry
  method: varchar("method", { length: 8 }).notNull().default("POST"),
  subject: text("subject"),
  body: text("body").notNull(),
  recipientEmail: varchar("recipient_email", { length: 320 }),
  recipientPhone: varchar("recipient_phone", { length: 32 }),
  cc: text("cc"),
  payload: jsonb("payload").notNull().default(sql`'{}'::jsonb`),
  blockCode: varchar("block_code", { length: 48 }).notNull(),
  blockMessage: text("block_message").notNull(),
  blockTokens: text("block_tokens").array(),
  contextLabel: varchar("context_label", { length: 120 }),
  // Task #692 review-fix: structured entity refs so composer / inbox can
  // resolve and re-attach the original send context without parsing `payload`.
  templateId: varchar("template_id"),
  seedKey: varchar("seed_key", { length: 120 }),
  leadId: varchar("lead_id"),
  clientId: varchar("client_id"),
  invoiceId: varchar("invoice_id"),
  originalSendPath: varchar("original_send_path", { length: 120 }),
  attachmentsJson: jsonb("attachments_json"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // ── Outbox auto-retry fields ──────────────────────────────────────────
  // status: 'pending' (in outbox, will retry) | 'sent' (auto-retry succeeded
  //   — kept briefly for UI then GC'd) | 'cancelled' (user discarded) |
  //   'failed_permanent' (hit max attempts, awaiting manual action).
  status: varchar("status", { length: 24 }).notNull().default("pending"),
  attemptCount: integer("attempt_count").notNull().default(0),
  lastAttemptAt: timestamp("last_attempt_at"),
  // When >= now, the outbox retry sweep will try this row again. Pre-seeded
  // to (createdAt + 1h) on insert by recordFailedSend; bumped by the sweep.
  nextAttemptAt: timestamp("next_attempt_at"),
  lastError: text("last_error"),
  // sourceType: which automated path produced this — surfaces in the
  // Outbox UI ("Reminder", "Drip campaign", "Confirmation", etc).
  sourceType: varchar("source_type", { length: 48 }),
  sourceId: varchar("source_id", { length: 128 }),
  // Sticky flag — when true, retry sweep skips this row and the global
  // GmailReconnectPrompt shows. Cleared automatically once the user's
  // Gmail tokens are valid again (the next successful sweep clears it).
  needsReconnect: boolean("needs_reconnect").notNull().default(false),
}, (t) => ({
  wsUserCreatedIdx: index("failed_email_drafts_ws_user_created_idx").on(t.workspaceId, t.userId, t.createdAt),
  wsCreatedIdx: index("failed_email_drafts_ws_created_idx").on(t.workspaceId, t.createdAt),
  wsLeadIdx: index("failed_email_drafts_ws_lead_idx").on(t.workspaceId, t.leadId),
  wsClientIdx: index("failed_email_drafts_ws_client_idx").on(t.workspaceId, t.clientId),
  wsInvoiceIdx: index("failed_email_drafts_ws_invoice_idx").on(t.workspaceId, t.invoiceId),
  // Drives the outbox retry sweep: "pending rows whose next_attempt_at has passed".
  wsStatusNextIdx: index("failed_email_drafts_ws_status_next_idx").on(t.workspaceId, t.status, t.nextAttemptAt),
}));
export const insertFailedEmailDraftSchema = createInsertSchema(failedEmailDrafts).omit({
  id: true, createdAt: true,
});
export type InsertFailedEmailDraft = z.infer<typeof insertFailedEmailDraftSchema>;
export type FailedEmailDraft = typeof failedEmailDrafts.$inferSelect;

// ============================================================================
// Voice Assistant
// ----------------------------------------------------------------------------
// Role-scoped AI voice + chat assistant. Workspace admins control whether it
// is enabled at all, which voice provider runs (OpenAI Realtime vs the
// cheaper Whisper+TTS pipeline), which roles can use it, and whether it can
// execute write actions on the user's behalf. Every conversation is logged
// turn-by-turn so admins can audit what the assistant did, and every write
// action records its confirmation utterance + execution outcome separately
// so a write that misfired can be traced and reversed.
// ============================================================================

// Per-workspace assistant configuration. One row per workspace; the row is
// lazily created on first read so the `enabled=false` default is what an
// untouched workspace sees.
export const voiceAssistantSettings = pgTable("voice_assistant_settings", {
  workspaceId: varchar("workspace_id").primaryKey(),
  enabled: boolean("enabled").notNull().default(false),
  // 'realtime' (gpt-4o-realtime, premium UX/cost), 'whisper' (Whisper STT
  // → gpt-4o-mini → TTS pipeline), or 'disabled' (kill switch independent
  // of the `enabled` flag — useful when a workspace wants the UI to show
  // "voice assistant is off" without losing per-role configuration).
  provider: varchar("provider", { length: 32 }).notNull().default("disabled"),
  // Global write-action kill switch. When false the assistant is read-only
  // even for roles that have action perms in the UI.
  allowActions: boolean("allow_actions").notNull().default(true),
  // Subset of {sales_rep, artist, billing, admin, marketing} that may use
  // the assistant. Empty array = no one.
  enabledRoles: text("enabled_roles")
    .array()
    .notNull()
    .default(sql`ARRAY['admin','sales_rep','artist','billing','marketing']::text[]`),
  // OpenAI voice persona (alloy/echo/fable/onyx/nova/shimmer).
  voicePersona: varchar("voice_persona", { length: 32 }).notNull().default("alloy"),
  // Mobile FAB behaviour.
  // 'drawer'       — tap opens the chat/voice panel (default).
  // 'voice_direct' — tap immediately starts voice; panel still reachable via More menu.
  // 'voice_panel'  — tap starts voice AND opens the panel (transcript visible).
  mobileButtonMode: varchar("mobile_button_mode", { length: 32 }).notNull().default("drawer"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  updatedBy: varchar("updated_by"),
});

// One row per opened voice/chat session. A "session" is the lifetime of the
// floating widget being open and engaged — typically a few turns. We keep
// session rows even after the widget closes so the audit trail survives.
// NOT append-only: turn_count is incremented as turns land and ended_at is
// filled at close. Classified as governance / state in
// `docs/security/audit-log-policy.md`. The actual append-only audit trail
// lives in voiceAssistantTurns and voiceAssistantActions below.
export const voiceAssistantSessions = pgTable(
  "voice_assistant_sessions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    workspaceId: varchar("workspace_id").notNull(),
    userId: varchar("user_id").notNull(),
    // Snapshot of the resolved role at session-open. We snapshot rather
    // than join in case the user's role changes mid-session.
    role: varchar("role", { length: 32 }).notNull(),
    // Snapshot of provider used for this session.
    provider: varchar("provider", { length: 32 }).notNull(),
    // Whether the session was opened in super-admin cross-workspace mode
    // (only true when the user's email is on the platform owner allowlist
    // AND they explicitly opened a "search all workspaces" session).
    crossWorkspace: boolean("cross_workspace").notNull().default(false),
    // Locked-at-session-open scope. Persisted so subsequent requests can
    // reload an immutable snapshot rather than re-resolving against live
    // storage on every tool call. This is the security invariant: once
    // an artist's session is opened, only that artist's talent IDs are
    // ever queryable through this session, even if the artist is later
    // re-linked or demoted mid-session.
    lockedArtistTalentIds: text("locked_artist_talent_ids").array().notNull().default(sql`ARRAY[]::text[]`),
    lockedRepFullName: varchar("locked_rep_full_name"),
    startedAt: timestamp("started_at").notNull().defaultNow(),
    endedAt: timestamp("ended_at"),
    turnCount: integer("turn_count").notNull().default(0),
  },
  (t) => ({
    byWorkspaceStart: index("vasn_by_ws_start").on(t.workspaceId, t.startedAt),
    byUserStart: index("vasn_by_user_start").on(t.userId, t.startedAt),
  }),
);

// One row per turn (user utterance, assistant utterance, or tool call).
// Tool turns capture name + args + result + error so a permission denial
// or an empty result can be replayed during audit. We do NOT store raw
// audio — only the transcribed text — to keep the row size bounded and
// to avoid PII storage problems we'd rather not solve right now.
// Append-only audit table; see `docs/security/audit-log-policy.md`.
export const voiceAssistantTurns = pgTable(
  "voice_assistant_turns",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    workspaceId: varchar("workspace_id").notNull(),
    userId: varchar("user_id").notNull(),
    role: varchar("role", { length: 32 }).notNull(),
    ts: timestamp("ts").notNull().defaultNow(),
    // 'user' | 'assistant' | 'tool' | 'system'
    speaker: varchar("speaker", { length: 16 }).notNull(),
    text: text("text"),
    toolName: varchar("tool_name", { length: 64 }),
    toolArgs: jsonb("tool_args"),
    toolResult: jsonb("tool_result"),
    toolError: text("tool_error"),
    // Intelligence metadata (migration 0099)
    modelUsed: varchar("model_used", { length: 64 }),
    creditsCharged: integer("credits_charged").notNull().default(0),
    autoFlagged: boolean("auto_flagged").notNull().default(false),
    flagReason: varchar("flag_reason", { length: 64 }),
    flagReviewed: boolean("flag_reviewed").notNull().default(false),
    flagReviewedAt: timestamp("flag_reviewed_at"),
    flagReviewedBy: varchar("flag_reviewed_by", { length: 255 }),
    // Measured wall-clock ms from user-message receipt to first assistant token
    // emitted (or full response for non-streaming path). Null on user/tool turns.
    // Migration 0192.
    latencyMs: integer("latency_ms"),
  },
  (t) => ({
    bySessionTs: index("vatn_by_session_ts").on(t.sessionId, t.ts),
    byWorkspaceTs: index("vatn_by_ws_ts").on(t.workspaceId, t.ts),
    byFlagged: index("vatn_flagged_idx").on(t.autoFlagged, t.flagReviewed),
  }),
);

// One row per write-action proposed by the assistant. The "propose" path
// inserts with executedAt=null. The "confirm" path flips success/executedAt
// after running the underlying handler. We keep a copy of the user's
// confirmation utterance so a misfired action can be traced to the exact
// "yes / send it / do it" the user spoke — this is the audit anchor.
// Append-only audit table; see `docs/security/audit-log-policy.md`. The
// proposal columns are absolutely immutable; the execution-result columns
// (executed_at, success, error_message, confirmed_utterance, result_ref)
// are NULL at propose-time and may be filled exactly once by
// `markVoiceAssistantActionExecuted` (server/storage.ts) — the trigger
// enforces fill-once semantics on those columns.
export const voiceAssistantActions = pgTable(
  "voice_assistant_actions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    workspaceId: varchar("workspace_id").notNull(),
    userId: varchar("user_id").notNull(),
    role: varchar("role", { length: 32 }).notNull(),
    actionKey: varchar("action_key", { length: 64 }).notNull(),
    parameters: jsonb("parameters").notNull(),
    // Human-language summary the assistant spoke before asking for
    // confirmation. Stored verbatim so an audit reviewer sees what the
    // user actually heard.
    proposedSummary: text("proposed_summary").notNull(),
    requestedAt: timestamp("requested_at").notNull().defaultNow(),
    // Filled at confirm-time. NULL until the user confirms.
    confirmedUtterance: text("confirmed_utterance"),
    executedAt: timestamp("executed_at"),
    success: boolean("success"),
    errorMessage: text("error_message"),
    resultRef: jsonb("result_ref"),
  },
  (t) => ({
    byWorkspaceRequested: index("vaac_by_ws_requested").on(
      t.workspaceId,
      t.requestedAt,
    ),
    bySession: index("vaac_by_session").on(t.sessionId),
  }),
);

export const insertVoiceAssistantSettingsSchema = createInsertSchema(
  voiceAssistantSettings,
).omit({ updatedAt: true });
export type InsertVoiceAssistantSettings = z.infer<
  typeof insertVoiceAssistantSettingsSchema
>;
export type VoiceAssistantSettings = typeof voiceAssistantSettings.$inferSelect;

export const insertVoiceAssistantSessionSchema = createInsertSchema(
  voiceAssistantSessions,
).omit({ id: true, startedAt: true });
export type InsertVoiceAssistantSession = z.infer<
  typeof insertVoiceAssistantSessionSchema
>;
export type VoiceAssistantSession = typeof voiceAssistantSessions.$inferSelect;

export const insertVoiceAssistantTurnSchema = createInsertSchema(
  voiceAssistantTurns,
).omit({ id: true, ts: true });
export type InsertVoiceAssistantTurn = z.infer<
  typeof insertVoiceAssistantTurnSchema
>;
export type VoiceAssistantTurn = typeof voiceAssistantTurns.$inferSelect;

export const insertVoiceAssistantActionSchema = createInsertSchema(
  voiceAssistantActions,
).omit({ id: true, requestedAt: true });
export type InsertVoiceAssistantAction = z.infer<
  typeof insertVoiceAssistantActionSchema
>;
export type VoiceAssistantAction = typeof voiceAssistantActions.$inferSelect;

// ── AI Attachment Intelligence (Task #246) ──────────────────────────────
// One row per file a user uploads to the assistant during a session. The
// raw bytes live encrypted at rest in object storage (objectPath); we keep
// the model-extracted text + lead assessment here so follow-up chat turns
// can answer questions about the file without re-downloading/re-OCRing it.
// `extractedText` is field-encrypted (it may contain document PII). Scoped
// to (workspaceId, sessionId, userId) so an attachment is only ever visible
// inside the session that produced it.
export const assistantAttachments = pgTable(
  "assistant_attachments",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    workspaceId: varchar("workspace_id").notNull(),
    sessionId: varchar("session_id").notNull(),
    userId: varchar("user_id").notNull(),
    fileName: varchar("file_name", { length: 500 }),
    contentType: varchar("content_type", { length: 200 }),
    // Canonical /objects/uploads/<uuid> path to the encrypted bytes.
    objectPath: varchar("object_path", { length: 500 }).notNull(),
    byteSize: integer("byte_size"),
    // One-sentence narration surfaced to the user in chat.
    summary: text("summary"),
    // Field-encrypted extracted text/OCR used to ground Q&A turns.
    extractedText: text("extracted_text"),
    // Proactive lead assessment.
    looksLikeLead: boolean("looks_like_lead").notNull().default(false),
    // { mappedFields, extras } shaped for the screenshot import flow.
    leadAssessment: jsonb("lead_assessment"),
    // Set once the user converts the attachment into a lead.
    createdLeadId: varchar("created_lead_id"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (t) => ({
    bySession: index("asat_by_session").on(t.workspaceId, t.sessionId, t.createdAt),
  }),
);

export const insertAssistantAttachmentSchema = createInsertSchema(
  assistantAttachments,
).omit({ id: true, createdAt: true });
export type InsertAssistantAttachment = z.infer<
  typeof insertAssistantAttachmentSchema
>;
export type AssistantAttachment = typeof assistantAttachments.$inferSelect;

// ── Realtime usage reports (Option A client-side cost tracking) ───────────
// One row per `response.done` event the browser fires during a Realtime
// session. Token counts are exactly as reported by OpenAI's event payload.
// The audit_flag column is populated server-side based on anomaly detection:
//   - 'over_rate'      : cumulative audio tokens exceed what's physically
//                        possible given the session's wall-clock age.
//   - 'zero_audio_ai_responded' : the AI generated output text (outputTextTokens > 50)
//                        but no audio tokens were reported — likely a manipulation.
// NULL = clean report.
export const realtimeUsageReports = pgTable(
  "realtime_usage_reports",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sessionId: varchar("session_id").notNull(),
    workspaceId: varchar("workspace_id").notNull(),
    userId: varchar("user_id").notNull(),
    reportedAt: timestamp("reported_at").notNull().defaultNow(),
    // Raw token counts straight from the response.done event payload.
    inputAudioTokens: integer("input_audio_tokens").notNull().default(0),
    outputAudioTokens: integer("output_audio_tokens").notNull().default(0),
    inputTextTokens: integer("input_text_tokens").notNull().default(0),
    outputTextTokens: integer("output_text_tokens").notNull().default(0),
    cachedTokens: integer("cached_tokens").notNull().default(0),
    // Computed cost in cents at the 2× margin applied server-side.
    costCents: integer("cost_cents").notNull().default(0),
    // Snapshot of session age (seconds) at report time — used by the
    // super-admin audit UI without needing to join voice_assistant_sessions.
    sessionAgeSecondsAtReport: integer("session_age_seconds_at_report"),
    // NULL = clean; see comment above for flag meanings.
    auditFlag: varchar("audit_flag", { length: 32 }),
  },
  (t) => ({
    bySession: index("rur_by_session").on(t.sessionId),
    byWorkspace: index("rur_by_ws_reported").on(t.workspaceId, t.reportedAt),
    byFlag: index("rur_by_flag").on(t.auditFlag),
  }),
);

export type RealtimeUsageReport = typeof realtimeUsageReports.$inferSelect;

// Provider type union mirrored on the client so the settings UI radio
// stays in lockstep with the DB.
export type VoiceAssistantProvider = "realtime" | "whisper" | "disabled";

// User-facing role list — kept in sync with `UserRole`. We don't reuse
// the same union directly because the assistant intentionally doesn't
// support all theoretical roles (e.g. legacy `marketing` works, but the
// settings panel groups them together).
export const VOICE_ASSISTANT_ROLES = [
  "sales_rep",
  "artist",
  "billing",
  "admin",
  "marketing",
] as const;
export type VoiceAssistantRole = (typeof VOICE_ASSISTANT_ROLES)[number];

// ═══════════════════════════════════════════════════════════════════
// Wave 10 — Multi-Show Runs, Multi-Talent, Cancellation Policies,
// Talent Billing
// ═══════════════════════════════════════════════════════════════════

// Runs — parent of one or more bookings under a single contract /
// promoter relationship. A run owns the cancellation policy and any
// run-level deposits; individual bookings still carry their own rate
// and event-level fields. When workspaces.runsEnabled = false, runs
// still exist in the schema but are hidden from the UI.
export const runs = pgTable("runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  recordId: varchar("record_id", { length: 20 }).unique(),
  // Optional link back to the originating lead (so a run created from a
  // promoter's multi-show inquiry can trace its lineage).
  leadId: varchar("lead_id"),
  // Account-level identity for the run (typically a promoter or buyer).
  // Mirrors the booking shape so reporting can group runs alongside
  // single-show bookings in the same revenue queries.
  accountName: varchar("account_name", { length: 255 }),
  contactName: varchar("contact_name", { length: 255 }),
  emailAddress: varchar("email_address", { length: 255 }),
  // PII: AES-256-GCM envelope (`enc:v1:...`). Same convention as clients.
  phoneNumber: text("phone_number"),
  // Free-form name like "Spring '26 East Coast Tour" or "Joe's 3-Show Run".
  name: varchar("name", { length: 255 }).notNull(),
  // 'planning' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled'
  status: varchar("status", { length: 20 }).notNull().default("planning"),
  // Settlement mode controls how invoices are generated for the bookings:
  //   per_show — one invoice per booking (legacy per-show flow)
  //   per_run  — single invoice covering every booking in the run
  //   custom   — admin manually groups bookings into one or more invoices
  //              (groups expressed via runInvoiceBookings junction)
  settlementMode: varchar("settlement_mode", { length: 16 }).notNull().default("per_show"),
  // Optional cancellation policy template snapshotted onto the run at
  // creation time. The text is *also* copied onto the contract so policy
  // edits don't retroactively alter signed contracts.
  cancellationPolicyId: varchar("cancellation_policy_id"),
  cancellationPolicyText: text("cancellation_policy_text"),
  // Free-form admin notes, never shown to client.
  notes: text("notes"),
  // Cancellation tracking — cancel reason + timestamp when status flips
  // to 'cancelled' (auto-cancels any non-paid bookings in the run).
  cancelledAt: timestamp("cancelled_at"),
  cancelReason: text("cancel_reason"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (t) => ({
  workspaceDeletedIdx: index("runs_ws_deleted_idx").on(t.workspaceId, t.deletedAt),
  leadIdx: index("runs_lead_idx").on(t.leadId),
  statusIdx: index("runs_ws_status_idx").on(t.workspaceId, t.status),
}));

export const runsRelations = relations(runs, ({ many }) => ({
  bookings: many(clients),
  deposits: many(runDeposits),
}));

export const insertRunSchema = createInsertSchema(runs).omit({
  id: true,
  recordId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  cancelledAt: true,
});
export type InsertRun = z.infer<typeof insertRunSchema>;
export type Run = typeof runs.$inferSelect;

// Run-level deposits — independent of any single booking. Each deposit
// can flag `refundability` so the artist portal can surface "this $X is
// refundable up to T-30, this $Y is non-refundable" without re-deriving
// from the cancellation policy text.
export const runDeposits = pgTable("run_deposits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  runId: varchar("run_id").notNull().references(() => runs.id, { onDelete: "cascade" }),
  // Free-form label ("Hold deposit", "Travel advance").
  label: varchar("label", { length: 100 }).notNull(),
  amountCents: integer("amount_cents").notNull(),
  dueDate: timestamp("due_date"),
  // 'refundable' | 'non_refundable' | 'partial' (text describes terms when partial).
  refundability: varchar("refundability", { length: 20 }).notNull().default("non_refundable"),
  refundabilityNotes: text("refundability_notes"),
  // 'pending' | 'invoiced' | 'paid' | 'refunded' | 'forfeited'
  status: varchar("status", { length: 16 }).notNull().default("pending"),
  // Optional link to the invoice that bills this deposit.
  invoiceId: varchar("invoice_id"),
  paidAt: timestamp("paid_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  runIdx: index("run_deposits_run_idx").on(t.runId),
  workspaceIdx: index("run_deposits_ws_idx").on(t.workspaceId),
}));

export const insertRunDepositSchema = createInsertSchema(runDeposits).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  paidAt: true,
});
export type InsertRunDeposit = z.infer<typeof insertRunDepositSchema>;
export type RunDeposit = typeof runDeposits.$inferSelect;

// ── Event Series — Recurring Event / Multi-date / Event Run / Multi-bill ──
// Single primitive that drives all four "preset modes":
//   * Multi-date      — finite (endsOn or maxOccurrences set)
//   * Recurring Event — indefinite (no end set; runs until paused/cancelled)
//   * Event Run       — Multi-date with venue-per-date overrides (Phase 2)
//   * Multi-bill      — billingMode controls 1 invoice vs N per occurrence
// Child invoices generated by the daily sweep carry `invoices.seriesId` so
// the parent can be located from the cancel/skip token flow and the public
// client view.
export const eventSeries = pgTable("event_series", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  recordId: varchar("record_id", { length: 20 }).unique(),
  clientId: varchar("client_id").notNull(),
  talentId: varchar("talent_id"),
  eventOfferingId: varchar("event_offering_id"),
  name: varchar("name", { length: 255 }).notNull(),
  // 'weekly' | 'biweekly' | 'monthly' | 'every_n_days'
  cadence: varchar("cadence", { length: 20 }).notNull().default("weekly"),
  // Used when cadence='every_n_days' (e.g. cadenceN=10 → every 10 days).
  // Always populated (defaults to 1) so the generator never has to null-check.
  cadenceN: integer("cadence_n").notNull().default(1),
  weekday: integer("weekday"),
  monthDay: integer("month_day"),
  eventTime: varchar("event_time", { length: 20 }),
  anchorDate: timestamp("anchor_date").notNull(),
  nextRunDate: timestamp("next_run_date"),
  endsOn: timestamp("ends_on"),
  maxOccurrences: integer("max_occurrences"),
  occurrencesGenerated: integer("occurrences_generated").notNull().default(0),
  preflightDays: integer("preflight_days").notNull().default(3),
  autoSendInvoice: boolean("auto_send_invoice").notNull().default(true),
  stopOnUnpaid: boolean("stop_on_unpaid").notNull().default(false),
  // 'per_show' | 'multi_bill_split' | 'multi_bill_combined'
  billingMode: varchar("billing_mode", { length: 20 }).notNull().default("per_show"),
  // Default talent set for the series. When non-empty and billingMode is
  // multi_bill_split, the generator emits one invoice per id; when
  // multi_bill_combined, the generator creates booking_talent rows on the
  // child booking. Empty array = single-talent series (uses talentId).
  talentIds: text("talent_ids").array().notNull().default(sql`ARRAY[]::text[]`),
  rate: decimal("rate", { precision: 10, scale: 2 }),
  discount: decimal("discount", { precision: 10, scale: 2 }).default("0"),
  bookingDetails: text("booking_details"),
  rep: varchar("rep", { length: 100 }),
  // 'active' | 'paused' | 'cancelled' | 'completed'
  status: varchar("status", { length: 20 }).notNull().default("active"),
  cancellationReason: text("cancellation_reason"),
  cancelledAt: timestamp("cancelled_at"),
  cancelledBy: varchar("cancelled_by"),
  preflightLastSentFor: timestamp("preflight_last_sent_for"),
  publicToken: varchar("public_token", { length: 64 }).unique(),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (t) => ({
  workspaceStatusIdx: index("event_series_ws_status_idx").on(t.workspaceId, t.status, t.deletedAt),
  nextRunIdx: index("event_series_next_run_idx").on(t.nextRunDate),
  clientIdx: index("event_series_client_idx").on(t.clientId),
}));

export const insertEventSeriesSchema = createInsertSchema(eventSeries).omit({
  id: true,
  recordId: true,
  publicToken: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});
export type InsertEventSeries = z.infer<typeof insertEventSeriesSchema>;
export type EventSeries = typeof eventSeries.$inferSelect;

// Per-occurrence override row for an event_series. Created either by the
// matrix UI at series-creation time (one row per planned date) or lazily
// by the daily sweep when the rep skips/edits a single date via the
// no-login action page. The generator looks up the row keyed by
// (series_id, occurrence_date) before minting child invoices and applies
// any non-null overrides on top of the parent series defaults.
//
// Status values:
//   * scheduled — generator should emit invoice(s) when the date is due
//   * skipped   — rep cancelled this single date; cadence skips it
//   * generated — sweep already produced the invoice(s); set after success
//   * cancelled — date is dead (e.g. venue fell through); same effect as skipped
export const eventSeriesOccurrences = pgTable("event_series_occurrences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  seriesId: varchar("series_id").notNull().references(() => eventSeries.id, { onDelete: "cascade" }),
  occurrenceDate: timestamp("occurrence_date").notNull(),
  venueAddress: text("venue_address"),
  eventTime: varchar("event_time", { length: 20 }),
  talentIds: text("talent_ids").array().notNull().default(sql`ARRAY[]::text[]`),
  rate: decimal("rate", { precision: 10, scale: 2 }),
  status: varchar("status", { length: 20 }).notNull().default("scheduled"),
  notes: text("notes"),
  generatedAt: timestamp("generated_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  seriesDateUnique: uniqueIndex("uq_event_series_occurrence_date").on(t.seriesId, t.occurrenceDate),
  seriesStatusIdx: index("idx_event_series_occurrences_status").on(t.seriesId, t.status),
}));

export const insertEventSeriesOccurrenceSchema = createInsertSchema(eventSeriesOccurrences).omit({
  id: true,
  generatedAt: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertEventSeriesOccurrence = z.infer<typeof insertEventSeriesOccurrenceSchema>;
export type EventSeriesOccurrence = typeof eventSeriesOccurrences.$inferSelect;

// Cancellation Policies — admin-CRUD templates that sit alongside
// confirmation email templates. A policy can be marked default for one
// or more clientType values so new bookings of that type pre-pick it.
// The template TEXT is snapshotted onto the run at the moment a run is
// created so future template edits don't rewrite signed contracts.
export const cancellationPolicies = pgTable("cancellation_policies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  body: text("body").notNull(),
  // When true this policy is the workspace-wide fallback when no
  // clientType-specific match exists. At most one row per workspace
  // should have this true; storage layer enforces.
  isDefault: boolean("is_default").notNull().default(false),
  // Array of clientType values (e.g. ["Senior Living", "Schools"]) for
  // which this policy is the default selection in the booking dialog.
  defaultForClientTypes: text("default_for_client_types").array().notNull().default(sql`ARRAY[]::text[]`),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceActiveIdx: index("cancellation_policies_ws_active_idx").on(t.workspaceId, t.isActive),
}));

export const insertCancellationPolicySchema = createInsertSchema(cancellationPolicies).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCancellationPolicy = z.infer<typeof insertCancellationPolicySchema>;
export type CancellationPolicy = typeof cancellationPolicies.$inferSelect;

// Booking ↔ Talent junction — additional talent on a booking beyond
// the primary `clients.talentId`. Per-talent role + payout drives both
// AP (artist payout) and commissions reporting.
export const bookingTalent = pgTable("booking_talent", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // Junction columns — the booking lives in the `clients` table.
  bookingId: varchar("booking_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  // 'headliner' | 'support' | 'opener' | 'feature' | 'host' | 'collab'
  role: varchar("role", { length: 24 }).notNull().default("collab"),
  // Display order for run-of-show / call sheet rendering.
  sortOrder: integer("sort_order").notNull().default(0),
  // Payout config — matches the AP/commissions model.
  //   none      — no payout (in-house talent rolling into base)
  //   flat      — payoutAmountCents is the dollar payout
  //   percent   — payoutPercent (0-100) of the booking rate
  payoutType: varchar("payout_type", { length: 16 }).notNull().default("none"),
  payoutAmountCents: integer("payout_amount_cents"),
  payoutPercent: decimal("payout_percent", { precision: 5, scale: 2 }),
  // Optional override of the talent's booking notes for this single show.
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  bookingTalentUnique: uniqueIndex("booking_talent_unique").on(t.bookingId, t.talentId),
  bookingIdx: index("booking_talent_booking_idx").on(t.bookingId),
  talentIdx: index("booking_talent_talent_idx").on(t.talentId),
  workspaceIdx: index("booking_talent_ws_idx").on(t.workspaceId),
}));

export const insertBookingTalentSchema = createInsertSchema(bookingTalent).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertBookingTalent = z.infer<typeof insertBookingTalentSchema>;
export type BookingTalent = typeof bookingTalent.$inferSelect;

// Run Invoice Bookings — many-to-many junction used only when a run's
// settlementMode = 'custom'. Each row says "invoice X covers booking Y".
// In per_show mode this junction is unused (the existing 1:1
// invoice.clientId carries the relationship). In per_run mode the
// storage layer creates one invoice and one row per booking.
export const runInvoiceBookings = pgTable("run_invoice_bookings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  runId: varchar("run_id").notNull().references(() => runs.id, { onDelete: "cascade" }),
  invoiceId: varchar("invoice_id").notNull().references(() => invoices.id, { onDelete: "cascade" }),
  bookingId: varchar("booking_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  // Display order inside the invoice (admin-controlled in the custom-grouping UI).
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  invoiceBookingUnique: uniqueIndex("run_invoice_bookings_unique").on(t.invoiceId, t.bookingId),
  runIdx: index("run_invoice_bookings_run_idx").on(t.runId),
  invoiceIdx: index("run_invoice_bookings_invoice_idx").on(t.invoiceId),
}));

export const insertRunInvoiceBookingSchema = createInsertSchema(runInvoiceBookings).omit({
  id: true,
  createdAt: true,
});
export type InsertRunInvoiceBooking = z.infer<typeof insertRunInvoiceBookingSchema>;
export type RunInvoiceBooking = typeof runInvoiceBookings.$inferSelect;

// Talent Billing Transactions — append-only ledger of every charge,
// top-up, refund, and credit applied per talent. Powers the talent
// portal Billing tab + admin roll-up. We persist amount in cents and
// a polarity convention: positive = money OWED BY the talent (or money
// the workspace charged out), negative = credit / top-up.
//
// AUDIT-LOG TABLE — task #607. Tamper-resistance trigger
// `audit_log_append_only` (migration 0022) blocks UPDATE outright and
// gates DELETE behind `SET LOCAL app.allow_audit_purge`. The talentId
// FK is `ON DELETE SET NULL` (NOT cascade) so a talent hard-delete
// does NOT wipe their billing history within the 365d retention
// window — orphaned rows survive with `talent_id = NULL` for the
// audit trail. See `docs/security/audit-log-policy.md`.
export const talentBillingTransactions = pgTable("talent_billing_transactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // Nullable + ON DELETE SET NULL (task #607): preserves the audit
  // trail when a talent is hard-deleted. Inserts always provide a
  // real talent id; the column only goes NULL post-talent-purge.
  talentId: varchar("talent_id").references(() => talent.id, { onDelete: "set null" }),
  // 'ai_credit_topup'         — talent (or admin on their behalf) bought AI credits
  // 'ai_credit_consumption'   — debit against aiCreditsBalanceCents
  // 'dues_monthly'            — recurring agency dues charge
  // 'dues_percent_of_booking' — per-show dues on settlement
  // 'dues_onboarding_fee'     — one-time signup fee
  // 'adhoc_charge'            — admin manually charged talent (merch advance etc)
  // 'refund'                  — workspace refunded talent
  // 'credit'                  — workspace gifted talent credit (bookkeeping)
  txnType: varchar("txn_type", { length: 32 }).notNull(),
  // Positive = talent owes / paid; negative = workspace credit to talent.
  amountCents: integer("amount_cents").notNull(),
  // 'pending' | 'succeeded' | 'failed' | 'refunded'
  status: varchar("status", { length: 16 }).notNull().default("succeeded"),
  // Stripe linkage when the txn went through Stripe.
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
  stripeChargeId: varchar("stripe_charge_id", { length: 255 }),
  failureReason: text("failure_reason"),
  // Optional links to the entity that triggered the txn.
  bookingId: varchar("booking_id"),
  invoiceId: varchar("invoice_id"),
  runId: varchar("run_id"),
  description: text("description"),
  // Who initiated the transaction. NULL = system-triggered (cron/automation).
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  talentCreatedIdx: index("talent_billing_txn_talent_created_idx").on(t.talentId, t.createdAt),
  workspaceCreatedIdx: index("talent_billing_txn_ws_created_idx").on(t.workspaceId, t.createdAt),
  typeIdx: index("talent_billing_txn_type_idx").on(t.txnType),
}));

export const insertTalentBillingTransactionSchema = createInsertSchema(talentBillingTransactions).omit({
  id: true,
  createdAt: true,
});
export type InsertTalentBillingTransaction = z.infer<typeof insertTalentBillingTransactionSchema>;
export type TalentBillingTransaction = typeof talentBillingTransactions.$inferSelect;

// ════════════════════════════════════════════════════════════════════
// M3 — ARTIST PORTAL + CALL SHEETS + COLLABORATORS
// ════════════════════════════════════════════════════════════════════

// Per-talent address book. Stores recurring crew and venue contacts the
// talent works with (sound techs, drivers, hair/makeup, hotel night-mgr,
// promoter contacts, etc) so the artist can drop them onto call sheets
// in one click. Workspace-scoped so admins can audit, but talents can
// own/edit their own rows.
export const talentContacts = pgTable("talent_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  // Free-form name ("Nashville Sound Co — Mike", "Hertz Nashville Apr").
  name: varchar("name", { length: 255 }).notNull(),
  // Free-form role: 'sound_tech' | 'driver' | 'hair_makeup' | 'venue_mgr'
  // | 'hotel' | 'promoter_contact' | 'tour_manager' | 'opener' | 'merch'
  // | 'photographer' | 'caterer' | 'security' | 'other'.
  role: varchar("role", { length: 32 }).notNull().default("other"),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 50 }),
  company: varchar("company", { length: 255 }),
  address: text("address"),
  notes: text("notes"),
  // Optional pin for "use these on every call sheet by default".
  isFavorite: boolean("is_favorite").notNull().default(false),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (t) => ({
  talentDeletedIdx: index("talent_contacts_talent_deleted_idx").on(t.talentId, t.deletedAt),
  workspaceIdx: index("talent_contacts_ws_idx").on(t.workspaceId),
  roleIdx: index("talent_contacts_role_idx").on(t.role),
}));

export const insertTalentContactSchema = createInsertSchema(talentContacts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});
export type InsertTalentContact = z.infer<typeof insertTalentContactSchema>;
export type TalentContact = typeof talentContacts.$inferSelect;

// Show / Run call sheet. Attached to either a single booking
// (`bookingId`) or a multi-show run (`runId`). Exactly one of the two
// must be set. Header fields are denormalised so the call sheet still
// renders correctly even if the underlying booking moves or cancels.
export const showCallSheets = pgTable("show_call_sheets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // Task #419 — a call sheet is anchored to at least one of: a booking, a run,
  // or a talent profile (standalone talent-scoped sheets). bookingId/runId keep
  // their original "one source event" meaning when present.
  bookingId: varchar("booking_id"),
  runId: varchar("run_id"),
  // Task #419 — talent scope for standalone call sheets (sheets created from the
  // Talent module that aren't tied to a booking/run). Nullable.
  talentId: varchar("talent_id").references(() => talent.id, { onDelete: "set null" }),
  // Task #419 — OPTIONAL attachment to an event offering (zero/one/many call
  // sheets per offering). Detaching leaves the sheet intact, so SET NULL.
  eventOfferingId: varchar("event_offering_id").references(() => eventOfferings.id, { onDelete: "set null" }),
  // Header — denormalised at create time, editable thereafter.
  title: varchar("title", { length: 255 }).notNull(),
  // Task #112 — canonical calendar-day storage. See shared/event-date.ts.
  eventDate: date("event_date", { mode: "string" }),
  venueName: varchar("venue_name", { length: 255 }),
  venueAddress: text("venue_address"),
  // Free-form admin notes (above-the-fold on every section view).
  generalNotes: text("general_notes"),
  // Task #429 — two-tab record header fields. Clock-time strings ("HH:mm")
  // local to the event, so they are DST/timezone-drift safe (the call sheet
  // is a single-day timeline). All denormalised + editable.
  //   eventTime           — performance start clock time (from booking).
  //   loadInTime          — auto-derived from the offering buffer
  //                         (eventTime − minutesBefore); editable thereafter.
  //   runtimeOverrideMinutes — manual Set List total-runtime override; when
  //                         null the UI falls back to the offering default.
  eventTime: varchar("event_time", { length: 5 }),
  loadInTime: varchar("load_in_time", { length: 5 }),
  runtimeOverrideMinutes: integer("runtime_override_minutes"),
  // Cached weather payload from Open-Meteo. Refreshed on call-sheet view
  // when stale (>6h). NULL until first lookup or when address missing.
  weatherCache: jsonb("weather_cache"),
  weatherCachedAt: timestamp("weather_cached_at"),
  // 'draft' | 'published' (collaborators only see 'published' sheets).
  status: varchar("status", { length: 16 }).notNull().default("draft"),
  publishedAt: timestamp("published_at"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  deletedAt: timestamp("deleted_at"),
}, (t) => ({
  workspaceDeletedIdx: index("call_sheets_ws_deleted_idx").on(t.workspaceId, t.deletedAt),
  bookingIdx: index("call_sheets_booking_idx").on(t.bookingId),
  runIdx: index("call_sheets_run_idx").on(t.runId),
  talentIdx: index("call_sheets_talent_idx").on(t.talentId),
  offeringIdx: index("call_sheets_offering_idx").on(t.eventOfferingId),
  statusIdx: index("call_sheets_status_idx").on(t.workspaceId, t.status),
}));

export const insertShowCallSheetSchema = createInsertSchema(showCallSheets, {
  // Task #112 — canonical date-only storage.
  eventDate: eventDateInputSchema,
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  weatherCache: true,
  weatherCachedAt: true,
  publishedAt: true,
});
export type InsertShowCallSheet = z.infer<typeof insertShowCallSheetSchema>;
export type ShowCallSheet = typeof showCallSheets.$inferSelect;

// One row per ordered section on the call sheet — load-in, sound check,
// doors, dinner, show, breakdown, hotel info, etc. Each section has a
// time, contact ref (optional), and a per-role visibility array so we
// can hide e.g. the "post-show settlement" section from the driver.
export const callSheetSections = pgTable("call_sheet_sections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  callSheetId: varchar("call_sheet_id").notNull().references(() => showCallSheets.id, { onDelete: "cascade" }),
  // Free-form label (e.g. "Load-in", "Sound Check", "Show", "Breakdown").
  title: varchar("title", { length: 100 }).notNull(),
  // 'load_in' | 'sound_check' | 'doors' | 'dinner' | 'show' | 'breakdown'
  // | 'hotel' | 'travel' | 'settlement' | 'meet_greet' | 'custom'.
  sectionType: varchar("section_type", { length: 32 }).notNull().default("custom"),
  // Time-of-day for the section (timezone derived from the booking).
  scheduledAt: timestamp("scheduled_at"),
  // Optional location override (hotel address, after-party venue, etc).
  location: text("location"),
  // Optional pinned contact (foreign key to talent_contacts).
  contactId: varchar("contact_id"),
  // Free-form details (call to action, special instructions).
  details: text("details"),
  // Per-role visibility. Empty array OR ['all'] = visible to every
  // collaborator. Otherwise must intersect with the collaborator's
  // `roles` array. Common roles: 'driver' | 'sound_tech' | 'hair_makeup'
  // | 'venue' | 'tour_manager' | 'opener' | 'merch' | 'all_crew'.
  visibleToRoles: text("visible_to_roles").array().notNull().default(sql`ARRAY[]::text[]`),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  callSheetSortIdx: index("call_sheet_sections_cs_sort_idx").on(t.callSheetId, t.sortOrder),
  workspaceIdx: index("call_sheet_sections_ws_idx").on(t.workspaceId),
}));

export const insertCallSheetSectionSchema = createInsertSchema(callSheetSections).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCallSheetSection = z.infer<typeof insertCallSheetSectionSchema>;
export type CallSheetSection = typeof callSheetSections.$inferSelect;

// Token-gated external access. Generated when the artist clicks "Share
// with sound tech" / "Share with driver" — gives them a single URL
// (with embedded `accessToken`) that renders only the sections their
// `roles` array intersects. Revocation flips `revokedAt`; future hits
// return 403.
export const callSheetCollaborators = pgTable("call_sheet_collaborators", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  callSheetId: varchar("call_sheet_id").notNull().references(() => showCallSheets.id, { onDelete: "cascade" }),
  // Display info.
  name: varchar("name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 50 }),
  // Roles this collaborator should see. Subset of section.visibleToRoles
  // values. Empty = sees only sections marked ['all'].
  roles: text("roles").array().notNull().default(sql`ARRAY[]::text[]`),
  // Cryptographically random share token (never expires; revocable).
  accessToken: varchar("access_token", { length: 64 }).notNull().unique(),
  // Tracking.
  invitedAt: timestamp("invited_at").defaultNow(),
  invitedBy: varchar("invited_by").notNull(),
  firstViewedAt: timestamp("first_viewed_at"),
  lastViewedAt: timestamp("last_viewed_at"),
  viewCount: integer("view_count").notNull().default(0),
  revokedAt: timestamp("revoked_at"),
  revokedBy: varchar("revoked_by"),
}, (t) => ({
  callSheetIdx: index("cs_collab_cs_idx").on(t.callSheetId),
  tokenIdx: index("cs_collab_token_idx").on(t.accessToken),
  workspaceIdx: index("cs_collab_ws_idx").on(t.workspaceId),
}));

export const insertCallSheetCollaboratorSchema = createInsertSchema(callSheetCollaborators).omit({
  id: true,
  invitedAt: true,
  firstViewedAt: true,
  lastViewedAt: true,
  viewCount: true,
  revokedAt: true,
  revokedBy: true,
});
export type InsertCallSheetCollaborator = z.infer<typeof insertCallSheetCollaboratorSchema>;
export type CallSheetCollaborator = typeof callSheetCollaborators.$inferSelect;

// ============================================================================
// Task #429 — COLLABORATOR / CONTACT ROLE CATALOG (three-tier)
// ----------------------------------------------------------------------------
// Mirrors the talent-type three-tier pattern (platform defaults + workspace
// custom + workspace-hides) and extends it with a talent (portal-manager)
// tier so each act can curate its own roster role list. The effective list a
// portal manager sees for a talent =
//   active platform roles
//     − workspace-hidden − talent-hidden
//     + workspace-custom + talent-custom
// A call-sheet roster row stores a stable composite (roleId + roleSource)
// reference, never a label, so renames/hides never strand existing rows.
// ============================================================================

// Tier 1 — platform defaults (seeded by super admin).
export const platformCollaboratorRoles = pgTable("platform_collaborator_roles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 100 }).notNull().unique(),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertPlatformCollaboratorRoleSchema = createInsertSchema(platformCollaboratorRoles).omit({
  id: true, createdAt: true,
});
export type InsertPlatformCollaboratorRole = z.infer<typeof insertPlatformCollaboratorRoleSchema>;
export type PlatformCollaboratorRole = typeof platformCollaboratorRoles.$inferSelect;

// Tier 2 — workspace-custom roles (visible only inside the workspace).
export const workspaceCollaboratorRoles = pgTable("workspace_collaborator_roles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  wsNameUq: uniqueIndex("workspace_collaborator_roles_ws_name_uq").on(t.workspaceId, t.name),
}));
export const insertWorkspaceCollaboratorRoleSchema = createInsertSchema(workspaceCollaboratorRoles).omit({
  id: true, createdAt: true,
});
export type InsertWorkspaceCollaboratorRole = z.infer<typeof insertWorkspaceCollaboratorRoleSchema>;
export type WorkspaceCollaboratorRole = typeof workspaceCollaboratorRoles.$inferSelect;

// Tier 2 hides — platform defaults a workspace has hidden.
export const workspaceCollaboratorRoleHides = pgTable("workspace_collaborator_role_hides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  platformCollaboratorRoleId: varchar("platform_collaborator_role_id").notNull().references(() => platformCollaboratorRoles.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  wsRoleUq: uniqueIndex("workspace_collaborator_role_hides_ws_role_uq").on(t.workspaceId, t.platformCollaboratorRoleId),
}));
export const insertWorkspaceCollaboratorRoleHideSchema = createInsertSchema(workspaceCollaboratorRoleHides).omit({
  id: true, createdAt: true,
});
export type InsertWorkspaceCollaboratorRoleHide = z.infer<typeof insertWorkspaceCollaboratorRoleHideSchema>;
export type WorkspaceCollaboratorRoleHide = typeof workspaceCollaboratorRoleHides.$inferSelect;

// Tier 3 — talent (portal-manager) custom roles, per act.
export const talentCollaboratorRoles = pgTable("talent_collaborator_roles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 100 }).notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  talentNameUq: uniqueIndex("talent_collaborator_roles_talent_name_uq").on(t.talentId, t.name),
  workspaceIdx: index("talent_collaborator_roles_ws_idx").on(t.workspaceId),
}));
export const insertTalentCollaboratorRoleSchema = createInsertSchema(talentCollaboratorRoles).omit({
  id: true, createdAt: true,
});
export type InsertTalentCollaboratorRole = z.infer<typeof insertTalentCollaboratorRoleSchema>;
export type TalentCollaboratorRole = typeof talentCollaboratorRoles.$inferSelect;

// Tier 3 hides — roles (platform OR workspace) a talent has hidden. The
// composite (roleId, roleSource) names which upstream role is hidden.
export const talentCollaboratorRoleHides = pgTable("talent_collaborator_role_hides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  roleId: varchar("role_id").notNull(),
  // 'platform' | 'workspace' — which catalog the hidden role lives in.
  roleSource: varchar("role_source", { length: 16 }).notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  talentRoleUq: uniqueIndex("talent_collaborator_role_hides_talent_role_uq").on(t.talentId, t.roleId, t.roleSource),
  workspaceIdx: index("talent_collaborator_role_hides_ws_idx").on(t.workspaceId),
}));
export const insertTalentCollaboratorRoleHideSchema = createInsertSchema(talentCollaboratorRoleHides).omit({
  id: true, createdAt: true,
});
export type InsertTalentCollaboratorRoleHide = z.infer<typeof insertTalentCollaboratorRoleHideSchema>;
export type TalentCollaboratorRoleHide = typeof talentCollaboratorRoleHides.$inferSelect;

// ============================================================================
// Task #429 — TWO-TAB CALL SHEET SUB-MODELS
// ----------------------------------------------------------------------------
// (a) roster — everyone on the sheet (talent member, contact, or manual),
//     with a resolved role and a per-person note.
// (b) schedule blocks — point-in-time call times or time ranges (clock-time
//     strings), each with zero-or-more assigned roster people.
// (c) block assignments — many-to-many between blocks and roster rows.
// (d) set-list items — ordered performance segments with optional notes.
// (e) shared notes — member-posted notes; read-access members may post but
//     cannot edit others' notes.
// ============================================================================

// (a) Roster — a person on the call sheet.
export const callSheetRoster = pgTable("call_sheet_roster", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  callSheetId: varchar("call_sheet_id").notNull().references(() => showCallSheets.id, { onDelete: "cascade" }),
  // 'member' | 'contact' | 'manual' — where this person came from.
  personType: varchar("person_type", { length: 16 }).notNull().default("manual"),
  // Source references (nullable; only the matching personType is set).
  talentMemberId: varchar("talent_member_id"),
  talentContactId: varchar("talent_contact_id"),
  // Denormalised display name (stable even if the source row changes).
  name: varchar("name", { length: 255 }).notNull(),
  // Composite role reference from the three-tier catalog (nullable).
  roleId: varchar("role_id"),
  // 'platform' | 'workspace' | 'talent' — catalog the role lives in.
  roleSource: varchar("role_source", { length: 16 }),
  // Denormalised role label (stable display; resolved at attach time).
  roleLabel: varchar("role_label", { length: 100 }),
  // Per-person note.
  note: text("note"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  callSheetSortIdx: index("call_sheet_roster_cs_sort_idx").on(t.callSheetId, t.sortOrder),
  workspaceIdx: index("call_sheet_roster_ws_idx").on(t.workspaceId),
}));
export const insertCallSheetRosterSchema = createInsertSchema(callSheetRoster).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertCallSheetRoster = z.infer<typeof insertCallSheetRosterSchema>;
export type CallSheetRoster = typeof callSheetRoster.$inferSelect;

// (b) Schedule blocks — clock-time call times / ranges.
export const callSheetBlocks = pgTable("call_sheet_blocks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  callSheetId: varchar("call_sheet_id").notNull().references(() => showCallSheets.id, { onDelete: "cascade" }),
  label: varchar("label", { length: 200 }).notNull(),
  // Clock-time strings ("HH:mm"). endTime null = point-in-time call.
  startTime: varchar("start_time", { length: 5 }).notNull(),
  endTime: varchar("end_time", { length: 5 }),
  location: text("location"),
  note: text("note"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  callSheetIdx: index("call_sheet_blocks_cs_idx").on(t.callSheetId),
  workspaceIdx: index("call_sheet_blocks_ws_idx").on(t.workspaceId),
}));
export const insertCallSheetBlockSchema = createInsertSchema(callSheetBlocks).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertCallSheetBlock = z.infer<typeof insertCallSheetBlockSchema>;
export type CallSheetBlock = typeof callSheetBlocks.$inferSelect;

// (c) Block ↔ roster assignments (many-to-many).
export const callSheetBlockAssignments = pgTable("call_sheet_block_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  blockId: varchar("block_id").notNull().references(() => callSheetBlocks.id, { onDelete: "cascade" }),
  rosterId: varchar("roster_id").notNull().references(() => callSheetRoster.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  blockRosterUq: uniqueIndex("call_sheet_block_assignments_block_roster_uq").on(t.blockId, t.rosterId),
  rosterIdx: index("call_sheet_block_assignments_roster_idx").on(t.rosterId),
  workspaceIdx: index("call_sheet_block_assignments_ws_idx").on(t.workspaceId),
}));
export const insertCallSheetBlockAssignmentSchema = createInsertSchema(callSheetBlockAssignments).omit({
  id: true, createdAt: true,
});
export type InsertCallSheetBlockAssignment = z.infer<typeof insertCallSheetBlockAssignmentSchema>;
export type CallSheetBlockAssignment = typeof callSheetBlockAssignments.$inferSelect;

// (d) Set-list items — ordered performance segments.
export const callSheetSetItems = pgTable("call_sheet_set_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  callSheetId: varchar("call_sheet_id").notNull().references(() => showCallSheets.id, { onDelete: "cascade" }),
  title: varchar("title", { length: 255 }).notNull(),
  note: text("note"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  callSheetSortIdx: index("call_sheet_set_items_cs_sort_idx").on(t.callSheetId, t.sortOrder),
  workspaceIdx: index("call_sheet_set_items_ws_idx").on(t.workspaceId),
}));
export const insertCallSheetSetItemSchema = createInsertSchema(callSheetSetItems).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertCallSheetSetItem = z.infer<typeof insertCallSheetSetItemSchema>;
export type CallSheetSetItem = typeof callSheetSetItems.$inferSelect;

// (e) Shared notes — member-posted notes on a sheet.
export const callSheetSharedNotes = pgTable("call_sheet_shared_notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  callSheetId: varchar("call_sheet_id").notNull().references(() => showCallSheets.id, { onDelete: "cascade" }),
  authorUserId: varchar("author_user_id").notNull(),
  authorName: varchar("author_name", { length: 255 }).notNull(),
  body: text("body").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  callSheetIdx: index("call_sheet_shared_notes_cs_idx").on(t.callSheetId),
  workspaceIdx: index("call_sheet_shared_notes_ws_idx").on(t.workspaceId),
}));
export const insertCallSheetSharedNoteSchema = createInsertSchema(callSheetSharedNotes).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertCallSheetSharedNote = z.infer<typeof insertCallSheetSharedNoteSchema>;
export type CallSheetSharedNote = typeof callSheetSharedNotes.$inferSelect;

// ============================================================================
// GMAIL ACTIVITY BACKFILL — tracks per-user mailbox sync progress
// ============================================================================
// Two modes of operation:
//   - "initial"   → one-time historical sweep (default since 2025-01-01)
//                   when a user first connects their Gmail; one row per
//                   (workspace, user).
//   - "recipient" → targeted backfill kicked off when a new lead/client
//                   is created with an email address; scoped to that one
//                   recipient's address across the workspace's mailboxes.
//   - "nightly"   → daily 2am sweep status row (most recent run); one
//                   row per (workspace, user) is kept by upsert.
//
// Status: pending → running → completed | failed. Re-running just upserts
// status=running, then completed. Failures store `lastError` for the
// admin UI.
export const gmailBackfillState = pgTable("gmail_backfill_state", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  mode: varchar("mode", { length: 20 }).notNull(), // "initial" | "recipient" | "nightly"
  status: varchar("status", { length: 20 }).notNull().default("pending"), // "pending" | "running" | "completed" | "failed"
  recipientEmail: varchar("recipient_email", { length: 255 }), // only set for mode='recipient'
  sinceDate: timestamp("since_date").notNull(),
  untilDate: timestamp("until_date"),
  processedCount: integer("processed_count").notNull().default(0),
  matchedCount: integer("matched_count").notNull().default(0),
  lastError: text("last_error"),
  startedAt: timestamp("started_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  wsUserModeIdx: index("gmail_backfill_ws_user_mode_idx").on(t.workspaceId, t.userId, t.mode),
  wsUserRecipIdx: index("gmail_backfill_ws_user_recip_idx").on(t.workspaceId, t.userId, t.recipientEmail),
  statusIdx: index("gmail_backfill_status_idx").on(t.status),
}));

export const insertGmailBackfillStateSchema = createInsertSchema(gmailBackfillState).omit({
  id: true,
  startedAt: true,
  completedAt: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertGmailBackfillState = z.infer<typeof insertGmailBackfillStateSchema>;
export type GmailBackfillState = typeof gmailBackfillState.$inferSelect;

export const schedulerLeadership = pgTable("scheduler_leadership", {
  name: varchar("name", { length: 128 }).primaryKey(),
  leaderId: varchar("leader_id", { length: 64 }).notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type SchedulerLeadership = typeof schedulerLeadership.$inferSelect;

export const rateLimitBuckets = pgTable("rate_limit_buckets", {
  bucketKey: varchar("bucket_key", { length: 256 }).primaryKey(),
  count: integer("count").notNull().default(1),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
}, (t) => ({
  expiresAtIdx: index("rate_limit_buckets_expires_at_idx").on(t.expiresAt),
}));
export type RateLimitBucket = typeof rateLimitBuckets.$inferSelect;

// ============================================================================
// TASK #600 — AI OUTBOUND CALLER (cloned-voice email finder)
// ----------------------------------------------------------------------------
// One agency-cloned voice + one workspace-level toggle that, when enabled,
// dials any lead Apollo + the email finder couldn't get an address for, asks
// the contact for the best email, and writes the captured address back to
// the Lead. Hard-stop conversation guard prevents the bot from ever quoting
// price or making commitments — it always defers to the rep.
//
// Tables:
//   - workspace_voice_profiles   one cloned voice per workspace, status
//                                machine: training → ready (or failed)
//   - outbound_caller_settings   per-workspace opt-in toggle, region
//                                attestation, calling-hours window, daily
//                                cap, voicemail policy
//   - do_not_call_numbers        per-workspace DNC list (E.164 + reason +
//                                source). Insert is permanent; the worker
//                                re-checks before every dial.
//   - outbound_call_attempts     per-attempt audit row. Links to `calls`
//                                (the Twilio leg) when one was placed.
//                                Outcome enum drives the dashboard tile.
// ============================================================================

// Voice profile lifecycle. `revoked` is terminal — we never reuse a revoked
// profile; the admin must re-record samples and create a new one. The
// elevenlabs_voice_id is opaque to us; we just hand it back on synthesize.
export type VoiceProfileStatus =
  | "draft"      // samples collected, not yet sent for cloning
  | "training"   // upload + clone job in flight
  | "ready"      // provider confirmed; safe to use for outbound calls
  | "failed"     // provider rejected the samples
  | "revoked";   // admin pulled consent — voice ID is unusable

export const workspaceVoiceProfiles = pgTable("workspace_voice_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // Provider key — currently only "elevenlabs". Stored so we can swap or
  // multi-provider later without a schema change.
  provider: varchar("provider", { length: 32 }).notNull().default("elevenlabs"),
  // Provider-side voice ID. Null until status = 'ready'. Treat as opaque.
  providerVoiceId: varchar("provider_voice_id", { length: 128 }),
  status: varchar("status", { length: 16 }).notNull().default("draft").$type<VoiceProfileStatus>(),
  // Object-storage URLs for the consent recording (the agency owner reads
  // a verbatim consent sentence into the mic before any cloning runs) and
  // the training samples. Null on draft until uploads complete.
  consentRecordingUrl: varchar("consent_recording_url", { length: 1024 }),
  trainingSampleUrls: text("training_sample_urls").array().notNull().default(sql`ARRAY[]::text[]`),
  // Display name and short description shown in the admin UI ("Sarah's
  // outbound voice"). Free-text.
  displayName: varchar("display_name", { length: 255 }),
  // Two attestations the admin must check before the workspace toggle
  // unlocks. Stored separately from `outbound_caller_settings` so the
  // gating logic doesn't have to join.
  consentAttestedBy: varchar("consent_attested_by"),
  consentAttestedAt: timestamp("consent_attested_at"),
  // Last provider error (truncated) when status='failed'. Cleared on retry.
  lastProviderError: text("last_provider_error"),
  createdBy: varchar("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  workspaceUnique: uniqueIndex("workspace_voice_profiles_ws_unique").on(t.workspaceId),
  statusIdx: index("workspace_voice_profiles_status_idx").on(t.status),
}));

export const insertWorkspaceVoiceProfileSchema = createInsertSchema(workspaceVoiceProfiles).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertWorkspaceVoiceProfile = z.infer<typeof insertWorkspaceVoiceProfileSchema>;
export type WorkspaceVoiceProfile = typeof workspaceVoiceProfiles.$inferSelect;

// Per-workspace settings for the outbound caller. The toggle is gated in
// the API layer: we refuse to set `enabled = true` unless the workspace
// also has a voice profile in status='ready' AND the region attestation
// is checked. See server/lib/outbound-caller/eligibility.ts.
export const outboundCallerSettings = pgTable("outbound_caller_settings", {
  workspaceId: varchar("workspace_id").primaryKey().references(() => workspaces.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(false),
  // Required attestation that the workspace operates only in regions where
  // an AI outbound voice call is permitted. The admin must re-attest
  // (=> NULL'd) any time the calling-hours window or workflow changes
  // materially. Stored as a boolean + timestamp; nullable timestamp means
  // not yet attested.
  regionAttested: boolean("region_attested").notNull().default(false),
  regionAttestedBy: varchar("region_attested_by"),
  regionAttestedAt: timestamp("region_attested_at"),
  // Calling-hours window in the lead's local timezone. We resolve TZ from
  // the lead's state on the fly; if state is unknown we fall back to the
  // workspace's default region. Stored as 24h "HH:MM" strings.
  callWindowStart: varchar("call_window_start", { length: 5 }).notNull().default("09:00"),
  callWindowEnd: varchar("call_window_end", { length: 5 }).notNull().default("18:00"),
  // Per-day cap on attempts (per workspace). Worker stops when reached.
  dailyAttemptCap: integer("daily_attempt_cap").notNull().default(50),
  // Per-call hard cost ceiling in cents. Conversation aborts when reached.
  maxCostPerCallCents: integer("max_cost_per_call_cents").notNull().default(40),
  // What to do when we hit voicemail: 'leave' | 'hangup'. Most workspaces
  // pick hangup so we don't burn voicemails on a no-email lead.
  voicemailPolicy: varchar("voicemail_policy", { length: 16 }).notNull().default("hangup"),
  // Caller ID surfaces the workspace's `dialerCallerIdNumber` when null.
  // Allow override in case the workspace wants a separate "outbound caller"
  // number distinct from the rep dialer.
  callerIdNumberOverride: varchar("caller_id_number_override", { length: 32 }),
  updatedBy: varchar("updated_by"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertOutboundCallerSettingsSchema = createInsertSchema(outboundCallerSettings).omit({
  updatedAt: true,
});
export type InsertOutboundCallerSettings = z.infer<typeof insertOutboundCallerSettingsSchema>;
export type OutboundCallerSettings = typeof outboundCallerSettings.$inferSelect;

// Per-workspace do-not-call list. Insert is permanent (we never auto-prune)
// because the legal exposure of a wrongly-dialed DNC contact is much higher
// than the cost of keeping a few bytes around.
export type DoNotCallSource =
  | "manual"          // admin added from the UI
  | "ai_detected"     // worker heard "take me off your list" and added it
  | "twilio_carrier"  // carrier returned an explicit DNC code
  | "import";         // bulk-imported from a CSV

export const doNotCallNumbers = pgTable("do_not_call_numbers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // E.164 normalized. The worker normalizes before lookup; the admin UI
  // also normalizes on insert so direct matches always work.
  phoneE164: varchar("phone_e164", { length: 20 }).notNull(),
  source: varchar("source", { length: 24 }).notNull().$type<DoNotCallSource>(),
  reason: text("reason"),
  // If the row was inserted by the AI worker, link the call attempt that
  // detected the DNC intent so admins can audit ("which call put this
  // number on the list?").
  detectedFromAttemptId: varchar("detected_from_attempt_id"),
  addedBy: varchar("added_by"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  workspacePhoneUnique: uniqueIndex("dnc_numbers_ws_phone_unique").on(t.workspaceId, t.phoneE164),
}));

export const insertDoNotCallNumberSchema = createInsertSchema(doNotCallNumbers).omit({
  id: true, createdAt: true,
});
export type InsertDoNotCallNumber = z.infer<typeof insertDoNotCallNumberSchema>;
export type DoNotCallNumber = typeof doNotCallNumbers.$inferSelect;

// Outcome enum for an outbound caller attempt. Drives the admin dashboard
// tile. `email_captured` is the only "happy path" — everything else is
// either a real-world dead end or a guardrail trip.
export type OutboundCallOutcome =
  | "queued"             // row created, dial not yet placed
  | "dialing"            // Twilio call in progress
  | "email_captured"     // got an email back; written to lead
  | "declined"           // contact answered but refused
  | "no_answer"          // rang out
  | "voicemail"          // policy = hangup ⇒ end here, else "left a message"
  | "wrong_number"       // contact said "no such person" / clearly disconnected
  | "do_not_call"        // contact asked to be removed → number added to DNC
  | "guardrail_aborted"  // hard-stop guard triggered (price/commit attempt)
  | "cost_cap_aborted"   // per-call cost ceiling hit
  | "carrier_failed"     // Twilio rejected the dial (busy, invalid, etc.)
  | "system_error";      // unhandled exception inside the worker

export const outboundCallAttempts = pgTable("outbound_call_attempts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  leadId: varchar("lead_id").notNull(),
  // Link to the underlying `calls` row (the Twilio leg). NULL until the
  // dial actually starts; remains NULL if eligibility re-check at dial
  // time pulls the row out of the queue.
  callId: varchar("call_id"),
  voiceProfileId: varchar("voice_profile_id"),
  // The phone number we dialed. Snapshot at queue time so a later edit to
  // the lead doesn't rewrite history.
  dialedNumberE164: varchar("dialed_number_e164", { length: 20 }).notNull(),
  outcome: varchar("outcome", { length: 24 }).notNull().default("queued").$type<OutboundCallOutcome>(),
  // The email we captured (when outcome = 'email_captured'). Stored
  // separately even though it also appears on the lead — we keep this
  // immutable record for billing dispute / audit.
  capturedEmail: varchar("captured_email", { length: 255 }),
  // Dialogue transcript (alternating turns) — JSON for easy admin review.
  // The hard-stop guard's interventions are recorded inline so admins can
  // see exactly what the bot refused to say.
  transcriptJson: jsonb("transcript_json"),
  // Total cost in cents = AI tokens + TTS chars + telecom seconds. Sum of
  // the corresponding ai_credit_usage rows; denormalized here for the
  // dashboard so admins don't need a join.
  costCents: integer("cost_cents").notNull().default(0),
  durationSeconds: integer("duration_seconds").notNull().default(0),
  // Free-text error if outcome ∈ { system_error, carrier_failed,
  // guardrail_aborted, cost_cap_aborted }. Truncated to 2k chars.
  errorReason: text("error_reason"),
  startedAt: timestamp("started_at"),
  endedAt: timestamp("ended_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  wsCreatedIdx: index("outbound_call_attempts_ws_created_idx").on(t.workspaceId, t.createdAt),
  leadIdx: index("outbound_call_attempts_lead_idx").on(t.leadId),
  outcomeIdx: index("outbound_call_attempts_ws_outcome_idx").on(t.workspaceId, t.outcome),
}));

export const insertOutboundCallAttemptSchema = createInsertSchema(outboundCallAttempts).omit({
  id: true, createdAt: true,
});
export type InsertOutboundCallAttempt = z.infer<typeof insertOutboundCallAttemptSchema>;
export type OutboundCallAttempt = typeof outboundCallAttempts.$inferSelect;

// ── Conflict Radar (task #584) ───────────────────────────────────────────
// Workspace-scoped record of detected conflicts. Three `kind`s today:
//   - "talent_double_booking" — same talent on two events same day
//   - "venue_vs_tour"         — booking on a day talent is travelling/off
//   - "rep_overcommitted"     — sales rep over the workspace overload threshold
//
// `key` is a stable, deterministic dedup key built by the scanner (e.g.
// "talent_dbl:<talentId>:<YYYY-MM-DD>"). The (workspaceId, key) unique
// index lets the scanner upsert by key instead of by id, which means
// re-running the scan is idempotent and won't multiply rows or
// re-notify on every tick (in-app dedup in notify.ts already throttles
// 5min, but the scanner runs every 5 minutes so we lean on the unique
// key for the full lifetime of an open conflict).
//
// Lifecycle: a conflict starts at status="open" with `firstSeenAt =
// lastSeenAt = now()`. Subsequent scans bump `lastSeenAt`. An admin
// can ack it (status="acknowledged"), which silences future
// notifications without resolving the underlying issue. When the
// scanner stops detecting it, status flips to "resolved" with
// `resolvedAt` stamped — surfaced in the UI for context, then aged
// out after 30 days by the same scanner tick.
export const conflicts = pgTable("conflicts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // 'talent_double_booking' | 'venue_vs_tour' | 'rep_overcommitted'
  kind: varchar("kind", { length: 32 }).notNull(),
  // Stable detector-supplied dedup key. Unique within a workspace.
  key: varchar("key", { length: 255 }).notNull(),
  // 'talent' | 'invoice' | 'schedule_entry' | 'user' — what the
  // conflict is about. Drives links in the UI.
  subjectType: varchar("subject_type", { length: 32 }).notNull(),
  // Array of related entity ids (talent ids, invoice ids, etc.) so the
  // UI can render the affected rows without a second round-trip.
  subjectIds: jsonb("subject_ids").notNull().default(sql`'[]'::jsonb`),
  // The single calendar day the conflict applies to (UTC midnight).
  // For rep_overcommitted this is the day the threshold was breached.
  // Used for filtering ("today vs upcoming") and aging.
  date: timestamp("date").notNull(),
  // Plain-text human summary, e.g. "Crystal Method double-booked on 2026-05-12 (2 events)".
  summary: text("summary").notNull(),
  // 'low' | 'medium' | 'high' — drives badge colour and sort order.
  severity: varchar("severity", { length: 16 }).notNull().default("medium"),
  // Lifecycle status. 'open' shows in the radar; 'acknowledged' hides
  // by default but is still in the DB; 'resolved' is auto-set by the
  // scanner when it stops detecting the conflict.
  status: varchar("status", { length: 16 }).notNull().default("open"),
  firstSeenAt: timestamp("first_seen_at").notNull().defaultNow(),
  lastSeenAt: timestamp("last_seen_at").notNull().defaultNow(),
  acknowledgedAt: timestamp("acknowledged_at"),
  acknowledgedBy: varchar("acknowledged_by"),
  resolvedAt: timestamp("resolved_at"),
}, (t) => ({
  workspaceKeyUnique: uniqueIndex("conflicts_workspace_key_uidx").on(t.workspaceId, t.key),
  workspaceStatusIdx: index("conflicts_ws_status_idx").on(t.workspaceId, t.status),
  workspaceDateIdx: index("conflicts_ws_date_idx").on(t.workspaceId, t.date),
  workspaceKindIdx: index("conflicts_ws_kind_idx").on(t.workspaceId, t.kind),
}));

export const insertConflictSchema = createInsertSchema(conflicts).omit({
  id: true,
  firstSeenAt: true,
  lastSeenAt: true,
  acknowledgedAt: true,
  acknowledgedBy: true,
  resolvedAt: true,
});

export type InsertConflict = z.infer<typeof insertConflictSchema>;
export type Conflict = typeof conflicts.$inferSelect;
export type ConflictKind = "talent_double_booking" | "venue_vs_tour" | "rep_overcommitted";
export type ConflictStatus = "open" | "acknowledged" | "resolved";
export type ConflictSeverity = "low" | "medium" | "high";

// ============================================================================
// PII column scanner — quarterly sweep over unencrypted free-text columns.
// Findings store ONLY a redacted snippet (e.g. `j***@e***.com`) plus a
// pointer back to the offending row, never the raw value.
export type PiiScanRunStatus = "pending" | "running" | "completed" | "failed";
export type PiiScanRunTrigger = "scheduled" | "manual";
export type PiiPatternKind = "email" | "phone" | "ssn";

export const piiScanRuns = pgTable("pii_scan_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  status: varchar("status", { length: 16 }).notNull().default("pending").$type<PiiScanRunStatus>(),
  triggerType: varchar("trigger_type", { length: 16 }).notNull().$type<PiiScanRunTrigger>(),
  triggeredByPlatformUserId: varchar("triggered_by_platform_user_id"),
  triggeredByEmail: varchar("triggered_by_email", { length: 255 }),
  startedAt: timestamp("started_at").notNull().defaultNow(),
  finishedAt: timestamp("finished_at"),
  rowsScanned: integer("rows_scanned").notNull().default(0),
  findingsCount: integer("findings_count").notNull().default(0),
  errorMessage: text("error_message"),
}, (t) => ({
  startedAtIdx: index("pii_scan_runs_started_at_idx").on(t.startedAt),
}));

export const piiScanFindings = pgTable("pii_scan_findings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => piiScanRuns.id, { onDelete: "cascade" }),
  // Workspace the offending row belongs to. Null only for rows in the
  // `workspaces` table itself (where `workspaceId` IS the row id) — the
  // scanner mirrors the row id into both `rowId` and `workspaceId` for
  // workspaces, so this is non-null in practice.
  workspaceId: varchar("workspace_id"),
  tableName: varchar("table_name", { length: 64 }).notNull(),
  columnName: varchar("column_name", { length: 64 }).notNull(),
  rowId: varchar("row_id").notNull(),
  patternKind: varchar("pattern_kind", { length: 16 }).notNull().$type<PiiPatternKind>(),
  // Always redacted (e.g. `j***@e***.com`, `***-***-1234`). Never raw PII.
  redactedSnippet: text("redacted_snippet").notNull(),
  matchCount: integer("match_count").notNull().default(1),
  detectedAt: timestamp("detected_at").notNull().defaultNow(),
}, (t) => ({
  runIdx: index("pii_scan_findings_run_idx").on(t.runId),
  runTableIdx: index("pii_scan_findings_run_table_idx").on(t.runId, t.tableName, t.columnName),
}));

export const insertPiiScanRunSchema = createInsertSchema(piiScanRuns).omit({
  id: true,
  startedAt: true,
  finishedAt: true,
  rowsScanned: true,
  findingsCount: true,
  errorMessage: true,
});
export type InsertPiiScanRun = z.infer<typeof insertPiiScanRunSchema>;
export type PiiScanRun = typeof piiScanRuns.$inferSelect;

export const insertPiiScanFindingSchema = createInsertSchema(piiScanFindings).omit({
  id: true,
  detectedAt: true,
});
export type InsertPiiScanFinding = z.infer<typeof insertPiiScanFindingSchema>;
export type PiiScanFinding = typeof piiScanFindings.$inferSelect;

// ── Workspace office hours (Task #627) ─────────────────────────────────
// Workspace-wide envelope that defers automated outbound (email + SMS +
// AI calls) outside business hours. Default-off; when enabled, anything
// the schedulers touch outside the window is rescheduled (NOT dropped)
// to the next opening. Manual sends bypass this gate entirely. The
// per-campaign business-hours rule still wins when stricter.
export const workspaceOfficeHours = pgTable("workspace_office_hours", {
  workspaceId: varchar("workspace_id").primaryKey().references(() => workspaces.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(false),
  // 0=Sunday … 6=Saturday. Stored explicitly so admins can pick e.g.
  // Mon–Fri only or include Saturday.
  daysOfWeek: integer("days_of_week").array().notNull().default(sql`ARRAY[1,2,3,4,5]::integer[]`),
  // 24h "HH:MM" wall-clock in the workspace's chosen timezone.
  startLocal: varchar("start_local", { length: 5 }).notNull().default("09:00"),
  endLocal: varchar("end_local", { length: 5 }).notNull().default("17:00"),
  // IANA TZ id, e.g. "America/Chicago".
  timezone: varchar("timezone", { length: 64 }).notNull().default("America/Chicago"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertWorkspaceOfficeHoursSchema = createInsertSchema(workspaceOfficeHours).omit({
  updatedAt: true,
});
export type InsertWorkspaceOfficeHours = z.infer<typeof insertWorkspaceOfficeHoursSchema>;
export type WorkspaceOfficeHours = typeof workspaceOfficeHours.$inferSelect;

// Durable record of every automated outbound that was held by the
// office-hours envelope. Written by `logOfficeHoursDeferral` so admins
// can audit what was deferred and when it's expected to send.
export const officeHoursDeferrals = pgTable("office_hours_deferrals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  scheduler: varchar("scheduler", { length: 64 }).notNull(),
  itemKind: varchar("item_kind", { length: 64 }).notNull(),
  itemId: varchar("item_id", { length: 128 }).notNull(),
  nextOpenAt: timestamp("next_open_at").notNull(),
  deferredAt: timestamp("deferred_at").notNull().defaultNow(),
}, (t) => ({
  wsKindIdx: index("office_hours_deferrals_ws_kind_idx").on(t.workspaceId, t.itemKind, t.deferredAt),
}));
export type OfficeHoursDeferral = typeof officeHoursDeferrals.$inferSelect;

// ── Press-kit share links (task #628) ──────────────────────────────────
// Per-recipient share URL (`/artist/share/:token`) with optional password,
// expiration, and per-share agency branding overrides. Views are logged
// to `artistPageViews` for the talent's owner to inspect.
export const artistPageShares = pgTable("artist_page_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  // Random URL-safe token. Unique so a stolen token from one workspace
  // can't be replayed against another.
  token: varchar("token", { length: 64 }).notNull().unique(),
  recipientLabel: varchar("recipient_label", { length: 255 }),
  recipientEmail: varchar("recipient_email", { length: 255 }),
  // bcrypt hash of optional password. NULL = no password required.
  passwordHash: text("password_hash"),
  expiresAt: timestamp("expires_at"),
  // Per-share branding overrides — agencies may want to white-label a
  // single share link without changing workspace branding. NULL fields
  // fall back to workspace branding.
  agencyName: varchar("agency_name", { length: 255 }),
  agencyLogoUrl: text("agency_logo_url"),
  agencyPrimaryColor: varchar("agency_primary_color", { length: 16 }),
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  revokedAt: timestamp("revoked_at"),
}, (t) => ({
  talentIdx: index("artist_page_shares_talent_idx").on(t.talentId, t.createdAt),
}));
export const insertArtistPageShareSchema = createInsertSchema(artistPageShares).omit({ id: true, createdAt: true });
export type InsertArtistPageShare = z.infer<typeof insertArtistPageShareSchema>;
export type ArtistPageShare = typeof artistPageShares.$inferSelect;

// View log — written on every successful page render (slug or share).
// We hash the IP so the table never stores raw PII; UA + referrer kept
// truncated for triage.
export const artistPageViews = pgTable("artist_page_views", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  shareId: varchar("share_id").references(() => artistPageShares.id, { onDelete: "set null" }),
  viewedAt: timestamp("viewed_at").notNull().defaultNow(),
  ipHash: varchar("ip_hash", { length: 64 }),
  userAgent: varchar("user_agent", { length: 255 }),
  referrer: varchar("referrer", { length: 255 }),
}, (t) => ({
  talentIdx: index("artist_page_views_talent_idx").on(t.talentId, t.viewedAt),
  shareIdx: index("artist_page_views_share_idx").on(t.shareId, t.viewedAt),
}));
export type ArtistPageView = typeof artistPageViews.$inferSelect;

// Pending press-kit edits queued by the approval workflow. Payload is a
// partial-talent shape (only the fields the requester changed); the
// approver merges it into the live row when accepting.
export const talentPagePendingEdits = pgTable("talent_page_pending_edits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").notNull().references(() => talent.id, { onDelete: "cascade" }),
  requestedBy: varchar("requested_by").notNull(),
  requestedByRole: varchar("requested_by_role", { length: 32 }).notNull(),
  payload: jsonb("payload").notNull(),
  status: varchar("status", { length: 16 }).notNull().default("pending"),
  decidedBy: varchar("decided_by"),
  decidedAt: timestamp("decided_at"),
  decisionNote: text("decision_note"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  talentStatusIdx: index("talent_pending_edits_status_idx").on(t.talentId, t.status),
  workspaceStatusIdx: index("talent_pending_edits_ws_status_idx").on(t.workspaceId, t.status),
}));
export const insertTalentPagePendingEditSchema = createInsertSchema(talentPagePendingEdits).omit({
  id: true,
  status: true,
  decidedBy: true,
  decidedAt: true,
  decisionNote: true,
  createdAt: true,
});
export type InsertTalentPagePendingEdit = z.infer<typeof insertTalentPagePendingEditSchema>;
export type TalentPagePendingEdit = typeof talentPagePendingEdits.$inferSelect;

// Press-kit field shape — the union of fields editable from the press-kit
// editor and pending-edit payloads. Centralized so client + server share it.
export type PressKitFieldsPatch = Partial<{
  pressKitPublished: boolean;
  heroPhotoUrl: string | null;
  longBio: string | null;
  videoLinks: string[] | null;
  audioLinks: string[] | null;
  galleryImageUrls: string[] | null;
  credits: Array<{ title: string; year?: string; role?: string; description?: string }> | null;
  pressQuotes: Array<{ quote: string; source?: string; sourceUrl?: string }> | null;
  logoUrls: string[] | null;
  epkUrl: string | null;
  techRiderUrl: string | null;
  hospitalityRiderUrl: string | null;
  stagePlotUrl: string | null;
  faqItems: Array<{ question: string; answer: string }> | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactCompany: string | null;
  pressKitDefaultBookingFormId: string | null;
}>;

export type PressKitApprovalMode = "off" | "talent_to_admin" | "rep_to_talent";

// ─────────────────────────────────────────────────────────────────────────────
// Per-workspace encryption keys (KEKs) — Task #588.
// Each workspace gets its own key-encryption-key in the configured KMS.
// New PII writes generate a per-row DEK and wrap it under this KEK
// (`enc:v2:` envelope). Legacy `enc:v1:` rows continue to decrypt under the
// platform master key with no migration. Rows are provisioned lazily on the
// first encrypted write for a workspace; workspace deletion records a
// destruction schedule so the KMS-side destroy can be audited later.
// ─────────────────────────────────────────────────────────────────────────────
export const workspaceEncryptionKeys = pgTable("workspace_encryption_keys", {
  // PK = workspace_id; one KEK per workspace.
  workspaceId: varchar("workspace_id")
    .primaryKey()
    .references(() => workspaces.id, { onDelete: "cascade" }),
  // "local" (HKDF-from-master, dev/test default), "gcp", "aws".
  kmsProvider: varchar("kms_provider", { length: 16 }).notNull(),
  // Provider-specific key reference. For local: `local:<workspaceId>`.
  // For gcp: full CryptoKey resource name. For aws: CMK ARN/alias.
  kmsKeyId: text("kms_key_id").notNull(),
  // Monotonically increasing version. Bumped by manual rotation. Old wrapped
  // DEKs continue to decrypt because the version is stored in the v2 envelope.
  keyVersion: integer("key_version").notNull().default(1),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  lastRotatedAt: timestamp("last_rotated_at"),
  // When the workspace was hard-deleted, this is the time at which the KMS
  // destroy was scheduled. The KEK row itself is removed by FK CASCADE; this
  // column is for the audit trail captured by the destruction call.
  destroyScheduledAt: timestamp("destroy_scheduled_at"),
});

export const insertWorkspaceEncryptionKeySchema = createInsertSchema(
  workspaceEncryptionKeys,
).omit({ createdAt: true, lastRotatedAt: true, destroyScheduledAt: true });
export type InsertWorkspaceEncryptionKey = z.infer<typeof insertWorkspaceEncryptionKeySchema>;
export type WorkspaceEncryptionKey = typeof workspaceEncryptionKeys.$inferSelect;

// ============================================================================
// Calendar Hub Coaching — Slice 5 (Foundation)
// ============================================================================
// Per-USER coaching configuration + the three append/upsert tables that store
// what the coach learns about each person. Per-user (not per-workspace)
// because two users in the same workspace will absolutely have different
// preferences. Default-off everywhere; nothing in the coach pipeline runs
// until the individual user opts in.

export const COACH_INITIATION_FREQUENCIES = [
  "never",
  "after_each_event",
  "daily",
  "weekly",
  "biweekly",
  "monthly",
] as const;
export type CoachInitiationFrequency =
  (typeof COACH_INITIATION_FREQUENCIES)[number];

export const COACH_INTENSITIES = ["gentle", "steady", "firm", "intense"] as const;
export type CoachIntensity = (typeof COACH_INTENSITIES)[number];

export const COACH_ARCHETYPES = [
  "mentor",
  "best_friend",
  "drill_sergeant",
  "warm_parent",
] as const;
export type CoachArchetype = (typeof COACH_ARCHETYPES)[number];

// Task #274 — work/life scheduling boundary modes (per-user).
// strict   — protect all personal blocks; never cross the line without explicit user consent.
// flexible — prefer work-hours scheduling; note softly when scheduling at the boundary.
// off      — no boundary enforcement from the coach.
export const COACH_SCHEDULING_MODES = ["strict", "flexible", "off"] as const;
export type CoachSchedulingMode = (typeof COACH_SCHEDULING_MODES)[number];

// Task #274 — workspace-level floor for coachSchedulingMode.
// The stricter of this and the per-user setting applies.
// allow  — defer entirely to the per-user coachSchedulingMode.
// avoid  — users may be flexible but never fully off (minimum: flexible).
// block  — force strict for all users in this workspace.
export const COACH_PERSONAL_BLOCK_POLICIES = ["allow", "avoid", "block"] as const;
export type CoachPersonalBlockPolicy = (typeof COACH_PERSONAL_BLOCK_POLICIES)[number];

// Internal-only pillar taxonomy. Never surfaced to users — used by the
// classifier to keep the coach's reasoning balanced ("you've talked
// about work 12 times this week and family zero").
export const COACH_PILLARS = [
  "family",
  "relationships",
  "health",
  "faith",
  "creative",
  "professional_growth",
  "financial",
  "rest",
  "service",
  "learning",
] as const;
export type CoachPillar = (typeof COACH_PILLARS)[number];

export const coachUserSettings = pgTable("coach_user_settings", {
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  enabled: boolean("enabled").notNull().default(false),
  initiationFrequency: varchar("initiation_frequency", { length: 32 })
    .notNull()
    .default("weekly"),
  intensity: varchar("intensity", { length: 32 }).notNull().default("gentle"),
  personaArchetype: varchar("persona_archetype", { length: 32 })
    .notNull()
    .default("mentor"),
  coachName: varchar("coach_name", { length: 80 }),
  toneOverrides: jsonb("tone_overrides").notNull().default(sql`'{}'::jsonb`),
  lastInitiatedAt: timestamp("last_initiated_at"),
  // Coach Hub v2 — call-transcript opt-in (off by default; user toggles on
  // to let the post-call worker mine transcripts for commitments).
  extractCommitmentsFromCalls: boolean("extract_commitments_from_calls").notNull().default(false),
  // Optional work-window hints (minutes from local midnight). The
  // available-slot finder uses these as soft bounds when present.
  workWindowStartMinute: integer("work_window_start_minute"),
  workWindowEndMinute: integer("work_window_end_minute"),
  workTimezone: varchar("work_timezone", { length: 64 }),
  // Coach v3 — opt-out toggles. Defaults true so the user gets the
  // empathic touch out of the box; they can dismiss in chat or in
  // notification settings.
  moodLogEnabled: boolean("mood_log_enabled").notNull().default(true),
  weeklyRetroEnabled: boolean("weekly_retro_enabled").notNull().default(true),
  // Task #274 — work/life scheduling boundary. See COACH_SCHEDULING_MODES above.
  coachSchedulingMode: varchar("coach_scheduling_mode", { length: 16 }).notNull().default("strict"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  pk: primaryKey({ columns: [t.workspaceId, t.userId] }),
}));

export const insertCoachUserSettingsSchema = createInsertSchema(
  coachUserSettings,
).omit({ createdAt: true, updatedAt: true });
export type InsertCoachUserSettings = z.infer<
  typeof insertCoachUserSettingsSchema
>;
export type CoachUserSettings = typeof coachUserSettings.$inferSelect;

export const coachGoals = pgTable("coach_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  title: text("title").notNull(),
  why: text("why"),
  // NULL = unclassified; the pillar-classifier fills it on insert path.
  internalPillar: varchar("internal_pillar", { length: 32 }),
  status: varchar("status", { length: 16 }).notNull().default("active"),
  priority: integer("priority").notNull().default(3),
  targetDate: date("target_date"),
  // Coach Hub v2 — stake level. Default 'try' so existing rows behave
  // sensibly. Promises get aggressive defense + firm follow-up;
  // wishes get soft mention only.
  stake: varchar("stake", { length: 16 }).notNull().default("try").$type<CoachStake>(),
  // Where the goal came from (manual capture vs extracted from a call
  // transcript vs other sources). Soft pointer (no FK).
  sourceType: varchar("source_type", { length: 32 }),
  sourceRef: varchar("source_ref", { length: 255 }),
  // Optional soft link to a long-term aspiration. No FK — aspirations
  // can be deleted without orphaning the goal.
  aspirationId: varchar("aspiration_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertCoachGoalSchema = createInsertSchema(coachGoals).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertCoachGoal = z.infer<typeof insertCoachGoalSchema>;
export type CoachGoal = typeof coachGoals.$inferSelect;

// ── Coach Aspirations — long-term North Star items ───────────────────
// Distinct from coach_goals (30–90 day commitments). Aspirations are
// the 1yr+ dreams a life coach actually works toward. domain column
// enforces business-vs-personal classification on every capture.
export const ASPIRATION_DOMAINS = ["business", "personal"] as const;
export type AspirationDomain = (typeof ASPIRATION_DOMAINS)[number];

export const ASPIRATION_HORIZONS = ["1yr", "3yr", "5yr", "10yr", "lifetime"] as const;
export type AspirationHorizon = (typeof ASPIRATION_HORIZONS)[number];

export const ASPIRATION_STATUSES = ["active", "paused", "done", "abandoned"] as const;
export type AspirationStatus = (typeof ASPIRATION_STATUSES)[number];

// Coach v2 — what real-world metric (if any) auto-advances an aspiration's
// progress bar. `manual` = only updated by the user/coach in conversation.
// The platform-metric values are wired to server-side booking/revenue hooks
// (see server/lib/coach/aspiration-events.ts).
export const ASPIRATION_METRIC_SOURCES = [
  "manual",
  "revenue",
  "shows_booked",
  "revenue_in_day",
] as const;
export type AspirationMetricSource = (typeof ASPIRATION_METRIC_SOURCES)[number];

export const coachAspirations = pgTable("coach_aspirations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  why: text("why"),
  domain: varchar("domain", { length: 16 }).notNull().$type<AspirationDomain>(),
  horizon: varchar("horizon", { length: 16 }).$type<AspirationHorizon>(),
  status: varchar("status", { length: 16 }).notNull().default("active").$type<AspirationStatus>(),
  priority: integer("priority").notNull().default(3),
  targetYear: integer("target_year"),
  // Coach v2 — quantified progress. NUMERIC (arbitrary-precision decimal),
  // never float, so money stays exact (dollars w/ cents) and generic units
  // (e.g. 26.2 miles) keep their fractional part. targetValue NULL = a
  // qualitative aspiration with no progress bar.
  targetValue: decimal("target_value", { precision: 20, scale: 4 }),
  currentValue: decimal("current_value", { precision: 20, scale: 4 }).notNull().default("0"),
  unit: text("unit"),
  metricSource: varchar("metric_source", { length: 32 })
    .notNull()
    .default("manual")
    .$type<AspirationMetricSource>(),
  completedAt: timestamp("completed_at"),
  // ── Auto-scheduling engine (Calendar Hub Aspirations) ───────────────
  // The coach commits the user to a number of hours/week and fills the
  // calendar runway up to targetDate with fluid "water" blocks toward
  // this aspiration. targetDate is the hard deadline (distinct from the
  // coarse targetYear). ratePerHour is the user's self-reported
  // throughput in `unit` per hour (e.g. 2 pages/hour) — the basis for
  // the hours recommendation. preferredDays/preferredHourOfDay steer
  // placement toward the times the user said they'd ideally work.
  // lastScheduledThrough lets the runway extender/rebalancer run
  // idempotently (only place blocks past the last point we filled).
  targetDate: date("target_date"),
  ratePerHour: decimal("rate_per_hour", { precision: 20, scale: 4 }),
  recommendedHoursPerWeek: decimal("recommended_hours_per_week", { precision: 6, scale: 2 }),
  committedHoursPerWeek: decimal("committed_hours_per_week", { precision: 6, scale: 2 }),
  preferredDays: integer("preferred_days").array(),
  preferredHourOfDay: integer("preferred_hour_of_day"),
  autoScheduleEnabled: boolean("auto_schedule_enabled").notNull().default(false),
  lastScheduledThrough: timestamp("last_scheduled_through"),
  // Pillar-classifier result — internal only, never surfaced in prompts.
  internalPillar: varchar("internal_pillar", { length: 32 }),
  // ── AI plan of action (one-time, Opus) ──────────────────────────────
  // Generated exactly once, the first time an aspiration is scheduled
  // (see aspiration-scheduler.ts). A written roadmap the user can read,
  // plus the block-size recommendation that informed the schedule.
  // preferredDays/preferredHourOfDay above are also written by this same
  // call when the user didn't already set them by hand. Never regenerated
  // by later replans/rebalances — those reuse what's already here.
  planOfAction: text("plan_of_action"),
  planGeneratedAt: timestamp("plan_generated_at"),
  planBlockSizeMinutes: integer("plan_block_size_minutes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertCoachAspirationSchema = createInsertSchema(coachAspirations).omit({
  id: true,
  createdAt: true,
});
export type InsertCoachAspiration = z.infer<typeof insertCoachAspirationSchema>;
export type CoachAspiration = typeof coachAspirations.$inferSelect;

export const coachBlockOutcomes = pgTable("coach_block_outcomes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  // No FK: source can be a calendar block, a show, an event_review, a
  // goal-direct check-in, or a free-form commitment from conversation.
  // The source row is allowed to disappear without losing this data.
  sourceType: varchar("source_type", { length: 32 }).notNull(),
  sourceId: varchar("source_id"),
  happened: boolean("happened").notNull(),
  plannedMinutes: integer("planned_minutes"),
  actualMinutes: integer("actual_minutes"),
  qualityRating: integer("quality_rating"),
  note: text("note"),
  internalPillar: varchar("internal_pillar", { length: 32 }),
  // Coach Hub v2 — reason taxonomy + free-text user context.
  // reasonCode powers excuse-vs-burnout discernment. contextNote is
  // free-form ("with Jen", "on the road alone", "after gig in
  // Chicago") — calibration clusters per-user; we never bake user-
  // specific context into the schema.
  reasonCode: varchar("reason_code", { length: 32 }),
  contextNote: text("context_note"),
  // Denormalized for fast day-of-week / hour-of-day pattern queries.
  dayOfWeek: integer("day_of_week"),
  hourOfDay: integer("hour_of_day"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertCoachBlockOutcomeSchema = createInsertSchema(
  coachBlockOutcomes,
).omit({ id: true, createdAt: true });
export type InsertCoachBlockOutcome = z.infer<
  typeof insertCoachBlockOutcomeSchema
>;
export type CoachBlockOutcome = typeof coachBlockOutcomes.$inferSelect;

export const coachUserInsights = pgTable("coach_user_insights", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  kind: varchar("kind", { length: 32 }).notNull(),
  payload: jsonb("payload").notNull(),
  // 0-1; bumped when reinforced, decayed by a later background job.
  confidence: decimal("confidence", { precision: 3, scale: 2 }).notNull().default("0.50"),
  reinforceCount: integer("reinforce_count").notNull().default(1),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  lastReinforcedAt: timestamp("last_reinforced_at").notNull().defaultNow(),
});

export const insertCoachUserInsightSchema = createInsertSchema(
  coachUserInsights,
).omit({ id: true, createdAt: true, lastReinforcedAt: true });
export type InsertCoachUserInsight = z.infer<
  typeof insertCoachUserInsightSchema
>;
export type CoachUserInsight = typeof coachUserInsights.$inferSelect;

// ── Coach Slice 6 — initiated pings (durable nudge queue) ─────────────
export const COACH_PING_KINDS = [
  "cadence_check_in",
  "post_event_review",
  "goal_followup",
  "self_initiated",
] as const;
export type CoachPingKind = (typeof COACH_PING_KINDS)[number];

export const coachInitiatedPings = pgTable("coach_initiated_pings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  kind: varchar("kind", { length: 48 }).notNull(),
  contextRef: jsonb("context_ref").notNull().default(sql`'{}'::jsonb`),
  openingLine: text("opening_line"),
  scheduledFor: timestamp("scheduled_for", { withTimezone: true }).notNull().defaultNow(),
  deliveredAt: timestamp("delivered_at", { withTimezone: true }),
  dismissedAt: timestamp("dismissed_at", { withTimezone: true }),
  // dedupe_key prevents the cadence scheduler from emitting duplicate
  // pings for the same window, and the post-event hook from emitting
  // duplicate pings for the same invoice.
  dedupeKey: varchar("dedupe_key", { length: 255 }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});

export const insertCoachInitiatedPingSchema = createInsertSchema(
  coachInitiatedPings,
).omit({ id: true, createdAt: true, deliveredAt: true, dismissedAt: true });
export type InsertCoachInitiatedPing = z.infer<
  typeof insertCoachInitiatedPingSchema
>;
export type CoachInitiatedPing = typeof coachInitiatedPings.$inferSelect;

// ── Coach Slice 8 — per-user style profile ───────────────────────────
export const coachStyleProfile = pgTable("coach_style_profile", {
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  avgSentenceLen: decimal("avg_sentence_len", { precision: 6, scale: 2 }).notNull().default("14"),
  vocabDiversity: decimal("vocab_diversity", { precision: 4, scale: 3 }).notNull().default("0.5"),
  emojiFreq: decimal("emoji_freq", { precision: 4, scale: 3 }).notNull().default("0"),
  formalityScore: decimal("formality_score", { precision: 4, scale: 3 }).notNull().default("0.5"),
  faithTermsUsed: boolean("faith_terms_used").notNull().default(false),
  profanityOk: boolean("profanity_ok").notNull().default(false),
  sampleCount: integer("sample_count").notNull().default(0),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({ pk: primaryKey({ columns: [t.workspaceId, t.userId] }) }));

export type CoachStyleProfile = typeof coachStyleProfile.$inferSelect;

// ── Coach Hub v2 — long-horizon goal decomposition ──────────────────
// "Write a 250pg book by end of 2026" → milestones at 50/100/150/200pg
// with target_dates. Drift detection compares (today / target_date)
// against (completed milestones / total milestones) to surface
// "you said 100pg by July, you're at 12 — what's going on?"
// updated_at added in migration 0069 so markCoachGoalMilestoneComplete
// can stamp it; see server/storage.ts.
export const coachGoalMilestones = pgTable("coach_goal_milestones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  goalId: varchar("goal_id").notNull(),
  title: text("title").notNull(),
  targetDate: date("target_date"),
  targetMetric: jsonb("target_metric").notNull().default(sql`'{}'::jsonb`),
  sortOrder: integer("sort_order").notNull().default(0),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type CoachGoalMilestone = typeof coachGoalMilestones.$inferSelect;
export const insertCoachGoalMilestoneSchema = createInsertSchema(coachGoalMilestones).omit({
  id: true,
  createdAt: true,
});
export type InsertCoachGoalMilestone = z.infer<typeof insertCoachGoalMilestoneSchema>;

// ── Coach Hub v2 — call-transcript commitment extraction queue ──────
// When the user opts in (coach_user_settings.extractCommitmentsFromCalls),
// a post-call worker reads existing call transcripts, asks the LLM to
// extract verbal commitments ("I'll send the quote tomorrow"), and
// queues them here for review. Status flow:
// pending_review → accepted (turned into a follow-up/goal) | dismissed.
export type CoachExtractedCommitmentStatus =
  | "pending_review"
  | "accepted"
  | "dismissed"
  | "converted";
export type CoachExtractedCommitmentSource = "call_transcript" | "voice_note";

export const coachExtractedCommitments = pgTable("coach_extracted_commitments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  sourceType: varchar("source_type", { length: 32 }).notNull().$type<CoachExtractedCommitmentSource>(),
  sourceId: varchar("source_id", { length: 255 }),
  extractedText: text("extracted_text").notNull(),
  context: jsonb("context").notNull().default(sql`'{}'::jsonb`),
  status: varchar("status", { length: 16 }).notNull().default("pending_review").$type<CoachExtractedCommitmentStatus>(),
  createdGoalId: varchar("created_goal_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  reviewedAt: timestamp("reviewed_at"),
});
export type CoachExtractedCommitment = typeof coachExtractedCommitments.$inferSelect;
export const insertCoachExtractedCommitmentSchema = createInsertSchema(coachExtractedCommitments).omit({
  id: true,
  createdAt: true,
  reviewedAt: true,
});
export type InsertCoachExtractedCommitment = z.infer<typeof insertCoachExtractedCommitmentSchema>;

// ── Coach Hub v2 — rolling per-user signal state ────────────────────
// Updated by the calibration job. The system prompt reads this row
// to know whether the user is in a burnout pattern, what days/times
// they actually follow through on, and which free-text context
// clusters predict productivity. Decoupled from coach_user_settings
// so the calibration job doesn't compete for write locks with the
// user's own toggle changes.
export const coachUserState = pgTable("coach_user_state", {
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  burnoutSignalScore: decimal("burnout_signal_score", { precision: 4, scale: 3 }).notNull().default("0.000"),
  recentReasonPattern: jsonb("recent_reason_pattern").notNull().default(sql`'{}'::jsonb`),
  contextClusters: jsonb("context_clusters").notNull().default(sql`'[]'::jsonb`),
  followthroughByDow: jsonb("followthrough_by_dow").notNull().default(sql`'{}'::jsonb`),
  followthroughByHour: jsonb("followthrough_by_hour").notNull().default(sql`'{}'::jsonb`),
  followthroughByPillar: jsonb("followthrough_by_pillar").notNull().default(sql`'{}'::jsonb`),
  trend7dVs30d: decimal("trend_7d_vs_30d", { precision: 5, scale: 3 }).notNull().default("1.000"),
  lastRecomputedAt: timestamp("last_recomputed_at"),
}, (t) => ({ pk: primaryKey({ columns: [t.workspaceId, t.userId] }) }));
export type CoachUserState = typeof coachUserState.$inferSelect;

// ─────────────────────────────────────────────────────────────────────
// Coach v3 — performance signals, baselines, dips, thresholds
// ─────────────────────────────────────────────────────────────────────
// One row per (user, day). The nightly rollup pulls counts from the
// canonical activity tables and writes here so the dip detector has a
// stable surface to read from. mood_score is filled in from
// coach_mood_logs after the fact (never recomputed from chat).
export const coachPerformanceSignals = pgTable("coach_performance_signals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  signalDate: date("signal_date").notNull(),
  emailsSent: integer("emails_sent").notNull().default(0),
  leadsAdded: integer("leads_added").notNull().default(0),
  callsLogged: integer("calls_logged").notNull().default(0),
  eventsBooked: integer("events_booked").notNull().default(0),
  showsWorked: integer("shows_worked").notNull().default(0),
  moodScore: smallint("mood_score"),
  computedAt: timestamp("computed_at").notNull().defaultNow(),
});
export type CoachPerformanceSignal = typeof coachPerformanceSignals.$inferSelect;

// Per-(user, signal) rolling 90d median. Recomputed nightly. Sample
// size lets the dip detector ignore "first week of work" false dips.
export const coachPerformanceBaselines = pgTable("coach_performance_baselines", {
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  signalName: varchar("signal_name", { length: 32 }).notNull(),
  baselineValue: decimal("baseline_value", { precision: 10, scale: 3 }).notNull().default("0.000"),
  sampleSize: integer("sample_size").notNull().default(0),
  recomputedAt: timestamp("recomputed_at").notNull().defaultNow(),
}, (t) => ({ pk: primaryKey({ columns: [t.workspaceId, t.userId, t.signalName] }) }));
export type CoachPerformanceBaseline = typeof coachPerformanceBaselines.$inferSelect;

// Open-dip state machine. The whole performance flow lives in this
// row's lifecycle: monitoring → user_nudged → user_explained →
// improvement_offered → resolved | manager_escalated.
export type CoachPerfDipState =
  | "monitoring"
  | "user_nudged"
  | "user_explained"
  | "improvement_offered"
  | "manager_escalated"
  | "resolved";

export type CoachPerfDipClassification = "acceptable" | "unclear" | "concerning";

export const coachPerformanceDips = pgTable("coach_performance_dips", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  signalName: varchar("signal_name", { length: 32 }).notNull(),
  noticedAt: timestamp("noticed_at").notNull().defaultNow(),
  baselineValue: decimal("baseline_value", { precision: 10, scale: 3 }).notNull(),
  currentValue: decimal("current_value", { precision: 10, scale: 3 }).notNull(),
  dipPct: decimal("dip_pct", { precision: 5, scale: 3 }).notNull(),
  state: varchar("state", { length: 32 }).notNull().default("monitoring").$type<CoachPerfDipState>(),
  userExplanation: text("user_explanation"),
  aiClassification: varchar("ai_classification", { length: 16 }).$type<CoachPerfDipClassification>(),
  aiClassifierReasoning: text("ai_classifier_reasoning"),
  nextCheckAt: timestamp("next_check_at"),
  resolvedAt: timestamp("resolved_at"),
  managerNotifiedAt: timestamp("manager_notified_at"),
  managerUserId: varchar("manager_user_id"),
});
export type CoachPerformanceDip = typeof coachPerformanceDips.$inferSelect;

// Conversationally-set per-(workspace, user-or-default, signal). The
// admin chatbot writes here when an admin says "give Sarah a longer
// leash on outbound" — set_via_text records the original phrasing.
export const coachPerformanceThresholds = pgTable("coach_performance_thresholds", {
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull().default(""),
  signalName: varchar("signal_name", { length: 32 }).notNull(),
  noticeAfterDays: integer("notice_after_days").notNull().default(10),
  escalateAfterDays: integer("escalate_after_days").notNull().default(21),
  dipThresholdPct: decimal("dip_threshold_pct", { precision: 4, scale: 3 }).notNull().default("0.400"),
  setByUserId: varchar("set_by_user_id"),
  setViaText: text("set_via_text"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({ pk: primaryKey({ columns: [t.workspaceId, t.userId, t.signalName] }) }));
export type CoachPerformanceThreshold = typeof coachPerformanceThresholds.$inferSelect;

// Mood log — STRICTLY private to the user. Manager-side code reads
// only the streak signal (e.g. "≤2 for 5 days") via a dedicated
// summarizer; never the actual scores. See coach-tools.summarize_team_health.
export const coachMoodLogs = pgTable("coach_mood_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  logDate: date("log_date").notNull(),
  moodScore: smallint("mood_score").notNull(),
  note: text("note"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type CoachMoodLog = typeof coachMoodLogs.$inferSelect;
export const insertCoachMoodLogSchema = createInsertSchema(coachMoodLogs).omit({
  id: true,
  createdAt: true,
});
export type InsertCoachMoodLog = z.infer<typeof insertCoachMoodLogSchema>;

// One row per (user, day) the user explicitly clicks "Skip today" / "Skip" on
// the daily check-in dialog. Presence of a row for CURRENT_DATE durably
// suppresses the whole dialog (both the clock-in prompt and the mood prompt)
// for the rest of that calendar day, mirroring the coach_mood_logs
// per-user/day uniqueness pattern.
export const coachCheckinSkips = pgTable("coach_checkin_skips", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  skipDate: date("skip_date").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type CoachCheckinSkip = typeof coachCheckinSkips.$inferSelect;
export const insertCoachCheckinSkipSchema = createInsertSchema(coachCheckinSkips).omit({
  id: true,
  createdAt: true,
});
export type InsertCoachCheckinSkip = z.infer<typeof insertCoachCheckinSkipSchema>;

// Show notes — per-show free-text journal. Loose ref to schedule
// entries (no FK; schedule_entries shape varies by artifact).
export const coachShowNotes = pgTable("coach_show_notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  showId: varchar("show_id").notNull(),
  note: text("note").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type CoachShowNote = typeof coachShowNotes.$inferSelect;

// Coach v3.1 — durable audit trail for every manager escalation.
// Append-only. Indexed by (workspace_id, escalated_at) and
// (workspace_id, subject_user_id, escalated_at) so per-tenant lookups
// stay cheap even as the table grows across 100k workspaces.
export const coachEscalationAudit = pgTable("coach_escalation_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  dipId: varchar("dip_id").notNull(),
  subjectUserId: varchar("subject_user_id").notNull(),
  managerUserId: varchar("manager_user_id"), // null when no manager could be resolved
  signalName: varchar("signal_name", { length: 40 }).notNull(),
  dipPct: decimal("dip_pct", { precision: 5, scale: 2 }).notNull(),
  classification: varchar("classification", { length: 20 }),
  escalationSource: varchar("escalation_source", { length: 20 }).notNull(), // 'auto' | 'manual'
  escalatedAt: timestamp("escalated_at").notNull().defaultNow(),
  context: jsonb("context").default({}),
});
export type CoachEscalationAudit = typeof coachEscalationAudit.$inferSelect;

// ── Super Admin Onboarding Import (Phase 1) ────────────────────────────────
// The mapping-memory asset. Each row records: "for source platform X, source
// object Y, source field Z, the platform-admin team has decided to map this
// to target table A, target column B with transform T". Confidence is
// learned over time (see migration 0072 for the math).
export const onboardingFieldMappings = pgTable("onboarding_field_mappings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sourcePlatform: varchar("source_platform", { length: 64 }).notNull(),
  sourceObject: varchar("source_object", { length: 128 }).notNull(),
  sourceField: varchar("source_field", { length: 255 }).notNull(),
  targetTable: varchar("target_table", { length: 64 }).notNull(),
  targetColumn: varchar("target_column", { length: 64 }).notNull(),
  transformKind: varchar("transform_kind", { length: 32 }).notNull().default("direct"),
  transformConfig: jsonb("transform_config").notNull().default({}),
  sampleValues: jsonb("sample_values").notNull().default([]),
  confidence: decimal("confidence", { precision: 3, scale: 2 }).notNull().default("0.50"),
  usageCount: integer("usage_count").notNull().default(0),
  lastUsedAt: timestamp("last_used_at"),
  confirmedBy: varchar("confirmed_by"),
  confirmedAt: timestamp("confirmed_at").notNull().defaultNow(),
});
export type OnboardingFieldMapping = typeof onboardingFieldMappings.$inferSelect;

export const onboardingImportJobs = pgTable("onboarding_import_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sourcePlatform: varchar("source_platform", { length: 64 }).notNull().default("spreadsheet"),
  sourceLabel: varchar("source_label", { length: 255 }),
  targetWorkspaceId: varchar("target_workspace_id").notNull(),
  // draft | ingesting | mapping | dry_run | committing | committed | aborted
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  operatorUserId: varchar("operator_user_id").notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  committedAt: timestamp("committed_at"),
  abortedAt: timestamp("aborted_at"),
  abortReason: text("abort_reason"),
});
export type OnboardingImportJob = typeof onboardingImportJobs.$inferSelect;

export const onboardingImportFiles = pgTable("onboarding_import_files", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobId: varchar("job_id").notNull(),
  fileName: varchar("file_name", { length: 512 }).notNull(),
  objectPath: varchar("object_path", { length: 1024 }).notNull(),
  fileType: varchar("file_type", { length: 16 }).notNull(),
  byteSize: bigint("byte_size", { mode: "number" }),
  sha256: varchar("sha256", { length: 64 }),
  sheetName: varchar("sheet_name", { length: 255 }),
  rowCount: integer("row_count"),
  headerColumns: text("header_columns").array(),
  inventory: jsonb("inventory").notNull().default({}),
  uploadedAt: timestamp("uploaded_at").notNull().defaultNow(),
});
export type OnboardingImportFile = typeof onboardingImportFiles.$inferSelect;

export const onboardingJobDecisions = pgTable("onboarding_job_decisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobId: varchar("job_id").notNull(),
  fileId: varchar("file_id").notNull(),
  sourceField: varchar("source_field", { length: 255 }).notNull(),
  targetTable: varchar("target_table", { length: 64 }).notNull(),
  targetColumn: varchar("target_column", { length: 64 }).notNull(),
  transformKind: varchar("transform_kind", { length: 32 }).notNull().default("direct"),
  transformConfig: jsonb("transform_config").notNull().default({}),
  confidence: decimal("confidence", { precision: 3, scale: 2 }),
  decidedBy: varchar("decided_by").notNull(),
  decidedAt: timestamp("decided_at").notNull().defaultNow(),
  appliedToMemory: boolean("applied_to_memory").notNull().default(false),
});
export type OnboardingJobDecision = typeof onboardingJobDecisions.$inferSelect;

export const onboardingImportAudit = pgTable("onboarding_import_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  jobId: varchar("job_id"),
  actorUserId: varchar("actor_user_id"),
  action: varchar("action", { length: 32 }).notNull(),
  details: jsonb("details").notNull().default({}),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type OnboardingImportAudit = typeof onboardingImportAudit.$inferSelect;


// ── Task #704 — Two-layer approvals workflow ───────────────────────────────
// `approval_policies`     = admin-set workspace-wide thresholds per category.
// `approval_overrides`    = manager-set *stricter* thresholds for their team.
// `approval_requests`     = each held action awaiting a decision.
// `approval_audit`        = append-only log of every decision (and the
//                            policy/override snapshot that triggered it).
export type ApprovalCategory = "discount" | "contract_exception" | "refund";
export type ApprovalStatus = "pending" | "approved" | "rejected" | "expired" | "cancelled";

export const approvalPolicies = pgTable("approval_policies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  category: varchar("category", { length: 32 }).notNull(),
  // OFF by default for every category. Discounts is specifically required
  // to default OFF (admin opts in). The other two default OFF as well so
  // existing workspaces don't suddenly start holding actions.
  enabled: boolean("enabled").notNull().default(false),
  // Numeric threshold above which approval is required. For discount this
  // is a percentage (e.g. 10 = 10%). For refund this is dollars.
  // For contract_exception this is unused (engine treats any flagged
  // field change as crossing the threshold).
  thresholdValue: decimal("threshold_value", { precision: 12, scale: 2 }),
  // Field-level flags for contract exceptions stored as a string array.
  // e.g. ['cancellation_terms', 'late_fee_schedule']
  contractFields: text("contract_fields").array().default(sql`ARRAY[]::text[]`),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
}, (table) => ({
  workspaceCategoryUq: uniqueIndex("approval_policies_workspace_category_uq")
    .on(table.workspaceId, table.category),
}));

export const insertApprovalPolicySchema = createInsertSchema(approvalPolicies).omit({
  id: true,
  updatedAt: true,
});
export type InsertApprovalPolicy = z.infer<typeof insertApprovalPolicySchema>;
export type ApprovalPolicy = typeof approvalPolicies.$inferSelect;

export const approvalOverrides = pgTable("approval_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  // The manager whose subtree this override applies to. Reports of this
  // user inherit the override; nobody else does.
  managerUserId: varchar("manager_user_id").notNull(),
  category: varchar("category", { length: 32 }).notNull(),
  // Always stricter than the corresponding admin policy. Engine enforces.
  thresholdValue: decimal("threshold_value", { precision: 12, scale: 2 }),
  contractFields: text("contract_fields").array().default(sql`ARRAY[]::text[]`),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by"),
}, (table) => ({
  managerCategoryUq: uniqueIndex("approval_overrides_mgr_category_uq")
    .on(table.workspaceId, table.managerUserId, table.category),
}));

export const insertApprovalOverrideSchema = createInsertSchema(approvalOverrides).omit({
  id: true,
  updatedAt: true,
});
export type InsertApprovalOverride = z.infer<typeof insertApprovalOverrideSchema>;
export type ApprovalOverride = typeof approvalOverrides.$inferSelect;

export const approvalRequests = pgTable("approval_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  requesterUserId: varchar("requester_user_id").notNull(),
  // Who the request was routed to (manager, or admin if no manager).
  assignedToUserId: varchar("assigned_to_user_id"),
  category: varchar("category", { length: 32 }).notNull(),
  // What kind of underlying entity is being held (e.g. 'invoice', 'contract', 'refund').
  entityType: varchar("entity_type", { length: 64 }),
  entityId: varchar("entity_id"),
  // Short human label shown in the inbox (e.g. "15% discount on INV-1042").
  summary: varchar("summary", { length: 500 }),
  // Snapshot of the payload that triggered the request (before/after).
  payload: jsonb("payload").notNull().default({}),
  // The effective threshold at request time (what the request crossed).
  effectiveThreshold: decimal("effective_threshold", { precision: 12, scale: 2 }),
  thresholdSource: varchar("threshold_source", { length: 16 }), // 'policy' | 'override'
  status: varchar("status", { length: 16 }).notNull().default("pending"),
  decidedByUserId: varchar("decided_by_user_id"),
  decisionNote: text("decision_note"),
  decidedAt: timestamp("decided_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  workspaceStatusIdx: index("approval_requests_ws_status_idx")
    .on(table.workspaceId, table.status),
  assignedIdx: index("approval_requests_assigned_idx")
    .on(table.assignedToUserId, table.status),
}));

export const insertApprovalRequestSchema = createInsertSchema(approvalRequests).omit({
  id: true,
  createdAt: true,
  decidedAt: true,
});
export type InsertApprovalRequest = z.infer<typeof insertApprovalRequestSchema>;
export type ApprovalRequest = typeof approvalRequests.$inferSelect;

export const approvalAudit = pgTable("approval_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  requestId: varchar("request_id").notNull(),
  action: varchar("action", { length: 32 }).notNull(), // 'created' | 'approved' | 'rejected' | 'expired' | 'cancelled'
  actorUserId: varchar("actor_user_id"),
  note: text("note"),
  snapshot: jsonb("snapshot").notNull().default({}),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  requestIdx: index("approval_audit_request_idx").on(table.requestId),
  workspaceCreatedIdx: index("approval_audit_ws_created_idx")
    .on(table.workspaceId, table.createdAt),
}));

export type ApprovalAuditEntry = typeof approvalAudit.$inferSelect;

// Task #704 — Manager Panel: pending seat requests.
// A manager submits a seat request from the Manager Panel; it lands here
// in 'pending' and surfaces in the Admin Approvals section. Only the
// admin can transition to approved/rejected. Approval means "go invite
// this person" — the invitation itself is out-of-band today.
export const managerSeatRequests = pgTable("manager_seat_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  requestedByUserId: varchar("requested_by_user_id").notNull(),
  email: varchar("email", { length: 320 }).notNull(),
  role: varchar("role", { length: 32 }).notNull(),
  reason: text("reason"),
  status: varchar("status", { length: 16 }).notNull().default("pending"), // 'pending' | 'approved' | 'rejected'
  decidedByUserId: varchar("decided_by_user_id"),
  decisionNote: text("decision_note"),
  decidedAt: timestamp("decided_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  wsStatusIdx: index("manager_seat_requests_ws_status_idx").on(table.workspaceId, table.status),
  requesterIdx: index("manager_seat_requests_requester_idx").on(table.workspaceId, table.requestedByUserId),
}));

export const insertManagerSeatRequestSchema = createInsertSchema(managerSeatRequests).omit({
  id: true,
  decidedByUserId: true,
  decisionNote: true,
  decidedAt: true,
  createdAt: true,
  status: true,
});
export type InsertManagerSeatRequest = z.infer<typeof insertManagerSeatRequestSchema>;
export type ManagerSeatRequest = typeof managerSeatRequests.$inferSelect;

// ─── Email Verification Cache ─────────────────────────────────────────────────
// Workspace-agnostic: an invalid email is invalid regardless of which tenant
// has it. Keyed by normalized (lowercase) email address. 30-day TTL matches
// NeverBounce's free-duplicate cache window — re-checking within 30 days is
// free; after that a fresh API call is made and the row is upserted.
export const emailVerifications = pgTable("email_verifications", {
  email:      text("email").primaryKey(),
  result:     varchar("result", { length: 20 }).notNull(), // valid|invalid|disposable|catchall|unknown
  verifiedAt: timestamp("verified_at").notNull().defaultNow(),
}, (table) => ({
  verifiedAtIdx: index("email_verifications_verified_at_idx").on(table.verifiedAt),
}));
export type EmailVerification = typeof emailVerifications.$inferSelect;

// ─── Waitlist Submissions ──────────────────────────────────────────────────────
// Public, unauthenticated — collected from the marketing site's
// "Clarik is launching soon" waitlist modal. Email is unique so duplicate
// submissions are idempotent (ON CONFLICT DO NOTHING on the server).
export const waitlistSubmissions = pgTable("waitlist_submissions", {
  id:        uuid("id").primaryKey().defaultRandom(),
  name:      text("name").notNull(),
  email:     text("email").notNull(),
  phone:     text("phone"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => ({
  emailIdx: uniqueIndex("waitlist_submissions_email_idx").on(table.email),
}));
export const insertWaitlistSubmissionSchema = createInsertSchema(waitlistSubmissions).omit({ id: true, createdAt: true });
export type InsertWaitlistSubmission = z.infer<typeof insertWaitlistSubmissionSchema>;
export type WaitlistSubmission = typeof waitlistSubmissions.$inferSelect;

// ─── Per-User Credit Allocations ─────────────────────────────────────────────
// Tracks individual credit balances when workspaces.aiCreditMode or
// leadCreditMode is 'per_user_monthly' or 'per_user_allocated', OR when
// the corresponding *Payer is 'user' (users buy their own credits).
// One row per (workspaceId, userId, creditType). Upserted on first spend/top-up.
export type CreditType = "ai" | "lead";
export type CreditMode = "shared" | "per_user_monthly" | "per_user_allocated";
export type CreditPayer = "workspace" | "user";
export type CreditOveragePolicy = "hard_stop" | "fallback_shared" | "allow_negative";

export const userCreditAllocations = pgTable("user_credit_allocations", {
  id:              uuid("id").primaryKey().defaultRandom(),
  workspaceId:     varchar("workspace_id").notNull(),
  userId:          varchar("user_id").notNull(),
  // 'ai' balances are in cents; 'lead' balances are credit counts
  creditType:      varchar("credit_type", { length: 8 }).notNull().$type<CreditType>(),
  // null = inherit from workspace aiCreditDefaultByRole / leadCreditDefaultByRole
  monthlyAllotment: integer("monthly_allotment"),
  // Spendable balance — can go negative up to the workspace's negativeLimit
  currentBalance:  integer("current_balance").notNull().default(0),
  // Credits carried forward from previous month(s), included in currentBalance
  rolloverBalance: integer("rollover_balance").notNull().default(0),
  lastResetAt:     timestamp("last_reset_at", { withTimezone: true }),
  createdAt:       timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt:       timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (table) => ({
  uniq:    uniqueIndex("user_credit_allocations_uniq").on(table.workspaceId, table.userId, table.creditType),
  wsUser:  index("user_credit_allocations_ws_user_idx").on(table.workspaceId, table.userId),
}));
export const insertUserCreditAllocationSchema = createInsertSchema(userCreditAllocations).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertUserCreditAllocation = z.infer<typeof insertUserCreditAllocationSchema>;
export type UserCreditAllocation = typeof userCreditAllocations.$inferSelect;

// ─── Calendar Meeting Requests ────────────────────────────────────────────────
// Calendly-style slot-proposal requests between org members.
// The requester proposes 1-3 time slots; the recipient picks one (or declines).
// proposedSlots is a jsonb array of { start: ISO, end: ISO } objects.
export type MeetingRequestStatus = "pending" | "accepted" | "declined";

export const calendarMeetingRequests = pgTable("calendar_meeting_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  requesterId: varchar("requester_id").notNull(),
  recipientId: varchar("recipient_id").notNull(),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  proposedSlots: jsonb("proposed_slots").notNull().default(sql`'[]'::jsonb`),
  status: varchar("status", { length: 16 }).notNull().default("pending").$type<MeetingRequestStatus>(),
  acceptedSlotIndex: integer("accepted_slot_index"),
  requesterTimezone: varchar("requester_timezone", { length: 64 }),
  recipientTimezone: varchar("recipient_timezone", { length: 64 }),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceIdx: index("meeting_requests_workspace_idx").on(t.workspaceId),
  recipientIdx: index("meeting_requests_recipient_idx").on(t.workspaceId, t.recipientId, t.status),
  requesterIdx: index("meeting_requests_requester_idx").on(t.workspaceId, t.requesterId),
}));

export const insertCalendarMeetingRequestSchema = createInsertSchema(calendarMeetingRequests, {
  status: z.enum(["pending", "accepted", "declined"]).optional(),
}).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertCalendarMeetingRequest = z.infer<typeof insertCalendarMeetingRequestSchema>;
export type CalendarMeetingRequest = typeof calendarMeetingRequests.$inferSelect;

// ── Super-admin IP allowlist (Task #53) ─────────────────────────────────────
// Trusted IP ranges / hosts for the platform admin login. Logins from IPs
// outside this list trigger dual-MFA (SMS + email). When the list is empty
// the feature is treated as "not configured" and the normal MFA flow applies.
export const superAdminIpAllowlist = pgTable("super_admin_ip_allowlist", {
  id:        uuid("id").primaryKey().defaultRandom(),
  cidr:      varchar("cidr", { length: 64 }).notNull(),
  label:     varchar("label", { length: 255 }).notNull().default(""),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});
export const insertSuperAdminIpAllowlistSchema = createInsertSchema(superAdminIpAllowlist).omit({ id: true, createdAt: true });
export type InsertSuperAdminIpAllowlist = z.infer<typeof insertSuperAdminIpAllowlistSchema>;
export type SuperAdminIpAllowlist = typeof superAdminIpAllowlist.$inferSelect;

// ── Super-admin dual-MFA challenges (Task #53) ──────────────────────────────
// Short-lived challenge rows created when a super-admin logs in from an
// IP that is NOT on the allowlist. Both sms_code_hash and email_code_hash
// must be verified (independently) before a session is issued.
export const superAdminMfaChallenges = pgTable("super_admin_mfa_challenges", {
  id:              uuid("id").primaryKey().defaultRandom(),
  platformUserId:  varchar("platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  loginToken:      text("login_token").notNull(),
  smsCodeHash:     text("sms_code_hash"),
  emailCodeHash:   text("email_code_hash").notNull(),
  attempts:        integer("attempts").notNull().default(0),
  smsVerifiedAt:   timestamp("sms_verified_at"),
  emailVerifiedAt: timestamp("email_verified_at"),
  consumedAt:      timestamp("consumed_at"),
  expiresAt:       timestamp("expires_at").notNull(),
  createdAt:       timestamp("created_at").defaultNow().notNull(),
});
export const insertSuperAdminMfaChallengeSchema = createInsertSchema(superAdminMfaChallenges).omit({ id: true, createdAt: true });
export type InsertSuperAdminMfaChallenge = z.infer<typeof insertSuperAdminMfaChallengeSchema>;
export type SuperAdminMfaChallenge = typeof superAdminMfaChallenges.$inferSelect;

// ── Platform login MFA challenges (Task #120) ───────────────────────────────
// Short-lived single-factor MFA challenges issued by the normal
// /api/platform/login flow when the device is not yet trusted. Previously
// stored in a per-process Map in login-state.ts, which broke on the multi-
// replica fleet (sign-in landed on replica A; verify on replica B → 401).
// Moving the store to the DB also lets us accept *any* unexpired code for
// a given (platformUserId, deviceCookieHash) — fixes the "user clicked Sign
// In twice while email was slow, first code arrives, server rejects it
// because the latest loginToken doesn't match" lockout.
export const platformLoginMfaChallenges = pgTable("platform_login_mfa_challenges", {
  id:               uuid("id").primaryKey().defaultRandom(),
  loginToken:       text("login_token").notNull().unique(),
  platformUserId:   varchar("platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  deviceCookieHash: text("device_cookie_hash").notNull(),
  codeHash:         text("code_hash").notNull(),
  attempts:         integer("attempts").notNull().default(0),
  consumedAt:       timestamp("consumed_at"),
  expiresAt:        timestamp("expires_at").notNull(),
  createdAt:        timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  byUserDevice: index("platform_login_mfa_challenges_user_device_idx").on(t.platformUserId, t.deviceCookieHash),
  byExpiresAt:  index("platform_login_mfa_challenges_expires_at_idx").on(t.expiresAt),
}));
export const insertPlatformLoginMfaChallengeSchema = createInsertSchema(platformLoginMfaChallenges).omit({ id: true, createdAt: true });
export type InsertPlatformLoginMfaChallenge = z.infer<typeof insertPlatformLoginMfaChallengeSchema>;
export type PlatformLoginMfaChallenge = typeof platformLoginMfaChallenges.$inferSelect;

// ── Platform MFA enrollment / step-up challenges ────────────────────────────
// Short-lived one-time codes used by the in-session Security hub to verify a
// platform admin's phone (enroll_sms), primary email (enroll_email), or
// secondary backup email (verify_secondary_email). Mirrors the tenant
// `user_mfa_challenges` table but references `platform_users` so platform
// admins (who are NOT rows in `users`) can enroll factors. Codes are bcrypt
// hashed; attempts are capped to defeat brute force.
export const platformMfaChallenges = pgTable(
  "platform_mfa_challenges",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    platformUserId: varchar("platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
    // 'enroll_sms' | 'enroll_email' | 'verify_secondary_email'
    purpose: varchar("purpose", { length: 32 }).notNull(),
    codeHash: text("code_hash").notNull(),
    // For enroll_sms, the destination phone (E.164). For email purposes, the
    // destination email being verified. Promoted onto platform_users on verify.
    destination: varchar("destination", { length: 255 }),
    attempts: integer("attempts").notNull().default(0),
    expiresAt: timestamp("expires_at").notNull(),
    consumedAt: timestamp("consumed_at"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (t) => ({
    byUserPurpose: index("platform_mfa_challenges_user_purpose_idx").on(t.platformUserId, t.purpose),
    byExpiresAt: index("platform_mfa_challenges_expires_at_idx").on(t.expiresAt),
  }),
);
export const insertPlatformMfaChallengeSchema = createInsertSchema(platformMfaChallenges).omit({ id: true, createdAt: true, consumedAt: true, attempts: true });
export type InsertPlatformMfaChallenge = z.infer<typeof insertPlatformMfaChallengeSchema>;
export type PlatformMfaChallenge = typeof platformMfaChallenges.$inferSelect;

// ── Super-admin break-glass recovery challenges (any-2-of-N) ─────────────────
// Issued by the public account-recovery flow. The admin proves ANY TWO of
// four independent factors — SMS code, primary-email code, secondary-email
// code, or a printed backup code — to mint a session even from a non-
// allowlisted IP. Each factor's code is bcrypt hashed; backup codes are
// verified against platform_users.mfa_backup_codes_hashed_json directly (no
// hash stored here). Single-use via consumedAt; attempts capped.
export const superAdminRecoveryChallenges = pgTable("super_admin_recovery_challenges", {
  id: uuid("id").primaryKey().defaultRandom(),
  platformUserId: varchar("platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  recoveryToken: text("recovery_token").notNull(),
  smsCodeHash: text("sms_code_hash"),
  primaryEmailCodeHash: text("primary_email_code_hash"),
  secondaryEmailCodeHash: text("secondary_email_code_hash"),
  smsVerifiedAt: timestamp("sms_verified_at"),
  primaryEmailVerifiedAt: timestamp("primary_email_verified_at"),
  secondaryEmailVerifiedAt: timestamp("secondary_email_verified_at"),
  backupCodeVerifiedAt: timestamp("backup_code_verified_at"),
  attempts: integer("attempts").notNull().default(0),
  consumedAt: timestamp("consumed_at"),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  byToken: index("super_admin_recovery_challenges_token_idx").on(t.recoveryToken),
  byExpiresAt: index("super_admin_recovery_challenges_expires_at_idx").on(t.expiresAt),
}));
export const insertSuperAdminRecoveryChallengeSchema = createInsertSchema(superAdminRecoveryChallenges).omit({ id: true, createdAt: true });
export type InsertSuperAdminRecoveryChallenge = z.infer<typeof insertSuperAdminRecoveryChallengeSchema>;
export type SuperAdminRecoveryChallenge = typeof superAdminRecoveryChallenges.$inferSelect;

// ── Platform password-reset tokens (forgot-password flow) ───────────────────
// Single-use, short-lived tokens for the platform-login "Forgot password?"
// flow. Only a SHA-256 hash of the raw token is stored. References
// platform_users (the tenant `password_reset_tokens` table FKs to `users`
// and cannot be reused for platform admins). A successful reset rewrites the
// password hash only — it never bypasses the IP hard-block or MFA.
export const platformPasswordResetTokens = pgTable("platform_password_reset_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platformUserId: varchar("platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  tokenHash: text("token_hash").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  byTokenHash: uniqueIndex("platform_password_reset_tokens_hash_idx").on(t.tokenHash),
  byUser: index("platform_password_reset_tokens_user_idx").on(t.platformUserId),
  byExpiresAt: index("platform_password_reset_tokens_expires_at_idx").on(t.expiresAt),
}));
export const insertPlatformPasswordResetTokenSchema = createInsertSchema(platformPasswordResetTokens).omit({ id: true, createdAt: true });
export type InsertPlatformPasswordResetToken = z.infer<typeof insertPlatformPasswordResetTokenSchema>;
export type PlatformPasswordResetToken = typeof platformPasswordResetTokens.$inferSelect;

// ── Client Contacts ───────────────────────────────────────────────────────────
// Additional named contacts attached to a client record (e.g. HR director,
// venue manager, billing dept). Storage only — drives CC suggestions and
// assistant email tooling. Does not overwrite the primary contact fields.
export const clientContacts = pgTable("client_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  clientId: varchar("client_id").notNull().references(() => clients.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  title: varchar("title", { length: 255 }),
  phone: varchar("phone", { length: 50 }),
  email: varchar("email", { length: 255 }),
  // Provenance — distinguishes AI-discovered contacts (ported from lead_contacts
  // on lead→client conversion) from manually-entered ones. `aiGenerated` drives
  // the tiny asterisk in the UI; `source` carries the originating lead_contact
  // source (e.g. deep-analyst-booker) for traceability. Manual adds set
  // aiGenerated=false / source="manual".
  aiGenerated: boolean("ai_generated").notNull().default(false),
  source: varchar("source", { length: 64 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  clientIdx: index("client_contacts_client_idx").on(t.clientId),
  workspaceIdx: index("client_contacts_workspace_idx").on(t.workspaceId),
}));

export const insertClientContactSchema = createInsertSchema(clientContacts).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertClientContact = z.infer<typeof insertClientContactSchema>;
export type ClientContact = typeof clientContacts.$inferSelect;

// ── Lead Contacts ─────────────────────────────────────────────────────────────
// Additional named contacts attached to a lead record (e.g. talent buyer,
// entertainment director, HR, executive assistant, CEO). Mirrors client_contacts
// but adds `source` (where we found them — apollo/scrape/web/pattern) and
// `emailConfidence` so the Deep Analyst booker hunt can stash every contact it
// pulls (one primary + the rest) without overwriting the lead's primary fields.
export const leadContacts = pgTable("lead_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  leadId: varchar("lead_id").notNull().references(() => leads.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  title: varchar("title", { length: 255 }),
  phone: varchar("phone", { length: 50 }),
  email: varchar("email", { length: 255 }),
  emailConfidence: varchar("email_confidence", { length: 32 }),
  source: varchar("source", { length: 64 }),
  isPrimary: boolean("is_primary").default(false),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  leadIdx: index("lead_contacts_lead_idx").on(t.leadId),
  workspaceIdx: index("lead_contacts_workspace_idx").on(t.workspaceId),
}));

export const insertLeadContactSchema = createInsertSchema(leadContacts).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertLeadContact = z.infer<typeof insertLeadContactSchema>;
export type LeadContact = typeof leadContacts.$inferSelect;

// ── Task #59 — Master Schedule AI Tour Planner ───────────────────────────────

// Step 1: Field-level source tracking on schedule entries.
// fieldSources: { [fieldName]: 'user' | 'ai' | 'empty' }
// AI write tools skip fields where source is 'user'.
// isDraft + draftGroupId: AI-written rows staged pending user confirmation.
// draftSnapshot: snapshot of previous values at commit time (for undo).
// These are added as columns to scheduleEntries via migration 0101.

// Step 2: Workspace-configurable status options.
// "Day Off" is pinned (isSystemLocked=true) and cannot be deleted.
// Seeded defaults: SL Shows, Public Show, Travel Day, Outside Show, Day Off.
export const workspaceScheduleStatuses = pgTable("workspace_schedule_statuses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  label: varchar("label", { length: 100 }).notNull(),
  color: varchar("color", { length: 20 }).notNull().default("#6b7280"),
  isSystemLocked: boolean("is_system_locked").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceIdx: index("wss_workspace_idx").on(t.workspaceId),
}));
export const insertWorkspaceScheduleStatusSchema = createInsertSchema(workspaceScheduleStatuses).omit({ id: true, createdAt: true });
export type InsertWorkspaceScheduleStatus = z.infer<typeof insertWorkspaceScheduleStatusSchema>;
export type WorkspaceScheduleStatus = typeof workspaceScheduleStatuses.$inferSelect;

// Step 3: Per-workspace custom columns beyond the hard-coded set.
// Hard-coded columns (Date, Day, Status, City, State, Notes, Drive Time,
// Total Booking $, Region) are rendered from a constant — not stored here.
// This table holds optional extras like "Option B", "Lead Pages", "Est. Days".
export const workspaceScheduleColumns = pgTable("workspace_schedule_columns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  key: varchar("key", { length: 50 }).notNull(),
  label: varchar("label", { length: 100 }).notNull(),
  dataType: varchar("data_type", { length: 20 }).notNull().default("text"),
  sortOrder: integer("sort_order").notNull().default(0),
  isVisible: boolean("is_visible").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  workspaceIdx: index("wsc_workspace_idx").on(t.workspaceId),
  workspaceKeyUniq: uniqueIndex("wsc_workspace_key_uniq").on(t.workspaceId, t.key),
}));
export const insertWorkspaceScheduleColumnSchema = createInsertSchema(workspaceScheduleColumns).omit({ id: true, createdAt: true });
export type InsertWorkspaceScheduleColumn = z.infer<typeof insertWorkspaceScheduleColumnSchema>;
export type WorkspaceScheduleColumn = typeof workspaceScheduleColumns.$inferSelect;

// Step 4: Tour planning sessions and messages.
// Sessions are scoped by workspace + user + year.
// Messages store the conversation history for context loading.
export const tourPlanningSessions = pgTable("tour_planning_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  year: integer("year").notNull(),
  name: varchar("name", { length: 255 }).notNull().default("Tour Plan"),
  // Active talent for this planning session — scopes all AI analysis and tool queries.
  talentId: varchar("talent_id"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceUserYearIdx: index("tps_workspace_user_year_idx").on(t.workspaceId, t.userId, t.year),
}));
export const insertTourPlanningSessionSchema = createInsertSchema(tourPlanningSessions).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTourPlanningSession = z.infer<typeof insertTourPlanningSessionSchema>;
export type TourPlanningSession = typeof tourPlanningSessions.$inferSelect;

export const tourPlanningMessages = pgTable("tour_planning_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: varchar("session_id").notNull().references(() => tourPlanningSessions.id, { onDelete: "cascade" }),
  role: varchar("role", { length: 20 }).notNull(),
  content: text("content").notNull(),
  tokenCount: integer("token_count"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  sessionIdx: index("tpm_session_idx").on(t.sessionId),
  sessionCreatedIdx: index("tpm_session_created_idx").on(t.sessionId, t.createdAt),
}));
export const insertTourPlanningMessageSchema = createInsertSchema(tourPlanningMessages).omit({ id: true, createdAt: true });
export type InsertTourPlanningMessage = z.infer<typeof insertTourPlanningMessageSchema>;
export type TourPlanningMessage = typeof tourPlanningMessages.$inferSelect;

// Step 6: Schedule runs — contiguous blocks of consecutive days sharing
// the same city/state. A Day Off or any calendar gap breaks a run.
// Auto-detected and stored whenever entries are created or updated.
export const scheduleRuns = pgTable("schedule_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  talentId: varchar("talent_id").references(() => talent.id, { onDelete: "cascade" }),
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date").notNull(),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  entryIds: text("entry_ids").array().notNull().default(sql`'{}'::text[]`),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  workspaceTalentIdx: index("sr_workspace_talent_idx").on(t.workspaceId, t.talentId),
  dateRangeIdx: index("sr_date_range_idx").on(t.workspaceId, t.startDate, t.endDate),
}));
export const insertScheduleRunSchema = createInsertSchema(scheduleRuns).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertScheduleRun = z.infer<typeof insertScheduleRunSchema>;
export type ScheduleRun = typeof scheduleRuns.$inferSelect;

// ── Sales Training Calls (Task #68) ─────────────────────────────────────────
// Platform-wide, always-on capture of every voice-assistant session. Entirely
// separate from the dialer `calls` table (Twilio outbound) — this table stores
// AI-assistant sessions for training purposes only.
// No workspace user ever sees these rows; only the super-admin panel reads them.
export type SalesTrainingCallStatus = "pending" | "scored" | "failed";

export const salesTrainingCalls = pgTable("sales_training_calls", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  sessionId: varchar("session_id").notNull().unique(),
  userId: varchar("user_id").notNull(),
  leadId: varchar("lead_id"),
  clientId: varchar("client_id"),
  clientType: varchar("client_type", { length: 255 }),
  // JSONB array of { role, text, timestamp_seconds }
  transcript: jsonb("transcript").notNull().default(sql`'[]'::jsonb`),
  turnCount: integer("turn_count").notNull().default(0),
  durationSeconds: integer("duration_seconds").notNull().default(0),
  // Classification tags: e.g. ['follow_up', 'cold_outreach', 'negotiation']
  tags: text("tags").array().notNull().default(sql`'{}'::text[]`),
  // JSONB scoring result from Claude — null until scored
  aiScores: jsonb("ai_scores"),
  isWinningCall: boolean("is_winning_call").notNull().default(false),
  outcomeUpdatedAt: timestamp("outcome_updated_at"),
  scoredAt: timestamp("scored_at"),
  scoreVersion: integer("score_version").notNull().default(0),
  status: varchar("status", { length: 16 }).notNull().default("pending").$type<SalesTrainingCallStatus>(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  sessionIdx: uniqueIndex("stc_session_unique_idx").on(t.sessionId),
  workspaceCreatedIdx: index("stc_ws_created_idx").on(t.workspaceId, t.createdAt),
  leadIdx: index("stc_lead_idx").on(t.leadId),
  statusIdx: index("stc_status_idx").on(t.status),
}));

export const insertSalesTrainingCallSchema = createInsertSchema(salesTrainingCalls).omit({ id: true, createdAt: true });
export type InsertSalesTrainingCall = z.infer<typeof insertSalesTrainingCallSchema>;
export type SalesTrainingCall = typeof salesTrainingCalls.$inferSelect;

// ════════════════════════════════════════════════════════════════════
// Assistant Learning Loop (superadmin-only plumbing)
// ════════════════════════════════════════════════════════════════════
// Phase 1: capture every assistant turn so Loops 2 & 3 have raw signal
// to learn from. Nothing in any of these tables is shown to normal
// admins — the entire surface lives under /super-admin/ai-usage.
//
// Within-workspace tables (assistant_interactions, *_user_profile_signals,
// *_workspace_daily_state) may store raw text — it's the workspace's own
// data. Cross-workspace table (assistant_org_patterns) is k-anonymised
// in the aggregation pipeline, never at the read layer.

export const assistantInteractions = pgTable("assistant_interactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  surface: varchar("surface", { length: 24 }).notNull(),
  sessionId: varchar("session_id"),
  role: varchar("role", { length: 32 }),
  questionText: text("question_text"),
  questionHash: varchar("question_hash", { length: 64 }),
  intentCategory: varchar("intent_category", { length: 48 }),
  toolsUsed: text("tools_used").array().notNull().default(sql`'{}'::text[]`),
  toolCount: integer("tool_count").notNull().default(0),
  modelUsed: varchar("model_used", { length: 48 }),
  latencyMs: integer("latency_ms"),
  inputTokens: integer("input_tokens"),
  outputTokens: integer("output_tokens"),
  answerText: text("answer_text"),
  answerChars: integer("answer_chars"),
  hadError: boolean("had_error").notNull().default(false),
  errorMessage: text("error_message"),
  userFollowupSignal: varchar("user_followup_signal", { length: 16 }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});
export const insertAssistantInteractionSchema = createInsertSchema(assistantInteractions).omit({ id: true, createdAt: true });
export type InsertAssistantInteraction = z.infer<typeof insertAssistantInteractionSchema>;
export type AssistantInteraction = typeof assistantInteractions.$inferSelect;

export const assistantUserProfileSignals = pgTable("assistant_user_profile_signals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  kind: varchar("kind", { length: 48 }).notNull(),
  payload: jsonb("payload").notNull(),
  sampleCount: integer("sample_count").notNull().default(1),
  confidence: decimal("confidence", { precision: 3, scale: 2 }).notNull().default("0.50"),
  lastUpdatedAt: timestamp("last_updated_at", { withTimezone: true }).notNull().defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});
export type AssistantUserProfileSignal = typeof assistantUserProfileSignals.$inferSelect;

export const assistantWorkspaceDailyState = pgTable("assistant_workspace_daily_state", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  day: date("day").notNull(),
  summaryText: text("summary_text"),
  metrics: jsonb("metrics").notNull().default(sql`'{}'::jsonb`),
  notableEvents: jsonb("notable_events").notNull().default(sql`'[]'::jsonb`),
  generatorModel: varchar("generator_model", { length: 48 }),
  inputTokens: integer("input_tokens"),
  outputTokens: integer("output_tokens"),
  generatedAt: timestamp("generated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type AssistantWorkspaceDailyState = typeof assistantWorkspaceDailyState.$inferSelect;

export const assistantOrgPatterns = pgTable("assistant_org_patterns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  bucketKey: varchar("bucket_key", { length: 128 }).notNull(),
  patternKind: varchar("pattern_kind", { length: 48 }).notNull(),
  payload: jsonb("payload").notNull(),
  sampleWorkspaces: integer("sample_workspaces").notNull(),
  periodStart: date("period_start").notNull(),
  periodEnd: date("period_end").notNull(),
  generatedAt: timestamp("generated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  // Migration 0114. Idempotency for daily reruns / replays.
  uqAssistOrgPattern: uniqueIndex("uq_assist_org_pattern").on(
    t.bucketKey, t.patternKind, t.periodStart, t.periodEnd,
  ),
}));
export type AssistantOrgPattern = typeof assistantOrgPatterns.$inferSelect;

export const assistantLearningProposals = pgTable("assistant_learning_proposals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  proposedAt: timestamp("proposed_at", { withTimezone: true }).notNull().defaultNow(),
  proposalKind: varchar("proposal_kind", { length: 48 }).notNull(),
  title: varchar("title", { length: 255 }).notNull(),
  rationale: text("rationale").notNull(),
  payload: jsonb("payload").notNull().default(sql`'{}'::jsonb`),
  evidence: jsonb("evidence").notNull().default(sql`'{}'::jsonb`),
  status: varchar("status", { length: 16 }).notNull().default("pending"),
  reviewedBy: varchar("reviewed_by"),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  reviewNote: text("review_note"),
});
export type AssistantLearningProposal = typeof assistantLearningProposals.$inferSelect;

// ============================================================================
// Task #102 — Clarik-as-Tenant SaaS funnel tables
// ============================================================================
// platform_customers: one row per Clarik customer workspace. Distinct from
//   the entertainment `clients` table (which is per-tenant booking clients).
//   Linked 1:1 to the customer's own tenant workspace, optionally to a
//   Stripe customer. The "we sold Clarik to a new customer" entity.
// platform_customer_contacts: mirror of the customer workspace's user
//   memberships, so the Clarik sales team can see who they're working with
//   without entering the workspace. Auto-synced from membership changes.
// subscription_invoices: per-Stripe-invoice mirror for Clarik subscription
//   billing. Source of truth stays in Stripe; this is the local read model
//   for fast list/detail views on /customers and the workspace billing tab.
// ============================================================================

export type PlatformCustomerStatus = "trial" | "active" | "past_due" | "canceled" | "churned";

export const platformCustomers = pgTable("platform_customers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // The customer's own tenant workspace. Unique — a workspace is either a
  // platform customer or not. Cascade delete keeps the mirror tidy if a
  // workspace is hard-deleted (rare; usually soft-delete instead).
  workspaceId: varchar("workspace_id").notNull().unique().references(() => workspaces.id, { onDelete: "cascade" }),
  companyName: varchar("company_name", { length: 255 }).notNull(),
  stripeCustomerId: varchar("stripe_customer_id", { length: 255 }).unique(),
  status: varchar("status", { length: 20 }).notNull().default("trial").$type<PlatformCustomerStatus>(),
  planTier: varchar("plan_tier", { length: 20 }).$type<WorkspaceTier>(),
  // Monthly recurring revenue in cents. Computed from the locked-in
  // subscribed price on the workspace; refreshed by the subscription
  // webhook and the convert-to-customer flow.
  mrrCents: integer("mrr_cents").notNull().default(0),
  // ── CRM fields (extended in migration 0124) ──────────────────────────────
  // Human contact at the customer account (may differ from workspace owner).
  contactName: varchar("contact_name", { length: 255 }),
  phoneNumber: varchar("phone_number", { length: 50 }),
  emailAddress: varchar("email_address", { length: 255 }),
  // Mailing / office address
  address: varchar("address", { length: 500 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  website: varchar("website", { length: 500 }),
  businessType: varchar("business_type", { length: 100 }),
  // Platform sales region (name string, matches platform_regions.name)
  region: varchar("region", { length: 100 }),
  // Assigned sales rep (display name)
  rep: varchar("rep", { length: 100 }),
  // Subscription pricing override. baseRateCents = plan default; discountPercent
  // = rep/admin override; mrrCents = baseRateCents * (1 - discountPercent/100).
  baseRateCents: integer("base_rate_cents").notNull().default(0),
  discountPercent: decimal("discount_percent", { precision: 5, scale: 2 }).notNull().default("0"),
  // Max discount % a rep is allowed to apply (admin can always override to 100).
  maxRepDiscountPercent: decimal("max_rep_discount_percent", { precision: 5, scale: 2 }).notNull().default("20"),
  // Prospect this customer was converted from (nullable — direct signups have none).
  prospectId: varchar("prospect_id").references(() => platformProspects.id, { onDelete: "set null" }),
  // Human-readable record ID: CU-001001
  recordId: varchar("record_id", { length: 20 }).unique(),
  lastContacted: timestamp("last_contacted"),
  emailOptedOut: boolean("email_opted_out").notNull().default(false),
  // Free-form notes the sales/support team can attach.
  notes: text("notes"),
  // Visual indicator: this customer needs a document sent to them.
  requiresDocument: boolean("requires_document").notNull().default(false),
  deletedAt: timestamp("deleted_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  byStatus: index("platform_customers_status_idx").on(t.status),
  byPlanTier: index("platform_customers_plan_tier_idx").on(t.planTier),
}));

// ─── Platform Customer Activities ─────────────────────────────────────────────
export const platformCustomerActivities = pgTable("platform_customer_activities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => platformCustomers.id, { onDelete: "cascade" }),
  type: varchar("type", { length: 20 }).notNull().$type<"call" | "email" | "note">(),
  notes: text("notes"),
  createdByPlatformUserId: varchar("created_by_platform_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertPlatformCustomerActivitySchema = createInsertSchema(platformCustomerActivities).omit({
  id: true, createdAt: true,
});
export type InsertPlatformCustomerActivity = z.infer<typeof insertPlatformCustomerActivitySchema>;
export type PlatformCustomerActivity = typeof platformCustomerActivities.$inferSelect;

export const platformCustomersRelations = relations(platformCustomers, ({ one, many }) => ({
  workspace: one(workspaces, {
    fields: [platformCustomers.workspaceId],
    references: [workspaces.id],
  }),
  contacts: many(platformCustomerContacts),
  invoices: many(subscriptionInvoices),
}));

export const insertPlatformCustomerSchema = createInsertSchema(platformCustomers).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertPlatformCustomer = z.infer<typeof insertPlatformCustomerSchema>;
export type PlatformCustomer = typeof platformCustomers.$inferSelect;

export const platformCustomerContacts = pgTable("platform_customer_contacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platformCustomerId: varchar("platform_customer_id").notNull().references(() => platformCustomers.id, { onDelete: "cascade" }),
  // Soft FK to users.id — kept as varchar (no FK constraint) because users
  // can be removed from a workspace and we want to keep historical contact
  // rows for audit. The auto-sync hook reconciles status with membership.
  userId: varchar("user_id", { length: 255 }),
  email: varchar("email", { length: 255 }).notNull(),
  name: varchar("name", { length: 255 }),
  // Workspace role at time of last sync. Useful for "who's an admin here?"
  // without joining back through workspace memberships.
  workspaceRole: varchar("workspace_role", { length: 50 }),
  isPrimary: boolean("is_primary").notNull().default(false),
  // Set when the user is removed from the workspace, so historical lookups
  // still find the row but the UI can show it as inactive.
  removedAt: timestamp("removed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  byCustomer: index("platform_customer_contacts_customer_idx").on(t.platformCustomerId),
  uniquePerCustomer: uniqueIndex("platform_customer_contacts_customer_user_uq").on(t.platformCustomerId, t.userId),
}));

export const platformCustomerContactsRelations = relations(platformCustomerContacts, ({ one }) => ({
  customer: one(platformCustomers, {
    fields: [platformCustomerContacts.platformCustomerId],
    references: [platformCustomers.id],
  }),
}));

export const insertPlatformCustomerContactSchema = createInsertSchema(platformCustomerContacts).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertPlatformCustomerContact = z.infer<typeof insertPlatformCustomerContactSchema>;
export type PlatformCustomerContact = typeof platformCustomerContacts.$inferSelect;

export type SubscriptionInvoiceStatus = "draft" | "open" | "paid" | "uncollectible" | "void";

export const subscriptionInvoices = pgTable("subscription_invoices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Local fast-path links. Both nullable so we can mirror a Stripe invoice
  // event that arrives before the customer/workspace mapping is resolved
  // (rare race), then back-fill once we can find them.
  platformCustomerId: varchar("platform_customer_id").references(() => platformCustomers.id, { onDelete: "set null" }),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "set null" }),
  // Stripe invoice id is the canonical key for idempotent webhook upserts.
  stripeInvoiceId: varchar("stripe_invoice_id", { length: 255 }).notNull().unique(),
  stripeCustomerId: varchar("stripe_customer_id", { length: 255 }),
  stripeSubscriptionId: varchar("stripe_subscription_id", { length: 255 }),
  amountDueCents: integer("amount_due_cents").notNull().default(0),
  amountPaidCents: integer("amount_paid_cents").notNull().default(0),
  currency: varchar("currency", { length: 8 }).notNull().default("usd"),
  status: varchar("status", { length: 20 }).notNull().default("open").$type<SubscriptionInvoiceStatus>(),
  periodStart: timestamp("period_start"),
  periodEnd: timestamp("period_end"),
  issuedAt: timestamp("issued_at"),
  paidAt: timestamp("paid_at"),
  hostedInvoiceUrl: text("hosted_invoice_url"),
  invoicePdfUrl: text("invoice_pdf_url"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  byCustomer: index("subscription_invoices_customer_idx").on(t.platformCustomerId),
  byWorkspace: index("subscription_invoices_workspace_idx").on(t.workspaceId),
  byStatus: index("subscription_invoices_status_idx").on(t.status),
}));

export const subscriptionInvoicesRelations = relations(subscriptionInvoices, ({ one }) => ({
  customer: one(platformCustomers, {
    fields: [subscriptionInvoices.platformCustomerId],
    references: [platformCustomers.id],
  }),
  workspace: one(workspaces, {
    fields: [subscriptionInvoices.workspaceId],
    references: [workspaces.id],
  }),
}));

export const insertSubscriptionInvoiceSchema = createInsertSchema(subscriptionInvoices).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertSubscriptionInvoice = z.infer<typeof insertSubscriptionInvoiceSchema>;
export type SubscriptionInvoice = typeof subscriptionInvoices.$inferSelect;

// ─── Platform Gmail OAuth ─────────────────────────────────────────────────────
// Mirrors user_gmail_tokens but scoped to a platform_user rather than a
// workspace user. One row per platform admin; replaced on reconnect.
export const platformGmailTokens = pgTable("platform_gmail_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platformUserId: varchar("platform_user_id").notNull().unique()
    .references(() => platformUsers.id, { onDelete: "cascade" }),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const insertPlatformGmailTokenSchema = createInsertSchema(platformGmailTokens).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type PlatformGmailToken = typeof platformGmailTokens.$inferSelect;

// Task #386 — single clarik.io Google account used as the backup Drive identity.
// Connected from Super Admin via Clarik's own OAuth client with the non-sensitive
// `drive.file` scope. Effectively a singleton (one connected backup account); the
// unique platform_user_id records which super admin connected it.
export const platformBackupDriveTokens = pgTable("platform_backup_drive_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platformUserId: varchar("platform_user_id").notNull().unique()
    .references(() => platformUsers.id, { onDelete: "cascade" }),
  accessToken: text("access_token").notNull(),
  refreshToken: text("refresh_token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});
export const insertPlatformBackupDriveTokenSchema = createInsertSchema(platformBackupDriveTokens).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertPlatformBackupDriveToken = z.infer<typeof insertPlatformBackupDriveTokenSchema>;
export type PlatformBackupDriveToken = typeof platformBackupDriveTokens.$inferSelect;

// ─── Platform Regions ─────────────────────────────────────────────────────────
// Geographic regions for the platform-level sales pipeline (prospects).
// Separate from workspace regions — no tenant FK.
export const platformRegions = pgTable("platform_regions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 100 }).notNull().unique(),
  // Structured geo for AI Agent regions (region name convention = "City, ST").
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertPlatformRegionSchema = createInsertSchema(platformRegions).omit({
  id: true, createdAt: true,
});
export type InsertPlatformRegion = z.infer<typeof insertPlatformRegionSchema>;
export type PlatformRegion = typeof platformRegions.$inferSelect;

// ─── Platform Prospects (platform-level "leads") ──────────────────────────────
// People who have expressed interest in Clarik but haven't signed up yet.
// Distinct from the tenant leads table — no workspace FK.
export const platformProspects = pgTable("platform_prospects", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  recordId: varchar("record_id", { length: 20 }).unique(),
  name: varchar("name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  phone: varchar("phone", { length: 50 }),
  company: varchar("company", { length: 255 }),
  contactName: varchar("contact_name", { length: 255 }),
  businessType: varchar("business_type", { length: 100 }),
  website: varchar("website", { length: 500 }),
  address: text("address"),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  zipCode: varchar("zip_code", { length: 20 }),
  region: varchar("region", { length: 100 }),
  rating: integer("rating"),
  rep: varchar("rep", { length: 255 }),
  lastContacted: timestamp("last_contacted"),
  lastActivityAt: timestamp("last_activity_at"),
  emailOptedOut: boolean("email_opted_out").notNull().default(false),
  starred: boolean("starred").notNull().default(false),
  notes: text("notes"),
  status: varchar("status", { length: 30 }).notNull().default("new"),
  isSuppressed: boolean("is_suppressed").notNull().default(false),
  suppressedAt: timestamp("suppressed_at"),
  suppressedReason: varchar("suppressed_reason", { length: 50 }),
  source: varchar("source", { length: 50 }).notNull().default("manual"),
  // Set when a prospect was created by an AI Agent run (cost attribution + tracking).
  sourceAgentRunId: varchar("source_agent_run_id")
    .references(() => aiAgentRuns.id, { onDelete: "set null" }),
  convertedAt: timestamp("converted_at"),
  convertedWorkspaceId: varchar("converted_workspace_id")
    .references(() => workspaces.id, { onDelete: "set null" }),
  convertedCustomerId: varchar("converted_customer_id")
    .references(() => platformCustomers.id, { onDelete: "set null" }),
  deletedAt: timestamp("deleted_at"),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  emailIdx: index("platform_prospects_email_idx").on(t.email),
  statusIdx: index("platform_prospects_status_idx").on(t.status),
}));
export const insertPlatformProspectSchema = createInsertSchema(platformProspects).omit({
  id: true, recordId: true, createdAt: true, updatedAt: true,
  convertedAt: true, convertedWorkspaceId: true, convertedCustomerId: true, deletedAt: true,
});
export type InsertPlatformProspect = z.infer<typeof insertPlatformProspectSchema>;
export type PlatformProspect = typeof platformProspects.$inferSelect;

export const platformProspectActivities = pgTable("platform_prospect_activities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  prospectId: varchar("prospect_id").notNull()
    .references(() => platformProspects.id, { onDelete: "cascade" }),
  type: varchar("type", { length: 50 }).notNull().$type<"call" | "email" | "note">(),
  notes: text("notes"),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertPlatformProspectActivitySchema = createInsertSchema(platformProspectActivities).omit({
  id: true, createdAt: true,
});
export type PlatformProspectActivity = typeof platformProspectActivities.$inferSelect;

// ─── Platform Email Templates ─────────────────────────────────────────────────
// Reusable email templates owned by the platform (no workspace FK).
export const platformEmailTemplates = pgTable("platform_email_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  subject: varchar("subject", { length: 500 }).notNull(),
  body: text("body").notNull(),
  scope: varchar("scope", { length: 20 }).notNull().default("STANDARD"),
  isActive: boolean("is_active").notNull().default(true),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertPlatformEmailTemplateSchema = createInsertSchema(platformEmailTemplates).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type PlatformEmailTemplate = typeof platformEmailTemplates.$inferSelect;

// ─── Platform Email Campaigns (mass sends) ────────────────────────────────────
export type PlatformCampaignAudience = "all" | "waitlist" | "prospects" | "customers" | "tenant_admins";
export type PlatformCampaignStatus = "draft" | "sending" | "paused" | "completed" | "failed";

export const platformCampaigns = pgTable("platform_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  subject: varchar("subject", { length: 500 }).notNull(),
  body: text("body").notNull(),
  audienceType: varchar("audience_type", { length: 30 }).notNull().$type<PlatformCampaignAudience>(),
  status: varchar("status", { length: 20 }).notNull().default("draft").$type<PlatformCampaignStatus>(),
  fromPlatformUserId: varchar("from_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  sentCount: integer("sent_count").notNull().default(0),
  failedCount: integer("failed_count").notNull().default(0),
  suppressedCount: integer("suppressed_count").notNull().default(0),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  statusIdx: index("platform_campaigns_status_idx").on(t.status),
  createdIdx: index("platform_campaigns_created_idx").on(t.createdAt),
}));
export const insertPlatformCampaignSchema = createInsertSchema(platformCampaigns).omit({
  id: true, createdAt: true, updatedAt: true, sentCount: true, failedCount: true, suppressedCount: true,
});
export type PlatformCampaign = typeof platformCampaigns.$inferSelect;

// ─── Platform Campaign Recipients ─────────────────────────────────────────────
export const platformCampaignRecipients = pgTable("platform_campaign_recipients", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull()
    .references(() => platformCampaigns.id, { onDelete: "cascade" }),
  email: varchar("email", { length: 255 }).notNull(),
  name: varchar("name", { length: 255 }),
  contactType: varchar("contact_type", { length: 30 }).notNull(),
  contactId: varchar("contact_id", { length: 255 }),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  error: text("error"),
  sentAt: timestamp("sent_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  campaignIdx: index("platform_campaign_recipients_campaign_idx").on(t.campaignId),
}));
export type PlatformCampaignRecipient = typeof platformCampaignRecipients.$inferSelect;

// ─── Platform Drip Campaigns ──────────────────────────────────────────────────
export type PlatformDripStatus = "draft" | "active" | "paused";
export type PlatformDripRoutingTier = "warm" | "cold";

export const platformDripCampaigns = pgTable("platform_drip_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("draft").$type<PlatformDripStatus>(),
  audienceType: varchar("audience_type", { length: 30 }).notNull().default("prospects"),
  autoEnrollNewProspects: boolean("auto_enroll_new_prospects").notNull().default(false),
  autoEnrollDelayMinutes: integer("auto_enroll_delay_minutes").notNull().default(0),
  // AI Agent linkage: when set, this campaign is the enrollment target for that agent's qualified leads.
  // FK set null on delete so the campaign survives agent deletion.
  aiAgentId: varchar("ai_agent_id").references(() => aiAgents.id, { onDelete: "set null" }),
  // Routing tier — 'warm' = connected Gmail inbox, 'cold' = Maildoso relay pool.
  routingTier: varchar("routing_tier", { length: 10 }).notNull().default("cold").$type<PlatformDripRoutingTier>(),
  // Exit rules — advance no further once these conditions are met.
  exitOnReply: boolean("exit_on_reply").notNull().default(true),
  exitOnBooked: boolean("exit_on_booked").notNull().default(false),
  // Optional send window HH:MM (local platform time). NULL = send any time.
  sendWindowStart: varchar("send_window_start", { length: 5 }),
  sendWindowEnd: varchar("send_window_end", { length: 5 }),
  // Campaign-level daily send cap (same semantics as drip_campaigns.max_sends_per_day).
  // Null = no cap.
  maxSendsPerDay: integer("max_sends_per_day"),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertPlatformDripCampaignSchema = createInsertSchema(platformDripCampaigns).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type PlatformDripCampaign = typeof platformDripCampaigns.$inferSelect;

export const platformDripCampaignSteps = pgTable("platform_drip_campaign_steps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull()
    .references(() => platformDripCampaigns.id, { onDelete: "cascade" }),
  stepOrder: integer("step_order").notNull(),
  delayMinutes: integer("delay_minutes").notNull().default(0),
  subjectTemplate: varchar("subject_template", { length: 500 }).notNull(),
  bodyTemplate: text("body_template").notNull(),
  pauseOnReply: boolean("pause_on_reply").notNull().default(true),
  // Optional HH:MM clock time (platform TZ) to fire this step. When set the
  // scheduler snaps next_send_at to this exact time and bypasses the
  // campaign-level send-window gate so the chosen time is always honoured.
  sendAtTime: varchar("send_at_time", { length: 5 }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  campaignOrderIdx: index("platform_drip_steps_campaign_order_idx").on(t.campaignId, t.stepOrder),
}));
export type PlatformDripCampaignStep = typeof platformDripCampaignSteps.$inferSelect;

export const platformDripEnrollments = pgTable("platform_drip_enrollments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull()
    .references(() => platformDripCampaigns.id, { onDelete: "cascade" }),
  contactId: varchar("contact_id").notNull(),
  contactType: varchar("contact_type", { length: 30 }).notNull(),
  contactEmail: varchar("contact_email", { length: 255 }).notNull(),
  contactName: varchar("contact_name", { length: 255 }),
  currentStepIndex: integer("current_step_index").notNull().default(0),
  nextSendAt: timestamp("next_send_at"),
  status: varchar("status", { length: 20 }).notNull().default("active"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  campaignContactIdx: uniqueIndex("platform_drip_enrollments_campaign_contact_uq")
    .on(t.campaignId, t.contactId, t.contactType),
}));
export type PlatformDripEnrollment = typeof platformDripEnrollments.$inferSelect;

// ─── Platform Drip Messages (send log for per-campaign stats) ─────────────────
export const platformDripMessages = pgTable("platform_drip_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  campaignId: varchar("campaign_id").notNull()
    .references(() => platformDripCampaigns.id, { onDelete: "cascade" }),
  enrollmentId: varchar("enrollment_id").notNull()
    .references(() => platformDripEnrollments.id, { onDelete: "cascade" }),
  stepOrder: integer("step_order").notNull().default(0),
  sentAt: timestamp("sent_at").notNull().defaultNow(),
  openedAt: timestamp("opened_at"),
  repliedAt: timestamp("replied_at"),
  status: varchar("status", { length: 20 }).notNull().default("sent"),
  // UUID written at send time; matches email_events.tracking_id so
  // /api/track/open and /api/track/click events can be joined back here.
  trackingId: varchar("tracking_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  campaignIdx: index("platform_drip_messages_campaign_idx").on(t.campaignId),
  enrollmentIdx: index("platform_drip_messages_enrollment_idx").on(t.enrollmentId),
}));
export type PlatformDripMessage = typeof platformDripMessages.$inferSelect;

// ─── Platform SMS Campaigns ───────────────────────────────────────────────────
export type PlatformSmsCampaignStatus = "draft" | "scheduled" | "sending" | "paused" | "completed";

export const platformSmsCampaigns = pgTable("platform_sms_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  audienceType: varchar("audience_type", { length: 30 }).notNull().default("prospects"),
  messageTemplate: text("message_template").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("draft").$type<PlatformSmsCampaignStatus>(),
  sendMode: varchar("send_mode", { length: 20 }).notNull().default("now"),
  scheduledFor: timestamp("scheduled_for"),
  sentCount: integer("sent_count").notNull().default(0),
  failedCount: integer("failed_count").notNull().default(0),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  statusIdx: index("platform_sms_campaigns_status_idx").on(t.status),
}));
export const insertPlatformSmsCampaignSchema = createInsertSchema(platformSmsCampaigns).omit({
  id: true, createdAt: true, updatedAt: true, sentCount: true, failedCount: true,
});
export type PlatformSmsCampaign = typeof platformSmsCampaigns.$inferSelect;

// ─── Platform Confirmation Email Templates ────────────────────────────────────
// "Client type" on the platform = performer/act category (musician, comedian…)
// "Offering" on the platform = subscription tier the contact signed up for.
export const platformConfirmationTemplates = pgTable("platform_confirmation_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  clientType: varchar("client_type", { length: 100 }),
  offeringTier: varchar("offering_tier", { length: 50 }),
  subject: varchar("subject", { length: 500 }).notNull(),
  body: text("body").notNull(),
  isActive: boolean("is_active").notNull().default(true),
  isDefault: boolean("is_default").notNull().default(false),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertPlatformConfirmationTemplateSchema = createInsertSchema(platformConfirmationTemplates).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type PlatformConfirmationTemplate = typeof platformConfirmationTemplates.$inferSelect;

// ─── Platform Approval Funnels ────────────────────────────────────────────────
export const platformApprovalFunnels = pgTable("platform_approval_funnels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 100 }).notNull().unique(),
  description: text("description"),
  isActive: boolean("is_active").notNull().default(true),
  fieldConfig: jsonb("field_config").notNull().default({}),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertPlatformApprovalFunnelSchema = createInsertSchema(platformApprovalFunnels).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type PlatformApprovalFunnel = typeof platformApprovalFunnels.$inferSelect;

// ─── Platform Approval Requests ───────────────────────────────────────────────
export const platformApprovalRequests = pgTable("platform_approval_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  funnelId: varchar("funnel_id").notNull()
    .references(() => platformApprovalFunnels.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  phone: varchar("phone", { length: 50 }),
  company: varchar("company", { length: 255 }),
  businessType: varchar("business_type", { length: 100 }),
  heardAbout: varchar("heard_about", { length: 100 }),
  teamSize: varchar("team_size", { length: 50 }),
  annualRevenue: varchar("annual_revenue", { length: 50 }),
  annualShows: integer("annual_shows"),
  goals: text("goals"),
  currentTech: jsonb("current_tech").notNull().default([]),
  extraFields: jsonb("extra_fields").notNull().default({}),
  stage: varchar("stage", { length: 30 }).notNull().default("submitted"),
  betaPhase: varchar("beta_phase", { length: 10 }),
  adminScore: smallint("admin_score"),
  adminNotes: text("admin_notes"),
  reviewedByPlatformUserId: varchar("reviewed_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  workspaceId: varchar("workspace_id")
    .references(() => workspaces.id, { onDelete: "set null" }),
  workspaceCreatedAt: timestamp("workspace_created_at", { withTimezone: true }),
  credentialsSentAt: timestamp("credentials_sent_at", { withTimezone: true }),
  welcomeEmailSentAt: timestamp("welcome_email_sent_at", { withTimezone: true }),
  isProspect: boolean("is_prospect").notNull().default(true),
  prospectId: varchar("prospect_id")
    .references(() => platformProspects.id, { onDelete: "set null" }),
  source: varchar("source", { length: 30 }).notNull().default("form"),
  ipAddress: varchar("ip_address", { length: 45 }),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertPlatformApprovalRequestSchema = createInsertSchema(platformApprovalRequests).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type PlatformApprovalRequest = typeof platformApprovalRequests.$inferSelect;

// ─── Platform Approval Notes ──────────────────────────────────────────────────
export const platformApprovalNotes = pgTable("platform_approval_notes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  requestId: varchar("request_id").notNull()
    .references(() => platformApprovalRequests.id, { onDelete: "cascade" }),
  platformUserId: varchar("platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  body: text("body").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertPlatformApprovalNoteSchema = createInsertSchema(platformApprovalNotes).omit({
  id: true, createdAt: true,
});
export type PlatformApprovalNote = typeof platformApprovalNotes.$inferSelect;

// ─── Platform Approval Stage History ─────────────────────────────────────────
export const platformApprovalStageHistory = pgTable("platform_approval_stage_history", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  requestId: varchar("request_id").notNull()
    .references(() => platformApprovalRequests.id, { onDelete: "cascade" }),
  fromStage: varchar("from_stage", { length: 30 }),
  toStage: varchar("to_stage", { length: 30 }).notNull(),
  changedByPlatformUserId: varchar("changed_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  note: text("note"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export type PlatformApprovalStageHistoryEntry = typeof platformApprovalStageHistory.$inferSelect;

// ─── Workspace Session Bindings (Task #128) ───────────────────────────────────
// ─── Platform Customer Documents ──────────────────────────────────────────────
// Mirrors the workspace forms/contracts module but scoped to platform customers.
// Templates are platform-global (not tied to any workspace); instances are tied
// to a specific customer.

export type PlatformDocTemplateType = "nda" | "service_agreement" | "custom_contract" | "terms_of_service" | "onboarding_agreement";
export type PlatformDocTemplateStatus = "draft" | "published" | "archived";
export type PlatformDocInstanceStatus = "draft" | "sent" | "completed" | "declined" | "voided";

export const platformDocumentTemplates = pgTable("platform_document_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  templateType: varchar("template_type", { length: 40 }).notNull().default("custom_contract").$type<PlatformDocTemplateType>(),
  status: varchar("status", { length: 20 }).notNull().default("draft").$type<PlatformDocTemplateStatus>(),
  // Body stored as plain HTML / rich text. Rendered in the signing page.
  bodyHtml: text("body_html").notNull().default(""),
  createdByPlatformUserId: varchar("created_by_platform_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertPlatformDocumentTemplateSchema = createInsertSchema(platformDocumentTemplates).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertPlatformDocumentTemplate = z.infer<typeof insertPlatformDocumentTemplateSchema>;
export type PlatformDocumentTemplate = typeof platformDocumentTemplates.$inferSelect;

export const platformDocumentInstances = pgTable("platform_document_instances", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => platformCustomers.id, { onDelete: "cascade" }),
  templateId: varchar("template_id").references(() => platformDocumentTemplates.id, { onDelete: "set null" }),
  // Human-readable label for this specific document send.
  displayName: varchar("display_name", { length: 255 }).notNull(),
  // Snapshot of bodyHtml at send time (so template edits don't alter sent docs).
  bodyHtmlSnapshot: text("body_html_snapshot").notNull().default(""),
  status: varchar("status", { length: 20 }).notNull().default("draft").$type<PlatformDocInstanceStatus>(),
  recipientEmail: varchar("recipient_email", { length: 255 }),
  recipientName: varchar("recipient_name", { length: 255 }),
  // Native e-signature fields (mirrors document_instances).
  signingToken: varchar("signing_token", { length: 64 }).unique(),
  signingTokenExpiresAt: timestamp("signing_token_expires_at"),
  signatureMethod: varchar("signature_method", { length: 20 }),
  signedSignatureDataUrl: text("signed_signature_data_url"),
  signedTypedName: varchar("signed_typed_name", { length: 255 }),
  signedByName: varchar("signed_by_name", { length: 255 }),
  signedByEmail: varchar("signed_by_email", { length: 255 }),
  signedAtIp: varchar("signed_at_ip", { length: 64 }),
  signedAtUserAgent: text("signed_at_user_agent"),
  // SHA-256 tamper-evident hash of instanceId|signatureData|ip|userAgent.
  signedAuditHash: varchar("signed_audit_hash", { length: 64 }),
  signedCompletedAt: timestamp("signed_completed_at"),
  sentAt: timestamp("sent_at"),
  createdByPlatformUserId: varchar("created_by_platform_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertPlatformDocumentInstanceSchema = createInsertSchema(platformDocumentInstances).omit({ id: true, createdAt: true });
export type InsertPlatformDocumentInstance = z.infer<typeof insertPlatformDocumentInstanceSchema>;
export type PlatformDocumentInstance = typeof platformDocumentInstances.$inferSelect;

// ─── Platform Billing ─────────────────────────────────────────────────────────
// Five-table schema for the platform-level Billing section.

// Expense categories — used for both AP ledger entries and auto-import vendors.
export type PlatformExpenseCategory =
  | "api"
  | "hosting"
  | "employee"
  | "contractor"
  | "ad_spend"
  | "startup"
  | "other";

export type PlatformExpenseVendorKey =
  | "openai"
  | "anthropic"
  | "twilio"
  | "elevenlabs"
  | "ipinfo"
  | "replit"
  | "stripe"
  | "google"
  | "meta"
  | "tiktok"
  | "youtube"
  | "other";

// AP ledger — every platform expense, manual or auto-imported.
export const platformExpenses = pgTable("platform_expenses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Human-readable label shown in the ledger.
  description: varchar("description", { length: 500 }).notNull(),
  category: varchar("category", { length: 20 }).notNull().default("other").$type<PlatformExpenseCategory>(),
  // Nullable vendor key enables future auto-import by vendor.
  vendorKey: varchar("vendor_key", { length: 30 }).$type<PlatformExpenseVendorKey>(),
  amountCents: integer("amount_cents").notNull(),
  currency: varchar("currency", { length: 8 }).notNull().default("usd"),
  // billingDate = date the expense was incurred / invoice date.
  billingDate: timestamp("billing_date").notNull(),
  dueDate: timestamp("due_date"),
  paidAt: timestamp("paid_at"),
  // If recurring, the period (monthly / quarterly / annual).
  isRecurring: boolean("is_recurring").notNull().default(false),
  recurringPeriod: varchar("recurring_period", { length: 20 }),
  // Optional link to a workspace this cost should be allocated to.
  allocatedWorkspaceId: varchar("allocated_workspace_id").references(() => workspaces.id, { onDelete: "set null" }),
  // Optional link to an uploaded billing statement.
  uploadId: varchar("upload_id"),
  notes: text("notes"),
  // Stripe processing fee fields (auto-computed for Stripe expenses).
  stripeFeesCents: integer("stripe_fees_cents"),
  // Soft-delete
  deletedAt: timestamp("deleted_at"),
  createdByPlatformUserId: varchar("created_by_platform_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  byCategory: index("platform_expenses_category_idx").on(t.category),
  byBillingDate: index("platform_expenses_billing_date_idx").on(t.billingDate),
  byVendor: index("platform_expenses_vendor_idx").on(t.vendorKey),
}));
export const insertPlatformExpenseSchema = createInsertSchema(platformExpenses).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertPlatformExpense = z.infer<typeof insertPlatformExpenseSchema>;
export type PlatformExpense = typeof platformExpenses.$inferSelect;

// Employee / contractor roster.
export type PlatformPayFrequency = "hourly" | "monthly" | "annual";
export type PlatformEmploymentType = "employee" | "contractor";

export const platformEmployees = pgTable("platform_employees", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  role: varchar("role", { length: 255 }),
  employmentType: varchar("employment_type", { length: 20 }).notNull().default("employee").$type<PlatformEmploymentType>(),
  payRateCents: integer("pay_rate_cents").notNull().default(0),
  payFrequency: varchar("pay_frequency", { length: 20 }).notNull().default("monthly").$type<PlatformPayFrequency>(),
  startDate: timestamp("start_date"),
  endDate: timestamp("end_date"),
  notes: text("notes"),
  deletedAt: timestamp("deleted_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertPlatformEmployeeSchema = createInsertSchema(platformEmployees).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertPlatformEmployee = z.infer<typeof insertPlatformEmployeeSchema>;
export type PlatformEmployee = typeof platformEmployees.$inferSelect;

// Commission rules — one row per rep per effective period.
export const platformCommissionRules = pgTable("platform_commission_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platformUserId: varchar("platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  // Commission % as a decimal (e.g. 0.10 = 10 %).
  ratePercent: decimal("rate_percent", { precision: 5, scale: 4 }).notNull(),
  effectiveFrom: timestamp("effective_from").notNull(),
  effectiveTo: timestamp("effective_to"),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  byUser: index("platform_commission_rules_user_idx").on(t.platformUserId),
}));
export const insertPlatformCommissionRuleSchema = createInsertSchema(platformCommissionRules).omit({
  id: true, createdAt: true,
});
export type InsertPlatformCommissionRule = z.infer<typeof insertPlatformCommissionRuleSchema>;
export type PlatformCommissionRule = typeof platformCommissionRules.$inferSelect;

// Commission payouts — one row per rep per month after calculation.
export const platformCommissionPayouts = pgTable("platform_commission_payouts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platformUserId: varchar("platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  // Period in YYYY-MM format.
  period: varchar("period", { length: 7 }).notNull(),
  grossCollectedCents: integer("gross_collected_cents").notNull().default(0),
  ratePercent: decimal("rate_percent", { precision: 5, scale: 4 }).notNull(),
  commissionCents: integer("commission_cents").notNull().default(0),
  paidAt: timestamp("paid_at"),
  notes: text("notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  byUser: index("platform_commission_payouts_user_idx").on(t.platformUserId),
  byPeriod: index("platform_commission_payouts_period_idx").on(t.period),
  uniq: index("platform_commission_payouts_uniq_idx").on(t.platformUserId, t.period),
}));
export const insertPlatformCommissionPayoutSchema = createInsertSchema(platformCommissionPayouts).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertPlatformCommissionPayout = z.infer<typeof insertPlatformCommissionPayoutSchema>;
export type PlatformCommissionPayout = typeof platformCommissionPayouts.$inferSelect;

// Billing statement uploads — PDFs/images the admin uploads to attach expenses.
export const platformBillingUploads = pgTable("platform_billing_uploads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  fileName: varchar("file_name", { length: 500 }).notNull(),
  vendorKey: varchar("vendor_key", { length: 30 }).$type<PlatformExpenseVendorKey>(),
  vendorLabel: varchar("vendor_label", { length: 255 }),
  // Billing period this statement covers (YYYY-MM).
  period: varchar("period", { length: 7 }),
  // Manually entered total from the statement.
  totalCents: integer("total_cents"),
  currency: varchar("currency", { length: 8 }).notNull().default("usd"),
  // Object storage path.
  objectPath: text("object_path"),
  status: varchar("status", { length: 20 }).notNull().default("uploaded"),
  notes: text("notes"),
  uploadedByPlatformUserId: varchar("uploaded_by_platform_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertPlatformBillingUploadSchema = createInsertSchema(platformBillingUploads).omit({
  id: true, createdAt: true,
});
export type InsertPlatformBillingUpload = z.infer<typeof insertPlatformBillingUploadSchema>;
export type PlatformBillingUpload = typeof platformBillingUploads.$inferSelect;

// ─── Platform Invoice Flags ───────────────────────────────────────────────────
// A lightweight "handled" note on any Stripe invoice. When a platform admin
// marks a past-due / failed invoice as handled it stops surfacing in the
// Needs-Attention list without requiring the charge to be retried.
export const platformInvoiceFlags = pgTable("platform_invoice_flags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  stripeInvoiceId: varchar("stripe_invoice_id", { length: 255 }).notNull().unique(),
  handledByPlatformUserId: varchar("handled_by_platform_user_id"),
  handledAt: timestamp("handled_at").notNull().defaultNow(),
  note: text("note"),
});
export type PlatformInvoiceFlag = typeof platformInvoiceFlags.$inferSelect;

// Append-only audit table. Every time a user selects/confirms a workspace via
// POST /api/auth/select-workspace the binding is recorded here for forensics.
// Super admins can query this from the Platform Workspace.
export const workspaceSessionBindings = pgTable("workspace_session_bindings", {
  id:          varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId:      text("user_id").notNull(),
  workspaceId: text("workspace_id").notNull(),
  boundAt:     timestamp("bound_at", { withTimezone: true }).notNull().defaultNow(),
  ipAddress:   text("ip_address"),
  userAgent:   text("user_agent"),
  sessionId:   text("session_id"),
});
export type WorkspaceSessionBinding = typeof workspaceSessionBindings.$inferSelect;

// ─── Platform Business Types ─────────────────────────────────────────────────
// Single source of truth for entertainment-industry business type labels used
// across the beta apply funnel, platform prospects, platform customers, and
// tenant onboarding. Managed by super-admins via Operations → Business Types.
export const platformBusinessTypes = pgTable("platform_business_types", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 100 }).notNull().unique(),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});
export const insertPlatformBusinessTypeSchema = createInsertSchema(platformBusinessTypes).omit({
  id: true, createdAt: true,
});
export type InsertPlatformBusinessType = z.infer<typeof insertPlatformBusinessTypeSchema>;
export type PlatformBusinessType = typeof platformBusinessTypes.$inferSelect;

// ============================================================================
// AI Agents — Super Admin Platform automation (Lead Outreach Agent)
// Internal, Platform-Workspace-only. A synthetic "sales rep" that bulk-generates
// prospects by city × state × business-type and enrolls them into cold drip /
// mass campaigns through the existing Maildoso cold-relay pipeline.
// ============================================================================
export const aiAgents = pgTable("ai_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  avatarUrl: varchar("avatar_url", { length: 500 }),
  status: varchar("status", { length: 20 }).notNull().default("active")
    .$type<"active" | "paused" | "archived">(),
  // Agent kind. "lead_outreach" = the existing prospect-sourcing wizard agent.
  // "engineer" = Task #555 Platform-only self-improvement diagnostics agent that
  // runs a weekly telemetry rollup → Claude Opus → human-reviewed engineering
  // brief. Engineer agents have no email identity / outreach scope.
  agentType: varchar("type", { length: 30 }).notNull().default("lead_outreach")
    .$type<"lead_outreach" | "engineer">(),
  // cold_mailbox = Maildoso rotation pool (recommended for volume); connected_gmail
  // = an owned mailbox connected via OAuth (replies / warm 1:1 sends).
  emailIdentityType: varchar("email_identity_type", { length: 20 })
    .$type<"cold_mailbox" | "connected_gmail">(),
  assignedEmail: varchar("assigned_email", { length: 255 }),
  // Synthetic user id (matches user_profiles.user_id / user_gmail_tokens.user_id)
  // so existing sender-resolution + "log in as" impersonation work unchanged.
  backingUserId: varchar("backing_user_id"),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertAiAgentSchema = createInsertSchema(aiAgents).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertAiAgent = z.infer<typeof insertAiAgentSchema>;
export type AiAgent = typeof aiAgents.$inferSelect;

// ============================================================================
// Platform Shared Mailboxes — Super Admin Platform-Workspace-only "send as"
// identities (e.g. sales@clarik.io, support@clarik.io). Each mailbox owns its
// own Gmail tokens via a synthetic `backingUserId` (matches
// user_gmail_tokens.user_id — that column is a FK-less unique varchar), so the
// existing sender-resolution path (sendEmailAsUser) works unchanged.
//
// `readEnabled` is the deliberate single switch for the future shared-INBOX
// (read) capability: it is provisioned here but left OFF and unwired. Today
// these are SEND-ONLY connections (OAuth requests gmail.send only). Flipping
// read on later = request gmail.readonly + surface the inbox; no schema change.
// ============================================================================
export const platformSharedMailboxes = pgTable("platform_shared_mailboxes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // The mailbox address being provisioned (e.g. "sales@clarik.io").
  email: varchar("email", { length: 255 }).notNull().unique(),
  // Human label shown in the Email Hub (e.g. "Sales", "Support").
  label: varchar("label", { length: 255 }).notNull(),
  // Synthetic user id that owns this mailbox's row in user_gmail_tokens. The
  // Gmail OAuth state token carries this so the callback lands tokens here.
  backingUserId: varchar("backing_user_id").notNull().unique(),
  // THE READ SWITCH. Default OFF. When true (future), the shared inbox is
  // surfaced and a gmail.readonly grant is requested. Unwired today.
  readEnabled: boolean("read_enabled").notNull().default(false),
  status: varchar("status", { length: 20 }).notNull().default("active")
    .$type<"active" | "paused">(),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertPlatformSharedMailboxSchema = createInsertSchema(platformSharedMailboxes).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertPlatformSharedMailbox = z.infer<typeof insertPlatformSharedMailboxSchema>;
export type PlatformSharedMailbox = typeof platformSharedMailboxes.$inferSelect;

// ============================================================================
// AI Agent Run Queue (batches) — Task #509
// An operator assembles an ordered batch of runs (each its own scope). Launching
// the batch runs them sequentially & unattended: the next queued run launches
// automatically when the prior one completes OR exhausts its budget. A batch has
// a default outreach campaign + optional per-business-type overrides, resolved at
// enrollment with fall-back to the default. Tenant batches enroll+send; platform
// batches import prospects only (send deferred).
// ============================================================================
export const aiAgentRunBatches = pgTable("ai_agent_run_batches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => aiAgents.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }),
  status: varchar("status", { length: 20 }).notNull().default("running")
    .$type<"running" | "completed" | "cancelled">(),
  // Per-run hard spend cap (true USD) applied to EVERY run launched in the batch.
  spendCapUsd: decimal("spend_cap_usd", { precision: 14, scale: 6 }),
  // Batch default campaign (tenant). Resolved at enrollment unless a per-type
  // override matches. Platform batches leave this null (send deferred).
  defaultCampaignId: varchar("default_campaign_id"),
  defaultCampaignType: varchar("default_campaign_type", { length: 10 }).$type<"drip" | "mass">(),
  // "Compose fresh" default (tenant): when set and defaultCampaignId is null,
  // launch auto-creates one shared one-step drip campaign and pins it here.
  defaultComposeSubject: text("default_compose_subject"),
  defaultComposeBody: text("default_compose_body"),
  // Per-business-type override map { [businessType: string]: campaignId }. Only
  // existing campaigns; resolved per run_item at enrollment, falling back to the
  // default campaign when no key matches.
  typeOverrides: jsonb("type_overrides"),
  // Per-batch reply-thread visibility, copied onto each run at creation.
  visibleUserIds: text("visible_user_ids").array(),
  warmFallbackAck: boolean("warm_fallback_ack").notNull().default(false),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  agentIdx: index("ai_agent_run_batches_agent_idx").on(t.agentId),
  statusIdx: index("ai_agent_run_batches_status_idx").on(t.status),
}));
export const insertAiAgentRunBatchSchema = createInsertSchema(aiAgentRunBatches).omit({
  id: true, createdAt: true, updatedAt: true, startedAt: true, completedAt: true,
});
export type InsertAiAgentRunBatch = z.infer<typeof insertAiAgentRunBatchSchema>;
export type AiAgentRunBatch = typeof aiAgentRunBatches.$inferSelect;

export const aiAgentRuns = pgTable("ai_agent_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => aiAgents.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }),
  // When part of a run queue, the batch this run belongs to + its 0-based slot.
  batchId: varchar("batch_id").references((): any => aiAgentRunBatches.id, { onDelete: "set null" }),
  queuePosition: integer("queue_position"),
  status: varchar("status", { length: 20 }).notNull().default("draft")
    .$type<"draft" | "estimating" | "queued" | "running" | "paused" | "completed" | "failed" | "cancelled">(),
  // { states, cities:[{state,stateAbbrev,city,population}], businessTypes, leadGenTool }
  scope: jsonb("scope"),
  campaignType: varchar("campaign_type", { length: 10 }).$type<"drip" | "mass">(),
  // Existing campaign to enroll qualified leads into (drip or mass). Mutually
  // exclusive with the compose-fresh fields below: a launchable run has either
  // campaignId set OR composeSubject+composeBody set.
  campaignId: varchar("campaign_id"),
  // "Compose fresh" message option (tenant go-live): when set (and campaignId is
  // null), launch auto-creates a one-step drip campaign from these and enrolls
  // the qualified leads into it.
  composeSubject: text("compose_subject"),
  composeBody: text("compose_body"),
  // Per-run reply-thread visibility: the workspace users allowed to see the
  // inbound reply threads for leads sourced by this run. Empty/null = all
  // workspace members with inbox access (no extra restriction).
  visibleUserIds: text("visible_user_ids").array(),
  // The operator accepted the warm-fallback risk warning at launch (the
  // workspace has no verified cold sending domain, so sends route warm).
  warmFallbackAck: boolean("warm_fallback_ack").notNull().default(false),
  verifyEmails: boolean("verify_emails").notNull().default(true),
  // Per-run hard spend cap (true USD), set by the super admin at launch.
  // There is NO default — a run cannot launch without one. The orchestrator
  // auto-pauses the instant actualCostUsd crosses this value.
  spendCapUsd: decimal("spend_cap_usd", { precision: 14, scale: 6 }),
  pausedReason: text("paused_reason"),
  // Credits the operator has explicitly approved this run to spend. On reaching
  // it the run pauses (paused_reason='confirm_threshold') and waits for a human
  // to approve the next tranche — a mid-run checkpoint, distinct from the
  // one-shot hard spendCapUsd. NULL = not yet set, read as the first step.
  confirmedCreditsCeiling: decimal("confirmed_credits_ceiling", { precision: 14, scale: 4 }),
  estimatedProspects: integer("estimated_prospects"),
  estimatedCostUsd: decimal("estimated_cost_usd", { precision: 14, scale: 6 }),
  estimatedCredits: decimal("estimated_credits", { precision: 14, scale: 4 }),
  actualCostUsd: decimal("actual_cost_usd", { precision: 14, scale: 6 }).notNull().default("0"),
  actualCredits: decimal("actual_credits", { precision: 14, scale: 4 }).notNull().default("0"),
  totalItems: integer("total_items").notNull().default(0),
  completedItems: integer("completed_items").notNull().default(0),
  searchedCount: integer("searched_count").notNull().default(0),
  importedCount: integer("imported_count").notNull().default(0),
  enrolledCount: integer("enrolled_count").notNull().default(0),
  suppressedCount: integer("suppressed_count").notNull().default(0),
  failedCount: integer("failed_count").notNull().default(0),
  error: text("error"),
  workspaceId: varchar("workspace_id").references(() => workspaces.id, { onDelete: "cascade" }),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  agentIdx: index("ai_agent_runs_agent_idx").on(t.agentId),
  statusIdx: index("ai_agent_runs_status_idx").on(t.status),
}));
export const insertAiAgentRunSchema = createInsertSchema(aiAgentRuns).omit({
  id: true, createdAt: true, updatedAt: true, startedAt: true, completedAt: true,
  actualCostUsd: true, actualCredits: true, completedItems: true,
  searchedCount: true, importedCount: true, enrolledCount: true,
  suppressedCount: true, failedCount: true,
});
export type InsertAiAgentRun = z.infer<typeof insertAiAgentRunSchema>;
export type AiAgentRun = typeof aiAgentRuns.$inferSelect;

export const aiAgentRunItems = pgTable("ai_agent_run_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull().references(() => aiAgentRuns.id, { onDelete: "cascade" }),
  state: varchar("state", { length: 50 }).notNull(),
  stateAbbrev: varchar("state_abbrev", { length: 2 }),
  city: varchar("city", { length: 100 }).notNull(),
  region: varchar("region", { length: 100 }).notNull(),
  businessType: varchar("business_type", { length: 100 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending")
    .$type<"pending" | "searching" | "imported" | "enrolled" | "failed" | "skipped">(),
  prospectsFound: integer("prospects_found").notNull().default(0),
  prospectsImported: integer("prospects_imported").notNull().default(0),
  prospectsEnrolled: integer("prospects_enrolled").notNull().default(0),
  costUsd: decimal("cost_usd", { precision: 14, scale: 6 }).notNull().default("0"),
  attempts: integer("attempts").notNull().default(0),
  error: text("error"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  runStatusIdx: index("ai_agent_run_items_run_status_idx").on(t.runId, t.status),
}));
export const insertAiAgentRunItemSchema = createInsertSchema(aiAgentRunItems).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertAiAgentRunItem = z.infer<typeof insertAiAgentRunItemSchema>;
export type AiAgentRunItem = typeof aiAgentRunItems.$inferSelect;

// Population-ranked US cities cache (live web_search results, reused across runs).
export const usCities = pgTable("us_cities", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  state: varchar("state", { length: 50 }).notNull(),
  stateAbbrev: varchar("state_abbrev", { length: 2 }).notNull(),
  city: varchar("city", { length: 100 }).notNull(),
  population: integer("population"),
  rank: integer("rank"),
  source: varchar("source", { length: 30 }).notNull().default("web_search"),
  fetchedAt: timestamp("fetched_at").notNull().defaultNow(),
}, (t) => ({
  stateIdx: index("us_cities_state_idx").on(t.stateAbbrev),
  cityUniq: uniqueIndex("us_cities_state_city_uniq").on(t.stateAbbrev, t.city),
}));
export const insertUsCitySchema = createInsertSchema(usCities).omit({
  id: true, fetchedAt: true,
});
export type InsertUsCity = z.infer<typeof insertUsCitySchema>;
export type UsCity = typeof usCities.$inferSelect;

// ============================================================================
// Task #555 — Engineer AI Agent telemetry sink (Platform-Workspace-only).
//
// A privacy-conscious, cross-workspace telemetry pool that feeds the weekly
// "Engineer" agent's self-improvement diagnostics. THREE invariants govern
// every column here:
//   1. NO PII and NO message/content bodies are ever stored. We keep error
//      signatures, routes, action *types*, timings, and env strings only.
//   2. workspaceId is nullable (frontend errors can fire pre-auth / platform).
//   3. Rows are short-lived telemetry, pruned after the rollup window; nothing
//      here is a system-of-record.
// ============================================================================

// Raw frontend + backend error occurrences. Aggregation groups by `signature`.
export const telemetryErrors = pgTable("telemetry_errors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // null = pre-auth frontend error or platform-context backend error.
  workspaceId: varchar("workspace_id"),
  // Opaque actor id for repro stitching only (synthetic/user id; never an email
  // or name). Nullable — anonymous/pre-auth errors carry none.
  actorId: varchar("actor_id"),
  source: varchar("source", { length: 10 }).notNull().$type<"frontend" | "backend">(),
  severity: varchar("severity", { length: 10 }).notNull().default("error")
    .$type<"error" | "warning">(),
  // Stable fingerprint (hash of normalized message+top frame) used to group
  // recurring errors in the weekly rollup.
  signature: varchar("signature", { length: 64 }).notNull(),
  // Sanitized error message (no interpolated PII — the client/server strip
  // emails, tokens, and long numeric ids before sending).
  message: text("message").notNull(),
  stack: text("stack"),
  // App route / API path where the error fired (path only, query stripped).
  route: varchar("route", { length: 500 }),
  httpMethod: varchar("http_method", { length: 10 }),
  statusCode: integer("status_code"),
  // Repro trail: the last N breadcrumb action *types* before the error.
  breadcrumbs: jsonb("breadcrumbs"),
  // Coarse environment strings for the exportable error log.
  browser: varchar("browser", { length: 100 }),
  os: varchar("os", { length: 100 }),
  viewport: varchar("viewport", { length: 20 }),
  appVersion: varchar("app_version", { length: 60 }),
  // Best-effort visual repro — object-storage paths (private, encrypted).
  screenshotObjectPath: varchar("screenshot_object_path", { length: 500 }),
  domSnapshotObjectPath: varchar("dom_snapshot_object_path", { length: 500 }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  sigIdx: index("telemetry_errors_sig_idx").on(t.signature),
  createdIdx: index("telemetry_errors_created_idx").on(t.createdAt),
}));
export const insertTelemetryErrorSchema = createInsertSchema(telemetryErrors).omit({
  id: true, createdAt: true,
});
export type InsertTelemetryError = z.infer<typeof insertTelemetryErrorSchema>;
export type TelemetryError = typeof telemetryErrors.$inferSelect;

// Page-load / web-vitals / API-latency timing samples.
export const telemetryPerfSamples = pgTable("telemetry_perf_samples", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id"),
  route: varchar("route", { length: 500 }),
  metric: varchar("metric", { length: 20 }).notNull()
    .$type<"page_load" | "lcp" | "fcp" | "ttfb" | "api_latency" | "long_task">(),
  valueMs: integer("value_ms").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  metricIdx: index("telemetry_perf_metric_idx").on(t.metric),
  createdIdx: index("telemetry_perf_created_idx").on(t.createdAt),
}));
export const insertTelemetryPerfSampleSchema = createInsertSchema(telemetryPerfSamples).omit({
  id: true, createdAt: true,
});
export type InsertTelemetryPerfSample = z.infer<typeof insertTelemetryPerfSampleSchema>;
export type TelemetryPerfSample = typeof telemetryPerfSamples.$inferSelect;

// Standalone user-action breadcrumb stream — drives UX-friction & repetitive-
// workflow analysis. Stores action TYPES (e.g. "click:button-submit-invoice",
// "nav:/leads") keyed to an anonymous session id; never field values.
export const telemetryBreadcrumbs = pgTable("telemetry_breadcrumbs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id"),
  // Anonymous per-tab session id used only to stitch a sequence of actions.
  sessionId: varchar("session_id", { length: 64 }).notNull(),
  category: varchar("category", { length: 20 }).notNull()
    .$type<"navigation" | "click" | "api" | "console" | "custom">(),
  actionType: varchar("action_type", { length: 200 }).notNull(),
  route: varchar("route", { length: 500 }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  sessionIdx: index("telemetry_breadcrumbs_session_idx").on(t.sessionId),
  createdIdx: index("telemetry_breadcrumbs_created_idx").on(t.createdAt),
}));
export const insertTelemetryBreadcrumbSchema = createInsertSchema(telemetryBreadcrumbs).omit({
  id: true, createdAt: true,
});
export type InsertTelemetryBreadcrumb = z.infer<typeof insertTelemetryBreadcrumbSchema>;
export type TelemetryBreadcrumb = typeof telemetryBreadcrumbs.$inferSelect;

// The headline deliverable: a human-reviewed engineering brief produced by the
// weekly Engineer run. `rollup` is the categorized aggregate fed to Opus;
// `categories` is the structured findings; `brief` is the markdown narrative.
export const engineerReports = pgTable("engineer_reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull().references(() => aiAgents.id, { onDelete: "cascade" }),
  runId: varchar("run_id").references((): any => aiAgentRuns.id, { onDelete: "set null" }),
  status: varchar("status", { length: 20 }).notNull().default("ready")
    .$type<"generating" | "ready" | "reviewed" | "archived" | "failed">(),
  periodStart: timestamp("period_start").notNull(),
  periodEnd: timestamp("period_end").notNull(),
  // Categorized aggregate input (Errors / Performance / UX-friction / Optimization).
  rollup: jsonb("rollup"),
  // Structured findings from Opus, same four categories.
  categories: jsonb("categories"),
  // Markdown engineering brief (the narrative deliverable).
  brief: text("brief"),
  model: varchar("model", { length: 60 }),
  inputTokens: integer("input_tokens").notNull().default(0),
  outputTokens: integer("output_tokens").notNull().default(0),
  costUsd: decimal("cost_usd", { precision: 14, scale: 6 }).notNull().default("0"),
  error: text("error"),
  reviewedByPlatformUserId: varchar("reviewed_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  reviewedAt: timestamp("reviewed_at"),
  createdByPlatformUserId: varchar("created_by_platform_user_id")
    .references(() => platformUsers.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  agentIdx: index("engineer_reports_agent_idx").on(t.agentId),
  createdIdx: index("engineer_reports_created_idx").on(t.createdAt),
}));
export const insertEngineerReportSchema = createInsertSchema(engineerReports).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertEngineerReport = z.infer<typeof insertEngineerReportSchema>;
export type EngineerReport = typeof engineerReports.$inferSelect;

// ============================================================================
// Task #171 — AI Assistants Hub (Super Admin Configuration Center)
// ============================================================================

export const aiAssistantHubConfigs = pgTable("ai_assistant_hub_configs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  surface: varchar("surface", { length: 32 }).notNull(),
  role: varchar("role", { length: 32 }).notNull(),
  stepKey: varchar("step_key", { length: 64 }),
  systemPrompt: text("system_prompt"),
  model: varchar("model", { length: 64 }).notNull().default("claude-sonnet-4-5"),
  temperature: real("temperature").notNull().default(0.7),
  maxResponseLength: integer("max_response_length").notNull().default(2000),
  escalationThreshold: real("escalation_threshold").notNull().default(0.3),
  enabled: boolean("enabled").notNull().default(true),
  onboardingCreditsPerMessage: integer("onboarding_credits_per_message"),
  onboardingCreditGift: integer("onboarding_credit_gift"),
  // When true, the platform-wide Master Context (Voice & Tone + general
  // library) is NOT injected for this surface/role. Lets a surface opt out
  // of the shared voice (e.g. billing wanting a stricter, humor-free tone).
  disableMasterContext: boolean("disable_master_context").notNull().default(false),
  // Freeform touring strategy document written by platform admins.
  // Non-empty value is appended to the Tour Planner system prompt under a
  // "## Touring Strategy" section. Only used by surface="tour".
  tourStrategyDoc: text("tour_strategy_doc").notNull().default(""),
  updatedBy: varchar("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  uqHubConfig: uniqueIndex("uq_ai_hub_config").on(t.surface, t.role),
}));
export type AiAssistantHubConfig = typeof aiAssistantHubConfigs.$inferSelect;

// Platform-wide "Master Context" injected into every Clarik agent (assistant,
// coach, tour, onboarding) EXCEPT the complimentary Help bot. Singleton row
// (id = 'singleton'). Editable by super admins only via the AI Assistants hub.
export const aiMasterContext = pgTable("ai_master_context", {
  id: varchar("id").primaryKey().default("singleton"),
  // Starter "Voice & Tone" block describing how every Clarik agent should sound.
  voiceToneBlock: text("voice_tone_block").notNull().default(""),
  // Free-text general library of platform-wide facts/policies/rules every agent
  // should know (the "remind me is cheaper" behavior, escalation norms, etc.).
  generalLibrary: text("general_library").notNull().default(""),
  updatedBy: varchar("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
export type AiMasterContext = typeof aiMasterContext.$inferSelect;

export const aiAssistantHubToolOverrides = pgTable("ai_assistant_hub_tool_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  surface: varchar("surface", { length: 32 }).notNull(),
  role: varchar("role", { length: 32 }).notNull(),
  toolName: varchar("tool_name", { length: 128 }).notNull(),
  enabled: boolean("enabled").notNull().default(true),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  uqHubToolOverride: uniqueIndex("uq_ai_hub_tool_override").on(t.surface, t.role, t.toolName),
}));
export type AiAssistantHubToolOverride = typeof aiAssistantHubToolOverrides.$inferSelect;

export const aiAssistantHubBehaviorRules = pgTable("ai_assistant_hub_behavior_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  surface: varchar("surface", { length: 32 }).notNull(),
  role: varchar("role", { length: 32 }).notNull(),
  rule: text("rule").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});
export type AiAssistantHubBehaviorRule = typeof aiAssistantHubBehaviorRules.$inferSelect;

export const aiAssistantHubExamples = pgTable("ai_assistant_hub_examples", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  surface: varchar("surface", { length: 32 }).notNull(),
  role: varchar("role", { length: 32 }).notNull(),
  question: text("question").notNull(),
  answer: text("answer").notNull(),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});
export type AiAssistantHubExample = typeof aiAssistantHubExamples.$inferSelect;

export const aiAssistantConversations = pgTable("ai_assistant_conversations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  surface: varchar("surface", { length: 32 }).notNull(),
  role: varchar("role", { length: 32 }),
  transcript: jsonb("transcript").notNull().default(sql`'[]'::jsonb`),
  toolsCalled: jsonb("tools_called").notNull().default(sql`'[]'::jsonb`),
  sentimentScore: real("sentiment_score"),
  isEscalated: boolean("is_escalated").notNull().default(false),
  escalationReason: varchar("escalation_reason", { length: 255 }),
  sessionId: varchar("session_id"),
  turnCount: integer("turn_count").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  endedAt: timestamp("ended_at", { withTimezone: true }),
}, (t) => ({
  idxAiConvWorkspace: index("idx_ai_conv_workspace").on(t.workspaceId),
  idxAiConvUser: index("idx_ai_conv_user").on(t.userId),
  idxAiConvCreated: index("idx_ai_conv_created").on(t.createdAt),
}));
export type AiAssistantConversation = typeof aiAssistantConversations.$inferSelect;

export const aiAssistantReviewItems = pgTable("ai_assistant_review_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  conversationId: varchar("conversation_id").notNull(),
  bucket: varchar("bucket", { length: 32 }).notNull(),
  surface: varchar("surface", { length: 32 }),
  role: varchar("role", { length: 32 }),
  abstractedContent: text("abstracted_content").notNull(),
  promotedBy: varchar("promoted_by"),
  promotedAt: timestamp("promoted_at", { withTimezone: true }).notNull().defaultNow(),
  status: varchar("status", { length: 16 }).notNull().default("pending"),
});
export type AiAssistantReviewItem = typeof aiAssistantReviewItems.$inferSelect;

export const aiToolGaps = pgTable("ai_tool_gaps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  surface: varchar("surface", { length: 32 }).notNull(),
  toolDescription: text("tool_description").notNull(),
  exampleQuery: text("example_query"),
  frequency: integer("frequency").notNull().default(1),
  reviewItemId: varchar("review_item_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
});
export type AiToolGap = typeof aiToolGaps.$inferSelect;

export const aiAssistantUserSummaries = pgTable("ai_assistant_user_summaries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  summaryText: text("summary_text").notNull(),
  topicsDiscussed: jsonb("topics_discussed").notNull().default(sql`'[]'::jsonb`),
  tasksCompleted: jsonb("tasks_completed").notNull().default(sql`'[]'::jsonb`),
  openItems: jsonb("open_items").notNull().default(sql`'[]'::jsonb`),
  sentimentTrend: varchar("sentiment_trend", { length: 16 }),
  toolsUsed: jsonb("tools_used").notNull().default(sql`'[]'::jsonb`),
  conversationCount: integer("conversation_count").notNull().default(0),
  lastConversationAt: timestamp("last_conversation_at", { withTimezone: true }),
  generatedAt: timestamp("generated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  uqAiUserSummary: uniqueIndex("uq_ai_user_summary").on(t.workspaceId, t.userId),
}));
export type AiAssistantUserSummary = typeof aiAssistantUserSummaries.$inferSelect;

// =====================================================================
// Durable assistant memory — the profile the AI actually remembers.
//
// Everything else in this area is a ROLLING WINDOW: ai_assistant_user_summaries
// is a 30-day roll-up, assistant_user_profile_signals is recomputed from a
// 30-day lookback, org patterns look back 7 days. That means the assistant
// structurally cannot remember anything older than a month, and facts learned
// during onboarding were never carried forward at all.
//
// These two tables are the durable half:
//   * assistant_user_profile_facts — accumulating, append-and-refine store of
//     individual facts, each with provenance and confidence so a low-quality
//     inference can never masquerade as something the user actually told us.
//   * assistant_user_profiles — the rendered, token-capped block that gets
//     injected into the prompt, so injection is one cheap read per turn
//     instead of a fan-out query.
// =====================================================================

/** One durable, individually-sourced fact about a user. */
export const assistantUserProfileFacts = pgTable("assistant_user_profile_facts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  /** Grouping for rendering: identity | role | preference | workflow | domain | goal. */
  category: varchar("category", { length: 24 }).notNull(),
  /**
   * Stable slug identifying WHAT this fact is about (e.g. "primary_market",
   * "reporting_cadence"). The merge key — a re-extraction of the same key
   * updates the existing row rather than appending a duplicate.
   */
  factKey: varchar("fact_key", { length: 64 }).notNull(),
  /** Free text, deliberately not a bounded varchar: model output goes here. */
  factValue: text("fact_value").notNull(),
  /** 0.00-1.00. Onboarding statements are high; passive inferences are low. */
  confidence: decimal("confidence", { precision: 3, scale: 2 }).notNull().default("0.50"),
  /** onboarding | conversation | explicit_correction. */
  source: varchar("source", { length: 24 }).notNull(),
  /** How many separate extractions have supported this fact. */
  evidenceCount: integer("evidence_count").notNull().default(1),
  firstSeenAt: timestamp("first_seen_at", { withTimezone: true }).notNull().defaultNow(),
  lastConfirmedAt: timestamp("last_confirmed_at", { withTimezone: true }).notNull().defaultNow(),
  /**
   * Set when a fact is contradicted or retired. Soft-retire rather than delete
   * so we keep the history of what the assistant used to believe.
   */
  supersededAt: timestamp("superseded_at", { withTimezone: true }),
  supersededReason: text("superseded_reason"),
}, (t) => ({
  uqProfileFact: uniqueIndex("uq_assistant_profile_fact").on(t.workspaceId, t.userId, t.factKey),
  idxProfileFactUser: index("idx_assistant_profile_fact_user").on(t.workspaceId, t.userId),
}));
export type AssistantUserProfileFact = typeof assistantUserProfileFacts.$inferSelect;

/** The rendered profile block injected into the assistant prompt. */
export const assistantUserProfiles = pgTable("assistant_user_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  /** Pre-rendered, token-capped markdown injected verbatim into the prompt. */
  profileText: text("profile_text").notNull().default(""),
  factCount: integer("fact_count").notNull().default(0),
  /** Newest source material folded in — the extractor's incremental watermark. */
  lastSourceAt: timestamp("last_source_at", { withTimezone: true }),
  lastBuiltAt: timestamp("last_built_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({
  uqAssistantUserProfile: uniqueIndex("uq_assistant_user_profile").on(t.workspaceId, t.userId),
}));
export type AssistantUserProfile = typeof assistantUserProfiles.$inferSelect;

// =====================================================================
// Sending Infrastructure — Task #222
// Sell & auto-provision cold-email sending domains + mailboxes via
// a pluggable provider adapter (Maildoso at launch). Clarik wraps the
// provider with a storefront, marked-up billing, and credential ingestion.
// =====================================================================

/** Package catalog — platform configures, tenants choose from. */
export const sendingInfraPackages = pgTable("sending_infra_packages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  purchaseType: varchar("purchase_type", { length: 10 }).notNull().default("lease"),
  domainsCount: integer("domains_count").notNull().default(1),
  mailboxesCount: integer("mailboxes_count").notNull().default(3),
  wholesaleCentsPerDomain: integer("wholesale_cents_per_domain").notNull().default(800),
  wholesaleCentsPerMailbox: integer("wholesale_cents_per_mailbox").notNull().default(300),
  retailCentsPerDomain: integer("retail_cents_per_domain").notNull().default(1400),
  retailCentsPerMailbox: integer("retail_cents_per_mailbox").notNull().default(500),
  domainBillingCycle: varchar("domain_billing_cycle", { length: 10 }).notNull().default("annual"),
  mailboxBillingCycle: varchar("mailbox_billing_cycle", { length: 10 }).notNull().default("monthly"),
  /** 'per_item' = separate annual-domain + monthly-mailbox charges (legacy).
   *  'flat_bundle' = one flat monthly charge for the whole bundle. */
  billingMode: varchar("billing_mode", { length: 15 }).notNull().default("per_item"),
  flatRateMonthlyWholesaleCents: integer("flat_rate_monthly_wholesale_cents").notNull().default(0),
  flatRateMonthlyRetailCents: integer("flat_rate_monthly_retail_cents").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertSendingInfraPackageSchema = createInsertSchema(sendingInfraPackages).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertSendingInfraPackage = z.infer<typeof insertSendingInfraPackageSchema>;
export type SendingInfraPackage = typeof sendingInfraPackages.$inferSelect;

/** Platform-level markup configuration (single canonical row). */
export const sendingInfraMarkupConfig = pgTable("sending_infra_markup_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  defaultMarkupPct: integer("default_markup_pct").notNull().default(40),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by", { length: 255 }),
});
export type SendingInfraMarkupConfig = typeof sendingInfraMarkupConfig.$inferSelect;

/**
 * Capacity audit (Task #396) — append-only log of every capacity-relevant
 * event in the Maildoso cold-sending pipeline: a within-buffer auto-provision,
 * a low-headroom buffer alert, or a capacity gate that blocked a purchase
 * BEFORE the customer was charged. Used by the platform UI ledger and by the
 * monitor / gate to dedup repeat alerts (transition-gating).
 */
export const sendingInfraCapacityAudit = pgTable("sending_infra_capacity_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // 'auto_provision' | 'buffer_alert' | 'capacity_gate_block'
  eventType: varchar("event_type", { length: 30 }).notNull(),
  // 'allowed' | 'blocked' | 'alerted' | 'recovered' | 'unknown_capacity'
  outcome: varchar("outcome", { length: 30 }).notNull(),
  workspaceId: varchar("workspace_id"),
  purchaseId: varchar("purchase_id"),
  domainsRequested: integer("domains_requested").notNull().default(0),
  mailboxesRequested: integer("mailboxes_requested").notNull().default(0),
  // Provider capacity snapshot at the time of the event (null when unknown).
  mailboxesCapacity: integer("mailboxes_capacity"),
  mailboxesUsed: integer("mailboxes_used"),
  mailboxesRemaining: integer("mailboxes_remaining"),
  bufferMailboxes: integer("buffer_mailboxes"),
  providerOrderId: varchar("provider_order_id", { length: 255 }),
  // Free-form extra context (recommendation snapshot, alert recipient, error).
  detail: jsonb("detail"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  eventIdx: index("sending_infra_capacity_audit_event_idx").on(t.eventType),
  createdIdx: index("sending_infra_capacity_audit_created_idx").on(t.createdAt),
}));

export const insertSendingInfraCapacityAuditSchema = createInsertSchema(sendingInfraCapacityAudit).omit({
  id: true, createdAt: true,
});
export type InsertSendingInfraCapacityAudit = z.infer<typeof insertSendingInfraCapacityAuditSchema>;
export type SendingInfraCapacityAudit = typeof sendingInfraCapacityAudit.$inferSelect;

/** Purchase ledger — one row per buy action. */
export const sendingInfraPurchases = pgTable("sending_infra_purchases", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  packageId: varchar("package_id"),
  domainsCount: integer("domains_count").notNull().default(0),
  mailboxesCount: integer("mailboxes_count").notNull().default(0),
  wholesaleCents: integer("wholesale_cents").notNull().default(0),
  retailCents: integer("retail_cents").notNull().default(0),
  marginCents: integer("margin_cents").notNull().default(0),
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
  stripeCheckoutSessionId: varchar("stripe_checkout_session_id", { length: 255 }),
  // Task #590 — the recurring Stripe subscription that actually bills this
  // purchase, captured from `checkout.session.completed` + kept fresh by the
  // subscription lifecycle webhooks. `stripeSubscriptionStatus` mirrors the
  // Stripe status string (active | trialing | past_due | canceled | …). True
  // billed MRR is computed only from purchases whose subscription is currently
  // active (reconciled live against Stripe); everything else contributes $0.
  stripeSubscriptionId: varchar("stripe_subscription_id", { length: 255 }),
  stripeSubscriptionStatus: varchar("stripe_subscription_status", { length: 30 }),
  provisioningStatus: varchar("provisioning_status", { length: 30 }).notNull().default("pending_payment"),
  provider: varchar("provider", { length: 30 }).notNull().default("maildoso"),
  // Task #391 — commercial/management mode for this purchase: 'lease' | 'buy'.
  // (BYOD never creates a purchase row — there is nothing to provision.) This
  // is the source of truth that gets denormalized onto the provisioned items
  // and cold sending domains during ingestion.
  managementMode: varchar("management_mode", { length: 10 }).notNull().default("lease"),
  // Where a PLATFORM-created carousel lives once it is built. Tenant purchases
  // are always owned by the buying workspace, so this only carries meaning for
  // rows owned by the `__platform_cold__` sentinel workspace:
  //   'unassigned'   — sandbox: warming, invisible to tenants (default)
  //   'leasable'     — listed in the tenant lease pool
  //   'platform_use' — reserved for Clarik's own outreach, never listed
  // Assigning a carousel to a specific tenant is expressed by moving
  // `workspaceId` off the sentinel, not by a disposition value.
  poolDisposition: varchar("pool_disposition", { length: 20 }).notNull().default("unassigned"),
  providerOrderId: varchar("provider_order_id", { length: 255 }),
  // Task #397 — deferred-fulfillment (charge-now / deliver-later) support.
  // `provisioningStatus` can be "awaiting_capacity" when a paid order can't be
  // provisioned yet because Maildoso licensed mailbox headroom is short. The
  // resolved domain acquisition choice is stashed here so the deferred poller /
  // restock check can finish provisioning later without the Stripe session.
  // `restockToken` is an unguessable token scoped to this waiting order, used
  // by the admin's tracked restock link.
  provisionSpec: jsonb("provision_spec"),
  restockToken: varchar("restock_token", { length: 64 }),
  provisionedAt: timestamp("provisioned_at"),
  cancelledAt: timestamp("cancelled_at"),
  purchasedByUserId: varchar("purchased_by_user_id", { length: 255 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  wsIdx: index("sending_infra_purchases_ws_idx").on(t.workspaceId),
  statusIdx: index("sending_infra_purchases_status_idx").on(t.provisioningStatus),
}));

export const insertSendingInfraPurchaseSchema = createInsertSchema(sendingInfraPurchases).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertSendingInfraPurchase = z.infer<typeof insertSendingInfraPurchaseSchema>;
export type SendingInfraPurchase = typeof sendingInfraPurchases.$inferSelect;

/** Per-item tracking — one row per provisioned domain or mailbox. */
export const sendingInfraProvisionedItems = pgTable("sending_infra_provisioned_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  purchaseId: varchar("purchase_id").notNull(),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  itemType: varchar("item_type", { length: 10 }).notNull(), // 'domain' | 'mailbox'
  identifier: varchar("identifier", { length: 255 }).notNull(),
  provider: varchar("provider", { length: 30 }).notNull().default("maildoso"),
  // Task #391 — management mode denormalized from the parent purchase:
  // 'lease' | 'buy' | 'byod'. Per-mailbox gating (pause-on-reply, warmup, IP
  // rotation, managed reply forwarding) reads this directly. A BYOD-linked
  // mailbox carries 'byod' and is excluded from all managed features.
  managementMode: varchar("management_mode", { length: 10 }).notNull().default("lease"),
  providerItemId: varchar("provider_item_id", { length: 255 }),
  emailSendingDomainId: varchar("email_sending_domain_id", { length: 255 }),
  status: varchar("status", { length: 20 }).notNull().default("provisioning"),
  health: varchar("health", { length: 20 }),
  smtpHost: varchar("smtp_host", { length: 255 }),
  smtpPort: integer("smtp_port"),
  smtpUser: varchar("smtp_user", { length: 255 }),
  smtpPasswordEncrypted: text("smtp_password_encrypted"),
  // IMAP coordinates for inbound reply polling (Task #385). The IMAP user is
  // the same as smtpUser (the mailbox address) and the IMAP password is the
  // same secret as smtpPasswordEncrypted, so we only persist host/port here.
  // imapLastUid / imapUidValidity / imapLastPolledAt are the per-mailbox
  // high-water mark so the poller only ingests messages it hasn't seen.
  imapHost: varchar("imap_host", { length: 255 }),
  imapPort: integer("imap_port"),
  imapLastUid: integer("imap_last_uid"),
  imapUidValidity: bigint("imap_uid_validity", { mode: "number" }),
  imapLastPolledAt: timestamp("imap_last_polled_at"),
  imapLastError: text("imap_last_error"),
  warmupStartDate: timestamp("warmup_start_date"),
  providerMetadata: jsonb("provider_metadata"),
  replacementRequestedAt: timestamp("replacement_requested_at"),
  replacedByItemId: varchar("replaced_by_item_id", { length: 255 }),
  // Task #407 — lease-reclaim lifecycle. Orthogonal to `status` (the provisioning
  // state). A mailbox stays status='live' through 'grace' so trailing IMAP replies
  // keep flowing; `lifecycleState` tracks the ownership/reclaim journey:
  //   active            — in active service for its current workspace
  //   grace             — lease churned; sending disabled, mailbox alive at provider
  //                       for a 30-day trailing-reply + reactivation window
  //   reclaiming        — claimed by the grace sweeper, provider delete in flight
  //   reclaimed         — mailbox deleted at provider (slot returned at zero cost)
  //   available         — (domain only) healthy + returned to the warm/premium pool
  //   retired           — (domain only) failed health check; not reusable
  //   pending_transfer_out — buy/portableOnExit churn; awaiting manual provider handoff
  //   transferred_out   — ownership transfer completed (excluded from recycle pool)
  lifecycleState: varchar("lifecycle_state", { length: 30 }).notNull().default("active"),
  churnedAt: timestamp("churned_at"),
  graceEndsAt: timestamp("grace_ends_at"),
  reclaimedAt: timestamp("reclaimed_at"),
  // Domain warm/premium pool flags. A warm-pool domain is a healthy, already-aged
  // sending domain ready to be reused by a fresh tenant order (fresh mailboxes).
  warmPool: boolean("warm_pool").notNull().default(false),
  premium: boolean("premium").notNull().default(false),
  transferOutRequestedAt: timestamp("transfer_out_requested_at"),
  // Set when a pooled item is reassigned to a new workspace on reuse — keeps the
  // provenance chain for the audit trail.
  previousWorkspaceId: varchar("previous_workspace_id", { length: 255 }),
  // ── Seasoning gate (migration 0191) ─────────────────────────────────────────
  // A mailbox must pass placement tests before being admitted to the cold-send
  // pool. `coldCapable` is the gate bit: false = seasoning in progress (no real
  // cold sends); true = cleared for cold sends. Existing live mailboxes are
  // grandfathered as coldCapable=true by the backfill in migration 0191.
  coldCapable: boolean("cold_capable").notNull().default(false),
  seasoningStartedAt: timestamp("seasoning_started_at"),
  seasoningPassedAt: timestamp("seasoning_passed_at"),
  dnsVerifiedAt: timestamp("dns_verified_at"),
  placementSentCount: integer("placement_sent_count").notNull().default(0),
  // ── Instantly.ai warmup tracking (migration 0201) ─────────────────────────
  // When a mailbox is enrolled in the Instantly warmup network, we record the
  // email address we registered (same as `identifier`/`smtpUser`, stored here
  // as a non-null sentinel so NULL = not yet enrolled) and the enrollment
  // timestamp. The poller writes health_score (0-100) back on every sweep so
  // the platform pool view can show warming progress.
  instantlyEmail: varchar("instantly_email", { length: 255 }),
  warmupEnrolledAt: timestamp("warmup_enrolled_at"),
  warmupHealthScore: integer("warmup_health_score"),
  warmupHealthCheckedAt: timestamp("warmup_health_checked_at"),
  warmupSent: integer("warmup_sent"),
  warmupLandedInbox: integer("warmup_landed_inbox"),
  warmupLandedSpam: integer("warmup_landed_spam"),
  warmupReceived: integer("warmup_received"),
  // Last Instantly enrollment attempt error (null = no error / not yet attempted).
  // Written on every addAccount/enableWarmup failure; cleared to null on success.
  // Queryable so "did my mailboxes enroll?" has an answer without grepping logs.
  warmupEnrollmentError: text("warmup_enrollment_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  purchaseIdx: index("sending_infra_items_purchase_idx").on(t.purchaseId),
  wsIdx: index("sending_infra_items_ws_idx").on(t.workspaceId),
  typeStatusIdx: index("sending_infra_items_type_status_idx").on(t.itemType, t.status),
  lifecycleIdx: index("sending_infra_items_lifecycle_idx").on(t.lifecycleState),
  warmPoolIdx: index("sending_infra_items_warm_pool_idx").on(t.warmPool),
  coldCapableIdx: index("sending_infra_items_cold_capable_idx").on(t.coldCapable),
  instantlyEmailIdx: index("sending_infra_items_instantly_email_idx").on(t.instantlyEmail),
}));

export const insertSendingInfraProvisionedItemSchema = createInsertSchema(sendingInfraProvisionedItems).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertSendingInfraProvisionedItem = z.infer<typeof insertSendingInfraProvisionedItemSchema>;
export type SendingInfraProvisionedItem = typeof sendingInfraProvisionedItems.$inferSelect;

/**
 * Lease-reclaim lifecycle audit — Task #407.
 *
 * One row per state transition of a provisioned item (churn → grace → reclaimed →
 * warm pool → reuse, plus buy/transfer-out and lease→buy conversions). Captures
 * the trigger, the provider-side outcome, and free-form detail so the platform
 * admin can reconstruct exactly what happened to every domain/mailbox and prove
 * provider spend was returned. `workspaceId` is the workspace at the time of the
 * transition (it changes on reuse), so audit rows are intentionally NOT cascade-
 * deleted with the workspace — they reference no FK.
 */
export const sendingInfraLifecycleAudit = pgTable("sending_infra_lifecycle_audit", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  itemId: varchar("item_id", { length: 255 }).notNull(),
  workspaceId: varchar("workspace_id", { length: 255 }),
  purchaseId: varchar("purchase_id", { length: 255 }),
  itemType: varchar("item_type", { length: 10 }),
  identifier: varchar("identifier", { length: 255 }),
  providerItemId: varchar("provider_item_id", { length: 255 }),
  fromState: varchar("from_state", { length: 30 }),
  toState: varchar("to_state", { length: 30 }).notNull(),
  // 'churn_webhook' | 'manual_cancel_tenant' | 'manual_cancel_platform' |
  // 'grace_sweeper' | 'domain_health_check' | 'reuse' | 'lease_to_buy' |
  // 'transfer_out' | 'hard_delete'
  trigger: varchar("trigger", { length: 40 }).notNull(),
  // 'ok' | 'failed' | 'skipped'
  outcome: varchar("outcome", { length: 20 }).notNull().default("ok"),
  detail: jsonb("detail"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  itemIdx: index("sending_infra_lifecycle_audit_item_idx").on(t.itemId),
  wsIdx: index("sending_infra_lifecycle_audit_ws_idx").on(t.workspaceId),
  createdIdx: index("sending_infra_lifecycle_audit_created_idx").on(t.createdAt),
}));

export const insertSendingInfraLifecycleAuditSchema = createInsertSchema(sendingInfraLifecycleAudit).omit({
  id: true, createdAt: true,
});
export type InsertSendingInfraLifecycleAudit = z.infer<typeof insertSendingInfraLifecycleAuditSchema>;
export type SendingInfraLifecycleAudit = typeof sendingInfraLifecycleAudit.$inferSelect;

/**
 * Placement tests — migration 0191.
 * One row per probe email sent to a seed address during mailbox seasoning.
 * The placement-test scheduler writes these rows; the platform admin UI reads
 * them to show warming progress per mailbox.
 */
export const placementTests = pgTable("placement_tests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // No FK — sending_infra_provisioned_items may be partitioned; integrity at app layer.
  itemId: varchar("item_id").notNull(),
  workspaceId: varchar("workspace_id"),
  testEmailAddress: varchar("test_email_address"),
  sentAt: timestamp("sent_at"),
  result: varchar("result", { length: 20 }).notNull().default("pending"),
  checkedAt: timestamp("checked_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (t) => ({
  itemIdx: index("placement_tests_item_idx").on(t.itemId),
  sentAtIdx: index("placement_tests_sent_at_idx").on(t.sentAt),
}));

export const insertPlacementTestSchema = createInsertSchema(placementTests).omit({ id: true, createdAt: true });
export type InsertPlacementTest = z.infer<typeof insertPlacementTestSchema>;
export type PlacementTest = typeof placementTests.$inferSelect;

/**
 * Cold-domain manual-fulfillment requests — Task #239 (legacy fallback).
 *
 * The Task #222 Maildoso auto-provision + Stripe checkout path is now the LIVE
 * default for cold-email setup. This table backs an optional fallback flow: a
 * tenant submits a lightweight "request cold domains" form and a super admin
 * fulfills it manually in the Maildoso dashboard. It is deliberately separate
 * from `sending_infra_purchases` so the auto-provision ledger is never touched
 * — no Stripe charge, no provider order is created by this flow.
 */
export const coldDomainRequests = pgTable("cold_domain_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  requestedDomains: integer("requested_domains").notNull().default(1),
  requestedMailboxes: integer("requested_mailboxes").notNull().default(3),
  // Snapshot of the estimated monthly resale cost shown to the tenant at
  // submit time (bigint-cents) so the super admin sees what was quoted.
  estimatedMonthlyCents: integer("estimated_monthly_cents").notNull().default(0),
  notes: text("notes"),
  // Task #391 — which management mode the tenant requested: 'lease' | 'buy'.
  // Recorded so the super admin fulfilling the request knows whether the
  // tenant is leasing or buying the cold infrastructure.
  managementMode: varchar("management_mode", { length: 10 }).notNull().default("lease"),
  // 'pending' | 'handled'
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  requestedByUserId: varchar("requested_by_user_id", { length: 255 }),
  handledByUserId: varchar("handled_by_user_id", { length: 255 }),
  handledAt: timestamp("handled_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  wsIdx: index("cold_domain_requests_ws_idx").on(t.workspaceId),
  statusIdx: index("cold_domain_requests_status_idx").on(t.status),
}));

export const insertColdDomainRequestSchema = createInsertSchema(coldDomainRequests).omit({
  id: true, createdAt: true, updatedAt: true, handledByUserId: true, handledAt: true,
});
export type InsertColdDomainRequest = z.infer<typeof insertColdDomainRequestSchema>;
export type ColdDomainRequest = typeof coldDomainRequests.$inferSelect;

// ── Platform Office Hours (Task #335) ───────────────────────────────────────
// Singleton row that gates ALL automated platform outbound — drip steps and
// mass email blasts. Manual one-off sends from the Email Hub always bypass.
// Fail-open: no row or enabled=false → always open.
export const platformOfficeHours = pgTable("platform_office_hours", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  enabled: boolean("enabled").notNull().default(false),
  daysOfWeek: integer("days_of_week").array().notNull().default(sql`ARRAY[1,2,3,4,5]::integer[]`),
  startLocal: varchar("start_local", { length: 5 }).notNull().default("09:00"),
  endLocal: varchar("end_local", { length: 5 }).notNull().default("17:00"),
  timezone: varchar("timezone", { length: 64 }).notNull().default("America/Chicago"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export const insertPlatformOfficeHoursSchema = createInsertSchema(platformOfficeHours).omit({
  updatedAt: true,
});
export type InsertPlatformOfficeHours = z.infer<typeof insertPlatformOfficeHoursSchema>;
export type PlatformOfficeHours = typeof platformOfficeHours.$inferSelect;

// Durable audit log of every automated send deferred by the platform envelope.
export const platformOfficeHoursDeferrals = pgTable("platform_office_hours_deferrals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  scheduler: varchar("scheduler", { length: 64 }).notNull(),
  itemKind: varchar("item_kind", { length: 64 }).notNull(),
  itemId: varchar("item_id", { length: 128 }).notNull(),
  nextOpenAt: timestamp("next_open_at").notNull(),
  deferredAt: timestamp("deferred_at").notNull().defaultNow(),
}, (t) => ({
  nextOpenIdx: index("platform_oh_deferrals_next_open_idx").on(t.nextOpenAt),
}));
export type PlatformOfficeHoursDeferral = typeof platformOfficeHoursDeferrals.$inferSelect;

// Per-user (or shared/workspace-wide) IP allow-list entries.
// userId = null → shared entry (applies to everyone in the workspace).
// userId = <id>  → entry only permits that specific user from that CIDR.
// Replaces reliance on the flat workspaces.ip_allowlist_cidrs array as the
// CRUD source of truth; the master toggle (ipAllowlistEnabled) stays on the
// workspaces row. Existing CIDRs are migrated as shared entries on first deploy.
export const workspaceIpAllowlistEntries = pgTable("workspace_ip_allowlist_entries", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: text("workspace_id").notNull(),
  cidr: text("cidr").notNull(),
  userId: text("user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  wsIdx: index("workspace_ip_allowlist_entries_ws_idx").on(t.workspaceId),
  userIdx: index("workspace_ip_allowlist_entries_user_idx").on(t.workspaceId, t.userId),
}));
export type WorkspaceIpAllowlistEntry = typeof workspaceIpAllowlistEntries.$inferSelect;

// ── Platform Credit Requests (Task #288) ─────────────────────────────────────
// Allows non-super-admin platform users to request credits from super admins.
// Super admins can also toggle per-user auto-approve so future requests bypass
// the queue and award immediately.
export const platformCreditRequests = pgTable("platform_credit_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  requesterPlatformUserId: varchar("requester_platform_user_id").notNull().references(() => platformUsers.id, { onDelete: "cascade" }),
  creditType: varchar("credit_type", { length: 10 }).notNull().$type<"ai" | "lead">(),
  amount: integer("amount").notNull(),
  reason: text("reason"),
  status: varchar("status", { length: 16 }).notNull().default("pending").$type<"pending" | "approved" | "denied">(),
  reviewerPlatformUserId: varchar("reviewer_platform_user_id").references(() => platformUsers.id),
  reviewerNote: text("reviewer_note"),
  autoApproved: boolean("auto_approved").notNull().default(false),
  seenByRequester: boolean("seen_by_requester").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  resolvedAt: timestamp("resolved_at"),
}, (t) => ({
  requesterIdx: index("platform_credit_requests_requester_idx").on(t.requesterPlatformUserId),
  statusIdx: index("platform_credit_requests_status_idx").on(t.status),
}));
export type PlatformCreditRequest = typeof platformCreditRequests.$inferSelect;

// Per-requester auto-approve toggle. When enabled, requests from this user
// skip the queue and award immediately (no super-admin action needed).
export const platformCreditRequestAutoApprove = pgTable("platform_credit_request_auto_approve", {
  platformUserId: varchar("platform_user_id").primaryKey().references(() => platformUsers.id, { onDelete: "cascade" }),
  enabled: boolean("enabled").notNull().default(false),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});
export type PlatformCreditRequestAutoApprove = typeof platformCreditRequestAutoApprove.$inferSelect;

// ──────────────────────────────────────────────────────────────────────────────
// Internal Messenger (iMessage-style unified threads with SMS fallback)
//
// A closed, in-app messaging surface that lives as a "Messages" tab inside the
// Inbox. One thread per person (or group). Delivery is unified: if the recipient
// is a Clarik user who has messaging ENABLED, the message is delivered free over
// the internal "internal" channel; otherwise it auto-falls-back to "sms" via the
// existing Twilio pipeline (sms_message_logs). Per-user opt-in, default OFF.
// Permission model is OPEN within a workspace.
// ──────────────────────────────────────────────────────────────────────────────

// Per-user, per-workspace messenger settings. Default OFF: a user is NOT
// reachable on the internal channel until they enable messaging here.
export const messengerUserSettings = pgTable("messenger_user_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  // Master opt-in. When false, the user is not reachable internally and the
  // Messages tab shows an enable prompt.
  enabled: boolean("enabled").notNull().default(false),
  // When a 1:1 recipient is unreachable internally, allow Twilio SMS fallback.
  smsFallbackEnabled: boolean("sms_fallback_enabled").notNull().default(true),
  // In-app/notification-hub ping on new inbound message.
  notifyOnNewMessage: boolean("notify_on_new_message").notNull().default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  workspaceUserUniqueIdx: uniqueIndex("messenger_user_settings_ws_user_idx").on(t.workspaceId, t.userId),
}));

export const insertMessengerUserSettingsSchema = createInsertSchema(messengerUserSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertMessengerUserSettings = z.infer<typeof insertMessengerUserSettingsSchema>;
export type MessengerUserSettings = typeof messengerUserSettings.$inferSelect;

// A conversation: a 1:1 DM or a named group.
export const messengerConversations = pgTable("messenger_conversations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  kind: varchar("kind", { length: 10 }).notNull().default("dm"), // dm | group
  title: text("title"), // group name; null for DMs (derived from the other participant)
  createdByUserId: varchar("created_by_user_id"),
  // Denormalized for cheap conversation-list sorting + preview.
  lastMessageAt: timestamp("last_message_at"),
  lastMessagePreview: text("last_message_preview"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  workspaceLastMsgIdx: index("messenger_conversations_ws_last_msg_idx").on(t.workspaceId, t.lastMessageAt),
}));

export const insertMessengerConversationSchema = createInsertSchema(messengerConversations).omit({
  id: true,
  createdAt: true,
  lastMessageAt: true,
  lastMessagePreview: true,
});
export type InsertMessengerConversation = z.infer<typeof insertMessengerConversationSchema>;
export type MessengerConversation = typeof messengerConversations.$inferSelect;

// A participant (a "person handle") in a conversation. A participant may be a
// Clarik user (userId set) and/or an SMS-reachable contact (phoneE164 set). The
// optional talentId records that this person is shown as a talent/collaborator.
export const messengerParticipants = pgTable("messenger_participants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  conversationId: varchar("conversation_id").notNull().references(() => messengerConversations.id, { onDelete: "cascade" }),
  userId: varchar("user_id"),                     // set when the participant is a Clarik user
  phoneE164: varchar("phone_e164", { length: 20 }), // set when SMS-reachable (fallback target / inbound match)
  talentId: varchar("talent_id"),                 // representation label: this person is/represents a talent
  displayName: text("display_name"),              // cached label (esp. for SMS-only contacts)
  // Read + state tracking (per participant).
  lastReadAt: timestamp("last_read_at"),
  mutedAt: timestamp("muted_at"),
  archivedAt: timestamp("archived_at"),
  leftAt: timestamp("left_at"),                   // for group membership changes
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  conversationIdx: index("messenger_participants_conversation_idx").on(t.conversationId),
  userIdx: index("messenger_participants_ws_user_idx").on(t.workspaceId, t.userId),
  phoneIdx: index("messenger_participants_ws_phone_idx").on(t.workspaceId, t.phoneE164),
}));

export const insertMessengerParticipantSchema = createInsertSchema(messengerParticipants).omit({
  id: true,
  createdAt: true,
});
export type InsertMessengerParticipant = z.infer<typeof insertMessengerParticipantSchema>;
export type MessengerParticipant = typeof messengerParticipants.$inferSelect;

// A single message within a conversation. channel records how it was actually
// delivered/received: "internal" (free) or "sms" (Twilio). For SMS messages,
// smsLogId links to the canonical sms_message_logs row.
export const messengerMessages = pgTable("messenger_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  conversationId: varchar("conversation_id").notNull().references(() => messengerConversations.id, { onDelete: "cascade" }),
  senderUserId: varchar("sender_user_id"),        // null for inbound SMS from a non-user contact
  senderParticipantId: varchar("sender_participant_id"), // which participant sent it
  channel: varchar("channel", { length: 10 }).notNull().default("internal"), // internal | sms
  body: text("body").notNull().default(""),
  // Array of { objectPath, name, contentType, size } uploaded via object storage.
  attachments: jsonb("attachments"),
  smsLogId: varchar("sms_log_id"),                // link to sms_message_logs for sms channel
  // Mainly meaningful for the sms channel: pending|sent|delivered|failed.
  deliveryStatus: varchar("delivery_status", { length: 20 }).notNull().default("sent"),
  failureReason: text("failure_reason"),
  editedAt: timestamp("edited_at"),
  deletedAt: timestamp("deleted_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  conversationCreatedIdx: index("messenger_messages_conversation_created_idx").on(t.conversationId, t.createdAt),
}));

export const insertMessengerMessageSchema = createInsertSchema(messengerMessages).omit({
  id: true,
  createdAt: true,
  editedAt: true,
  deletedAt: true,
});
export type InsertMessengerMessage = z.infer<typeof insertMessengerMessageSchema>;
export type MessengerMessage = typeof messengerMessages.$inferSelect;

// Per-recipient delivery tracking for group messages (M4).
// For DMs the message-level channel/deliveryStatus is sufficient; for groups
// each non-sender participant gets one row so we can track internal vs SMS
// fan-out independently per person.
export const messengerMessageDeliveries = pgTable("messenger_message_deliveries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  messageId: varchar("message_id").notNull().references(() => messengerMessages.id, { onDelete: "cascade" }),
  participantId: varchar("participant_id").notNull().references(() => messengerParticipants.id, { onDelete: "cascade" }),
  channel: varchar("channel", { length: 10 }).notNull().default("internal"), // internal | sms
  phoneE164: varchar("phone_e164", { length: 20 }),
  deliveryStatus: varchar("delivery_status", { length: 20 }).notNull().default("sent"), // pending|sent|delivered|failed
  smsLogId: varchar("sms_log_id"),
  failureReason: text("failure_reason"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  messageIdx: index("messenger_deliveries_message_idx").on(t.messageId),
  participantIdx: index("messenger_deliveries_participant_idx").on(t.participantId),
}));

export const insertMessengerMessageDeliverySchema = createInsertSchema(messengerMessageDeliveries).omit({
  id: true,
  createdAt: true,
});
export type InsertMessengerMessageDelivery = z.infer<typeof insertMessengerMessageDeliverySchema>;
export type MessengerMessageDelivery = typeof messengerMessageDeliveries.$inferSelect;

// ============================================================================
// FINANCIAL AUDIT LOG — immutable ledger of every financial record change
// ============================================================================
// `audit_log_append_only` (migration 0022) blocks UPDATE and gates
// DELETE behind `SET LOCAL app.allow_audit_purge`; retention floor is
// 365d on `created_at`. See docs/security/audit-log-policy.md.
//
// ─── Lead & Client Audit Log ──────────────────────────────────────────────────
// Immutable append-only ledgers for every lead and client record change.
// Actor fields are denormalised so rows stay readable after account deletion.

export type LeadAuditAction =
  | "lead_created"
  | "lead_updated"
  | "lead_status_changed"
  | "lead_booked"
  | "lead_deleted"
  | "lead_restored";

export const leadAuditLog = pgTable("lead_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  entityId: varchar("entity_id").notNull(),
  action: varchar("action", { length: 50 }).notNull().$type<LeadAuditAction>(),
  actorUserId: varchar("actor_user_id"),
  actorEmail: varchar("actor_email", { length: 255 }),
  actorType: varchar("actor_type", { length: 20 }).notNull().default("system").$type<"user" | "system" | "platform_admin">(),
  entitySnapshot: jsonb("entity_snapshot"),
  changedFields: jsonb("changed_fields"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  workspaceCreatedIdx: index("lead_audit_log_ws_created_idx").on(t.workspaceId, t.createdAt),
  entityIdx: index("lead_audit_log_entity_idx").on(t.entityId, t.createdAt),
  actionIdx: index("lead_audit_log_action_idx").on(t.workspaceId, t.action),
}));

export const insertLeadAuditLogSchema = createInsertSchema(leadAuditLog).omit({ id: true, createdAt: true });
export type InsertLeadAuditLog = z.infer<typeof insertLeadAuditLogSchema>;
export type LeadAuditLogRow = typeof leadAuditLog.$inferSelect;

export type ClientAuditAction =
  | "client_created"
  | "client_updated"
  | "client_deleted"
  | "client_restored";

export const clientAuditLog = pgTable("client_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  entityId: varchar("entity_id").notNull(),
  action: varchar("action", { length: 50 }).notNull().$type<ClientAuditAction>(),
  actorUserId: varchar("actor_user_id"),
  actorEmail: varchar("actor_email", { length: 255 }),
  actorType: varchar("actor_type", { length: 20 }).notNull().default("system").$type<"user" | "system" | "platform_admin">(),
  entitySnapshot: jsonb("entity_snapshot"),
  changedFields: jsonb("changed_fields"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  workspaceCreatedIdx: index("client_audit_log_ws_created_idx").on(t.workspaceId, t.createdAt),
  entityIdx: index("client_audit_log_entity_idx").on(t.entityId, t.createdAt),
  actionIdx: index("client_audit_log_action_idx").on(t.workspaceId, t.action),
}));

export const insertClientAuditLogSchema = createInsertSchema(clientAuditLog).omit({ id: true, createdAt: true });
export type InsertClientAuditLog = z.infer<typeof insertClientAuditLogSchema>;
export type ClientAuditLogRow = typeof clientAuditLog.$inferSelect;

// ─── Financial Audit Log ──────────────────────────────────────────────────────
// Actor fields are denormalised (stored by value, not FK) so audit rows
// remain readable even if the user account or workspace is later deleted.

export type FinancialAuditEntityType = "invoice" | "deposit" | "payment";

export type FinancialAuditAction =
  | "invoice_created"
  | "invoice_updated"
  | "invoice_deleted"
  | "invoice_paid"
  | "invoice_voided"
  | "invoice_cancelled"
  | "deposit_recorded"
  | "deposit_updated"
  | "balance_paid";

export const financialAuditLog = pgTable("financial_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  entityType: varchar("entity_type", { length: 30 }).notNull().$type<FinancialAuditEntityType>(),
  entityId: varchar("entity_id").notNull(),
  action: varchar("action", { length: 50 }).notNull().$type<FinancialAuditAction>(),
  actorUserId: varchar("actor_user_id"),
  actorEmail: varchar("actor_email", { length: 255 }),
  actorType: varchar("actor_type", { length: 20 }).notNull().default("system").$type<"user" | "system" | "platform_admin">(),
  entitySnapshot: jsonb("entity_snapshot"),
  changedFields: jsonb("changed_fields"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  workspaceCreatedIdx: index("financial_audit_log_ws_created_idx").on(t.workspaceId, t.createdAt),
  entityIdx: index("financial_audit_log_entity_idx").on(t.entityId, t.createdAt),
  actionIdx: index("financial_audit_log_action_idx").on(t.workspaceId, t.action),
}));

export const insertFinancialAuditLogSchema = createInsertSchema(financialAuditLog).omit({ id: true, createdAt: true });
export type InsertFinancialAuditLog = z.infer<typeof insertFinancialAuditLogSchema>;
export type FinancialAuditLogRow = typeof financialAuditLog.$inferSelect;

// ─── Timecard (Task #557) ─────────────────────────────────────────────────────
// Tenant-workspace feature. Staff can clock in/out; managers review hours.
// Day values are workspace-timezone calendar dates (never UTC-shifted).
// Instants (clockInAt, clockOutAt, lastActivityAt) are always UTC.

export const timecardUserSettings = pgTable("timecard_user_settings", {
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  enabled: boolean("enabled").notNull().default(false),
  discrepancyThresholdPct: integer("discrepancy_threshold_pct").notNull().default(25),
  discrepancyThresholdMinutes: integer("discrepancy_threshold_minutes").notNull().default(30),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  pk: primaryKey({ columns: [t.workspaceId, t.userId] }),
}));

export const insertTimecardUserSettingsSchema = createInsertSchema(timecardUserSettings).omit({ createdAt: true, updatedAt: true });
export type InsertTimecardUserSettings = z.infer<typeof insertTimecardUserSettingsSchema>;
export type TimecardUserSettings = typeof timecardUserSettings.$inferSelect;

export const TIMECARD_CLOCK_OUT_SOURCES = [
  "manual",
  "tab_close",
  "idle_timeout",
  "midnight",
  "manager_edit",
  "forgot_clockout",
] as const;
export type TimecardClockOutSource = (typeof TIMECARD_CLOCK_OUT_SOURCES)[number];

export const timecardShifts = pgTable("timecard_shifts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull().references(() => workspaces.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull(),
  workDate: date("work_date").notNull(),
  clockInAt: timestamp("clock_in_at", { withTimezone: true }).notNull(),
  clockOutAt: timestamp("clock_out_at", { withTimezone: true }),
  clockOutSource: varchar("clock_out_source", { length: 32 }).$type<TimecardClockOutSource>(),
  lastActivityAt: timestamp("last_activity_at", { withTimezone: true }),
  managerNote: text("manager_note"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  workspaceUserDateIdx: index("timecard_shifts_ws_user_date_idx").on(t.workspaceId, t.userId, t.workDate),
}));

export const insertTimecardShiftSchema = createInsertSchema(timecardShifts).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTimecardShift = z.infer<typeof insertTimecardShiftSchema>;
export type TimecardShift = typeof timecardShifts.$inferSelect;

export const timecardEditLog = pgTable("timecard_edit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  shiftId: varchar("shift_id").notNull(),
  actorUserId: varchar("actor_user_id").notNull(),
  action: varchar("action", { length: 64 }).notNull(),
  previousData: jsonb("previous_data"),
  newData: jsonb("new_data"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  workspaceShiftIdx: index("timecard_edit_log_ws_shift_idx").on(t.workspaceId, t.shiftId),
}));

export const insertTimecardEditLogSchema = createInsertSchema(timecardEditLog).omit({ id: true, createdAt: true });
export type InsertTimecardEditLog = z.infer<typeof insertTimecardEditLogSchema>;
export type TimecardEditLog = typeof timecardEditLog.$inferSelect;

export const TIMECARD_FLAG_TYPES = ["discrepancy", "forgot_clockout_soft_note"] as const;
export type TimecardFlagType = (typeof TIMECARD_FLAG_TYPES)[number];

export const TIMECARD_FLAG_STATUSES = ["open", "resolved"] as const;
export type TimecardFlagStatus = (typeof TIMECARD_FLAG_STATUSES)[number];

export const timecardFlags = pgTable("timecard_flags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  shiftId: varchar("shift_id"),
  workDate: date("work_date").notNull(),
  flagType: varchar("flag_type", { length: 32 }).notNull().$type<TimecardFlagType>(),
  status: varchar("status", { length: 16 }).notNull().default("open").$type<TimecardFlagStatus>(),
  clockedMinutes: integer("clocked_minutes"),
  engagedMinutes: integer("engaged_minutes"),
  note: text("note"),
  resolvedAt: timestamp("resolved_at"),
  resolvedByUserId: varchar("resolved_by_user_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  workspaceUserIdx: index("timecard_flags_ws_user_idx").on(t.workspaceId, t.userId),
  openIdx: index("timecard_flags_open_idx").on(t.workspaceId, t.status),
}));

export const insertTimecardFlagSchema = createInsertSchema(timecardFlags).omit({ id: true, createdAt: true });
export type InsertTimecardFlag = z.infer<typeof insertTimecardFlagSchema>;
export type TimecardFlag = typeof timecardFlags.$inferSelect;
